import * as i0 from '@angular/core';
import { Input, Directive, EventEmitter, Output, Component, HostBinding, Injectable, forwardRef, HostListener, ViewChild, Pipe, NgModule, InjectionToken, Inject, Optional, ViewChildren, ChangeDetectionStrategy, Injector, ViewContainerRef, ComponentRef, ElementRef, TemplateRef, ContentChild, QueryList, ContentChildren, createComponent } from '@angular/core';
import * as i1 from '@angular/common';
import { CommonModule, DatePipe, DOCUMENT } from '@angular/common';
import * as i2 from '@angular/forms';
import { UntypedFormGroup, Validators, UntypedFormControl, NG_VALUE_ACCESSOR, NgControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subscription, Subject, of, fromEvent, BehaviorSubject, merge, Observable } from 'rxjs';
import { debounceTime, takeUntil, distinctUntilChanged, filter, scan, map } from 'rxjs/operators';
import * as i1$1 from '@angular/platform-browser';
import * as i1$2 from '@ngx-translate/core';
import { TranslateModule } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PhoneNumberUtil, PhoneNumberFormat } from 'google-libphonenumber';
import { trigger, transition, style, animate } from '@angular/animations';

var ButtonAlignment;
(function (ButtonAlignment) {
    ButtonAlignment["Left"] = "Left";
    ButtonAlignment["Center"] = "Center";
})(ButtonAlignment || (ButtonAlignment = {}));

var BtnClassName;
(function (BtnClassName) {
    BtnClassName["VpgPrimaryDefault"] = "vpg-p-btn";
    BtnClassName["VpgSecondaryDefault"] = "vpg-s-btn";
    BtnClassName["VpgTertiaryDefault"] = "vpg-t-btn";
    BtnClassName["VpgDangerDefault"] = "vpg-d-btn";
    BtnClassName["VpgActionDefaultButton"] = "vpg-a-btn";
    BtnClassName["VpgPrimaryIconBtn"] = "vpg-primary-icon-btn";
    BtnClassName["VpgSecondaryIconBtn"] = "vpg-secondary-icon-btn";
    BtnClassName["VpgSecondaryIconBtnCompleted"] = "vpg-secondary-icon-btn-completed";
    BtnClassName["VpgTertiaryIconBtn"] = "vpg-tertiary-icon-btn";
    BtnClassName["VpgDangerIconBtn"] = "vpg-danger-icon-btn";
    BtnClassName["VpgActionIconButton"] = "vpg-action-icon-btn";
})(BtnClassName || (BtnClassName = {}));

var ButtonSize;
(function (ButtonSize) {
    ButtonSize["Large"] = "Large";
    ButtonSize["Medium"] = "Medium";
    ButtonSize["Small"] = "Small";
})(ButtonSize || (ButtonSize = {}));

var ButtonType;
(function (ButtonType) {
    ButtonType["Primary"] = "primary";
    ButtonType["Secondary"] = "secondary";
    ButtonType["Tertiary"] = "tertiary";
    ButtonType["Danger"] = "danger";
    ButtonType["Action"] = "action";
    ButtonType["IconPrimary"] = "icon-primary";
    ButtonType["IconSecondary"] = "icon-secondary";
    ButtonType["IconTertiary"] = "icon-tertiary";
    ButtonType["IconDanger"] = "icon-danger";
    ButtonType["IconAction"] = "icon-action";
})(ButtonType || (ButtonType = {}));

var IconClassName;
(function (IconClassName) {
    IconClassName["NoIcon"] = "";
    IconClassName["Edit"] = "fa-edit";
    IconClassName["InfoCircle"] = "fa-info-circle";
    IconClassName["EllipsisVertical"] = "fa-ellipsis-v";
    IconClassName["EllipsisHorizontal"] = "fa-ellipsis-h";
    IconClassName["Search"] = "fa-search";
    IconClassName["Trophy"] = "fa-trophy";
    IconClassName["ShoppingCart"] = "fa-shopping-cart";
    IconClassName["Building"] = "fa-building";
    IconClassName["CalendarAlt"] = "fa-calendar";
    IconClassName["Calendar"] = "fa-calendar-o";
    IconClassName["MapMarkerAlt"] = "fa-map-marker";
    IconClassName["Hearth"] = "fa-heart";
    IconClassName["Stethoscope"] = "fa-stethoscope";
    IconClassName["CalendarCheck"] = "fa-calendar-check-o";
    IconClassName["LockAlt"] = "fa-lock";
    IconClassName["HandPaper"] = "fa-hand-paper-o";
    IconClassName["Check"] = "fa-check";
    IconClassName["Times"] = "fa-times";
    IconClassName["Star"] = "fa-star";
    IconClassName["Eye"] = "fa-eye";
    IconClassName["EyeSlash"] = "fa-eye-slash";
    IconClassName["AngleDown"] = "fa-angle-down";
    IconClassName["AngleUp"] = "fa-angle-up";
    IconClassName["LeftArrow"] = "fa-arrow-left";
    IconClassName["Plus"] = "fa-plus";
    IconClassName["AngleLeft"] = "fa-angle-left";
    IconClassName["AngleDoubleLeft"] = "fa-angle-double-left";
    IconClassName["AngleRight"] = "fa-angle-right";
    IconClassName["AngleDoubleRight"] = "fa-angle-double-right";
})(IconClassName || (IconClassName = {}));

class IdDirective {
    constructor(element, renderer) {
        this.element = element;
        this.renderer = renderer;
        this.id = '';
    }
    ngOnInit() {
        if (this.id !== '') {
            this.assignId();
        }
    }
    assignId() {
        const classList = this.element.nativeElement.classList;
        const firstClassName = classList[0];
        this.renderer.setAttribute(this.element.nativeElement, 'id', this.generateId(this.id, firstClassName));
    }
    generateId(id, prefix) {
        if (!prefix) {
            return id;
        }
        return `${prefix}-${id}`;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: IdDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.9", type: IdDirective, selector: "[vpgId]", inputs: { id: ["vpgId", "id"] }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: IdDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[vpgId]'
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.Renderer2 }], propDecorators: { id: [{
                type: Input,
                args: ['vpgId']
            }] } });

class IconComponent {
    constructor() {
        /**
         * Sets an unique id for the component
         */
        this.id = '';
        /**
         * class name for font awesome icon
         */
        this.className = IconClassName.NoIcon;
        /**
         * aria-label for icon. If ariaLabel is defined, icon component will be visible for screen readers and emit click event including space and enter.
         */
        this.ariaLabel = '';
        /**
         * font awesome class for different sizes (e.g "fa-7x")
         */
        this.size = '';
        this.prefix = 'fa';
        /**
         * Event emitter informing the parent component that icon was clicked.
         */
        this.iconClicked = new EventEmitter();
        this.iconClassName = IconClassName.NoIcon;
    }
    /**
     * Method called on icon clicked. Takes one parameter od type MouseEvent.
     * @param $event
     */
    onClick($event) {
        this.iconClicked.emit($event);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: IconComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: IconComponent, selector: "vpg-icon", inputs: { id: "id", className: "className", ariaLabel: "ariaLabel", size: "size", prefix: "prefix" }, outputs: { iconClicked: "iconClicked" }, ngImport: i0, template: "<span\n    [attr.aria-label]=\"ariaLabel\"\n    [attr.aria-hidden]=\"ariaLabel ? false : true\"\n    [attr.tabindex]=\"ariaLabel ? 0 : null\"\n    (click)=\"onClick($event)\"\n    class=\"vpg-icon-wrapper\"\n    [vpgId]=\"id\"\n>\n    <i\n        class=\"vpg-icon-cmp {{ prefix }} {{ size }} {{ iconClassName || className }}\"\n        aria-hidden=\"true\"\n        [vpgId]=\"id\"\n    >\n    </i>\n</span>\n", dependencies: [{ kind: "directive", type: IdDirective, selector: "[vpgId]", inputs: ["vpgId"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: IconComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-icon', template: "<span\n    [attr.aria-label]=\"ariaLabel\"\n    [attr.aria-hidden]=\"ariaLabel ? false : true\"\n    [attr.tabindex]=\"ariaLabel ? 0 : null\"\n    (click)=\"onClick($event)\"\n    class=\"vpg-icon-wrapper\"\n    [vpgId]=\"id\"\n>\n    <i\n        class=\"vpg-icon-cmp {{ prefix }} {{ size }} {{ iconClassName || className }}\"\n        aria-hidden=\"true\"\n        [vpgId]=\"id\"\n    >\n    </i>\n</span>\n" }]
        }], propDecorators: { id: [{
                type: Input
            }], className: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], size: [{
                type: Input
            }], prefix: [{
                type: Input
            }], iconClicked: [{
                type: Output
            }] } });

class ButtonComponent {
    constructor() {
        /**
         * Sets an unique id for the component
         */
        this.id = '';
        /**
         * Setting a flag indicating if the button is of submit type.
         */
        this.buttonSubmit = false;
        /**
         * Setting the button label. Should be different to button label.
         */
        this.label = '';
        /**
         * Setting button disable state based on the parent condition.
         */
        this.isDisabled = false;
        /**
         * Setting icon class name for the icon button. Available predetermined classes.
         */
        this.className = IconClassName.NoIcon;
        /**
         * Setting type of button. Options are: primary, secondary, tertiary, danger, action, primary icon only, secondary icon only, tertiary icon only, danger icon only, action icon only.
         */
        this.btnType = ButtonType.Primary;
        /**
         * Setting aria-label value.
         */
        this.ariaLabel = '';
        /**
         * Flag used on IconSecondary type button.
         */
        this.isCompleted = false;
        /**
         * Setting size of button. Options are: Large, Medium, Small. Default value is Large.
         */
        this.buttonSize = ButtonSize.Large;
        /**
         * Setting position of the icon. Default is on the left.
         */
        this.iconOnTheRight = false;
        /**
         * Flag used to set full width button.
         */
        this.fullWidth = false;
        /**
         * Setting button alignment. Options are: Left and Center. Default value is Center.
         */
        this.buttonAlignment = ButtonAlignment.Center;
        /**
         * Event emitter informing the parent component that button was clicked.
         */
        this.buttonClicked = new EventEmitter();
        this.conditionalBtnClass = '';
        this.buttonSizeClass = '';
        this.buttonWidthClass = '';
        this.buttonAlignmentClass = '';
        this.iconClass = IconClassName.NoIcon;
        this.hasBorder = false;
        /**
         * Enum values for button types
         */
        this.buttonType = ButtonType;
        /**
         * Enum values for icon class name types
         */
        this.iconClassName = IconClassName;
        /**
         * isBtnIconOnly flag set to true if button only contains of an icon
         */
        this.isBtnIconOnly = false;
        /**
         * holds backup value for label
         */
        this.btnLabel = '';
        this.btnDisabled = false;
    }
    /**
     * Angular lifecycle hook reacting on any input changes.
     */
    ngOnChanges(change) {
        this.setBtnTypeFlag();
        this.setBtnType();
        this.setBtnSize();
        this.setButtonAlignment();
        this.setButtonWidth();
        this.setHasBorderFlag(this.btnType);
        this.iconClass = this.className ? this.className : IconClassName.NoIcon;
        if (change.isDisabled) {
            this.btnDisabled = change.isDisabled.currentValue;
        }
        if (change.ariaLabel && !change.ariaLabel) {
            this.ariaLabel = this.label;
        }
        if (change.label) {
            this.btnLabel = change.label.currentValue;
        }
        if (change.label && !this.isLabelAllowed(this.btnType)) {
            this.label = '';
        }
        if (change.btnType && !this.isLabelAllowed(this.btnType)) {
            this.label = '';
        }
        if (change.btnType && this.isLabelAllowed(change.btnType.currentValue)) {
            this.label = this.btnLabel;
        }
    }
    isLabelAllowed(buttonType) {
        return buttonType !== this.buttonType.IconDanger
            && buttonType !== this.buttonType.IconPrimary
            && buttonType !== this.buttonType.IconSecondary
            && buttonType !== this.buttonType.IconTertiary
            && buttonType !== this.buttonType.IconAction;
    }
    /**
     * Function dispatching an event emitter for parent component to act on.
     */
    onClick($event) {
        if (!this.isDisabled) {
            this.buttonClicked.emit($event);
        }
    }
    /**
     * Method used to set the initial, default, class depending on the btnType.
     */
    setBtnType() {
        this.conditionalBtnClass = BtnClassName.VpgPrimaryDefault;
        switch (this.btnType) {
            case this.buttonType.Primary:
                this.conditionalBtnClass = BtnClassName.VpgPrimaryDefault;
                break;
            case this.buttonType.Secondary:
                this.conditionalBtnClass = BtnClassName.VpgSecondaryDefault;
                break;
            case this.buttonType.Tertiary:
                this.conditionalBtnClass = BtnClassName.VpgTertiaryDefault;
                break;
            case this.buttonType.Danger:
                this.conditionalBtnClass = BtnClassName.VpgDangerDefault;
                break;
            case this.buttonType.IconPrimary:
                this.conditionalBtnClass = BtnClassName.VpgPrimaryIconBtn;
                break;
            case this.buttonType.IconSecondary:
                this.conditionalBtnClass = this.isCompleted ? BtnClassName.VpgSecondaryIconBtnCompleted : BtnClassName.VpgSecondaryIconBtn;
                this.isDisabled = this.isCompleted;
                break;
            case this.buttonType.IconTertiary:
                this.conditionalBtnClass = BtnClassName.VpgTertiaryIconBtn;
                break;
            case this.buttonType.IconDanger:
                this.conditionalBtnClass = BtnClassName.VpgDangerIconBtn;
                break;
            case this.buttonType.IconAction:
                this.conditionalBtnClass = BtnClassName.VpgActionIconButton;
                break;
            case this.buttonType.Action:
                this.conditionalBtnClass = BtnClassName.VpgActionDefaultButton;
                break;
        }
    }
    setBtnTypeFlag() {
        this.isBtnIconOnly = !this.isLabelAllowed(this.btnType);
    }
    setBtnSize() {
        this.buttonSizeClass = '';
        switch (this.buttonSize) {
            case ButtonSize.Medium:
                this.buttonSizeClass = this.isBtnIconOnly ? 'vpg-button-medium-icon-only' : 'vpg-button-medium';
                break;
            case ButtonSize.Small:
                this.buttonSizeClass = this.isBtnIconOnly ? 'vpg-button-small-icon-only' : 'vpg-button-small';
                break;
            default:
                this.buttonSizeClass = this.isBtnIconOnly ? 'vpg-button-large-icon-only' : 'vpg-button-large';
        }
    }
    setHasBorderFlag(buttonType) {
        this.hasBorder = buttonType === this.buttonType.Secondary
            || buttonType === this.buttonType.Tertiary
            || buttonType === this.buttonType.IconSecondary
            || buttonType === this.buttonType.IconTertiary
            || buttonType === this.buttonType.Action
            || buttonType === this.buttonType.IconAction;
    }
    setButtonWidth() {
        this.buttonWidthClass = this.fullWidth ? 'vpg-button-full-width' : '';
    }
    setButtonAlignment() {
        this.buttonAlignmentClass = this.buttonAlignment === ButtonAlignment.Left ? 'vpg-button-left-alignment' : '';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ButtonComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: ButtonComponent, selector: "vpg-button", inputs: { id: "id", buttonSubmit: "buttonSubmit", label: "label", isDisabled: "isDisabled", className: "className", btnType: "btnType", ariaLabel: "ariaLabel", isCompleted: "isCompleted", buttonSize: "buttonSize", iconOnTheRight: "iconOnTheRight", fullWidth: "fullWidth", buttonAlignment: "buttonAlignment" }, outputs: { buttonClicked: "buttonClicked" }, usesOnChanges: true, ngImport: i0, template: "<button\n    (click)=\"onClick($event)\"\n    [ngClass]=\"[conditionalBtnClass, buttonSizeClass, buttonWidthClass, buttonAlignmentClass]\"\n    [attr.aria-label]=\"ariaLabel\"\n    [disabled]=\"btnDisabled\"\n    [type]=\"buttonSubmit ? 'submit' : 'button'\"\n    [attr.tabindex]=\"isDisabled ? -1 : 0\"\n    [vpgId]=\"id\"\n>\n    <div\n        class=\"vpg-button-content\"\n        [ngClass]=\"{ 'vpg-button-icon-position-right' : iconOnTheRight,\n                     'vpg-button-borderless-additional-padding' : !hasBorder\n                    }\"\n    >\n        <vpg-icon\n            *ngIf=\"iconClass && !isBtnIconOnly\"\n            class=\"vpg-btn-with-icon-and-text\"\n            [ngClass]=\"iconOnTheRight ? 'vpg-button-right-icon-padding' : 'vpg-button-left-icon-padding'\"\n            [className]=\"iconClass\"\n            [id]=\"id\"\n        >\n        </vpg-icon>\n        <div\n            class=\"vpg-button-text\"\n            *ngIf=\"!isBtnIconOnly\"\n            [attr.aria-hidden]=\"isDisabled\"\n        >{{ label }}</div>\n        <vpg-icon\n            class=\"btn-icon\"\n            *ngIf=\"isBtnIconOnly\"\n            [className]=\"iconClass\"\n            [ngClass]=\"{'vpg-check-icon' : className === iconClassName.Check}\"\n            [id]=\"id\"\n        >\n        </vpg-icon>\n    </div>\n</button>\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: IconComponent, selector: "vpg-icon", inputs: ["id", "className", "ariaLabel", "size", "prefix"], outputs: ["iconClicked"] }, { kind: "directive", type: IdDirective, selector: "[vpgId]", inputs: ["vpgId"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ButtonComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-button', template: "<button\n    (click)=\"onClick($event)\"\n    [ngClass]=\"[conditionalBtnClass, buttonSizeClass, buttonWidthClass, buttonAlignmentClass]\"\n    [attr.aria-label]=\"ariaLabel\"\n    [disabled]=\"btnDisabled\"\n    [type]=\"buttonSubmit ? 'submit' : 'button'\"\n    [attr.tabindex]=\"isDisabled ? -1 : 0\"\n    [vpgId]=\"id\"\n>\n    <div\n        class=\"vpg-button-content\"\n        [ngClass]=\"{ 'vpg-button-icon-position-right' : iconOnTheRight,\n                     'vpg-button-borderless-additional-padding' : !hasBorder\n                    }\"\n    >\n        <vpg-icon\n            *ngIf=\"iconClass && !isBtnIconOnly\"\n            class=\"vpg-btn-with-icon-and-text\"\n            [ngClass]=\"iconOnTheRight ? 'vpg-button-right-icon-padding' : 'vpg-button-left-icon-padding'\"\n            [className]=\"iconClass\"\n            [id]=\"id\"\n        >\n        </vpg-icon>\n        <div\n            class=\"vpg-button-text\"\n            *ngIf=\"!isBtnIconOnly\"\n            [attr.aria-hidden]=\"isDisabled\"\n        >{{ label }}</div>\n        <vpg-icon\n            class=\"btn-icon\"\n            *ngIf=\"isBtnIconOnly\"\n            [className]=\"iconClass\"\n            [ngClass]=\"{'vpg-check-icon' : className === iconClassName.Check}\"\n            [id]=\"id\"\n        >\n        </vpg-icon>\n    </div>\n</button>\n" }]
        }], propDecorators: { id: [{
                type: Input
            }], buttonSubmit: [{
                type: Input
            }], label: [{
                type: Input
            }], isDisabled: [{
                type: Input
            }], className: [{
                type: Input
            }], btnType: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], isCompleted: [{
                type: Input
            }], buttonSize: [{
                type: Input
            }], iconOnTheRight: [{
                type: Input
            }], fullWidth: [{
                type: Input
            }], buttonAlignment: [{
                type: Input
            }], buttonClicked: [{
                type: Output
            }] } });

var CardContentType;
(function (CardContentType) {
    CardContentType["ImageOnly"] = "image only";
    CardContentType["TextOnly"] = "text only";
    CardContentType["ImageText"] = "image and text";
    CardContentType["List"] = "list";
    CardContentType["Promotion"] = "promotion";
})(CardContentType || (CardContentType = {}));

class ContainerComponent {
    constructor() {
        this.variant = 'primary';
        this.borderRadius = 'large';
        this.hover = true;
    }
    get classNames() {
        return {
            ['has-hover']: this.hover,
            [`is-${this.variant}`]: true,
            [`has-radius-${this.borderRadius}`]: true
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ContainerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: ContainerComponent, selector: "vpg-container", inputs: { variant: "variant", borderRadius: "borderRadius", hover: "hover" }, host: { properties: { "class": "this.classNames" }, classAttribute: "v-container" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ContainerComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vpg-container',
                    template: '<ng-content></ng-content>',
                    host: {
                        class: 'v-container'
                    }
                }]
        }], propDecorators: { variant: [{
                type: Input
            }], borderRadius: [{
                type: Input
            }], hover: [{
                type: Input
            }], classNames: [{
                type: HostBinding,
                args: ['class']
            }] } });

class CardHeaderComponent {
    constructor() {
        /**
         * Sets an unique id for the component
         */
        this.id = '';
        /**
         * Card header title.
         */
        this.title = '';
        /**
         * Card Header subtitle.
         */
        this.subtitle = '';
        /**
         * Setting aria-label value for card header button.
         */
        this.ariaBtnDesc = '';
        /**
         * Icon class name for the icon. Available predetermined classes.
         */
        this.className = '';
        /**
         * Card header action.
         */
        this.hasAction = false;
        /**
         * Setting aria-level value for card header; default value set at 2.
         */
        this.ariaLevel = '2';
        /**
         * Event emitter informing that card header button was clicked.
         */
        this.headerButtonClicked = new EventEmitter();
        /**
         * Event emitter informing that card header link was clicked.
         */
        this.headerLinkClicked = new EventEmitter();
        this.subtitleAriaLevel = '';
        this.buttonType = ButtonType;
        this.iconClassName = IconClassName;
        this.chevronRight = 'fa-chevron-right';
    }
    ngOnInit() {
        this.subtitleAriaLevel = (+this.ariaLevel + 1).toString();
    }
    /**
     * Function dispatching an event emitter for parent component to act on.
     */
    onClick($event) {
        this.headerButtonClicked.emit($event);
    }
    /**
     * Function dispatching an event emitter for parent component to act on.
     */
    onLinkClick($event) {
        this.headerLinkClicked.emit($event);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CardHeaderComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: CardHeaderComponent, selector: "vpg-card-header", inputs: { id: "id", title: "title", subtitle: "subtitle", ariaBtnDesc: "ariaBtnDesc", className: "className", hasAction: "hasAction", ariaLevel: "ariaLevel" }, outputs: { headerButtonClicked: "headerButtonClicked", headerLinkClicked: "headerLinkClicked" }, ngImport: i0, template: "<div\n    class=\"vpg-card-header-main\"\n>\n    <div\n        *ngIf=\"className\"\n        class=\"vpg-header-icon\"\n    >\n        <vpg-button\n            [btnType]=\"buttonType.IconTertiary\"\n            [className]=\"className\"\n            [ariaLabel]=\"ariaBtnDesc\"\n            (buttonClicked)=\"onClick($event)\"\n            [ngClass]=\"{ 'vpg-card-header-close-btn' : className === iconClassName.Times }\"\n            [id]=\"id\"\n        >\n        </vpg-button>\n    </div>\n    <div\n        *ngIf=\"hasAction\"\n        class=\"vpg-header-title-with-link\"\n        (click)=\"onLinkClick($event)\"\n        role=\"heading\"\n        [attr.aria-level]=\"ariaLevel\"\n        [vpgId]=\"id\"\n    >\n        <div\n            class=\"vpg-header-title\"\n            [vpgId]=\"id\"\n            role=\"link\"\n            (keyup.enter)=\"onLinkClick()\"\n            tabindex=\"0\"\n        >{{ title }}</div>\n        <vpg-icon\n            [className]=\"chevronRight\"\n            class=\"vpg-header-chevron-icon\"\n            [id]=\"id + '-chevron-icon'\"\n        >\n        </vpg-icon>\n    </div>\n    <div\n        *ngIf=\"!hasAction\"\n        class=\"vpg-header-title\"\n        [vpgId]=\"id\"\n        role=\"heading\"\n        [attr.aria-level]=\"ariaLevel\">\n        {{ title }}\n    </div>\n    <div\n        *ngIf=\"subtitle\"\n        class=\"vpg-header-subtitle\"\n        [vpgId]=\"id\"\n        role=\"heading\"\n        [attr.aria-level]=\"subtitleAriaLevel\">\n        {{ subtitle }}\n    </div>\n</div>\n", styles: [".vpg-card-header-main .vpg-header-title{font-family:Open Sans,sans-serif;font-weight:700;color:var(--v-color-gray-100);margin:0;text-transform:none}.vpg-card-header-main .vpg-header-subtitle{font-family:Open Sans,sans-serif;color:var(--v-color-gray-100);margin:0}.vpg-card-header-main{overflow-wrap:break-word}.vpg-card-header-main .vpg-header-icon vpg-button{padding:0 0 10px;padding-inline-end:0;padding-inline-start:10px}.vpg-card-header-main .vpg-header-title-with-link{display:flex;align-items:center;justify-content:space-between;cursor:pointer}.vpg-card-header-main .vpg-header-title-with-link .vpg-header-chevron-icon{font-size:1.6rem;line-height:1.4}.vpg-card-header-main .vpg-header-title{font-size:2rem;line-height:1.4}.vpg-card-header-main .vpg-header-subtitle{margin-top:5px;font-size:1.6rem;line-height:1.4}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: IconComponent, selector: "vpg-icon", inputs: ["id", "className", "ariaLabel", "size", "prefix"], outputs: ["iconClicked"] }, { kind: "component", type: ButtonComponent, selector: "vpg-button", inputs: ["id", "buttonSubmit", "label", "isDisabled", "className", "btnType", "ariaLabel", "isCompleted", "buttonSize", "iconOnTheRight", "fullWidth", "buttonAlignment"], outputs: ["buttonClicked"] }, { kind: "directive", type: IdDirective, selector: "[vpgId]", inputs: ["vpgId"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CardHeaderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-card-header', template: "<div\n    class=\"vpg-card-header-main\"\n>\n    <div\n        *ngIf=\"className\"\n        class=\"vpg-header-icon\"\n    >\n        <vpg-button\n            [btnType]=\"buttonType.IconTertiary\"\n            [className]=\"className\"\n            [ariaLabel]=\"ariaBtnDesc\"\n            (buttonClicked)=\"onClick($event)\"\n            [ngClass]=\"{ 'vpg-card-header-close-btn' : className === iconClassName.Times }\"\n            [id]=\"id\"\n        >\n        </vpg-button>\n    </div>\n    <div\n        *ngIf=\"hasAction\"\n        class=\"vpg-header-title-with-link\"\n        (click)=\"onLinkClick($event)\"\n        role=\"heading\"\n        [attr.aria-level]=\"ariaLevel\"\n        [vpgId]=\"id\"\n    >\n        <div\n            class=\"vpg-header-title\"\n            [vpgId]=\"id\"\n            role=\"link\"\n            (keyup.enter)=\"onLinkClick()\"\n            tabindex=\"0\"\n        >{{ title }}</div>\n        <vpg-icon\n            [className]=\"chevronRight\"\n            class=\"vpg-header-chevron-icon\"\n            [id]=\"id + '-chevron-icon'\"\n        >\n        </vpg-icon>\n    </div>\n    <div\n        *ngIf=\"!hasAction\"\n        class=\"vpg-header-title\"\n        [vpgId]=\"id\"\n        role=\"heading\"\n        [attr.aria-level]=\"ariaLevel\">\n        {{ title }}\n    </div>\n    <div\n        *ngIf=\"subtitle\"\n        class=\"vpg-header-subtitle\"\n        [vpgId]=\"id\"\n        role=\"heading\"\n        [attr.aria-level]=\"subtitleAriaLevel\">\n        {{ subtitle }}\n    </div>\n</div>\n", styles: [".vpg-card-header-main .vpg-header-title{font-family:Open Sans,sans-serif;font-weight:700;color:var(--v-color-gray-100);margin:0;text-transform:none}.vpg-card-header-main .vpg-header-subtitle{font-family:Open Sans,sans-serif;color:var(--v-color-gray-100);margin:0}.vpg-card-header-main{overflow-wrap:break-word}.vpg-card-header-main .vpg-header-icon vpg-button{padding:0 0 10px;padding-inline-end:0;padding-inline-start:10px}.vpg-card-header-main .vpg-header-title-with-link{display:flex;align-items:center;justify-content:space-between;cursor:pointer}.vpg-card-header-main .vpg-header-title-with-link .vpg-header-chevron-icon{font-size:1.6rem;line-height:1.4}.vpg-card-header-main .vpg-header-title{font-size:2rem;line-height:1.4}.vpg-card-header-main .vpg-header-subtitle{margin-top:5px;font-size:1.6rem;line-height:1.4}\n"] }]
        }], propDecorators: { id: [{
                type: Input
            }], title: [{
                type: Input
            }], subtitle: [{
                type: Input
            }], ariaBtnDesc: [{
                type: Input
            }], className: [{
                type: Input
            }], hasAction: [{
                type: Input
            }], ariaLevel: [{
                type: Input
            }], headerButtonClicked: [{
                type: Output
            }], headerLinkClicked: [{
                type: Output
            }] } });

var CardActionType;
(function (CardActionType) {
    CardActionType["Primary"] = "Primary";
    CardActionType["CheckAndTertiary"] = "Check And Tertiary";
    CardActionType["SideBySide"] = "Side By Side";
    CardActionType["Link"] = "Link";
})(CardActionType || (CardActionType = {}));

class LinkDirective {
    constructor() {
        this.size = 'medium';
        this.align = 'left';
        this.bold = false;
    }
    get classNames() {
        return {
            ['v-link']: true,
            ['is-bold']: this.bold,
            [`is-${this.size}`]: true,
            [`is-${this.align}`]: true
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: LinkDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.9", type: LinkDirective, selector: "a[vpg-link], a[vpgLink], button[vpg-link], button[vpgLink]", inputs: { size: "size", align: "align", bold: "bold" }, host: { properties: { "class": "this.classNames" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: LinkDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'a[vpg-link], a[vpgLink], button[vpg-link], button[vpgLink]'
                }]
        }], propDecorators: { size: [{
                type: Input
            }], align: [{
                type: Input
            }], bold: [{
                type: Input
            }], classNames: [{
                type: HostBinding,
                args: ['class']
            }] } });

class CardActionComponent {
    constructor() {
        /**
         * Sets an unique id for the component
         */
        this.id = '';
        /**
         * Card action type.
         */
        this.actionType = CardActionType.Primary;
        /**
         * Label for button text - used for primary button, for the first label within Side By Side type or for link
         */
        this.primaryLabel = '';
        /**
         * Label for button text - used for secondary button within Check And Secondary type or the second label within Side By Side type
         */
        this.secondaryLabel = '';
        /**
         * Aria label for icon or link
         */
        this.ariaLabel = '';
        /**
         * Action Link URL
         */
        this.actionLinkURL = '';
        /**
         * Event emitter informing that primary action was clicked (Primary button, icon, first button within Side By Side type, link)
         */
        this.primaryActionClicked = new EventEmitter();
        /**
         * Event emitter informing that secondary action was clicked (second button within Check And Secondary type, second button within Side By Side type)
         */
        this.secondaryActionClicked = new EventEmitter();
        this.iconSize = 'fa-3x';
        this.cardActionType = CardActionType;
        this.buttonType = ButtonType;
        this.iconClassName = IconClassName;
    }
    /**
     * Function dispatching an event emitter for parent component from primary action
     */
    onPrimaryClicked($event) {
        this.primaryActionClicked.emit($event);
    }
    /**
     * Function dispatching an event emitter for parent component from secondary action
     */
    onSecondaryClicked($event) {
        this.secondaryActionClicked.emit($event);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CardActionComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: CardActionComponent, selector: "vpg-card-action", inputs: { id: "id", actionType: "actionType", primaryLabel: "primaryLabel", secondaryLabel: "secondaryLabel", ariaLabel: "ariaLabel", actionLinkURL: "actionLinkURL" }, outputs: { primaryActionClicked: "primaryActionClicked", secondaryActionClicked: "secondaryActionClicked" }, ngImport: i0, template: "<div class=\"vpg-card-action-main\">\n    <div *ngIf=\"actionType === cardActionType.Primary\" class=\"vpg-primary-btn\">\n        <vpg-button\n            [label]=\"primaryLabel\"\n            (buttonClicked)=\"onPrimaryClicked($event)\"\n            [id]=\"id + '-primary-btn'\"\n        ></vpg-button>\n    </div>\n\n    <div *ngIf=\"actionType === cardActionType.CheckAndTertiary\" class=\"vpg-check-and-sec\">\n        <div class=\"vpg-action-icon\">\n            <vpg-button\n                [btnType]=\"buttonType.IconSecondary\"\n                [ariaLabel]=\"ariaLabel\"\n                [className]=\"iconClassName.Check\"\n                [isCompleted]=\"true\"\n                [isDisabled]=\"true\"\n                [id]=\"id + '-secondary-check-btn'\"\n            >\n            </vpg-button>\n        </div>\n        <vpg-button\n            [label]=\"secondaryLabel\"\n            [btnType]=\"buttonType.Tertiary\"\n            (buttonClicked)=\"onSecondaryClicked($event)\"\n            [id]=\"id + '-tertiary-btn'\"\n        >\n        </vpg-button>\n    </div>\n\n    <div *ngIf=\"actionType === cardActionType.SideBySide\" class=\"vpg-action-side-by-side\">\n        <div class=\"vpg-action-side-by-side-left\">\n            <vpg-button\n                [label]=\"primaryLabel\"\n                [btnType]=\"buttonType.Secondary\"\n                (buttonClicked)=\"onPrimaryClicked($event)\"\n                [id]=\"id + '-left-btn'\"\n            >\n            </vpg-button>\n        </div>\n        <div class=\"vpg-action-side-by-side-right\">\n            <vpg-button\n                [label]=\"secondaryLabel\"\n                [btnType]=\"buttonType.Secondary\"\n                (buttonClicked)=\"onSecondaryClicked($event)\"\n                [id]=\"id + '-right-btn'\"\n            >\n            </vpg-button>\n        </div>\n    </div>\n\n    <div *ngIf=\"actionType === cardActionType.Link\">\n        <a\n            vpg-link\n            [size]=\"'large'\"\n            [href]=\"actionLinkURL\"\n            [attr.aria-label]=\"ariaLabel\"\n            (click)=\"onPrimaryClicked($event)\"\n            [id]=\"id + '-link'\"\n        >{{ primaryLabel }}</a>\n    </div>\n</div>\n", styles: [".vpg-card-action-main .vpg-check-and-sec{display:flex}.vpg-card-action-main .vpg-check-and-sec .vpg-action-icon{margin-inline-end:10px;align-self:center}.vpg-card-action-main .vpg-action-side-by-side{display:flex}.vpg-card-action-main .vpg-action-side-by-side .vpg-action-side-by-side-left{margin-inline-end:10px;max-width:50%}.vpg-card-action-main .vpg-action-side-by-side .vpg-action-side-by-side-right{max-width:50%}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: LinkDirective, selector: "a[vpg-link], a[vpgLink], button[vpg-link], button[vpgLink]", inputs: ["size", "align", "bold"] }, { kind: "component", type: ButtonComponent, selector: "vpg-button", inputs: ["id", "buttonSubmit", "label", "isDisabled", "className", "btnType", "ariaLabel", "isCompleted", "buttonSize", "iconOnTheRight", "fullWidth", "buttonAlignment"], outputs: ["buttonClicked"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CardActionComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-card-action', template: "<div class=\"vpg-card-action-main\">\n    <div *ngIf=\"actionType === cardActionType.Primary\" class=\"vpg-primary-btn\">\n        <vpg-button\n            [label]=\"primaryLabel\"\n            (buttonClicked)=\"onPrimaryClicked($event)\"\n            [id]=\"id + '-primary-btn'\"\n        ></vpg-button>\n    </div>\n\n    <div *ngIf=\"actionType === cardActionType.CheckAndTertiary\" class=\"vpg-check-and-sec\">\n        <div class=\"vpg-action-icon\">\n            <vpg-button\n                [btnType]=\"buttonType.IconSecondary\"\n                [ariaLabel]=\"ariaLabel\"\n                [className]=\"iconClassName.Check\"\n                [isCompleted]=\"true\"\n                [isDisabled]=\"true\"\n                [id]=\"id + '-secondary-check-btn'\"\n            >\n            </vpg-button>\n        </div>\n        <vpg-button\n            [label]=\"secondaryLabel\"\n            [btnType]=\"buttonType.Tertiary\"\n            (buttonClicked)=\"onSecondaryClicked($event)\"\n            [id]=\"id + '-tertiary-btn'\"\n        >\n        </vpg-button>\n    </div>\n\n    <div *ngIf=\"actionType === cardActionType.SideBySide\" class=\"vpg-action-side-by-side\">\n        <div class=\"vpg-action-side-by-side-left\">\n            <vpg-button\n                [label]=\"primaryLabel\"\n                [btnType]=\"buttonType.Secondary\"\n                (buttonClicked)=\"onPrimaryClicked($event)\"\n                [id]=\"id + '-left-btn'\"\n            >\n            </vpg-button>\n        </div>\n        <div class=\"vpg-action-side-by-side-right\">\n            <vpg-button\n                [label]=\"secondaryLabel\"\n                [btnType]=\"buttonType.Secondary\"\n                (buttonClicked)=\"onSecondaryClicked($event)\"\n                [id]=\"id + '-right-btn'\"\n            >\n            </vpg-button>\n        </div>\n    </div>\n\n    <div *ngIf=\"actionType === cardActionType.Link\">\n        <a\n            vpg-link\n            [size]=\"'large'\"\n            [href]=\"actionLinkURL\"\n            [attr.aria-label]=\"ariaLabel\"\n            (click)=\"onPrimaryClicked($event)\"\n            [id]=\"id + '-link'\"\n        >{{ primaryLabel }}</a>\n    </div>\n</div>\n", styles: [".vpg-card-action-main .vpg-check-and-sec{display:flex}.vpg-card-action-main .vpg-check-and-sec .vpg-action-icon{margin-inline-end:10px;align-self:center}.vpg-card-action-main .vpg-action-side-by-side{display:flex}.vpg-card-action-main .vpg-action-side-by-side .vpg-action-side-by-side-left{margin-inline-end:10px;max-width:50%}.vpg-card-action-main .vpg-action-side-by-side .vpg-action-side-by-side-right{max-width:50%}\n"] }]
        }], propDecorators: { id: [{
                type: Input
            }], actionType: [{
                type: Input
            }], primaryLabel: [{
                type: Input
            }], secondaryLabel: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], actionLinkURL: [{
                type: Input
            }], primaryActionClicked: [{
                type: Output
            }], secondaryActionClicked: [{
                type: Output
            }] } });

var ImageType;
(function (ImageType) {
    ImageType["Hero"] = "hero";
    ImageType["Thumbnail"] = "thumbnail";
    ImageType["SmallAvatar"] = "small";
    ImageType["LargeAvatar"] = "large";
    ImageType["SquareThumbnail"] = "square thumbnail";
    ImageType["LargeSquareThumbnail"] = "square thumbnail large";
    ImageType["SmallPortrait"] = "small portrait";
    ImageType["LargePortrait"] = "large portrait";
})(ImageType || (ImageType = {}));

class ImageComponent {
    constructor() {
        /**
         * Sets an unique id for the component
         */
        this.id = '';
        /**
         * Image source provided for the component.
         */
        this.imageSource = '';
        /**
         * Image source background color provided for the component.
         */
        this.imageSourceBackgroundColor = '';
        /**
         * Sets the value for the alt attribute for the image.
         */
        this.alternativeText = '';
        /**
         * Determines the type of the image. Values available: hero, thumbnail, large (avatar), small (avatar), large portrait, small portrait
         */
        this.imageType = ImageType.SmallAvatar;
        /**
         * Sets the style for the image.
         */
        this.className = '';
        /**
         * Sets the style for the image container wrapper.
         */
        this.containerClassName = '';
        this.ariaHidden = false;
    }
    /**
     * Angular lifecycle hook with a function that facilitates setting the element styles.
     */
    ngOnChanges(changes) {
        if (changes.imageType && changes.imageType.currentValue !== changes.imageType.previousValue) {
            this.setStyles(changes.imageType.currentValue);
        }
        if (changes.alternativeText && changes.alternativeText.currentValue !== changes.alternativeText.previousValue) {
            this.ariaHidden = changes.alternativeText.currentValue === '';
        }
    }
    ngOnInit() {
        this.setStyles(this.imageType);
    }
    /**
     * Sets css classes for different elements.
     * @param type image type
     * @returns the image ratio depending on the image type
     */
    setStyles(type) {
        this.className = '';
        this.containerClassName = '';
        switch (type) {
            case ImageType.Hero:
                this.className = 'vpg-hero';
                break;
            case ImageType.Thumbnail:
                this.className = 'vpg-thumbnail';
                break;
            case ImageType.LargeAvatar:
                this.className = 'vpg-avatar-large';
                this.className = this.imageSourceBackgroundColor ? 'vpg-avatar-large-with-icon' : this.className;
                this.containerClassName = 'vpg-avatar-large-container';
                break;
            case ImageType.SmallAvatar:
                this.className = 'vpg-avatar-small';
                this.containerClassName = 'vpg-avatar-small-container';
                break;
            case ImageType.SquareThumbnail:
                this.className = 'vpg-square-thumbnail';
                break;
            case ImageType.LargeSquareThumbnail:
                this.className = 'vpg-square-thumbnail-large';
                break;
            case ImageType.SmallPortrait:
                this.className = 'vpg-portrait-small';
                this.containerClassName = 'vpg-portrait-small-container';
                break;
            case ImageType.LargePortrait:
                this.className = 'vpg-portrait-large';
                this.containerClassName = 'vpg-portrait-large-container';
                break;
        }
    }
    onError() {
        this.updateImgSource();
    }
    /**
     * Image sources falls back to image placeholder in case of an error obtaining the provided image source.
     */
    updateImgSource() {
        switch (this.imageType) {
            case ImageType.Hero:
            case ImageType.Thumbnail:
            case ImageType.LargePortrait:
            case ImageType.SmallPortrait:
                this.imageSource = '/assets/images/heroPlaceholderImage.png';
                break;
            case ImageType.LargeAvatar:
            case ImageType.SmallAvatar:
            case ImageType.LargeSquareThumbnail:
                this.imageSource = '/assets/images/avatarPlaceholderImage.png';
                break;
            case ImageType.SquareThumbnail:
                this.imageSource = '/assets/images/squareThumbnailPlaceholder.png';
                break;
            default:
                break;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ImageComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: ImageComponent, selector: "vpg-image", inputs: { id: "id", imageSource: "imageSource", imageSourceBackgroundColor: "imageSourceBackgroundColor", alternativeText: "alternativeText", imageType: "imageType" }, usesOnChanges: true, ngImport: i0, template: "<div\n    class=\"vpg-image-wrapper {{ containerClassName }}\">\n    <img\n        [src]=\"imageSource\"\n        [ngClass]=\"className\"\n        [style.background]=\"imageSourceBackgroundColor\"\n        [alt]=\"alternativeText\"\n        [attr.aria-hidden]=\"ariaHidden\"\n        (error)=\"onError()\"\n        [vpgId]=\"id\"\n    />\n</div>\n", styles: [".vpg-image-wrapper{overflow:hidden;position:relative}img{width:100%;height:auto}.vpg-thumbnail{border-radius:5px}.vpg-avatar-large-container{height:80px;width:80px;display:inline-block}.vpg-avatar-small-container{height:50px;width:50px;display:inline-block}.vpg-avatar-large,.vpg-avatar-small,.vpg-avatar-large-with-icon{width:100%;height:100%;border-radius:100%}.vpg-avatar-large-with-icon{object-fit:scale-down}.vpg-avatar-large,.vpg-avatar-small{object-fit:cover}.vpg-square-thumbnail{height:48px;width:48px;border-radius:2px}.vpg-square-thumbnail-large{height:95px;width:95px;border-radius:2px}.vpg-portrait-large-container{aspect-ratio:2/3;max-height:100%;max-width:100%}.vpg-portrait-large{aspect-ratio:2/3;object-fit:cover}.vpg-portrait-small-container{aspect-ratio:3/4;max-height:100%;max-width:100%}.vpg-portrait-small{aspect-ratio:3/4;object-fit:cover}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: IdDirective, selector: "[vpgId]", inputs: ["vpgId"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ImageComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-image', template: "<div\n    class=\"vpg-image-wrapper {{ containerClassName }}\">\n    <img\n        [src]=\"imageSource\"\n        [ngClass]=\"className\"\n        [style.background]=\"imageSourceBackgroundColor\"\n        [alt]=\"alternativeText\"\n        [attr.aria-hidden]=\"ariaHidden\"\n        (error)=\"onError()\"\n        [vpgId]=\"id\"\n    />\n</div>\n", styles: [".vpg-image-wrapper{overflow:hidden;position:relative}img{width:100%;height:auto}.vpg-thumbnail{border-radius:5px}.vpg-avatar-large-container{height:80px;width:80px;display:inline-block}.vpg-avatar-small-container{height:50px;width:50px;display:inline-block}.vpg-avatar-large,.vpg-avatar-small,.vpg-avatar-large-with-icon{width:100%;height:100%;border-radius:100%}.vpg-avatar-large-with-icon{object-fit:scale-down}.vpg-avatar-large,.vpg-avatar-small{object-fit:cover}.vpg-square-thumbnail{height:48px;width:48px;border-radius:2px}.vpg-square-thumbnail-large{height:95px;width:95px;border-radius:2px}.vpg-portrait-large-container{aspect-ratio:2/3;max-height:100%;max-width:100%}.vpg-portrait-large{aspect-ratio:2/3;object-fit:cover}.vpg-portrait-small-container{aspect-ratio:3/4;max-height:100%;max-width:100%}.vpg-portrait-small{aspect-ratio:3/4;object-fit:cover}\n"] }]
        }], propDecorators: { id: [{
                type: Input
            }], imageSource: [{
                type: Input
            }], imageSourceBackgroundColor: [{
                type: Input
            }], alternativeText: [{
                type: Input
            }], imageType: [{
                type: Input
            }] } });

class CardContentImgTxtComponent {
    constructor() {
        /**
         * Sets an unique id for the component
         */
        this.id = '';
        /**
         * Card content type. Options include: image only, text only, image and text, and list.
         */
        this.cardContentType = CardContentType.ImageOnly;
        this.contentType = CardContentType;
        this.imageType = ImageType;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CardContentImgTxtComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: CardContentImgTxtComponent, selector: "vpg-card-content-img-txt", inputs: { id: "id", cardContentType: "cardContentType", contentText: "contentText", imageSource: "imageSource", alternativeText: "alternativeText" }, ngImport: i0, template: "<vpg-image\n    *ngIf=\"cardContentType !== contentType.TextOnly\"\n    [imageSource]=\"imageSource\"\n    [alternativeText]=\"alternativeText || ''\"\n    [imageType]=\"imageType.Hero\"\n    [id]=\"id + '-content-image'\"\n>\n</vpg-image>\n<p\n    *ngIf=\"cardContentType !== contentType.ImageOnly\"\n    class=\"vpg-content-text-content\"\n    [innerHtml]=\"contentText\"\n    [vpgId]=\"id\"\n>\n</p>\n", styles: [".vpg-content-text-content{font-family:Open Sans,sans-serif;color:var(--v-color-gray-100);margin:0}.vpg-content-text-content{font-size:1.6rem;line-height:1.4;padding:20px 20px 0;text-align:start}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: ImageComponent, selector: "vpg-image", inputs: ["id", "imageSource", "imageSourceBackgroundColor", "alternativeText", "imageType"] }, { kind: "directive", type: IdDirective, selector: "[vpgId]", inputs: ["vpgId"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CardContentImgTxtComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-card-content-img-txt', template: "<vpg-image\n    *ngIf=\"cardContentType !== contentType.TextOnly\"\n    [imageSource]=\"imageSource\"\n    [alternativeText]=\"alternativeText || ''\"\n    [imageType]=\"imageType.Hero\"\n    [id]=\"id + '-content-image'\"\n>\n</vpg-image>\n<p\n    *ngIf=\"cardContentType !== contentType.ImageOnly\"\n    class=\"vpg-content-text-content\"\n    [innerHtml]=\"contentText\"\n    [vpgId]=\"id\"\n>\n</p>\n", styles: [".vpg-content-text-content{font-family:Open Sans,sans-serif;color:var(--v-color-gray-100);margin:0}.vpg-content-text-content{font-size:1.6rem;line-height:1.4;padding:20px 20px 0;text-align:start}\n"] }]
        }], propDecorators: { id: [{
                type: Input
            }], cardContentType: [{
                type: Input
            }], contentText: [{
                type: Input
            }], imageSource: [{
                type: Input
            }], alternativeText: [{
                type: Input
            }] } });

class CardContentPromotionComponent {
    constructor() {
        /**
         * Sets an unique id for the component
         */
        this.id = '';
        /**
         * Sets the text content. Value also used for aria attribute
         */
        this.promotionText = '';
        /**
         * Sets the image source for the image component
         */
        this.imageSource = '';
        /**
         * Image source background color provided for the component.
         */
        this.imageSourceBackgroundColor = '';
        /**
         * Sets the value for the alt attribute for the image.
         */
        this.alternativeText = '';
        /**
         * Local image type enum values. Used to pass image type input to the Image component
         */
        this.imageTypes = ImageType;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CardContentPromotionComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: CardContentPromotionComponent, selector: "vpg-card-content-promotion", inputs: { id: "id", promotionText: "promotionText", imageSource: "imageSource", imageSourceBackgroundColor: "imageSourceBackgroundColor", alternativeText: "alternativeText" }, ngImport: i0, template: "<div class=\"vpg-card-content-promotion-wrapper\">\n    <p\n        class=\"vpg-promotion-text\"\n        [vpgId]=\"id\"\n        [innerHTML]=\"promotionText\"\n    ></p>\n    <vpg-image\n        *ngIf=\"imageSource\"\n        [imageType]=\"imageTypes.LargeAvatar\"\n        [imageSource]=\"imageSource\"\n        [alternativeText]=\"alternativeText || ''\"\n        [imageSourceBackgroundColor]=\"imageSourceBackgroundColor\"\n        [id]=\"id + '-promotion-image'\"\n    >\n    </vpg-image>\n</div>\n", styles: [".vpg-promotion-text{font-family:Open Sans,sans-serif;color:var(--v-color-gray-100);margin:0}.vpg-card-content-promotion-wrapper{padding:10px 20px 20px;display:flex;justify-content:space-between}.vpg-card-content-promotion-wrapper vpg-image{margin-inline-start:20px}.vpg-promotion-text{font-size:1.6rem;line-height:1.4;word-break:break-word}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: ImageComponent, selector: "vpg-image", inputs: ["id", "imageSource", "imageSourceBackgroundColor", "alternativeText", "imageType"] }, { kind: "directive", type: IdDirective, selector: "[vpgId]", inputs: ["vpgId"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CardContentPromotionComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-card-content-promotion', template: "<div class=\"vpg-card-content-promotion-wrapper\">\n    <p\n        class=\"vpg-promotion-text\"\n        [vpgId]=\"id\"\n        [innerHTML]=\"promotionText\"\n    ></p>\n    <vpg-image\n        *ngIf=\"imageSource\"\n        [imageType]=\"imageTypes.LargeAvatar\"\n        [imageSource]=\"imageSource\"\n        [alternativeText]=\"alternativeText || ''\"\n        [imageSourceBackgroundColor]=\"imageSourceBackgroundColor\"\n        [id]=\"id + '-promotion-image'\"\n    >\n    </vpg-image>\n</div>\n", styles: [".vpg-promotion-text{font-family:Open Sans,sans-serif;color:var(--v-color-gray-100);margin:0}.vpg-card-content-promotion-wrapper{padding:10px 20px 20px;display:flex;justify-content:space-between}.vpg-card-content-promotion-wrapper vpg-image{margin-inline-start:20px}.vpg-promotion-text{font-size:1.6rem;line-height:1.4;word-break:break-word}\n"] }]
        }], propDecorators: { id: [{
                type: Input
            }], promotionText: [{
                type: Input
            }], imageSource: [{
                type: Input
            }], imageSourceBackgroundColor: [{
                type: Input
            }], alternativeText: [{
                type: Input
            }] } });

class DividerLineComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: DividerLineComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: DividerLineComponent, selector: "vpg-divider-line", host: { attributes: { "role": "separator" }, properties: { "attr.aria-hidden": "true" }, classAttribute: "v-divider-line" }, ngImport: i0, template: '', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: DividerLineComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vpg-divider-line',
                    template: '',
                    host: {
                        class: 'v-divider-line',
                        role: 'separator',
                        '[attr.aria-hidden]': 'true'
                    }
                }]
        }] });

var LabelType;
(function (LabelType) {
    LabelType["Attention"] = "attention";
    LabelType["Completed"] = "completed";
    LabelType["New"] = "new";
    LabelType["Overdue"] = "overdue";
    LabelType["UrgentNeutral"] = "urgent-neutral";
    LabelType["Verification"] = "verification";
    LabelType["Missed"] = "missed";
    LabelType["Complete"] = "complete";
    LabelType["InformativeNeutral"] = "informative-neutral";
})(LabelType || (LabelType = {}));

class IconService {
    constructor() {
        this.classMap = {
            'edit': 'fa fa-edit',
            'user': 'fa fa-user',
            'user-circle': 'fa fa-user-circle',
            'info-circle': 'fa fa-info-circle',
            'ellipsis-v': 'fa fa-ellipsis-v',
            'ellipsis-h': 'fa fa-ellipsis-h',
            'search': 'fa fa-search',
            'trophy': 'fa fa-trophy',
            'fa-trophy': 'fa fa-trophy',
            'shopping-cart': 'fa fa-shopping-cart',
            'building': 'fa fa-building',
            'fa-building': 'fa fa-building',
            'calendar-alt': 'fa fa-calendar',
            'calendar': 'fa fa-calendar-o',
            'fa-calendar-o': 'fa fa-calendar-o',
            'map-marker': 'fa fa-map-marker',
            'map-marker-alt': 'fa fa-map-marker',
            'heart': 'fa fa-heart',
            'stethoscope': 'fa fa-stethoscope',
            'calendar-check': 'fa fa-calendar-check-o',
            'lock': 'fa fa-lock',
            'hand-paper': 'fa fa-hand-paper-o',
            'check': 'fa fa-check',
            'times': 'fa fa-times',
            'star': 'fa fa-star',
            'eye': 'fa fa-eye',
            'eye-slash': 'fa fa-eye-slash',
            'fa-eye-slash': 'fa fa-eye-slash',
            'angle-down': 'fa fa-angle-down',
            'angle-up': 'fa fa-angle-up',
            'arrow-left': 'fa fa-arrow-left',
            'arrow-right': 'fa fa-arrow-right',
            'ban': 'fa fa-ban',
            'phone': 'fa fa-phone',
            'user-md': 'fa fa-user-md',
            'dollar': 'fa fa-dollar',
            'chevron-left': 'fa fa-chevron-left',
            'chevron-right': 'fa fa-chevron-right',
            'chevron-up': 'fa fa-chevron-up',
            'chevron-down': 'fa fa-chevron-down',
            'plus': 'fa fa-plus',
            'plus-circle': 'fa fa-plus-circle',
            'angle-left': 'fa fa-angle-left',
            'angle-double-left': 'fa fa-angle-double-left',
            'angle-right': 'fa fa-angle-right',
            'angle-double-right': 'fa fa-angle-double-right',
            'remove': 'fa fa-remove',
            'heart-o': 'fa fa-heart-o',
            'headphones': 'fa fa-headphones',
            'play': 'fa fa-play',
            'video-camera': 'fa fa-video-camera',
            'exclamation-triangle': 'fa fa-exclamation-triangle',
            'link': 'fa fa-link',
            'external-link': 'fa fa-external-link',
            'lg': 'fa-lg',
            '2x': 'fa-2x',
            '3x': 'fa-3x',
            '4x': 'fa-4x',
            '5x': 'fa-5x'
        };
    }
    register(classMap) {
        for (const key of Object.keys(classMap)) {
            this.classMap[key] = classMap[key];
        }
    }
    getIconClass(name) {
        if (typeof name !== 'string') {
            return '';
        }
        return this.classMap[name] || name;
    }
    getIcons() {
        return this.classMap;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: IconService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: IconService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: IconService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

class IconNextComponent {
    get classNames() {
        const iconClass = this.iconService.getIconClass(this.icon);
        const sizeClass = this.iconService.getIconClass(this.size);
        return {
            ['v-icon']: true,
            [`${iconClass}`]: iconClass !== '',
            [`${sizeClass}`]: sizeClass !== '',
            [`${this.prefix}`]: this.prefix !== ''
        };
    }
    constructor(iconService) {
        this.iconService = iconService;
        this.icon = '';
        this.size = '';
        this.prefix = '';
        this.screenReaderText = '';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: IconNextComponent, deps: [{ token: IconService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: IconNextComponent, selector: "vpg-icon-next", inputs: { icon: "icon", size: "size", prefix: "prefix", screenReaderText: "screenReaderText" }, host: { properties: { "class.v-icon-element": "true" } }, ngImport: i0, template: "<span\n    [ngClass]=\"classNames\"\n    aria-hidden=\"true\"\n>\n</span>\n<span\n    *ngIf=\"screenReaderText\"\n    class=\"sr-only\"\n>\n    {{ screenReaderText }}\n</span>", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: IconNextComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-icon-next', host: {
                        '[class.v-icon-element]': 'true'
                    }, template: "<span\n    [ngClass]=\"classNames\"\n    aria-hidden=\"true\"\n>\n</span>\n<span\n    *ngIf=\"screenReaderText\"\n    class=\"sr-only\"\n>\n    {{ screenReaderText }}\n</span>" }]
        }], ctorParameters: () => [{ type: IconService }], propDecorators: { icon: [{
                type: Input
            }], size: [{
                type: Input
            }], prefix: [{
                type: Input
            }], screenReaderText: [{
                type: Input
            }] } });

class InlineLabelComponent {
    constructor() {
        this.id = '';
        this.size = 'medium';
        this.variant = 'attention';
        this.category = 'urgent';
        this.align = 'left';
        this.text = '';
        this.icon = '';
        this.fullWidth = false;
    }
    get classNames() {
        return {
            'v-inline-label': true,
            'is-full-width': this.fullWidth,
            [`is-${this.size}`]: true,
            [`is-${this.variant}`]: true,
            [`is-${this.category}`]: true,
            [`is-${this.align}-aligned`]: true
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: InlineLabelComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: InlineLabelComponent, selector: "vpg-inline-label", inputs: { id: "id", size: "size", variant: "variant", category: "category", align: "align", text: "text", icon: "icon", fullWidth: "fullWidth" }, host: { properties: { "id": "id", "class": "this.classNames" } }, ngImport: i0, template: "<vpg-icon-next\n    *ngIf=\"icon\"\n    [icon]=\"icon\"\n    id=\"{{id}}-icon\"\n></vpg-icon-next>\n\n<span\n    *ngIf=\"text else content\"\n    class=\"v-inline-label-text\"\n    id=\"{{id}}-text\"\n    [innerHTML]=\"text\"\n>\n</span>\n\n<ng-template #content>\n    <ng-content></ng-content>\n</ng-template>", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: IconNextComponent, selector: "vpg-icon-next", inputs: ["icon", "size", "prefix", "screenReaderText"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: InlineLabelComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-inline-label', host: {
                        '[id]': 'id'
                    }, template: "<vpg-icon-next\n    *ngIf=\"icon\"\n    [icon]=\"icon\"\n    id=\"{{id}}-icon\"\n></vpg-icon-next>\n\n<span\n    *ngIf=\"text else content\"\n    class=\"v-inline-label-text\"\n    id=\"{{id}}-text\"\n    [innerHTML]=\"text\"\n>\n</span>\n\n<ng-template #content>\n    <ng-content></ng-content>\n</ng-template>" }]
        }], propDecorators: { id: [{
                type: Input
            }], size: [{
                type: Input
            }], variant: [{
                type: Input
            }], category: [{
                type: Input
            }], align: [{
                type: Input
            }], text: [{
                type: Input
            }], icon: [{
                type: Input
            }], fullWidth: [{
                type: Input
            }], classNames: [{
                type: HostBinding,
                args: ['class']
            }] } });

class CheckmarkComponent {
    constructor() {
        this.variant = 'earned';
    }
    get classNames() {
        return {
            ['v-checkmark']: true,
            [`is-${this.variant}`]: true
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CheckmarkComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: CheckmarkComponent, selector: "vpg-checkmark", inputs: { variant: "variant" }, host: { properties: { "class": "this.classNames" } }, ngImport: i0, template: '', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CheckmarkComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vpg-checkmark',
                    template: ''
                }]
        }], propDecorators: { variant: [{
                type: Input
            }], classNames: [{
                type: HostBinding,
                args: ['class']
            }] } });

const ENTER$1 = 'Enter';
const SPACE$1 = 'Space';
class ListItemComponent {
    constructor() {
        /**
         * Sets an unique id for the component
         */
        this.id = '';
        /**
         * Indicates if list item includes a button.
         */
        this.hasActionBtn = false;
        /**
         * Setting item number, if list item is numbered.
         */
        this.itemNumber = '';
        /**
         * Setting context to the item number, if list item is numbered. Information not displayed but instead used for accessibility purpose.
         */
        this.itemNumberContext = '';
        /**
         * Setting image source for the components image.
         */
        this.imageSource = '';
        /**
         * Used for setting sub-component's image type.
         */
        this.imageType = ImageType.SmallAvatar;
        /**
         * Provides image alternative text for the screen reader.
         */
        this.alternativeText = '';
        /**
         * Setting line item header text.
         */
        this.headerContent = '';
        /**
         * Heading level for accessibility purpose; set to 3 by default
         */
        this.headingLevel = '3';
        /**
         * Flag for indicating if header content is clickable
         */
        this.isHeaderContentClickable = false;
        /**
         * Setting line item body text.
         */
        this.bodyContent = '';
        /**
         * If line item has action button, setting button action description for the screen reader.
         */
        this.buttonActionDescription = '';
        /**
         * If line item has action button, setting button type. Two options available - icon or tertiary.
         */
        this.buttonType = ButtonType.IconSecondary;
        /**
        * Flag for the icon button.
        */
        this.isCompleted = false;
        /**
        * Flag determining if button is disabled.
        */
        this.isDisabled = false;
        /**
         * If line item has action button that is of type tertiary, setting the button label.
         */
        this.btnLabel = '';
        /**
         * If line item has action button that is of type icon, setting the icon class.
         */
        this.className = IconClassName.Check;
        /**
         * Flag used to wheter all items in list have padding.
         */
        this.hasPadding = false;
        /**
         * Flag used to determine if specific item in list has padding disabled.
         */
        this.listPaddingDisabled = false;
        /**
         * Input used for setting background color of list item.
         */
        this.bgColor = '';
        /**
         * Flag used to determine if list item has text on right.
         */
        this.hasTextRight = false;
        /**
         * Input used to set text on the right side.
         */
        this.textRightValue = '';
        /**
         * Setting context to the text on the right side, if present on list item. Information not displayed but instead used for accessibility purpose.
         */
        this.textRightContext = '';
        /**
        * Input used to set background corner radius of a list item.
        */
        this.bgCornerRadius = '';
        /**
         * Flag used to determine if list item has inline label on right.
         */
        this.hasInlineLabel = false;
        this.hasIcon = false;
        this._labelType = 'attention';
        this._labelCategory = 'urgent';
        this._labelText = '';
        this._iconClass = '';
        this._align = 'left';
        this._fullWidth = false;
        this._size = 'medium';
        /**
         * Event emitter informing that item button was clicked.
         */
        this.itemButtonClicked = new EventEmitter();
        /**
         * Event emitter informing that item button was clicked.
         */
        this.itemHeaderClicked = new EventEmitter();
        /**
         * Used for checking if allowed button type is passed.
         */
        this.buttonTypes = ButtonType;
        this.iconClassName = IconClassName.NoIcon;
        this.completedCheckmarkType = 'earned';
        this.itemNumberContextScreenreaderText = '';
        this.textRightContextScreenreaderText = '';
    }
    set labelType(value) {
        switch (value) {
            case LabelType.Verification:
                this._labelType = 'verification';
                this._labelCategory = 'informative';
                break;
            case LabelType.Completed:
                this._labelType = 'completed';
                this._labelCategory = 'urgent';
                break;
            case LabelType.Complete:
                this._labelType = 'completed';
                this._labelCategory = 'informative';
                break;
            case LabelType.New:
                this._labelType = 'new';
                this._labelCategory = 'urgent';
                break;
            case LabelType.Overdue:
                this._labelType = 'overdue';
                this._labelCategory = 'urgent';
                break;
            case LabelType.Missed:
                this._labelType = 'missed';
                this._labelCategory = 'informative';
                break;
            case LabelType.UrgentNeutral:
                this._labelType = 'neutral';
                this._labelCategory = 'urgent';
                break;
            case LabelType.InformativeNeutral:
                this._labelType = 'neutral';
                this._labelCategory = 'informative';
                break;
            default:
                this._labelType = 'attention';
                this._labelCategory = 'urgent';
                break;
        }
    }
    set labelText(value) {
        this._labelText = value;
    }
    set iconClass(value) {
        this._iconClass = value?.substring(3) || '';
    }
    set rtl(value) {
        this._align = value !== undefined ? 'right' : 'left';
    }
    set longBox(value) {
        this._fullWidth = value || false;
    }
    set isSmall(value) {
        this._size = value ? 'small' : 'medium';
    }
    onItemButtonClicked() {
        this.itemButtonClicked.emit();
    }
    onItemHeaderClicked() {
        if (this.isHeaderContentClickable) {
            this.itemHeaderClicked.emit();
        }
    }
    onKeyUpHeader($event) {
        if (this.isHeaderContentClickable && ($event.code === ENTER$1 || $event.code === SPACE$1)) {
            this.itemHeaderClicked.emit();
        }
    }
    ngOnChanges(changes) {
        if (changes.className && changes.className.currentValue !== changes.className.previousValue) {
            this.iconClassName = changes.className.currentValue;
        }
        this.getClassName(changes);
        this.getScreenreaderContextInfo(changes);
    }
    getClassName(changes) {
        if (changes.buttonType && changes.buttonType.currentValue === ButtonType.Tertiary) {
            this.className = IconClassName.NoIcon;
        }
        if (changes.buttonType && changes.buttonType.currentValue === ButtonType.IconSecondary) {
            this.className = this.iconClassName ? this.iconClassName : IconClassName.Check;
        }
        if (changes.className && this.buttonType === ButtonType.Tertiary) {
            this.className = IconClassName.NoIcon;
        }
    }
    getScreenreaderContextInfo(changes) {
        if (changes.itemNumber || changes.itemNumberContext) {
            this.itemNumberContextScreenreaderText = this.itemNumberContext ? this.itemNumberContext.concat(this.itemNumber, ',') : this.itemNumber;
        }
        if (changes.textRightValue || changes.textRightContext) {
            this.textRightContextScreenreaderText = this.textRightContext ? this.textRightContext.concat(this.textRightValue || '', ',') : this.textRightValue || '';
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ListItemComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: ListItemComponent, selector: "vpg-list-item", inputs: { id: "id", hasActionBtn: "hasActionBtn", itemNumber: "itemNumber", itemNumberContext: "itemNumberContext", imageSource: "imageSource", imageType: "imageType", alternativeText: "alternativeText", headerContent: "headerContent", headingLevel: "headingLevel", isHeaderContentClickable: "isHeaderContentClickable", bodyContent: "bodyContent", buttonActionDescription: "buttonActionDescription", buttonType: "buttonType", isCompleted: "isCompleted", isDisabled: "isDisabled", btnLabel: "btnLabel", className: "className", hasPadding: "hasPadding", listPaddingDisabled: "listPaddingDisabled", bgColor: "bgColor", hasTextRight: "hasTextRight", textRightValue: "textRightValue", textRightContext: "textRightContext", bgCornerRadius: "bgCornerRadius", hasInlineLabel: "hasInlineLabel", hasIcon: "hasIcon", labelType: "labelType", labelText: "labelText", iconClass: "iconClass", rtl: "rtl", longBox: "longBox", isSmall: "isSmall" }, outputs: { itemButtonClicked: "itemButtonClicked", itemHeaderClicked: "itemHeaderClicked" }, usesOnChanges: true, ngImport: i0, template: "<div [ngClass]=\"{'vpg-list-item' : hasPadding && !listPaddingDisabled }\">\n    <div\n        [style.background-color]=\"bgColor\"\n        [style.border-radius]=\"bgCornerRadius\"\n    >\n        <div\n            class=\"list-item-wrapper\"\n            [ngClass]=\"{'vpg-list-item-paddingless' : hasPadding && listPaddingDisabled }\"\n        >\n            <div\n                class=\"list-item-section\"\n                [ngClass]=\"{'vpg-list-item-section-hide' : !itemNumber && !imageSource }\"\n            >\n                <div class=\"list-item-section-wrapper\">\n                    <div\n                        *ngIf=\"itemNumber\"\n                        class=\"sr-only\"\n                        [innerHtml]=\"itemNumberContextScreenreaderText\"\n                    >\n                    </div>\n                    <p\n                        *ngIf=\"itemNumber\"\n                        class=\"vpg-list-item-number\"\n                        [innerHtml]=\"itemNumber\"\n                        [vpgId]=\"id\"\n                        aria-hidden=\"true\"\n                    ></p>\n                    <vpg-image\n                        *ngIf=\"imageSource\"\n                        [imageType]=\"imageType\"\n                        [imageSource]=\"imageSource\"\n                        [alternativeText]=\"alternativeText || ''\"\n                        [id]=\"id\"\n                    >\n                    </vpg-image>\n                </div>\n            </div>\n            <div\n                class=\"list-item-section-main\"\n                [ngClass]=\"{'vpg-list-section-main-extension' : imageSource || itemNumber, 'vpg-list-item-section-shrink' : !itemNumber && !imageSource }\"\n                [vpgId]=\"id\"\n            >\n                <div\n                    class=\"header-section\"\n                    [vpgId]=\"id\"\n                    [ngClass]=\"{ 'vpg-header-clickable' : isHeaderContentClickable }\"\n                    [attr.aria-label]=\"headerContent\"\n                    [attr.aria-level]=\"isHeaderContentClickable ? null : headingLevel\"\n                    [attr.role]=\"isHeaderContentClickable ? 'link' : 'heading'\"\n                    [attr.tabindex]=\"isHeaderContentClickable ? '0' : ''\"\n                    (click)=\"onItemHeaderClicked()\"\n                    (keyup)=\"onKeyUpHeader($event)\"\n                >\n                    {{ headerContent }}\n                </div>\n                <p\n                    *ngIf=\"bodyContent\"\n                    class=\"body-section\"\n                    [vpgId]=\"id\"\n                >\n                    {{ bodyContent }}\n                </p>\n            </div>\n            <div\n                class=\"list-item-section\"\n                *ngIf=\"hasActionBtn\"\n            >\n                <vpg-button\n                    *ngIf=\"!isCompleted && (buttonType === buttonTypes.IconSecondary || buttonType === buttonTypes.Tertiary)\"\n                    class=\"vpg-list-item-button\"\n                    [btnType]=\"buttonType\"\n                    [ariaLabel]=\"buttonActionDescription\"\n                    [label]=\"btnLabel\"\n                    [className]=\"className\"\n                    [isCompleted]=\"isCompleted\"\n                    [isDisabled]=\"isDisabled\"\n                    (buttonClicked)=\"onItemButtonClicked()\"\n                    [id]=\"id + '-list-item-button'\"\n                >\n                </vpg-button>\n                <vpg-checkmark\n                    *ngIf=\"isCompleted\"\n                    class=\"vpg-list-item-button\"\n                    [attr.aria-label]=\"buttonActionDescription || ''\"\n                    [variant]=\"completedCheckmarkType\"\n                    [id]=\"id + '-checkmark'\"\n                ></vpg-checkmark>\n            </div>\n            <div\n                class=\"list-item-section vpg-list-item-inline-label\"\n                *ngIf=\"hasInlineLabel\"\n            >\n                <vpg-inline-label\n                    id=\"{{id}}-list-item-inline-label\"\n                    [variant]=\"_labelType\"\n                    [category]=\"_labelCategory\"\n                    [align]=\"_align\"\n                    [icon]=\"_iconClass\"\n                    [text]=\"_labelText\"\n                    [fullWidth]=\"_fullWidth\"\n                    [size]=\"_size\"\n                >\n                </vpg-inline-label>\n            </div>\n            <div\n                class=\"list-item-section\"\n                *ngIf=\"hasTextRight\"\n            >\n                <div\n                    *ngIf=\"textRightValue\"\n                    class=\"sr-only\"\n                    [innerHtml]=\"textRightContextScreenreaderText\"\n                ></div>\n                <div\n                    [innerHtml]=\"textRightValue\"\n                    class=\"vpg-list-item-text-right\"\n                    aria-hidden=\"true\"\n                ></div>\n            </div>\n        </div>\n    </div>\n</div>\n", styles: [".list-item-section-main .header-section,.vpg-list-item-text-right{font-family:Open Sans,sans-serif;font-weight:700;color:var(--v-color-gray-100);margin:0;text-transform:none}.list-item-section:first-child .vpg-list-item-number,.list-item-section-main .body-section{font-family:Open Sans,sans-serif;color:var(--v-color-gray-100);margin:0}.list-item-wrapper{display:flex;flex:1 auto 1;justify-content:center;align-items:center;padding:20px}.vpg-list-section-main-extension{margin-inline-start:10px}.list-item-section:first-child{flex-basis:max-content}.list-item-section:first-child.vpg-list-item-section-hide{flex:0 0 0}.list-item-section:first-child .list-item-section-wrapper{display:flex;align-items:center}.list-item-section:first-child .list-item-section-wrapper :first-child{margin-inline-end:10px}.list-item-section:first-child .vpg-list-item-number{font-size:1.4rem;line-height:1.3;word-break:break-word;margin:0}.list-item-section-main{flex:2 1 max-content;word-break:break-word}.list-item-section-main.vpg-list-item-section-shrink{flex-basis:min-content}.list-item-section-main .header-section{font-size:1.6rem;line-height:1.4}.list-item-section-main .vpg-header-clickable{cursor:pointer;word-wrap:break-word}.list-item-section-main .body-section{font-size:1.6rem;line-height:1.4}.list-item-section:last-child{flex-basis:max-content;padding-inline-start:10px;text-align:right;word-break:break-word}.vpg-list-item{padding-inline-start:20px;padding-inline-end:20px}.vpg-list-item-paddingless{margin-inline-start:20px;margin-inline-end:20px}.vpg-list-item-text-right{font-size:2rem;line-height:1.4}.vpg-list-item-inline-label{margin-inline-end:-5px}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: InlineLabelComponent, selector: "vpg-inline-label", inputs: ["id", "size", "variant", "category", "align", "text", "icon", "fullWidth"] }, { kind: "component", type: CheckmarkComponent, selector: "vpg-checkmark", inputs: ["variant"] }, { kind: "component", type: ButtonComponent, selector: "vpg-button", inputs: ["id", "buttonSubmit", "label", "isDisabled", "className", "btnType", "ariaLabel", "isCompleted", "buttonSize", "iconOnTheRight", "fullWidth", "buttonAlignment"], outputs: ["buttonClicked"] }, { kind: "component", type: ImageComponent, selector: "vpg-image", inputs: ["id", "imageSource", "imageSourceBackgroundColor", "alternativeText", "imageType"] }, { kind: "directive", type: IdDirective, selector: "[vpgId]", inputs: ["vpgId"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ListItemComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-list-item', template: "<div [ngClass]=\"{'vpg-list-item' : hasPadding && !listPaddingDisabled }\">\n    <div\n        [style.background-color]=\"bgColor\"\n        [style.border-radius]=\"bgCornerRadius\"\n    >\n        <div\n            class=\"list-item-wrapper\"\n            [ngClass]=\"{'vpg-list-item-paddingless' : hasPadding && listPaddingDisabled }\"\n        >\n            <div\n                class=\"list-item-section\"\n                [ngClass]=\"{'vpg-list-item-section-hide' : !itemNumber && !imageSource }\"\n            >\n                <div class=\"list-item-section-wrapper\">\n                    <div\n                        *ngIf=\"itemNumber\"\n                        class=\"sr-only\"\n                        [innerHtml]=\"itemNumberContextScreenreaderText\"\n                    >\n                    </div>\n                    <p\n                        *ngIf=\"itemNumber\"\n                        class=\"vpg-list-item-number\"\n                        [innerHtml]=\"itemNumber\"\n                        [vpgId]=\"id\"\n                        aria-hidden=\"true\"\n                    ></p>\n                    <vpg-image\n                        *ngIf=\"imageSource\"\n                        [imageType]=\"imageType\"\n                        [imageSource]=\"imageSource\"\n                        [alternativeText]=\"alternativeText || ''\"\n                        [id]=\"id\"\n                    >\n                    </vpg-image>\n                </div>\n            </div>\n            <div\n                class=\"list-item-section-main\"\n                [ngClass]=\"{'vpg-list-section-main-extension' : imageSource || itemNumber, 'vpg-list-item-section-shrink' : !itemNumber && !imageSource }\"\n                [vpgId]=\"id\"\n            >\n                <div\n                    class=\"header-section\"\n                    [vpgId]=\"id\"\n                    [ngClass]=\"{ 'vpg-header-clickable' : isHeaderContentClickable }\"\n                    [attr.aria-label]=\"headerContent\"\n                    [attr.aria-level]=\"isHeaderContentClickable ? null : headingLevel\"\n                    [attr.role]=\"isHeaderContentClickable ? 'link' : 'heading'\"\n                    [attr.tabindex]=\"isHeaderContentClickable ? '0' : ''\"\n                    (click)=\"onItemHeaderClicked()\"\n                    (keyup)=\"onKeyUpHeader($event)\"\n                >\n                    {{ headerContent }}\n                </div>\n                <p\n                    *ngIf=\"bodyContent\"\n                    class=\"body-section\"\n                    [vpgId]=\"id\"\n                >\n                    {{ bodyContent }}\n                </p>\n            </div>\n            <div\n                class=\"list-item-section\"\n                *ngIf=\"hasActionBtn\"\n            >\n                <vpg-button\n                    *ngIf=\"!isCompleted && (buttonType === buttonTypes.IconSecondary || buttonType === buttonTypes.Tertiary)\"\n                    class=\"vpg-list-item-button\"\n                    [btnType]=\"buttonType\"\n                    [ariaLabel]=\"buttonActionDescription\"\n                    [label]=\"btnLabel\"\n                    [className]=\"className\"\n                    [isCompleted]=\"isCompleted\"\n                    [isDisabled]=\"isDisabled\"\n                    (buttonClicked)=\"onItemButtonClicked()\"\n                    [id]=\"id + '-list-item-button'\"\n                >\n                </vpg-button>\n                <vpg-checkmark\n                    *ngIf=\"isCompleted\"\n                    class=\"vpg-list-item-button\"\n                    [attr.aria-label]=\"buttonActionDescription || ''\"\n                    [variant]=\"completedCheckmarkType\"\n                    [id]=\"id + '-checkmark'\"\n                ></vpg-checkmark>\n            </div>\n            <div\n                class=\"list-item-section vpg-list-item-inline-label\"\n                *ngIf=\"hasInlineLabel\"\n            >\n                <vpg-inline-label\n                    id=\"{{id}}-list-item-inline-label\"\n                    [variant]=\"_labelType\"\n                    [category]=\"_labelCategory\"\n                    [align]=\"_align\"\n                    [icon]=\"_iconClass\"\n                    [text]=\"_labelText\"\n                    [fullWidth]=\"_fullWidth\"\n                    [size]=\"_size\"\n                >\n                </vpg-inline-label>\n            </div>\n            <div\n                class=\"list-item-section\"\n                *ngIf=\"hasTextRight\"\n            >\n                <div\n                    *ngIf=\"textRightValue\"\n                    class=\"sr-only\"\n                    [innerHtml]=\"textRightContextScreenreaderText\"\n                ></div>\n                <div\n                    [innerHtml]=\"textRightValue\"\n                    class=\"vpg-list-item-text-right\"\n                    aria-hidden=\"true\"\n                ></div>\n            </div>\n        </div>\n    </div>\n</div>\n", styles: [".list-item-section-main .header-section,.vpg-list-item-text-right{font-family:Open Sans,sans-serif;font-weight:700;color:var(--v-color-gray-100);margin:0;text-transform:none}.list-item-section:first-child .vpg-list-item-number,.list-item-section-main .body-section{font-family:Open Sans,sans-serif;color:var(--v-color-gray-100);margin:0}.list-item-wrapper{display:flex;flex:1 auto 1;justify-content:center;align-items:center;padding:20px}.vpg-list-section-main-extension{margin-inline-start:10px}.list-item-section:first-child{flex-basis:max-content}.list-item-section:first-child.vpg-list-item-section-hide{flex:0 0 0}.list-item-section:first-child .list-item-section-wrapper{display:flex;align-items:center}.list-item-section:first-child .list-item-section-wrapper :first-child{margin-inline-end:10px}.list-item-section:first-child .vpg-list-item-number{font-size:1.4rem;line-height:1.3;word-break:break-word;margin:0}.list-item-section-main{flex:2 1 max-content;word-break:break-word}.list-item-section-main.vpg-list-item-section-shrink{flex-basis:min-content}.list-item-section-main .header-section{font-size:1.6rem;line-height:1.4}.list-item-section-main .vpg-header-clickable{cursor:pointer;word-wrap:break-word}.list-item-section-main .body-section{font-size:1.6rem;line-height:1.4}.list-item-section:last-child{flex-basis:max-content;padding-inline-start:10px;text-align:right;word-break:break-word}.vpg-list-item{padding-inline-start:20px;padding-inline-end:20px}.vpg-list-item-paddingless{margin-inline-start:20px;margin-inline-end:20px}.vpg-list-item-text-right{font-size:2rem;line-height:1.4}.vpg-list-item-inline-label{margin-inline-end:-5px}\n"] }]
        }], propDecorators: { id: [{
                type: Input
            }], hasActionBtn: [{
                type: Input
            }], itemNumber: [{
                type: Input
            }], itemNumberContext: [{
                type: Input
            }], imageSource: [{
                type: Input
            }], imageType: [{
                type: Input
            }], alternativeText: [{
                type: Input
            }], headerContent: [{
                type: Input
            }], headingLevel: [{
                type: Input
            }], isHeaderContentClickable: [{
                type: Input
            }], bodyContent: [{
                type: Input
            }], buttonActionDescription: [{
                type: Input
            }], buttonType: [{
                type: Input
            }], isCompleted: [{
                type: Input
            }], isDisabled: [{
                type: Input
            }], btnLabel: [{
                type: Input
            }], className: [{
                type: Input
            }], hasPadding: [{
                type: Input
            }], listPaddingDisabled: [{
                type: Input
            }], bgColor: [{
                type: Input
            }], hasTextRight: [{
                type: Input
            }], textRightValue: [{
                type: Input
            }], textRightContext: [{
                type: Input
            }], bgCornerRadius: [{
                type: Input
            }], hasInlineLabel: [{
                type: Input
            }], hasIcon: [{
                type: Input
            }], labelType: [{
                type: Input
            }], labelText: [{
                type: Input
            }], iconClass: [{
                type: Input
            }], rtl: [{
                type: Input
            }], longBox: [{
                type: Input
            }], isSmall: [{
                type: Input
            }], itemButtonClicked: [{
                type: Output
            }], itemHeaderClicked: [{
                type: Output
            }] } });

class ListComponent {
    constructor() {
        /**
         * Sets an unique id for the component
         */
        this.id = '';
        /**
         * List of items that are to be shown in the list. Items are of type ListItemInputs.
         */
        this.listItems = [];
        /**
         * Indicates if list includes divider lines.
         */
        this.hasDividerLine = true;
        /**
         * Indicates if list items include left and right padding.
         */
        this.hasPadding = false;
        /**
         * Used for accessibility purpose, providing external reference to the component.
         */
        this.labelledByRef = '';
        /**
         * Event emitter informing that list item button was clicked.
         * Emitting list index value
         */
        this.listItemButtonClicked = new EventEmitter();
        /**
         * Event emitter informing that list item header content was clicked.
         * Emitting list index value
         */
        this.listItemHeaderClicked = new EventEmitter();
    }
    onlistItemButtonClicked(index) {
        this.listItemButtonClicked.emit(index);
    }
    onlistItemHeaderClicked(index) {
        this.listItemHeaderClicked.emit(index);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ListComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: ListComponent, selector: "vpg-list", inputs: { id: "id", listItems: "listItems", hasDividerLine: "hasDividerLine", hasPadding: "hasPadding", labelledByRef: "labelledByRef" }, outputs: { listItemButtonClicked: "listItemButtonClicked", listItemHeaderClicked: "listItemHeaderClicked" }, ngImport: i0, template: "<ul\n    *ngIf=\"listItems\"\n    class=\"vpg-list\"\n    [attr.aria-labelledby]=\"labelledByRef\"\n    role=\"list\"\n>\n    <li\n        *ngFor=\"let li of listItems; let i = index\"\n        [id]=\"'option' + i\"\n        class=\"vpg-list-item-container\"\n        role=\"listitem\"\n    >\n        <span class=\"sr-only\">{{ i + 1 }}</span>\n        <vpg-list-item\n            [hasActionBtn]=\"li.hasActionBtn\"\n            [itemNumber]=\"li.itemNumber\"\n            [itemNumberContext]=\"li.itemNumberContext\"\n            [imageSource]=\"li.imageSource\"\n            [imageType]=\"li.imageType\"\n            [alternativeText]=\"li.alternativeText\"\n            [headerContent]=\"li.headerContent\"\n            [bodyContent]=\"li.bodyContent\"\n            [buttonActionDescription]=\"li.buttonActionDescription\"\n            [buttonType]=\"li.buttonType\"\n            [btnLabel]=\"li.btnLabel\"\n            [className]=\"li.className\"\n            [isHeaderContentClickable]=\"li.isHeaderContentClickable\"\n            [isCompleted]=\"li.isCompleted\"\n            [isDisabled]=\"li.isDisabled\"\n            (itemButtonClicked)=\"onlistItemButtonClicked(i)\"\n            (itemHeaderClicked)=\"onlistItemHeaderClicked(i)\"\n            [textRightValue]=\"li.textRightValue\"\n            [textRightContext]=\"li.textRightContext\"\n            [listPaddingDisabled]=\"li.listPaddingDisabled\"\n            [bgColor]=\"li.bgColor\"\n            [bgCornerRadius]=\"li.bgCornerRadius\"\n            [hasInlineLabel]=\"li.hasInlineLabel\"\n            [hasTextRight]=\"li.hasTextRight\"\n            [labelType]=\"li.labelType\"\n            [labelText]=\"li.labelText\"\n            [iconClass]=\"li.iconClass\"\n            [hasIcon]=\"li.hasIcon\"\n            [rtl]=\"li.rtl\"\n            [longBox]=\"li.longBox\"\n            [isSmall]=\"li.isSmall\"\n            [hasPadding]=\"hasPadding\"\n            [id]=\"id + '-' + i\"\n        >\n        </vpg-list-item>\n        <vpg-divider-line *ngIf=\"hasDividerLine\"></vpg-divider-line>\n    </li>\n</ul>\n", dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: DividerLineComponent, selector: "vpg-divider-line" }, { kind: "component", type: ListItemComponent, selector: "vpg-list-item", inputs: ["id", "hasActionBtn", "itemNumber", "itemNumberContext", "imageSource", "imageType", "alternativeText", "headerContent", "headingLevel", "isHeaderContentClickable", "bodyContent", "buttonActionDescription", "buttonType", "isCompleted", "isDisabled", "btnLabel", "className", "hasPadding", "listPaddingDisabled", "bgColor", "hasTextRight", "textRightValue", "textRightContext", "bgCornerRadius", "hasInlineLabel", "hasIcon", "labelType", "labelText", "iconClass", "rtl", "longBox", "isSmall"], outputs: ["itemButtonClicked", "itemHeaderClicked"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ListComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-list', template: "<ul\n    *ngIf=\"listItems\"\n    class=\"vpg-list\"\n    [attr.aria-labelledby]=\"labelledByRef\"\n    role=\"list\"\n>\n    <li\n        *ngFor=\"let li of listItems; let i = index\"\n        [id]=\"'option' + i\"\n        class=\"vpg-list-item-container\"\n        role=\"listitem\"\n    >\n        <span class=\"sr-only\">{{ i + 1 }}</span>\n        <vpg-list-item\n            [hasActionBtn]=\"li.hasActionBtn\"\n            [itemNumber]=\"li.itemNumber\"\n            [itemNumberContext]=\"li.itemNumberContext\"\n            [imageSource]=\"li.imageSource\"\n            [imageType]=\"li.imageType\"\n            [alternativeText]=\"li.alternativeText\"\n            [headerContent]=\"li.headerContent\"\n            [bodyContent]=\"li.bodyContent\"\n            [buttonActionDescription]=\"li.buttonActionDescription\"\n            [buttonType]=\"li.buttonType\"\n            [btnLabel]=\"li.btnLabel\"\n            [className]=\"li.className\"\n            [isHeaderContentClickable]=\"li.isHeaderContentClickable\"\n            [isCompleted]=\"li.isCompleted\"\n            [isDisabled]=\"li.isDisabled\"\n            (itemButtonClicked)=\"onlistItemButtonClicked(i)\"\n            (itemHeaderClicked)=\"onlistItemHeaderClicked(i)\"\n            [textRightValue]=\"li.textRightValue\"\n            [textRightContext]=\"li.textRightContext\"\n            [listPaddingDisabled]=\"li.listPaddingDisabled\"\n            [bgColor]=\"li.bgColor\"\n            [bgCornerRadius]=\"li.bgCornerRadius\"\n            [hasInlineLabel]=\"li.hasInlineLabel\"\n            [hasTextRight]=\"li.hasTextRight\"\n            [labelType]=\"li.labelType\"\n            [labelText]=\"li.labelText\"\n            [iconClass]=\"li.iconClass\"\n            [hasIcon]=\"li.hasIcon\"\n            [rtl]=\"li.rtl\"\n            [longBox]=\"li.longBox\"\n            [isSmall]=\"li.isSmall\"\n            [hasPadding]=\"hasPadding\"\n            [id]=\"id + '-' + i\"\n        >\n        </vpg-list-item>\n        <vpg-divider-line *ngIf=\"hasDividerLine\"></vpg-divider-line>\n    </li>\n</ul>\n" }]
        }], propDecorators: { id: [{
                type: Input
            }], listItems: [{
                type: Input
            }], hasDividerLine: [{
                type: Input
            }], hasPadding: [{
                type: Input
            }], labelledByRef: [{
                type: Input
            }], listItemButtonClicked: [{
                type: Output
            }], listItemHeaderClicked: [{
                type: Output
            }] } });

class CardContentComponent {
    constructor() {
        this.id = '';
        this.contentType = CardContentType.ImageText;
        /**
         * Event emitter informing that list item button was clicked.
         * Emitting list index value
         */
        this.listItemButtonClicked = new EventEmitter();
        /**
         * Event emitter informing that list item header content was clicked.
         * Emitting list index value
         */
        this.listItemHeaderClicked = new EventEmitter();
        this.cardContentType = CardContentType;
    }
    onlistItemButtonClicked(index) {
        this.listItemButtonClicked.emit(index);
    }
    onlistHeaderClicked(index) {
        this.listItemHeaderClicked.emit(index);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CardContentComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: CardContentComponent, selector: "vpg-card-content", inputs: { id: "id", contentType: "contentType", cardContentImgTxtInputs: "cardContentImgTxtInputs", cardContentListInputs: "cardContentListInputs", cardContentPromotionInputs: "cardContentPromotionInputs" }, outputs: { listItemButtonClicked: "listItemButtonClicked", listItemHeaderClicked: "listItemHeaderClicked" }, ngImport: i0, template: "<div *ngIf=\"cardContentImgTxtInputs && (contentType === cardContentType.ImageOnly || contentType === cardContentType.TextOnly || contentType === cardContentType.ImageText)\">\n    <vpg-card-content-img-txt\n        [cardContentType]=\"cardContentImgTxtInputs.cardContentType\"\n        [contentText]=\"cardContentImgTxtInputs.contentText\"\n        [imageSource]=\"cardContentImgTxtInputs.imageSource\"\n        [alternativeText]=\"cardContentImgTxtInputs.alternativeText\"\n        [id]=\"id\"\n    >\n    </vpg-card-content-img-txt>\n</div>\n\n<div *ngIf=\"cardContentListInputs && contentType === cardContentType.List\">\n    <vpg-list\n        [listItems]=\"cardContentListInputs\"\n        (listItemHeaderClicked)=\"onlistHeaderClicked($event)\"\n        (listItemButtonClicked)=\"onlistItemButtonClicked($event)\"\n        [id]=\"id\"\n    ></vpg-list>\n</div>\n\n<div *ngIf=\"cardContentPromotionInputs && contentType === cardContentType.Promotion\">\n    <vpg-card-content-promotion\n        [promotionText]=\"cardContentPromotionInputs.promotionText\"\n        [imageSource]=\"cardContentPromotionInputs.imageSource\"\n        [alternativeText]=\"cardContentPromotionInputs.alternativeText\"\n        [imageSourceBackgroundColor]=\"cardContentPromotionInputs.imageSourceBackgroundColor\"\n        [id]=\"id\"\n    ></vpg-card-content-promotion>\n</div>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: CardContentImgTxtComponent, selector: "vpg-card-content-img-txt", inputs: ["id", "cardContentType", "contentText", "imageSource", "alternativeText"] }, { kind: "component", type: CardContentPromotionComponent, selector: "vpg-card-content-promotion", inputs: ["id", "promotionText", "imageSource", "imageSourceBackgroundColor", "alternativeText"] }, { kind: "component", type: ListComponent, selector: "vpg-list", inputs: ["id", "listItems", "hasDividerLine", "hasPadding", "labelledByRef"], outputs: ["listItemButtonClicked", "listItemHeaderClicked"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CardContentComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-card-content', template: "<div *ngIf=\"cardContentImgTxtInputs && (contentType === cardContentType.ImageOnly || contentType === cardContentType.TextOnly || contentType === cardContentType.ImageText)\">\n    <vpg-card-content-img-txt\n        [cardContentType]=\"cardContentImgTxtInputs.cardContentType\"\n        [contentText]=\"cardContentImgTxtInputs.contentText\"\n        [imageSource]=\"cardContentImgTxtInputs.imageSource\"\n        [alternativeText]=\"cardContentImgTxtInputs.alternativeText\"\n        [id]=\"id\"\n    >\n    </vpg-card-content-img-txt>\n</div>\n\n<div *ngIf=\"cardContentListInputs && contentType === cardContentType.List\">\n    <vpg-list\n        [listItems]=\"cardContentListInputs\"\n        (listItemHeaderClicked)=\"onlistHeaderClicked($event)\"\n        (listItemButtonClicked)=\"onlistItemButtonClicked($event)\"\n        [id]=\"id\"\n    ></vpg-list>\n</div>\n\n<div *ngIf=\"cardContentPromotionInputs && contentType === cardContentType.Promotion\">\n    <vpg-card-content-promotion\n        [promotionText]=\"cardContentPromotionInputs.promotionText\"\n        [imageSource]=\"cardContentPromotionInputs.imageSource\"\n        [alternativeText]=\"cardContentPromotionInputs.alternativeText\"\n        [imageSourceBackgroundColor]=\"cardContentPromotionInputs.imageSourceBackgroundColor\"\n        [id]=\"id\"\n    ></vpg-card-content-promotion>\n</div>\n" }]
        }], propDecorators: { id: [{
                type: Input
            }], contentType: [{
                type: Input
            }], cardContentImgTxtInputs: [{
                type: Input
            }], cardContentListInputs: [{
                type: Input
            }], cardContentPromotionInputs: [{
                type: Input
            }], listItemButtonClicked: [{
                type: Output
            }], listItemHeaderClicked: [{
                type: Output
            }] } });

class CardComponent {
    constructor() {
        /**
         * Sets an unique id for the component
         */
        this.id = '';
        this.cardHeaderInputs = {
            title: ''
        };
        this.cardContentInputs = {
            contentType: CardContentType.ImageText
        };
        /**
         * Event emitter informing that card header button was clicked.
         */
        this.headerButtonClicked = new EventEmitter();
        /**
         * Event emitter informing that card header link was clicked.
         */
        this.headerLinkClicked = new EventEmitter();
        /**
         * Event emitter informing that primary action was clicked
         */
        this.actionPrimaryActionClicked = new EventEmitter();
        /**
         * Event emitter informing that secondary action was clicked
         */
        this.actionSecondaryActionClicked = new EventEmitter();
        /**
         * Event emitter informing that content list item button was clicked.
         * Emitting list index value
         */
        this.contentlistItemButtonClicked = new EventEmitter();
        /**
         * Event emitter informing that list item header content was clicked.
         * Emitting list index value
         */
        this.contentListItemHeaderClicked = new EventEmitter();
        this.CardContentType = CardContentType;
    }
    onHeaderButtonClicked() {
        this.headerButtonClicked.emit();
    }
    onHeaderLinkClicked() {
        this.headerLinkClicked.emit();
    }
    onActionPrimaryClicked() {
        this.actionPrimaryActionClicked.emit();
    }
    onActionSecondaryClicked() {
        this.actionSecondaryActionClicked.emit();
    }
    onContentlistItemButtonClicked(itemIndex) {
        this.contentlistItemButtonClicked.emit(itemIndex);
    }
    onContentListItemHeaderClicked(index) {
        this.contentListItemHeaderClicked.emit(index);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CardComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: CardComponent, selector: "vpg-card", inputs: { id: "id", cardHeaderInputs: "cardHeaderInputs", cardContentInputs: "cardContentInputs", cardActionInputs: "cardActionInputs" }, outputs: { headerButtonClicked: "headerButtonClicked", headerLinkClicked: "headerLinkClicked", actionPrimaryActionClicked: "actionPrimaryActionClicked", actionSecondaryActionClicked: "actionSecondaryActionClicked", contentlistItemButtonClicked: "contentlistItemButtonClicked", contentListItemHeaderClicked: "contentListItemHeaderClicked" }, ngImport: i0, template: "<div class=\"vpg-card-main\">\n    <vpg-container>\n        <div class=\"vpg-card-main-container\">\n            <div class=\"vpg-card-header-container\">\n                <vpg-card-header\n                    [title]=\"cardHeaderInputs.title\"\n                    [ariaLevel]=\"cardHeaderInputs.ariaLevel || '2'\"\n                    [subtitle]=\"cardHeaderInputs.subtitle\"\n                    [ariaBtnDesc]=\"cardHeaderInputs.ariaBtnDesc\"\n                    [className]=\"cardHeaderInputs.className\"\n                    [hasAction]=\"cardHeaderInputs.hasAction\"\n                    (headerButtonClicked)=\"onHeaderButtonClicked()\"\n                    (headerLinkClicked)=\"onHeaderLinkClicked()\"\n                    [id]=\"id\"\n                >\n                </vpg-card-header>\n            </div>\n\n            <div\n                class=\"vpg-card-content-container\"\n                [ngClass]=\"{\n                    'vpg-card-content-container-only-text' : cardContentInputs.contentType ===  CardContentType.TextOnly,\n                    'vpg-card-content-container-list' : cardContentInputs.contentType ===  CardContentType.List }\"\n                >\n                <vpg-card-content\n                    [contentType]=\"cardContentInputs.contentType\"\n                    [cardContentListInputs]=\"cardContentInputs.cardContentListInputs\"\n                    [cardContentImgTxtInputs]=\"cardContentInputs.cardContentImgTxtInputs\"\n                    [cardContentPromotionInputs]=\"cardContentInputs.cardContentPromotionInputs\"\n                    (listItemButtonClicked)=\"onContentlistItemButtonClicked($event)\"\n                    (listItemHeaderClicked)=\"onContentListItemHeaderClicked($event)\"\n                    [id]=\"id\"\n                >\n                </vpg-card-content>\n            </div>\n\n            <div\n                *ngIf=\"cardActionInputs\"\n                class=\"vpg-card-action-container\"\n                [ngClass]=\"{ 'vpg-card-action-container-list' : cardContentInputs.contentType === CardContentType.List }\"\n            >\n                <vpg-card-action\n                    [actionType]=\"cardActionInputs.actionType\"\n                    [primaryLabel]=\"cardActionInputs.primaryLabel\"\n                    [secondaryLabel]=\"cardActionInputs.secondaryLabel\"\n                    [ariaLabel]=\"cardActionInputs.ariaLabel\"\n                    [actionLinkURL]=\"cardActionInputs.actionLinkURL\"\n                    (primaryActionClicked)=\"onActionPrimaryClicked()\"\n                    (secondaryActionClicked)=\"onActionSecondaryClicked()\"\n                    [id]=\"id\"\n                ></vpg-card-action>\n            </div>\n        </div>\n    </vpg-container>\n</div>\n", styles: [".vpg-card-main{max-width:100%}.vpg-card-main .vpg-card-main-container .vpg-card-content-container{margin:20px -20px}.vpg-card-main .vpg-card-main-container .vpg-card-content-container-only-text{margin-top:-10px;margin-inline-end:0}.vpg-card-main .vpg-card-main-container .vpg-card-content-container-list{margin-inline-start:0;margin-bottom:0;margin-inline-end:0}.vpg-card-main .vpg-card-main-container .vpg-card-action-container-list{margin-top:20px}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: ContainerComponent, selector: "vpg-container", inputs: ["variant", "borderRadius", "hover"] }, { kind: "component", type: CardHeaderComponent, selector: "vpg-card-header", inputs: ["id", "title", "subtitle", "ariaBtnDesc", "className", "hasAction", "ariaLevel"], outputs: ["headerButtonClicked", "headerLinkClicked"] }, { kind: "component", type: CardActionComponent, selector: "vpg-card-action", inputs: ["id", "actionType", "primaryLabel", "secondaryLabel", "ariaLabel", "actionLinkURL"], outputs: ["primaryActionClicked", "secondaryActionClicked"] }, { kind: "component", type: CardContentComponent, selector: "vpg-card-content", inputs: ["id", "contentType", "cardContentImgTxtInputs", "cardContentListInputs", "cardContentPromotionInputs"], outputs: ["listItemButtonClicked", "listItemHeaderClicked"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CardComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-card', template: "<div class=\"vpg-card-main\">\n    <vpg-container>\n        <div class=\"vpg-card-main-container\">\n            <div class=\"vpg-card-header-container\">\n                <vpg-card-header\n                    [title]=\"cardHeaderInputs.title\"\n                    [ariaLevel]=\"cardHeaderInputs.ariaLevel || '2'\"\n                    [subtitle]=\"cardHeaderInputs.subtitle\"\n                    [ariaBtnDesc]=\"cardHeaderInputs.ariaBtnDesc\"\n                    [className]=\"cardHeaderInputs.className\"\n                    [hasAction]=\"cardHeaderInputs.hasAction\"\n                    (headerButtonClicked)=\"onHeaderButtonClicked()\"\n                    (headerLinkClicked)=\"onHeaderLinkClicked()\"\n                    [id]=\"id\"\n                >\n                </vpg-card-header>\n            </div>\n\n            <div\n                class=\"vpg-card-content-container\"\n                [ngClass]=\"{\n                    'vpg-card-content-container-only-text' : cardContentInputs.contentType ===  CardContentType.TextOnly,\n                    'vpg-card-content-container-list' : cardContentInputs.contentType ===  CardContentType.List }\"\n                >\n                <vpg-card-content\n                    [contentType]=\"cardContentInputs.contentType\"\n                    [cardContentListInputs]=\"cardContentInputs.cardContentListInputs\"\n                    [cardContentImgTxtInputs]=\"cardContentInputs.cardContentImgTxtInputs\"\n                    [cardContentPromotionInputs]=\"cardContentInputs.cardContentPromotionInputs\"\n                    (listItemButtonClicked)=\"onContentlistItemButtonClicked($event)\"\n                    (listItemHeaderClicked)=\"onContentListItemHeaderClicked($event)\"\n                    [id]=\"id\"\n                >\n                </vpg-card-content>\n            </div>\n\n            <div\n                *ngIf=\"cardActionInputs\"\n                class=\"vpg-card-action-container\"\n                [ngClass]=\"{ 'vpg-card-action-container-list' : cardContentInputs.contentType === CardContentType.List }\"\n            >\n                <vpg-card-action\n                    [actionType]=\"cardActionInputs.actionType\"\n                    [primaryLabel]=\"cardActionInputs.primaryLabel\"\n                    [secondaryLabel]=\"cardActionInputs.secondaryLabel\"\n                    [ariaLabel]=\"cardActionInputs.ariaLabel\"\n                    [actionLinkURL]=\"cardActionInputs.actionLinkURL\"\n                    (primaryActionClicked)=\"onActionPrimaryClicked()\"\n                    (secondaryActionClicked)=\"onActionSecondaryClicked()\"\n                    [id]=\"id\"\n                ></vpg-card-action>\n            </div>\n        </div>\n    </vpg-container>\n</div>\n", styles: [".vpg-card-main{max-width:100%}.vpg-card-main .vpg-card-main-container .vpg-card-content-container{margin:20px -20px}.vpg-card-main .vpg-card-main-container .vpg-card-content-container-only-text{margin-top:-10px;margin-inline-end:0}.vpg-card-main .vpg-card-main-container .vpg-card-content-container-list{margin-inline-start:0;margin-bottom:0;margin-inline-end:0}.vpg-card-main .vpg-card-main-container .vpg-card-action-container-list{margin-top:20px}\n"] }]
        }], propDecorators: { id: [{
                type: Input
            }], cardHeaderInputs: [{
                type: Input
            }], cardContentInputs: [{
                type: Input
            }], cardActionInputs: [{
                type: Input
            }], headerButtonClicked: [{
                type: Output
            }], headerLinkClicked: [{
                type: Output
            }], actionPrimaryActionClicked: [{
                type: Output
            }], actionSecondaryActionClicked: [{
                type: Output
            }], contentlistItemButtonClicked: [{
                type: Output
            }], contentListItemHeaderClicked: [{
                type: Output
            }] } });

class ControlValueAccessorConnector {
    constructor() {
        this.parentForm = new UntypedFormGroup({});
        this.fieldName = '';
        this.customValidators = [];
        this.isDisabled = false;
        this.value = '';
        this.valueRestrictedByLength = '';
        this.errorMessage = '';
    }
    get formField() {
        return this.parentForm.get(this.fieldName);
    }
    writeValue(val) {
        if (val !== undefined) {
            this.value = val;
        }
    }
    registerOnChange(fn) {
        this.changed = fn;
    }
    registerOnTouched(fn) {
        this.touched = fn;
    }
    setDisabledState(isDisabled) {
        this.isDisabled = isDisabled;
    }
    onChange(event) {
        const value = event.target.value;
        this.changed(value);
    }
    getErrors() {
        this.errorMessage = '';
        const fieldErrors = this.formField.errors;
        if (this.customValidators?.length && fieldErrors) {
            this.addCustomValidatorsMessages(fieldErrors);
        }
        return this.errorMessage;
    }
    addCustomValidatorsMessages(fieldErrors) {
        this.customValidators.forEach((validator) => {
            if (fieldErrors && validator in fieldErrors) {
                this.errorMessage = this.errorMessage.length ? `${this.errorMessage}; ${fieldErrors.message}` : `${fieldErrors.message}`;
            }
        });
    }
    onChangeWithMaxLengthRestriction(event, maxLength) {
        const input = event.target;
        const inputValue = input.value;
        this.valueRestrictedByLength = this.setValueRestrictedByLength(maxLength, inputValue);
        input.value = this.valueRestrictedByLength;
        this.changed(this.valueRestrictedByLength);
    }
    setValueRestrictedByLength(maxLength, inputValue) {
        if (maxLength && inputValue.length > maxLength) {
            return inputValue.slice(0, maxLength);
        }
        return inputValue;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ControlValueAccessorConnector, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ControlValueAccessorConnector }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ControlValueAccessorConnector, decorators: [{
            type: Injectable
        }], propDecorators: { parentForm: [{
                type: Input
            }], fieldName: [{
                type: Input
            }], customValidators: [{
                type: Input
            }] } });

class DropdownResultItemComponent {
    constructor() {
        /**
         * Sets an unique id for the component
         */
        this.id = '';
        /**
        * Class name for font awesome icon (only light types are supported)
        */
        this.iconClassName = IconClassName.NoIcon;
        /**
         * Provides image alternative text for the screen reader.
         */
        this.alternativeText = '';
        /**
        * Setting line item body text.
        */
        this.text = '';
        /**
        * Setting item screen reader text.
        */
        this.screenReaderText = '';
        /**
         * Flag indicating if icon is included.
         */
        this.hasIcon = false;
        /**
         * Flag indicating if image is included.
         */
        this.hasImage = false;
        /**
         * Image type of the image used.
         */
        this.imageType = ImageType.SmallAvatar;
        /**
         * Image source if component includes image/
         */
        this.imageSource = '';
        /**
         * Icon size ration in select option.
         */
        this.sizeRatio = 'fa-lg';
        this.iconClass = IconClassName.NoIcon;
    }
    ngOnInit() {
        this.iconClass = this.iconClassName ? this.iconClassName : IconClassName.NoIcon;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: DropdownResultItemComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: DropdownResultItemComponent, selector: "vpg-dropdown-result-item", inputs: { id: "id", iconClassName: "iconClassName", alternativeText: "alternativeText", text: "text", screenReaderText: "screenReaderText", hasIcon: "hasIcon", hasImage: "hasImage", imageType: "imageType", imageSource: "imageSource" }, ngImport: i0, template: "<div class=\"vpg-dropdown-results-item-wrapper\">\n    <div class=\"vpg-dropdown-results-item-section\" *ngIf=\"hasIcon\">\n        <vpg-icon [size]=\"sizeRatio\" [className]=\"iconClass\" [id]=\"id\"></vpg-icon>\n    </div>\n    <div class=\"vpg-dropdown-results-item-section-main\">\n        <div\n            *ngIf=\"text\"\n            [attr.aria-label]=\"screenReaderText\"\n            class=\"vpg-dropdown-result-item-body-section\"\n            [vpgId]=\"id\"\n        >\n            {{text}}\n        </div>\n    </div>\n</div>", styles: [".vpg-dropdown-results-item-section-main .vpg-dropdown-result-item-body-section{font-family:Open Sans,sans-serif;color:var(--v-color-gray-100);margin:0}*{box-sizing:border-box}.vpg-dropdown-results-item-wrapper{display:flex;overflow:auto}.vpg-dropdown-results-item-section{font-size:1.6rem;line-height:1.4;display:flex;align-items:center;padding-inline-start:10px}.vpg-dropdown-results-item-section-main{position:relative;margin-inline-start:10px;flex:2 1;min-height:48px}.vpg-dropdown-results-item-section-main .vpg-dropdown-result-item-body-section{position:absolute;top:50%;transform:translateY(-50%);font-size:1.4rem;line-height:1.3;color:var(--v-color-gray-100)}.vpg-dropdown-result-item-with-border{border-bottom:1px solid var(--v-color-gray-30)}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: IconComponent, selector: "vpg-icon", inputs: ["id", "className", "ariaLabel", "size", "prefix"], outputs: ["iconClicked"] }, { kind: "directive", type: IdDirective, selector: "[vpgId]", inputs: ["vpgId"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: DropdownResultItemComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-dropdown-result-item', template: "<div class=\"vpg-dropdown-results-item-wrapper\">\n    <div class=\"vpg-dropdown-results-item-section\" *ngIf=\"hasIcon\">\n        <vpg-icon [size]=\"sizeRatio\" [className]=\"iconClass\" [id]=\"id\"></vpg-icon>\n    </div>\n    <div class=\"vpg-dropdown-results-item-section-main\">\n        <div\n            *ngIf=\"text\"\n            [attr.aria-label]=\"screenReaderText\"\n            class=\"vpg-dropdown-result-item-body-section\"\n            [vpgId]=\"id\"\n        >\n            {{text}}\n        </div>\n    </div>\n</div>", styles: [".vpg-dropdown-results-item-section-main .vpg-dropdown-result-item-body-section{font-family:Open Sans,sans-serif;color:var(--v-color-gray-100);margin:0}*{box-sizing:border-box}.vpg-dropdown-results-item-wrapper{display:flex;overflow:auto}.vpg-dropdown-results-item-section{font-size:1.6rem;line-height:1.4;display:flex;align-items:center;padding-inline-start:10px}.vpg-dropdown-results-item-section-main{position:relative;margin-inline-start:10px;flex:2 1;min-height:48px}.vpg-dropdown-results-item-section-main .vpg-dropdown-result-item-body-section{position:absolute;top:50%;transform:translateY(-50%);font-size:1.4rem;line-height:1.3;color:var(--v-color-gray-100)}.vpg-dropdown-result-item-with-border{border-bottom:1px solid var(--v-color-gray-30)}\n"] }]
        }], propDecorators: { id: [{
                type: Input
            }], iconClassName: [{
                type: Input
            }], alternativeText: [{
                type: Input
            }], text: [{
                type: Input
            }], screenReaderText: [{
                type: Input
            }], hasIcon: [{
                type: Input
            }], hasImage: [{
                type: Input
            }], imageType: [{
                type: Input
            }], imageSource: [{
                type: Input
            }] } });

const ENTER = 'Enter';
const SPACE = 'Space';
const ARROW_DOWN = 'ArrowDown';
const ARROW_UP = 'ArrowUp';
const BACKSPACE = 'Backspace';
const KEY = 'Key';
const HOME = 'Home';
const END = 'End';
const TAB = 'Tab';
const ESCAPE = 'Escape';
const DIGIT = 'Digit';
const DEBOUNCE_TIME$1 = 300;
const ARROW_LEFT = 'ArrowLeft';
const ARROW_RIGHT = 'ArrowRight';
class DropdownComponent extends ControlValueAccessorConnector {
    constructor() {
        super(...arguments);
        /**
         * Sets an unique id for the component
         */
        this.id = '';
        /**
         * Parent form component instance will be part of.
         */
        this.parentForm = new UntypedFormGroup({});
        /**
         * Field name assigned to the component within the parent form.
         */
        this.fieldName = '';
        /**
         * Label for select component
         */
        this.label = '';
        /**
         * Placeholder for the input field.
         */
        this.placeholder = '';
        /**
         * Aria label, for WCAG compliance.
         */
        this.ariaLabel = '';
        /**
         * Array with data used for setting values for the dropdown components.
         */
        this.data = [];
        /**
         * Data in the dropdown section of the component can be preselected by providing the selectedOption input.
         */
        this.selectedOption = '';
        /**
         * Disables the dropdown based on the form field rules and/or interaction.
         */
        this.isDisabled = false;
        /**
         * Min number a characters user is to enter before search filter is triggered.
         */
        this.minCharacters = 3;
        /**
         * Text displayed in the header when no results resulted from the search
         */
        this.noResultsHeader = '';
        /**
         * Text displayed in the body when no results resulted from the search
         */
        this.noResultsText = '';
        /**
         * Label can be visually hidden, but used for wcag needs
         */
        this.showLabel = true;
        /**
         * Any additional validation for this form field. Errors for custom validators are supposed to contain an object with two key-value properties, first being a validator name thats to be sent as part of this input, and the second being message with accompanying text to be displayed if the error occurs.
         */
        this.customValidators = [];
        /**
         * Event emitter triggered when value selected. Emits selected value and selected index
         */
        this.valueSelected = new EventEmitter();
        /**
         * Arrow icon size ratio and declaration.
         */
        this.iconClassName = IconClassName;
        this.doubleIconSize = 'fa-2x';
        /**
         * Local flag indicating is value was selected from the offered results.
         */
        this.isValueSelected = false;
        /**
         * Local flag indicating is dropdown menu focused.
         */
        this.isActive = false;
        this.isRequired = false;
        this.fieldNameId = '';
        this.dropdownIconId = '';
        this.dropdownInputId = '';
        this.dropdownResultsId = '';
        this.elementHoverIndex = -1;
        this.elementInFocus = -1;
        this.newSearchValue = '';
        this.oldSearchValue = '';
        this.searchValueSubscription = new Subscription();
        this.searchResults = [];
        this.searchTerm$ = new Subject();
        this.inputSubscription = new Subscription();
        this.selected = '';
    }
    get containerElement() {
        return this.container.nativeElement;
    }
    ngOnInit() {
        const dropdownFormControl = this.parentForm.get(this.fieldName);
        if (!dropdownFormControl) {
            return;
        }
        this.isRequired = dropdownFormControl.hasValidator(Validators.required);
    }
    ngOnChanges(changes) {
        if (changes.data &&
            changes.data.currentValue &&
            !changes.data.currentValue.find((item) => item.text === this.selectedOption)) {
            this.isValueSelected = false;
            this.searchResults = [...this.data];
            this.selected = this.isValueSelected ? this.selectedOption : '';
        }
        if (changes.selectedOption) {
            this.selectedValue(this.selectedOption);
        }
        if (changes.fieldName) {
            this.fieldNameId = this.fieldName + 'Id';
            this.dropdownIconId = this.fieldName + 'dropdownIconId';
            this.dropdownInputId = this.fieldName + 'dropdownInputId';
            this.dropdownResultsId = this.fieldName + 'dropdownResultsId';
        }
    }
    ngOnDestroy() {
        this.searchValueSubscription.unsubscribe();
        this.inputSubscription.unsubscribe();
    }
    onClick($event) {
        const enterOnSubmitForm = $event.pointerId === -1;
        if (!this.hasParentClass($event.target, 'vpg-dropdown-option-container') && !enterOnSubmitForm) {
            this.resetValues();
            this.isActive = false;
            if (this.searchInput) {
                this.searchInput.nativeElement.value = '';
            }
        }
    }
    onKeyUp($event) {
        if (!this.hasParentClass($event.target, 'vpg-dropdown-option-container')) {
            this.whenTargetElementHasNoParentClass();
        }
        if (this.isActive) {
            this.modifySearchValue($event);
            if (this.newSearchValue !== this.oldSearchValue && this.newSearchValue.length) {
                this.onSearchValueChange();
            }
            if (!this.newSearchValue.length && this.oldSearchValue !== this.newSearchValue) {
                this.whenNewSearchValueIsEmpty();
            }
        }
    }
    modifySearchValue($event) {
        if (this.newSearchValue.length && $event.code === BACKSPACE) {
            this.newSearchValue = this.newSearchValue.slice(0, -1);
        }
        if ($event.code.startsWith(KEY) || $event.code.startsWith(DIGIT)) {
            this.newSearchValue += $event.key;
        }
    }
    onSearchValueChange() {
        this.oldSearchValue = this.newSearchValue;
        const searchValue = of(this.newSearchValue);
        this.createSubscription(searchValue);
    }
    createSubscription(searchValue) {
        this.searchValueSubscription = searchValue.subscribe((val) => {
            const index = this.data.findIndex((x) => x.text.toLocaleLowerCase().startsWith(val.toLocaleLowerCase()));
            if (this.isActive) {
                this.elementInFocus = index;
            }
        });
    }
    whenNewSearchValueIsEmpty() {
        this.oldSearchValue = this.newSearchValue;
        this.elementInFocus = -1;
    }
    selectValue(value) {
        this.selectedValue(value);
    }
    whenTargetElementHasNoParentClass() {
        this.isActive = false;
        this.elementInFocus = -1;
    }
    /**
     * Getting the form control from the parent form and setting the local value variable.
     */
    get formField() {
        let formControl = new UntypedFormControl();
        if (Object.keys(this.parentForm.controls).length) {
            this.value = this.parentForm.get(this.fieldName).value;
            formControl = this.parentForm.get(this.fieldName);
        }
        return formControl;
    }
    selectValueAndFocusContainer(text) {
        this.selectedValue(text);
        this.containerElement.focus();
    }
    selectedValue(text) {
        const selection = this.data?.find((x) => x.text === text);
        if (selection?.text) {
            this.selectedOption = text;
            this.isValueSelected = true;
            this.selected = this.selectedOption;
            this.formField.setValue(text);
            this.valueSelected.emit(text);
            document.getElementById(this.dropdownInputId)?.focus();
            this.isActive = false;
            if (this.searchInput) {
                this.searchInput.nativeElement.value = '';
            }
        }
        else {
            this.isValueSelected = false;
        }
    }
    hasParentClass(child, className) {
        if (child.className.split(' ').indexOf(className) >= 0 && child.id === this.fieldNameId) {
            return true;
        }
        try {
            const elNode = child.parentNode;
            return elNode && this.hasParentClass(elNode, className);
        }
        catch (TypeError) {
            return false;
        }
    }
    getDomElements() {
        return document.querySelectorAll('.vpg-dropdown-results-element');
    }
    hasDomElements() {
        return this.getDomElements().length > 0;
    }
    moveToOptions(istFirstOption = true) {
        this.isActive = true;
        if (this.searchResults.length === 0) {
            return;
        }
        setTimeout(() => {
            if (istFirstOption) {
                this.setFocusOnOption(0);
                return;
            }
            this.setFocusOnOption(this.searchResults.length - 1);
        }, 500);
    }
    navigateOptions($event, i, text) {
        const domElements = this.getDomElements();
        if (domElements.length === 0) {
            return;
        }
        const maxIndex = this.searchResults.length - 1;
        if ($event.code === SPACE || $event.code === ENTER) {
            $event.preventDefault();
            $event.stopPropagation();
            this.isActive = false;
            const val = this.searchResults[i].text;
            this.selectedValue(val);
            this.containerElement.focus();
        }
        if ($event.code === ARROW_DOWN) {
            if (i < maxIndex) {
                domElements[++i].focus();
                this.elementInFocus = i;
                return;
            }
            domElements[0].focus();
            this.elementInFocus = 0;
        }
        if ($event.code === ARROW_UP) {
            if (i > 0) {
                domElements[--i].focus();
                this.elementInFocus = i;
                return;
            }
            domElements[maxIndex].focus();
            this.elementInFocus = maxIndex;
        }
        if ($event.code === END || $event.code === HOME) {
            this.setFocus($event.code, maxIndex, domElements);
        }
        if ($event.code === TAB) {
            this.selectValue(text);
        }
        if ($event.code === ESCAPE) {
            this.isActive = false;
            document.getElementById(this.dropdownInputId)?.focus();
        }
        if ($event.code === ARROW_RIGHT) {
            document.getElementById(this.dropdownInputId)?.focus();
        }
        if ($event.code === ARROW_LEFT) {
            const value = this.searchInput?.nativeElement.value;
            const input = document.getElementById(this.dropdownInputId);
            input?.focus();
            input?.setSelectionRange(value.length - 1, value.length);
        }
    }
    resetValues() {
        this.newSearchValue = '';
        this.oldSearchValue = '';
        this.elementInFocus = -1;
        this.searchResults = this.data;
    }
    toggleListAndFocusInput($event) {
        if (this.isActive && ($event?.target).id === this.dropdownInputId || this.isDisabled) {
            return;
        }
        this.isActive = !this.isActive;
        if (this.isActive) {
            const value = this.searchInput?.nativeElement.value;
            this.handleInputActions(value);
        }
        setTimeout(() => {
            const inputField = document.getElementById(this.dropdownInputId);
            inputField?.focus();
        });
    }
    onUserInput($event) {
        this.isActive = true;
        const value = $event.target.value;
        this.searchTerm$.next(value);
        this.inputSubscription = this.searchTerm$.pipe(debounceTime(DEBOUNCE_TIME$1)).subscribe((val) => {
            this.handleInputActions(val);
        });
    }
    handleInputActions(value) {
        if (value.length >= this.minCharacters) {
            this.searchResults = this.data.filter((x) => {
                return x.text.toLowerCase().startsWith(value.toLowerCase().toString());
            });
            if (this.searchResults.length && value) {
                this.elementInFocus = 0;
            }
        }
        else {
            this.searchResults = this.data;
        }
    }
    onInputKeyDown(event) {
        const value = this.searchInput?.nativeElement.value;
        this.handleInputActions(value);
        if (event.code === ARROW_DOWN && event.altKey) {
            this.isActive = true;
            return;
        }
        if (event.code === ARROW_DOWN) {
            this.moveToOptions();
            return;
        }
        if (event.code === ARROW_UP) {
            this.moveToOptions(false);
            return;
        }
        if (event.code === ENTER || event.code === TAB) {
            this.isActive = false;
            if (this.searchInput) {
                this.searchInput.nativeElement.value = '';
            }
            return;
        }
        if (event.code === ESCAPE && this.isActive) {
            this.isActive = false;
            return;
        }
        if (event.code === ESCAPE && !this.isActive) {
            if (this.searchInput) {
                this.searchInput.nativeElement.value = '';
            }
        }
    }
    setFocus(code, maxIndex, elements) {
        const elementToFocusIndex = code === HOME ? 0 : maxIndex;
        elements[elementToFocusIndex].focus();
        this.elementInFocus = elementToFocusIndex;
    }
    setFocusOnOption(index) {
        const domElements = this.getDomElements();
        domElements[index].focus();
        this.elementInFocus = index;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: DropdownComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: DropdownComponent, selector: "vpg-dropdown", inputs: { id: "id", parentForm: "parentForm", fieldName: "fieldName", label: "label", placeholder: "placeholder", ariaLabel: "ariaLabel", data: "data", selectedOption: "selectedOption", isDisabled: "isDisabled", minCharacters: "minCharacters", noResultsHeader: "noResultsHeader", noResultsText: "noResultsText", showLabel: "showLabel", customValidators: "customValidators" }, outputs: { valueSelected: "valueSelected" }, host: { listeners: { "document:click": "onClick($event)", "document:keyup": "onKeyUp($event)" } }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => DropdownComponent),
                multi: true
            }
        ], viewQueries: [{ propertyName: "searchInput", first: true, predicate: ["searchInput"], descendants: true, static: true }, { propertyName: "container", first: true, predicate: ["container"], descendants: true, static: true }], usesInheritance: true, usesOnChanges: true, ngImport: i0, template: "<div class=\"vpg-dropdown-main\" \n    [ngClass]=\"{'vpg-dropdown-main-with-error': formField.touched && formField.invalid && formField.errors}\">\n    <label\n        *ngIf=\"label && showLabel\"\n        class=\"vpg-dropdown-label\"\n        id=\"{{fieldName}}Label\"\n        [for]=\"dropdownInputId\"\n    >\n        {{ label }}\n    </label>\n    <span\n        *ngIf=\"isRequired\"\n        class=\"vpg-required-indicator\"\n        aria-hidden=\"true\"\n    >*</span>\n    <div\n        #container\n        class=\"vpg-dropdown-option-container\"\n        [ngClass]=\"{\n            'vpg-dropdown-option-container-disabled': isDisabled,\n            'vpg-dropdown-active': isActive,\n            'vpg-dropdown-invalid': formField.invalid && formField.touched\n        }\"\n        [id]=\"fieldNameId\"\n        (click)=\"toggleListAndFocusInput($event)\"\n    >\n        <div class=\"vpg-dropdown-text-container-wrapper\"\n            [ngClass]=\"{'vpg-dropdown-input-field-placeholder-not-active': !isActive}\">\n            <input\n                #searchInput\n                [id]=\"dropdownInputId\"\n                [ngClass]=\"{'vpg-dropdown-input-field-background': isDisabled}\"\n                type=\"text\"\n                role=\"combobox\"\n                [attr.aria-invalid]=\"formField.invalid && formField.touched\"\n                [attr.aria-expanded]=\"isActive\"\n                [attr.aria-controls]=\"dropdownResultsId\"\n                [attr.aria-label]=\"label\"\n                [attr.aria-describedby]=\"fieldName + 'no-results ' + fieldName + '-validation-error'\"\n                [attr.aria-required]=\"isRequired\"\n                (input)=\"onUserInput($event)\"\n                (keydown)=\"onInputKeyDown($event)\"\n                [placeholder]=\"selected || placeholder\"\n                [disabled]=\"isDisabled\"\n                tabindex=\"0\"\n                (blur)=\"touched()\"\n            >\n            <vpg-icon\n                [id]=\"dropdownIconId\"\n                class=\"vpg-dropdown-arrow-wrapper\"\n                [className]=\"isActive ? iconClassName.AngleUp : iconClassName.AngleDown\"\n                [size]=\"doubleIconSize\"\n                role=\"button\"\n                [attr.aria-disabled]=\"isDisabled\"\n                [attr.aria-label]=\"label\"\n                [attr.aria-expanded]=\"isActive\"\n                [attr.aria-controls]=\"dropdownResultsId\"\n                tabindex=\"-1\"\n            ></vpg-icon>\n        </div>\n\n        <div\n            *ngIf=\"isActive\"\n            class=\"vpg-dropdown-result-items-wrapper\"\n            >\n            <ng-container *ngIf=\"searchResults.length > 0\">\n                <ng-container *ngTemplateOutlet=\"results\"></ng-container>\n            </ng-container>\n\n            <ng-container *ngIf=\"searchResults.length === 0 && noResultsHeader && noResultsText\">\n                <ng-container *ngTemplateOutlet=\"noResults\"></ng-container>\n            </ng-container>\n        </div>\n    </div>\n\n    <div\n        *ngIf=\"parentForm\"\n        class=\"vpg-dropdown-additional-elements\"\n        [attr.aria-hidden]=\"isDisabled\">\n        <span\n            *ngIf=\"formField.touched && formField.invalid && formField.errors\"\n            class=\"vpg-dropdown-error\"\n            role=\"alert\"\n            aria-live=\"assertive\"\n            id=\"{{fieldName}}-validation-error\">\n            {{ getErrors() }}\n        </span>\n    </div>\n</div>\n\n<ng-template #results>\n    <ul [id]=\"dropdownResultsId\"\n        class=\"dropdown-result-items\"\n        role=\"listbox\"\n        [attr.aria-label]=\"label\">\n        <li\n            *ngFor=\"let result of searchResults; let i = index\"\n            role=\"option\"\n            tabindex=\"-1\"\n            class=\"vpg-dropdown-results-element\"\n            [ngClass]=\"{\n                'vpg-dropdown-result-selected' : selectedOption === result.text && isValueSelected,\n                'dropdown-element-wcag-hover': elementInFocus === i\n            }\"\n            (click)=\"selectValueAndFocusContainer(result.text)\"\n            (keydown)=\"navigateOptions($event, i, result.text)\"\n            [vpgId]=\"id\"\n        >\n            <vpg-dropdown-result-item\n                [hasIcon]=\"result.hasIcon\"\n                [iconClassName]=\"result.iconClassName\"\n                class=\"vpg-dropdown-result-item\"\n                [alternativeText]=\"result.alternativeText\"\n                [text]=\"result.text\"\n                [screenReaderText]=\"result.screenReaderText\"\n                [id]=\"id + '-' + i\"\n            >\n            </vpg-dropdown-result-item>\n        </li>\n    </ul>\n</ng-template>\n\n<ng-template #noResults>\n    <div\n        *ngIf=\"isActive && searchResults.length === 0 && noResultsHeader && noResultsText\"\n        class=\"vpg-dropdown-no-results\"\n        id=\"{{fieldName}}no-results\"\n        role=\"alert\"\n    >\n        <div\n            *ngIf=\"noResultsHeader\"\n            class=\"vpg-dropdown-no-results-header\"\n            [vpgId]=\"id\"\n        >\n            {{ noResultsHeader }}\n        </div>\n        <div\n            *ngIf=\"noResultsText\"\n            class=\"vpg-dropdown-no-results-body\"\n            [vpgId]=\"id\"\n        >\n            {{ noResultsText }}\n        </div>\n    </div>\n</ng-template>\n", styles: [".vpg-dropdown-main .vpg-dropdown-label,.vpg-dropdown-no-results .vpg-dropdown-no-results-header{font-family:Open Sans,sans-serif;font-weight:700;color:var(--v-color-gray-100);margin:0;text-transform:none}.vpg-dropdown-main .vpg-dropdown-text-container-wrapper .vpg-dropdown-text-container,.vpg-dropdown-main .vpg-dropdown-text-container-wrapper input,.vpg-dropdown-main .vpg-dropdown-additional-elements .vpg-dropdown-error,.vpg-dropdown-no-results .vpg-dropdown-no-results-body,.vpg-dropdown-input-field-placeholder-not-active ::placeholder{font-family:Open Sans,sans-serif;color:var(--v-color-gray-100);margin:0}.vpg-dropdown-main{max-width:100%;width:100%;height:80px;position:relative}.vpg-dropdown-main .vpg-dropdown-label{font-size:1.6rem;line-height:1.4;width:fit-content;margin-bottom:5px}.vpg-dropdown-main .vpg-dropdown-value-selected{color:var(--v-color-gray-100)}.vpg-dropdown-main .vpg-dropdown-text-container-wrapper{padding:0 10px;height:48px;display:flex;align-items:center;justify-content:space-between;width:100%;overflow-y:hidden;cursor:text}.vpg-dropdown-main .vpg-dropdown-text-container-wrapper:focus-within{outline:2px solid -webkit-focus-ring-color}.vpg-dropdown-main .vpg-dropdown-text-container-wrapper .vpg-dropdown-text-container{font-size:1.4rem;line-height:1.3;word-wrap:break-word;white-space:nowrap;text-overflow:ellipsis;overflow:hidden}.vpg-dropdown-main .vpg-dropdown-text-container-wrapper .vpg-dropdown-placeholder-text{color:var(--v-color-gray-90)}.vpg-dropdown-main .vpg-dropdown-text-container-wrapper input{width:100%;outline:none;border:none;background-color:var(--v-color-white);font-size:1.4rem;line-height:1.3;padding:0}.vpg-dropdown-main .vpg-dropdown-text-container-wrapper input:placeholder-shown{word-wrap:break-word;white-space:nowrap;text-overflow:ellipsis;overflow:hidden}.vpg-dropdown-main .vpg-dropdown-text-container-wrapper .vpg-dropdown-input-field-background{background-color:var(--v-color-gray-30)}.vpg-dropdown-main .vpg-dropdown-active{position:relative;z-index:1}.vpg-dropdown-main .vpg-dropdown-arrow-wrapper{font-size:1.6rem;line-height:1.4;color:var(--v-color-gray-90)}.vpg-dropdown-main .vpg-dropdown-option-container{color:var(--v-color-gray-90);background-color:var(--v-color-white);border:1px solid var(--v-color-gray-90);border-radius:5px}.vpg-dropdown-main .vpg-dropdown-option-container:hover{border:2px solid var(--v-color-gray-100);margin:-1px}.vpg-dropdown-main .vpg-dropdown-option-container-disabled{color:var(--v-color-gray-80);background-color:var(--v-color-gray-20);border:1px solid var(--v-color-gray-100);border-radius:5px;pointer-events:none}.vpg-dropdown-main .dropdown-result-items{list-style:none;background-color:var(--v-color-white);width:100%;overflow-y:scroll;max-height:245px;border-bottom-left-radius:5px;border-bottom-right-radius:5px;cursor:pointer;padding:0;margin:0}.vpg-dropdown-main .dropdown-element-wcag-hover{background-color:var(--v-color-gray-20)}.vpg-dropdown-main .vpg-dropdown-results-element:focus{background-color:var(--v-color-gray-20)}.vpg-dropdown-main .vpg-dropdown-results-element:hover{background-color:var(--v-color-gray-20)}.vpg-dropdown-main .vpg-dropdown-additional-elements{display:flex;margin-top:5px;position:absolute;top:75px}.vpg-dropdown-main .vpg-dropdown-additional-elements .vpg-dropdown-error{font-size:1.4rem;line-height:1.3;flex:auto;color:var(--v-color-red-60)}.vpg-dropdown-main .vpg-dropdown-invalid{border:2px solid var(--v-color-red-60);margin:-1px}.vpg-dropdown-main-with-error{height:100px}.vpg-dropdown-result-item{outline:none}.vpg-dropdown-no-results{padding:10px 14px;margin:0;background-color:var(--v-color-white);overflow-y:scroll;max-height:245px;border-bottom-left-radius:5px;border-bottom-right-radius:5px;border-top:1px solid var(--v-color-gray-30);cursor:pointer}.vpg-dropdown-no-results .vpg-dropdown-no-results-header{font-size:1.6rem;line-height:1.4}.vpg-dropdown-no-results .vpg-dropdown-no-results-body{font-size:1.4rem;line-height:1.3}.vpg-dropdown-input-field-placeholder-not-active ::placeholder{font-size:1.4rem;line-height:1.3}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "component", type: IconComponent, selector: "vpg-icon", inputs: ["id", "className", "ariaLabel", "size", "prefix"], outputs: ["iconClicked"] }, { kind: "component", type: DropdownResultItemComponent, selector: "vpg-dropdown-result-item", inputs: ["id", "iconClassName", "alternativeText", "text", "screenReaderText", "hasIcon", "hasImage", "imageType", "imageSource"] }, { kind: "directive", type: IdDirective, selector: "[vpgId]", inputs: ["vpgId"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: DropdownComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-dropdown', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => DropdownComponent),
                            multi: true
                        }
                    ], template: "<div class=\"vpg-dropdown-main\" \n    [ngClass]=\"{'vpg-dropdown-main-with-error': formField.touched && formField.invalid && formField.errors}\">\n    <label\n        *ngIf=\"label && showLabel\"\n        class=\"vpg-dropdown-label\"\n        id=\"{{fieldName}}Label\"\n        [for]=\"dropdownInputId\"\n    >\n        {{ label }}\n    </label>\n    <span\n        *ngIf=\"isRequired\"\n        class=\"vpg-required-indicator\"\n        aria-hidden=\"true\"\n    >*</span>\n    <div\n        #container\n        class=\"vpg-dropdown-option-container\"\n        [ngClass]=\"{\n            'vpg-dropdown-option-container-disabled': isDisabled,\n            'vpg-dropdown-active': isActive,\n            'vpg-dropdown-invalid': formField.invalid && formField.touched\n        }\"\n        [id]=\"fieldNameId\"\n        (click)=\"toggleListAndFocusInput($event)\"\n    >\n        <div class=\"vpg-dropdown-text-container-wrapper\"\n            [ngClass]=\"{'vpg-dropdown-input-field-placeholder-not-active': !isActive}\">\n            <input\n                #searchInput\n                [id]=\"dropdownInputId\"\n                [ngClass]=\"{'vpg-dropdown-input-field-background': isDisabled}\"\n                type=\"text\"\n                role=\"combobox\"\n                [attr.aria-invalid]=\"formField.invalid && formField.touched\"\n                [attr.aria-expanded]=\"isActive\"\n                [attr.aria-controls]=\"dropdownResultsId\"\n                [attr.aria-label]=\"label\"\n                [attr.aria-describedby]=\"fieldName + 'no-results ' + fieldName + '-validation-error'\"\n                [attr.aria-required]=\"isRequired\"\n                (input)=\"onUserInput($event)\"\n                (keydown)=\"onInputKeyDown($event)\"\n                [placeholder]=\"selected || placeholder\"\n                [disabled]=\"isDisabled\"\n                tabindex=\"0\"\n                (blur)=\"touched()\"\n            >\n            <vpg-icon\n                [id]=\"dropdownIconId\"\n                class=\"vpg-dropdown-arrow-wrapper\"\n                [className]=\"isActive ? iconClassName.AngleUp : iconClassName.AngleDown\"\n                [size]=\"doubleIconSize\"\n                role=\"button\"\n                [attr.aria-disabled]=\"isDisabled\"\n                [attr.aria-label]=\"label\"\n                [attr.aria-expanded]=\"isActive\"\n                [attr.aria-controls]=\"dropdownResultsId\"\n                tabindex=\"-1\"\n            ></vpg-icon>\n        </div>\n\n        <div\n            *ngIf=\"isActive\"\n            class=\"vpg-dropdown-result-items-wrapper\"\n            >\n            <ng-container *ngIf=\"searchResults.length > 0\">\n                <ng-container *ngTemplateOutlet=\"results\"></ng-container>\n            </ng-container>\n\n            <ng-container *ngIf=\"searchResults.length === 0 && noResultsHeader && noResultsText\">\n                <ng-container *ngTemplateOutlet=\"noResults\"></ng-container>\n            </ng-container>\n        </div>\n    </div>\n\n    <div\n        *ngIf=\"parentForm\"\n        class=\"vpg-dropdown-additional-elements\"\n        [attr.aria-hidden]=\"isDisabled\">\n        <span\n            *ngIf=\"formField.touched && formField.invalid && formField.errors\"\n            class=\"vpg-dropdown-error\"\n            role=\"alert\"\n            aria-live=\"assertive\"\n            id=\"{{fieldName}}-validation-error\">\n            {{ getErrors() }}\n        </span>\n    </div>\n</div>\n\n<ng-template #results>\n    <ul [id]=\"dropdownResultsId\"\n        class=\"dropdown-result-items\"\n        role=\"listbox\"\n        [attr.aria-label]=\"label\">\n        <li\n            *ngFor=\"let result of searchResults; let i = index\"\n            role=\"option\"\n            tabindex=\"-1\"\n            class=\"vpg-dropdown-results-element\"\n            [ngClass]=\"{\n                'vpg-dropdown-result-selected' : selectedOption === result.text && isValueSelected,\n                'dropdown-element-wcag-hover': elementInFocus === i\n            }\"\n            (click)=\"selectValueAndFocusContainer(result.text)\"\n            (keydown)=\"navigateOptions($event, i, result.text)\"\n            [vpgId]=\"id\"\n        >\n            <vpg-dropdown-result-item\n                [hasIcon]=\"result.hasIcon\"\n                [iconClassName]=\"result.iconClassName\"\n                class=\"vpg-dropdown-result-item\"\n                [alternativeText]=\"result.alternativeText\"\n                [text]=\"result.text\"\n                [screenReaderText]=\"result.screenReaderText\"\n                [id]=\"id + '-' + i\"\n            >\n            </vpg-dropdown-result-item>\n        </li>\n    </ul>\n</ng-template>\n\n<ng-template #noResults>\n    <div\n        *ngIf=\"isActive && searchResults.length === 0 && noResultsHeader && noResultsText\"\n        class=\"vpg-dropdown-no-results\"\n        id=\"{{fieldName}}no-results\"\n        role=\"alert\"\n    >\n        <div\n            *ngIf=\"noResultsHeader\"\n            class=\"vpg-dropdown-no-results-header\"\n            [vpgId]=\"id\"\n        >\n            {{ noResultsHeader }}\n        </div>\n        <div\n            *ngIf=\"noResultsText\"\n            class=\"vpg-dropdown-no-results-body\"\n            [vpgId]=\"id\"\n        >\n            {{ noResultsText }}\n        </div>\n    </div>\n</ng-template>\n", styles: [".vpg-dropdown-main .vpg-dropdown-label,.vpg-dropdown-no-results .vpg-dropdown-no-results-header{font-family:Open Sans,sans-serif;font-weight:700;color:var(--v-color-gray-100);margin:0;text-transform:none}.vpg-dropdown-main .vpg-dropdown-text-container-wrapper .vpg-dropdown-text-container,.vpg-dropdown-main .vpg-dropdown-text-container-wrapper input,.vpg-dropdown-main .vpg-dropdown-additional-elements .vpg-dropdown-error,.vpg-dropdown-no-results .vpg-dropdown-no-results-body,.vpg-dropdown-input-field-placeholder-not-active ::placeholder{font-family:Open Sans,sans-serif;color:var(--v-color-gray-100);margin:0}.vpg-dropdown-main{max-width:100%;width:100%;height:80px;position:relative}.vpg-dropdown-main .vpg-dropdown-label{font-size:1.6rem;line-height:1.4;width:fit-content;margin-bottom:5px}.vpg-dropdown-main .vpg-dropdown-value-selected{color:var(--v-color-gray-100)}.vpg-dropdown-main .vpg-dropdown-text-container-wrapper{padding:0 10px;height:48px;display:flex;align-items:center;justify-content:space-between;width:100%;overflow-y:hidden;cursor:text}.vpg-dropdown-main .vpg-dropdown-text-container-wrapper:focus-within{outline:2px solid -webkit-focus-ring-color}.vpg-dropdown-main .vpg-dropdown-text-container-wrapper .vpg-dropdown-text-container{font-size:1.4rem;line-height:1.3;word-wrap:break-word;white-space:nowrap;text-overflow:ellipsis;overflow:hidden}.vpg-dropdown-main .vpg-dropdown-text-container-wrapper .vpg-dropdown-placeholder-text{color:var(--v-color-gray-90)}.vpg-dropdown-main .vpg-dropdown-text-container-wrapper input{width:100%;outline:none;border:none;background-color:var(--v-color-white);font-size:1.4rem;line-height:1.3;padding:0}.vpg-dropdown-main .vpg-dropdown-text-container-wrapper input:placeholder-shown{word-wrap:break-word;white-space:nowrap;text-overflow:ellipsis;overflow:hidden}.vpg-dropdown-main .vpg-dropdown-text-container-wrapper .vpg-dropdown-input-field-background{background-color:var(--v-color-gray-30)}.vpg-dropdown-main .vpg-dropdown-active{position:relative;z-index:1}.vpg-dropdown-main .vpg-dropdown-arrow-wrapper{font-size:1.6rem;line-height:1.4;color:var(--v-color-gray-90)}.vpg-dropdown-main .vpg-dropdown-option-container{color:var(--v-color-gray-90);background-color:var(--v-color-white);border:1px solid var(--v-color-gray-90);border-radius:5px}.vpg-dropdown-main .vpg-dropdown-option-container:hover{border:2px solid var(--v-color-gray-100);margin:-1px}.vpg-dropdown-main .vpg-dropdown-option-container-disabled{color:var(--v-color-gray-80);background-color:var(--v-color-gray-20);border:1px solid var(--v-color-gray-100);border-radius:5px;pointer-events:none}.vpg-dropdown-main .dropdown-result-items{list-style:none;background-color:var(--v-color-white);width:100%;overflow-y:scroll;max-height:245px;border-bottom-left-radius:5px;border-bottom-right-radius:5px;cursor:pointer;padding:0;margin:0}.vpg-dropdown-main .dropdown-element-wcag-hover{background-color:var(--v-color-gray-20)}.vpg-dropdown-main .vpg-dropdown-results-element:focus{background-color:var(--v-color-gray-20)}.vpg-dropdown-main .vpg-dropdown-results-element:hover{background-color:var(--v-color-gray-20)}.vpg-dropdown-main .vpg-dropdown-additional-elements{display:flex;margin-top:5px;position:absolute;top:75px}.vpg-dropdown-main .vpg-dropdown-additional-elements .vpg-dropdown-error{font-size:1.4rem;line-height:1.3;flex:auto;color:var(--v-color-red-60)}.vpg-dropdown-main .vpg-dropdown-invalid{border:2px solid var(--v-color-red-60);margin:-1px}.vpg-dropdown-main-with-error{height:100px}.vpg-dropdown-result-item{outline:none}.vpg-dropdown-no-results{padding:10px 14px;margin:0;background-color:var(--v-color-white);overflow-y:scroll;max-height:245px;border-bottom-left-radius:5px;border-bottom-right-radius:5px;border-top:1px solid var(--v-color-gray-30);cursor:pointer}.vpg-dropdown-no-results .vpg-dropdown-no-results-header{font-size:1.6rem;line-height:1.4}.vpg-dropdown-no-results .vpg-dropdown-no-results-body{font-size:1.4rem;line-height:1.3}.vpg-dropdown-input-field-placeholder-not-active ::placeholder{font-size:1.4rem;line-height:1.3}\n"] }]
        }], propDecorators: { id: [{
                type: Input
            }], parentForm: [{
                type: Input
            }], fieldName: [{
                type: Input
            }], label: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], data: [{
                type: Input
            }], selectedOption: [{
                type: Input
            }], isDisabled: [{
                type: Input
            }], minCharacters: [{
                type: Input
            }], noResultsHeader: [{
                type: Input
            }], noResultsText: [{
                type: Input
            }], showLabel: [{
                type: Input
            }], customValidators: [{
                type: Input
            }], valueSelected: [{
                type: Output
            }], searchInput: [{
                type: ViewChild,
                args: ['searchInput', { static: true }]
            }], container: [{
                type: ViewChild,
                args: ['container', { static: true }]
            }], onClick: [{
                type: HostListener,
                args: ['document:click', ['$event']]
            }], onKeyUp: [{
                type: HostListener,
                args: ['document:keyup', ['$event']]
            }] } });

class MainSideComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: MainSideComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: MainSideComponent, selector: "vpg-main-side", ngImport: i0, template: "<div class=\"vpg-grid-row\">\n    <div class=\"vpg-grid-col-md-8\">\n        <ng-content select=\"[main]\"></ng-content>\n    </div>\n    <div class=\"vpg-grid-col-md-4\">\n        <div class=\"vpg-grid-row\" id=\"first\">\n            <div class=\"vpg-grid-col-md-12\">\n                <ng-content select=\"[side-first]\"></ng-content>\n            </div>\n        </div>\n        <div class=\"vpg-grid-row\" id=\"second\">\n            <div class=\"vpg-grid-col-md-12\">\n                <ng-content select=\"[side-second]\"></ng-content>\n            </div>\n        </div>\n    </div>\n</div>\n", styles: [".vpg-grid-col-md-8{padding-inline-start:10px;padding-inline-end:10px;margin-bottom:20px}.vpg-grid-col-md-4{padding-inline-start:0px;padding-inline-end:0px}.vpg-grid-col-md-12{padding-inline-start:10px;padding-inline-end:10px;margin-bottom:20px}#first,#second{margin-inline-start:0px;margin-inline-end:0px}\n"] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: MainSideComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-main-side', template: "<div class=\"vpg-grid-row\">\n    <div class=\"vpg-grid-col-md-8\">\n        <ng-content select=\"[main]\"></ng-content>\n    </div>\n    <div class=\"vpg-grid-col-md-4\">\n        <div class=\"vpg-grid-row\" id=\"first\">\n            <div class=\"vpg-grid-col-md-12\">\n                <ng-content select=\"[side-first]\"></ng-content>\n            </div>\n        </div>\n        <div class=\"vpg-grid-row\" id=\"second\">\n            <div class=\"vpg-grid-col-md-12\">\n                <ng-content select=\"[side-second]\"></ng-content>\n            </div>\n        </div>\n    </div>\n</div>\n", styles: [".vpg-grid-col-md-8{padding-inline-start:10px;padding-inline-end:10px;margin-bottom:20px}.vpg-grid-col-md-4{padding-inline-start:0px;padding-inline-end:0px}.vpg-grid-col-md-12{padding-inline-start:10px;padding-inline-end:10px;margin-bottom:20px}#first,#second{margin-inline-start:0px;margin-inline-end:0px}\n"] }]
        }] });

class OneColumnRowComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: OneColumnRowComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: OneColumnRowComponent, selector: "vpg-one-column-row", ngImport: i0, template: "<div class=\"vpg-grid-row\">\n    <div class=\"vpg-grid-col-md-12\">\n        <ng-content></ng-content>\n    </div>\n</div>\n", styles: [".vpg-grid-col-md-12{padding-inline-start:10px;padding-inline-end:10px;margin-bottom:20px}\n"] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: OneColumnRowComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-one-column-row', template: "<div class=\"vpg-grid-row\">\n    <div class=\"vpg-grid-col-md-12\">\n        <ng-content></ng-content>\n    </div>\n</div>\n", styles: [".vpg-grid-col-md-12{padding-inline-start:10px;padding-inline-end:10px;margin-bottom:20px}\n"] }]
        }] });

class TwoColumnRowComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: TwoColumnRowComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: TwoColumnRowComponent, selector: "vpg-two-column-row", ngImport: i0, template: "<div class=\"vpg-grid-row\">\n    <div class=\"vpg-grid-col-md-6\">\n        <ng-content select=\"[first]\"></ng-content>\n    </div>\n    <div class=\"vpg-grid-col-md-6\">\n        <ng-content select=\"[second]\"></ng-content>\n    </div>\n</div>\n", styles: [".vpg-grid-col-md-6{padding-inline-start:10px;padding-inline-end:10px;margin-bottom:20px}\n"] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: TwoColumnRowComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-two-column-row', template: "<div class=\"vpg-grid-row\">\n    <div class=\"vpg-grid-col-md-6\">\n        <ng-content select=\"[first]\"></ng-content>\n    </div>\n    <div class=\"vpg-grid-col-md-6\">\n        <ng-content select=\"[second]\"></ng-content>\n    </div>\n</div>\n", styles: [".vpg-grid-col-md-6{padding-inline-start:10px;padding-inline-end:10px;margin-bottom:20px}\n"] }]
        }] });

class ThreeColumnRowComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ThreeColumnRowComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: ThreeColumnRowComponent, selector: "vpg-three-column-row", ngImport: i0, template: "<div class=\"vpg-grid-row\">\n    <div class=\"vpg-grid-col-md-4\">\n        <ng-content select=\"[first]\"></ng-content>\n    </div>\n    <div class=\"vpg-grid-col-md-4\">\n        <ng-content select=\"[second]\"></ng-content>\n    </div>\n    <div class=\"vpg-grid-col-md-4\">\n        <ng-content select=\"[third]\"></ng-content>\n    </div>\n</div>\n", styles: [".vpg-grid-col-md-4{padding-inline-start:10px;padding-inline-end:10px;margin-bottom:20px}\n"] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ThreeColumnRowComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-three-column-row', template: "<div class=\"vpg-grid-row\">\n    <div class=\"vpg-grid-col-md-4\">\n        <ng-content select=\"[first]\"></ng-content>\n    </div>\n    <div class=\"vpg-grid-col-md-4\">\n        <ng-content select=\"[second]\"></ng-content>\n    </div>\n    <div class=\"vpg-grid-col-md-4\">\n        <ng-content select=\"[third]\"></ng-content>\n    </div>\n</div>\n", styles: [".vpg-grid-col-md-4{padding-inline-start:10px;padding-inline-end:10px;margin-bottom:20px}\n"] }]
        }] });

class FourColumnRowComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: FourColumnRowComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: FourColumnRowComponent, selector: "vpg-four-column-row", ngImport: i0, template: "<div class=\"vpg-grid-row\">\n    <div class=\"vpg-grid-col-md-3\">\n        <ng-content select=\"[first]\"></ng-content>\n    </div>\n    <div class=\"vpg-grid-col-md-3\">\n        <ng-content select=\"[second]\"></ng-content>\n    </div>\n    <div class=\"vpg-grid-col-md-3\">\n        <ng-content select=\"[third]\"></ng-content>\n    </div>\n    <div class=\"vpg-grid-col-md-3\">\n        <ng-content select=\"[fourth]\"></ng-content>\n    </div>\n</div>\n", styles: [".vpg-grid-col-md-3{padding-inline-start:10px;padding-inline-end:10px;margin-bottom:20px}\n"] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: FourColumnRowComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-four-column-row', template: "<div class=\"vpg-grid-row\">\n    <div class=\"vpg-grid-col-md-3\">\n        <ng-content select=\"[first]\"></ng-content>\n    </div>\n    <div class=\"vpg-grid-col-md-3\">\n        <ng-content select=\"[second]\"></ng-content>\n    </div>\n    <div class=\"vpg-grid-col-md-3\">\n        <ng-content select=\"[third]\"></ng-content>\n    </div>\n    <div class=\"vpg-grid-col-md-3\">\n        <ng-content select=\"[fourth]\"></ng-content>\n    </div>\n</div>\n", styles: [".vpg-grid-col-md-3{padding-inline-start:10px;padding-inline-end:10px;margin-bottom:20px}\n"] }]
        }] });

class FocusTrapDirective {
    constructor(element) {
        this.element = element;
        this.TAB = 'Tab';
    }
    handleTabNavigation() {
        this.focusableElements = 'button, a[href], [tabindex]:not([tabindex="-1"])';
        this.focusableContent = this.element.nativeElement.querySelectorAll(this.focusableElements);
        if (!this.focusableContent.length) {
            return;
        }
        this.firstFocusableElement = this.focusableContent[0];
        this.lastFocusableElement = this.focusableContent[this.focusableContent.length - 1];
        this.element.nativeElement.addEventListener('keydown', (e) => {
            const isTabPressed = e.key === this.TAB || e.shiftKey;
            if (!isTabPressed) {
                return;
            }
            if (e.key === this.TAB && e.shiftKey && document.activeElement === this.firstFocusableElement) {
                this.lastFocusableElement.focus();
                e.preventDefault();
                return;
            }
            if (e.key === this.TAB && !e.shiftKey && document.activeElement === this.lastFocusableElement) {
                this.firstFocusableElement.focus();
                e.preventDefault();
            }
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: FocusTrapDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.9", type: FocusTrapDirective, selector: "[vpgFocusTrap]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: FocusTrapDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[vpgFocusTrap]'
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }] });

var ModalActionType;
(function (ModalActionType) {
    ModalActionType["Primary"] = "Primary";
    ModalActionType["PrimaryAndSecondary"] = "Primary And Secondary";
    ModalActionType["PrimaryAndTertiary"] = "Primary And Tertiary";
    ModalActionType["PrimaryAndLink"] = "Primary And Link";
})(ModalActionType || (ModalActionType = {}));

var ModalType;
(function (ModalType) {
    ModalType["TextModal"] = "TextModal";
    ModalType["HtmlModal"] = "HtmlModal";
    ModalType["WebView"] = "WebView";
})(ModalType || (ModalType = {}));

class VpgModalService {
    constructor() {
        this.modals = [];
    }
    add(modal) {
        // add modal to array of active modals
        this.modals.push(modal);
    }
    remove(id) {
        // remove modal from array of active modals
        this.modals = this.modals.filter((x) => x.id !== id);
    }
    open(id) {
        // open modal specified by id
        const modal = this.modals.find((x) => x.id === id);
        modal.open();
    }
    close(id) {
        // close modal specified by id
        const modal = this.modals.find((x) => x.id === id);
        modal.close();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: VpgModalService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: VpgModalService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: VpgModalService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

class ModalActionsComponent {
    constructor() {
        this.modalActionType = ModalActionType.Primary;
        this.primaryButtonLabel = '';
        this.secondaryButtonLabel = '';
        this.tertiaryButtonLabel = '';
        this.linkLabel = '';
        this.linkUrl = '';
        this.id = '';
        /**
         * Event emitter informing that primary button was clicked.
         */
        this.primaryButtonClicked = new EventEmitter();
        /**
         * Event emitter informing that secondary button was clicked.
         */
        this.secondaryButtonClicked = new EventEmitter();
        /**
         * Event emitter informing that tertiary button was clicked.
         */
        this.tertiaryButtonClicked = new EventEmitter();
        /**
         * Event emitter informing that link was clicked.
         */
        this.linkClicked = new EventEmitter();
        this.modalAction = ModalActionType;
        this.buttonType = ButtonType;
        this.linkType = 'large';
        this.linkIdSuffix = 'modal-action-link';
        this.primaryBtnIdSuffix = 'primary';
        this.secondaryBtnIdSuffix = 'secondary';
        this.tertiaryBtnIdSuffix = 'tertiary';
    }
    onPrimaryButtonClicked() {
        this.primaryButtonClicked.emit();
    }
    onSecondaryButtonClicked() {
        this.secondaryButtonClicked.emit();
    }
    onTertiaryButtonClicked() {
        this.tertiaryButtonClicked.emit();
    }
    onLinkClicked() {
        this.linkClicked.emit();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ModalActionsComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: ModalActionsComponent, selector: "vpg-modal-actions", inputs: { modalActionType: "modalActionType", primaryButtonLabel: "primaryButtonLabel", secondaryButtonLabel: "secondaryButtonLabel", tertiaryButtonLabel: "tertiaryButtonLabel", linkLabel: "linkLabel", linkUrl: "linkUrl", id: "id" }, outputs: { primaryButtonClicked: "primaryButtonClicked", secondaryButtonClicked: "secondaryButtonClicked", tertiaryButtonClicked: "tertiaryButtonClicked", linkClicked: "linkClicked" }, ngImport: i0, template: "<div class=\"vpg-modal-actions-container\">\n    <div\n        *ngIf=\"modalActionType === modalAction.Primary\"\n        class=\"vpg-modal-actions-primary\"\n    >\n        <vpg-button\n            [label]=\"primaryButtonLabel\"\n            [id]=\"id\"\n            (buttonClicked)=\"onPrimaryButtonClicked()\"\n        ></vpg-button>\n    </div>\n    <div\n        *ngIf=\"modalActionType === modalAction.PrimaryAndSecondary\"\n        class=\"vpg-modal-actions-primary-and-secondary\"\n    >\n        <vpg-button\n            class=\"vpg-modal-actions-primary\"\n            [label]=\"primaryButtonLabel\"\n            [id]=\"id\"\n            (buttonClicked)=\"onPrimaryButtonClicked()\"\n        ></vpg-button>\n        <vpg-button\n            [label]=\"secondaryButtonLabel\"\n            [btnType]=\"buttonType.Secondary\"\n            [id]=\"id\"\n            (buttonClicked)=\"onSecondaryButtonClicked()\"\n        ></vpg-button>\n    </div>\n    <div\n        *ngIf=\"modalActionType === modalAction.PrimaryAndTertiary\"\n        class=\"vpg-modal-actions-primary-and-tertiary\"\n    >\n        <vpg-button\n            class=\"vpg-modal-actions-tertiary\"\n            [label]=\"tertiaryButtonLabel\"\n            [id]=\"id\"\n            [btnType]=\"buttonType.Tertiary\"\n            (buttonClicked)=\"onTertiaryButtonClicked()\"\n        >\n        </vpg-button>\n        <vpg-button\n            [label]=\"primaryButtonLabel\"\n            [id]=\"id\"\n            (buttonClicked)=\"onPrimaryButtonClicked()\"\n        ></vpg-button>\n    </div>\n    <div\n        *ngIf=\"modalActionType === modalAction.PrimaryAndLink\"\n        class=\"vpg-modal-actions-primary-and-link\"\n    >\n        <vpg-button\n            class=\"vpg-modal-actions-primary\"\n            [label]=\"primaryButtonLabel\"\n            [id]=\"id\"\n            (buttonClicked)=\"onPrimaryButtonClicked()\"\n        ></vpg-button>\n        <a\n            vpg-link\n            [href]=\"linkUrl\"\n            [size]=\"linkType\"\n            [vpgId]=\"id + linkIdSuffix\"\n            [attr.aria-label]=\"linkLabel\"\n            (click)=\"onLinkClicked()\"\n        > {{ linkLabel }}</a>\n    </div>\n</div>\n", styles: [".vpg-modal-actions-container{text-align:center}.vpg-modal-actions-container .vpg-modal-actions-primary-and-secondary .vpg-modal-actions-primary,.vpg-modal-actions-container .vpg-modal-actions-primary-and-tertiary .vpg-modal-actions-tertiary{margin-inline-end:20px}.vpg-modal-actions-container .vpg-modal-actions-primary-and-link .vpg-modal-actions-primary{margin-bottom:20px;display:block}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: LinkDirective, selector: "a[vpg-link], a[vpgLink], button[vpg-link], button[vpgLink]", inputs: ["size", "align", "bold"] }, { kind: "component", type: ButtonComponent, selector: "vpg-button", inputs: ["id", "buttonSubmit", "label", "isDisabled", "className", "btnType", "ariaLabel", "isCompleted", "buttonSize", "iconOnTheRight", "fullWidth", "buttonAlignment"], outputs: ["buttonClicked"] }, { kind: "directive", type: IdDirective, selector: "[vpgId]", inputs: ["vpgId"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ModalActionsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-modal-actions', template: "<div class=\"vpg-modal-actions-container\">\n    <div\n        *ngIf=\"modalActionType === modalAction.Primary\"\n        class=\"vpg-modal-actions-primary\"\n    >\n        <vpg-button\n            [label]=\"primaryButtonLabel\"\n            [id]=\"id\"\n            (buttonClicked)=\"onPrimaryButtonClicked()\"\n        ></vpg-button>\n    </div>\n    <div\n        *ngIf=\"modalActionType === modalAction.PrimaryAndSecondary\"\n        class=\"vpg-modal-actions-primary-and-secondary\"\n    >\n        <vpg-button\n            class=\"vpg-modal-actions-primary\"\n            [label]=\"primaryButtonLabel\"\n            [id]=\"id\"\n            (buttonClicked)=\"onPrimaryButtonClicked()\"\n        ></vpg-button>\n        <vpg-button\n            [label]=\"secondaryButtonLabel\"\n            [btnType]=\"buttonType.Secondary\"\n            [id]=\"id\"\n            (buttonClicked)=\"onSecondaryButtonClicked()\"\n        ></vpg-button>\n    </div>\n    <div\n        *ngIf=\"modalActionType === modalAction.PrimaryAndTertiary\"\n        class=\"vpg-modal-actions-primary-and-tertiary\"\n    >\n        <vpg-button\n            class=\"vpg-modal-actions-tertiary\"\n            [label]=\"tertiaryButtonLabel\"\n            [id]=\"id\"\n            [btnType]=\"buttonType.Tertiary\"\n            (buttonClicked)=\"onTertiaryButtonClicked()\"\n        >\n        </vpg-button>\n        <vpg-button\n            [label]=\"primaryButtonLabel\"\n            [id]=\"id\"\n            (buttonClicked)=\"onPrimaryButtonClicked()\"\n        ></vpg-button>\n    </div>\n    <div\n        *ngIf=\"modalActionType === modalAction.PrimaryAndLink\"\n        class=\"vpg-modal-actions-primary-and-link\"\n    >\n        <vpg-button\n            class=\"vpg-modal-actions-primary\"\n            [label]=\"primaryButtonLabel\"\n            [id]=\"id\"\n            (buttonClicked)=\"onPrimaryButtonClicked()\"\n        ></vpg-button>\n        <a\n            vpg-link\n            [href]=\"linkUrl\"\n            [size]=\"linkType\"\n            [vpgId]=\"id + linkIdSuffix\"\n            [attr.aria-label]=\"linkLabel\"\n            (click)=\"onLinkClicked()\"\n        > {{ linkLabel }}</a>\n    </div>\n</div>\n", styles: [".vpg-modal-actions-container{text-align:center}.vpg-modal-actions-container .vpg-modal-actions-primary-and-secondary .vpg-modal-actions-primary,.vpg-modal-actions-container .vpg-modal-actions-primary-and-tertiary .vpg-modal-actions-tertiary{margin-inline-end:20px}.vpg-modal-actions-container .vpg-modal-actions-primary-and-link .vpg-modal-actions-primary{margin-bottom:20px;display:block}\n"] }]
        }], propDecorators: { modalActionType: [{
                type: Input
            }], primaryButtonLabel: [{
                type: Input
            }], secondaryButtonLabel: [{
                type: Input
            }], tertiaryButtonLabel: [{
                type: Input
            }], linkLabel: [{
                type: Input
            }], linkUrl: [{
                type: Input
            }], id: [{
                type: Input
            }], primaryButtonClicked: [{
                type: Output
            }], secondaryButtonClicked: [{
                type: Output
            }], tertiaryButtonClicked: [{
                type: Output
            }], linkClicked: [{
                type: Output
            }] } });

class SafeUrlPipe {
    constructor(sanitized) {
        this.sanitized = sanitized;
    }
    transform(value) {
        if (value) {
            return this.sanitized.bypassSecurityTrustResourceUrl(value);
        }
        return '';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: SafeUrlPipe, deps: [{ token: i1$1.DomSanitizer }], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.3.9", ngImport: i0, type: SafeUrlPipe, name: "safeUrl" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: SafeUrlPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'safeUrl' }]
        }], ctorParameters: () => [{ type: i1$1.DomSanitizer }] });

class ModalComponent {
    constructor(modalService, el) {
        this.modalService = modalService;
        /**
         * Id for modal, required and must be unique
         */
        this.id = '';
        this.modalType = ModalType.TextModal;
        /**
         *  title
         */
        this.title = '';
        /**
         * Content text (can be string or plain html)
         */
        this.content = '';
        /**
         * web link that should be opened within modal
         */
        this.webLink = '';
        /**
         * Aria label for close icon
         */
        this.ariaLabelCloseIcon = '';
        /**
         * Determines if close icon should be visible or not
         */
        this.showCloseIcon = true;
        /**
         * Determines if close icon should be visible or not
         */
        this.showActionSection = true;
        this.modalActionInputs = {
            primaryButtonLabel: ''
        };
        /**
         * Event emitter informing that close icon or Escape key was clicked.
         */
        this.closeIconButtonClicked = new EventEmitter();
        /**
         * Event emitter informing that primary button was clicked.
         */
        this.actionPrimaryButtonClicked = new EventEmitter();
        /**
         * Event emitter informing that secondary button was clicked.
         */
        this.actionSecondaryButtonClicked = new EventEmitter();
        /**
         * Event emitter informing that tertiary button was clicked.
         */
        this.actionTertiaryButtonClicked = new EventEmitter();
        /**
         * Event emitter informing that link was clicked.
         */
        this.actionLinkClicked = new EventEmitter();
        this.isOpen = false;
        this.modalOpenId = '';
        this.titleId = '';
        this.contentId = '';
        this.closeId = '';
        this.modalContentId = '';
        this.actionsId = '';
        this.previousActiveElement = null;
        this.firstFocusableElement = null;
        this.closeIcon = IconClassName.Times;
        this.buttonType = ButtonType;
        this.type = ModalType;
        this.modalActionType = ModalActionType.Primary;
        this.element = el.nativeElement;
    }
    ngOnInit() {
        this.modalService.add(this);
    }
    ngOnChanges() {
        this.setIds();
    }
    ngOnDestroy() {
        this.modalService.remove(this.id);
        this.element.remove();
    }
    onEscape() {
        this.closeIconButtonClicked.emit();
    }
    onCloseIconButtonClicked() {
        this.closeIconButtonClicked.emit();
    }
    onPrimaryButtonClicked() {
        this.actionPrimaryButtonClicked.emit();
    }
    onSecondaryButtonClicked() {
        this.actionSecondaryButtonClicked.emit();
    }
    onTertiaryButtonClicked() {
        this.actionTertiaryButtonClicked.emit();
    }
    onLinkClicked() {
        this.actionLinkClicked.emit();
    }
    open() {
        this.previousActiveElement = document.activeElement;
        this.element.style.display = 'block';
        document.body.classList.add('vpg-modal-open');
        document.body.classList.add(this.modalOpenId);
        setTimeout(() => {
            this.setInitialFocus();
        });
    }
    setInitialFocus() {
        this.isOpen = true;
        this.element.querySelector('[tabindex="-1"')?.focus();
        this.firstFocusableElement = this.element.querySelector('[tabindex = "0"]');
        this.firstFocusableElement?.focus();
        setTimeout(() => {
            this.vpgFocusTrap.handleTabNavigation();
        });
    }
    close() {
        this.element.style.display = 'none';
        document.body.classList.remove('vpg-modal-open');
        document.body.classList.remove(this.modalOpenId);
        this.previousActiveElement?.focus();
        this.isOpen = false;
    }
    closeModal(id) {
        this.modalService.close(id);
    }
    setIds() {
        this.modalOpenId = this.id + 'modalOpen';
        this.titleId = this.id + 'modalTitle';
        this.contentId = this.id + 'modalContent';
        this.modalContentId = this.id + 'modalContentWrapper';
        this.closeId = this.id + 'modalClose';
        this.actionsId = this.id + '-modal-actions';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ModalComponent, deps: [{ token: VpgModalService }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: ModalComponent, selector: "vpg-modal", inputs: { id: "id", modalType: "modalType", title: "title", content: "content", webLink: "webLink", ariaLabelCloseIcon: "ariaLabelCloseIcon", showCloseIcon: "showCloseIcon", showActionSection: "showActionSection", modalActionInputs: "modalActionInputs" }, outputs: { closeIconButtonClicked: "closeIconButtonClicked", actionPrimaryButtonClicked: "actionPrimaryButtonClicked", actionSecondaryButtonClicked: "actionSecondaryButtonClicked", actionTertiaryButtonClicked: "actionTertiaryButtonClicked", actionLinkClicked: "actionLinkClicked" }, host: { listeners: { "document:keydown.escape": "onEscape($event)" } }, viewQueries: [{ propertyName: "vpgFocusTrap", first: true, predicate: FocusTrapDirective, descendants: true }], usesOnChanges: true, ngImport: i0, template: "<div\n    vpgFocusTrap\n    class=\"vpg-modal-main\"\n    role=\"dialog\"\n    tabindex=\"-1\"\n    [attr.aria-modal]=\"isOpen\"\n    [attr.aria-labelledby]=\"titleId\"\n    [attr.aria-describedby]=\"showCloseIcon ? null : modalContentId\"\n>\n    <div\n        class=\"vpg-modal-close\"\n        [id]=\"closeId\">\n        <vpg-button\n            *ngIf=\"showCloseIcon\"\n            [id]=\"closeId\"\n            class=\"vpg-modal-close-btn\"\n            [btnType]=\"buttonType.IconTertiary\"\n            [className]=\"closeIcon\"\n            [ariaLabel]=\"ariaLabelCloseIcon\"\n            (buttonClicked)=\"onCloseIconButtonClicked()\"\n        >\n        </vpg-button>\n    </div>\n    <div class=\"vpg-modal-content-container\">\n        <div class=\"vpg-modal-title\">\n            <h2\n                class=\"vpg-modal-content-title\"\n                [id]=\"titleId\"\n            >\n                {{ title }}\n            </h2>\n        </div>\n        <div\n            *ngIf=\"content && (modalType === type.TextModal || modalType === type.HtmlModal)\"\n            class=\"vpg-modal-content\"\n            [id]=\"modalContentId\"\n            >\n            <div\n                [attr.tabindex]=\"showCloseIcon ? null : '-1'\"\n                [id]=\"contentId\"\n                class=\"vpg-modal-content-text\"\n                [innerHTML]=\"content\"\n            ></div>\n        </div>\n        <div\n            *ngIf=\"modalActionInputs && showActionSection\"\n            [id]=\"actionsId\"\n            class=\"vpg-modal-actions\"\n        >\n            <vpg-modal-actions\n                [modalActionType]=\"modalActionInputs.modalActionType\"\n                [id]=\"actionsId\"\n                [primaryButtonLabel]=\"modalActionInputs.primaryButtonLabel\"\n                [secondaryButtonLabel]=\"modalActionInputs.secondaryButtonLabel\"\n                [tertiaryButtonLabel]=\"modalActionInputs.tertiaryButtonLabel\"\n                [linkLabel]=\"modalActionInputs.linkLabel\"\n                [linkUrl]=\"modalActionInputs.linkUrl\"\n                (primaryButtonClicked)=\"onPrimaryButtonClicked()\"\n                (secondaryButtonClicked)=\"onSecondaryButtonClicked()\"\n                (tertiaryButtonClicked)=\"onTertiaryButtonClicked()\"\n                (linkClicked)=\"onLinkClicked()\"\n            ></vpg-modal-actions>\n        </div>\n        <div\n            *ngIf=\"modalType === type.WebView\"\n            class=\"vpg-modal-web-view\"\n        >\n            <iframe\n                [src]=\"webLink | safeUrl\"\n                title=\"iframe Example 1\"\n                width=\"400\" height=\"300\"\n            >\n            </iframe>\n            <div class=\"web-view-hidden-element\" aria-hidden=\"true\" tabindex=\"0\"></div>\n        </div>\n    </div>\n</div>\n\n<div class=\"vpg-modal-background\" (click)=\"close()\"></div>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: ButtonComponent, selector: "vpg-button", inputs: ["id", "buttonSubmit", "label", "isDisabled", "className", "btnType", "ariaLabel", "isCompleted", "buttonSize", "iconOnTheRight", "fullWidth", "buttonAlignment"], outputs: ["buttonClicked"] }, { kind: "component", type: ModalActionsComponent, selector: "vpg-modal-actions", inputs: ["modalActionType", "primaryButtonLabel", "secondaryButtonLabel", "tertiaryButtonLabel", "linkLabel", "linkUrl", "id"], outputs: ["primaryButtonClicked", "secondaryButtonClicked", "tertiaryButtonClicked", "linkClicked"] }, { kind: "directive", type: FocusTrapDirective, selector: "[vpgFocusTrap]" }, { kind: "pipe", type: SafeUrlPipe, name: "safeUrl" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ModalComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-modal', template: "<div\n    vpgFocusTrap\n    class=\"vpg-modal-main\"\n    role=\"dialog\"\n    tabindex=\"-1\"\n    [attr.aria-modal]=\"isOpen\"\n    [attr.aria-labelledby]=\"titleId\"\n    [attr.aria-describedby]=\"showCloseIcon ? null : modalContentId\"\n>\n    <div\n        class=\"vpg-modal-close\"\n        [id]=\"closeId\">\n        <vpg-button\n            *ngIf=\"showCloseIcon\"\n            [id]=\"closeId\"\n            class=\"vpg-modal-close-btn\"\n            [btnType]=\"buttonType.IconTertiary\"\n            [className]=\"closeIcon\"\n            [ariaLabel]=\"ariaLabelCloseIcon\"\n            (buttonClicked)=\"onCloseIconButtonClicked()\"\n        >\n        </vpg-button>\n    </div>\n    <div class=\"vpg-modal-content-container\">\n        <div class=\"vpg-modal-title\">\n            <h2\n                class=\"vpg-modal-content-title\"\n                [id]=\"titleId\"\n            >\n                {{ title }}\n            </h2>\n        </div>\n        <div\n            *ngIf=\"content && (modalType === type.TextModal || modalType === type.HtmlModal)\"\n            class=\"vpg-modal-content\"\n            [id]=\"modalContentId\"\n            >\n            <div\n                [attr.tabindex]=\"showCloseIcon ? null : '-1'\"\n                [id]=\"contentId\"\n                class=\"vpg-modal-content-text\"\n                [innerHTML]=\"content\"\n            ></div>\n        </div>\n        <div\n            *ngIf=\"modalActionInputs && showActionSection\"\n            [id]=\"actionsId\"\n            class=\"vpg-modal-actions\"\n        >\n            <vpg-modal-actions\n                [modalActionType]=\"modalActionInputs.modalActionType\"\n                [id]=\"actionsId\"\n                [primaryButtonLabel]=\"modalActionInputs.primaryButtonLabel\"\n                [secondaryButtonLabel]=\"modalActionInputs.secondaryButtonLabel\"\n                [tertiaryButtonLabel]=\"modalActionInputs.tertiaryButtonLabel\"\n                [linkLabel]=\"modalActionInputs.linkLabel\"\n                [linkUrl]=\"modalActionInputs.linkUrl\"\n                (primaryButtonClicked)=\"onPrimaryButtonClicked()\"\n                (secondaryButtonClicked)=\"onSecondaryButtonClicked()\"\n                (tertiaryButtonClicked)=\"onTertiaryButtonClicked()\"\n                (linkClicked)=\"onLinkClicked()\"\n            ></vpg-modal-actions>\n        </div>\n        <div\n            *ngIf=\"modalType === type.WebView\"\n            class=\"vpg-modal-web-view\"\n        >\n            <iframe\n                [src]=\"webLink | safeUrl\"\n                title=\"iframe Example 1\"\n                width=\"400\" height=\"300\"\n            >\n            </iframe>\n            <div class=\"web-view-hidden-element\" aria-hidden=\"true\" tabindex=\"0\"></div>\n        </div>\n    </div>\n</div>\n\n<div class=\"vpg-modal-background\" (click)=\"close()\"></div>\n" }]
        }], ctorParameters: () => [{ type: VpgModalService }, { type: i0.ElementRef }], propDecorators: { id: [{
                type: Input
            }], modalType: [{
                type: Input
            }], title: [{
                type: Input
            }], content: [{
                type: Input
            }], webLink: [{
                type: Input
            }], ariaLabelCloseIcon: [{
                type: Input
            }], showCloseIcon: [{
                type: Input
            }], showActionSection: [{
                type: Input
            }], modalActionInputs: [{
                type: Input
            }], closeIconButtonClicked: [{
                type: Output
            }], actionPrimaryButtonClicked: [{
                type: Output
            }], actionSecondaryButtonClicked: [{
                type: Output
            }], actionTertiaryButtonClicked: [{
                type: Output
            }], actionLinkClicked: [{
                type: Output
            }], vpgFocusTrap: [{
                type: ViewChild,
                args: [FocusTrapDirective, { static: false }]
            }], onEscape: [{
                type: HostListener,
                args: ['document:keydown.escape', ['$event']]
            }] } });

class WelcomeMatComponent {
    constructor(modalService, el) {
        this.modalService = modalService;
        /**
         * Id for modal, required and must be unique
         */
        this.id = '';
        /**
         * Image source for modal
         */
        this.imageSource = '';
        /**
         * Sets the value for the alt attribute for the image.
         */
        this.alternativeText = '';
        /**
         * Content title
         */
        this.contentTitle = '';
        /**
         * Content text
         */
        this.contentText = '';
        /**
         * Aria label for close icon
         */
        this.ariaLabelCloseIcon = '';
        /**
         * Determines if close icon should be visible or not
         */
        this.showCloseIcon = true;
        this.matActionInputs = {
            primaryButtonLabel: ''
        };
        /**
         * Event emitter informing that close icon or Escape key was clicked.
         */
        this.closeIconButtonClicked = new EventEmitter();
        /**
         * Event emitter informing that primary button was clicked.
         */
        this.actionPrimaryButtonClicked = new EventEmitter();
        /**
         * Event emitter informing that secondary button was clicked.
         */
        this.actionSecondaryButtonClicked = new EventEmitter();
        /**
         * Event emitter informing that tertiary button was clicked.
         */
        this.actionTertiaryButtonClicked = new EventEmitter();
        /**
         * Event emitter informing that link was clicked.
         */
        this.actionLinkClicked = new EventEmitter();
        this.modalOpenId = '';
        this.titleId = '';
        this.contentId = '';
        this.modalContentId = '';
        this.imageId = '';
        this.closeId = '';
        this.actionsId = '';
        this.modalActionsId = '';
        this.previousActiveElement = null;
        this.firstFocusableElement = null;
        this.showImage = true;
        this.isOpen = false;
        this.closeIcon = IconClassName.Times;
        this.buttonType = ButtonType;
        this.element = el.nativeElement;
    }
    ngOnChanges() {
        this.setIds();
    }
    ngOnInit() {
        this.modalService.add(this);
    }
    ngOnDestroy() {
        this.modalService.remove(this.id);
        this.element.remove();
    }
    onEscape() {
        this.closeIconButtonClicked.emit();
    }
    onCloseIconButtonClicked() {
        this.closeIconButtonClicked.emit();
    }
    onPrimaryButtonClicked() {
        this.actionPrimaryButtonClicked.emit();
    }
    onSecondaryButtonClicked() {
        this.actionSecondaryButtonClicked.emit();
    }
    onTertiaryButtonClicked() {
        this.actionTertiaryButtonClicked.emit();
    }
    onLinkClicked() {
        this.actionLinkClicked.emit();
    }
    open() {
        this.previousActiveElement = document.activeElement;
        this.element.style.display = 'block';
        document.body.classList.add('vpg-welcome-mat-modal-open');
        setTimeout(() => {
            this.setInitialFocus();
        });
    }
    setInitialFocus() {
        this.isOpen = true;
        this.element.querySelector('[tabindex="-1"')?.focus();
        this.firstFocusableElement = this.element.querySelector('[tabindex = "0"]');
        this.firstFocusableElement?.focus();
        setTimeout(() => {
            this.vpgFocusTrap.handleTabNavigation();
        });
    }
    close() {
        this.isOpen = false;
        this.element.style.display = 'none';
        document.body.classList.remove('vpg-welcome-mat-modal-open');
        this.previousActiveElement?.focus();
    }
    closeModal(id) {
        this.modalService.close(id);
    }
    onImageError() {
        this.showImage = false;
    }
    setIds() {
        this.modalOpenId = this.id + 'modalOpen';
        this.titleId = this.id + 'modalTitle';
        this.contentId = this.id + 'modalContent';
        this.modalContentId = this.id + 'modalContentWrapper';
        this.closeId = this.id + 'modalClose';
        this.actionsId = this.id + 'modalActions';
        this.imageId = this.id + 'modalImage';
        this.modalActionsId = this.id + '-welcome-modal-actions';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: WelcomeMatComponent, deps: [{ token: VpgModalService }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: WelcomeMatComponent, selector: "vpg-welcome-mat", inputs: { id: "id", imageSource: "imageSource", alternativeText: "alternativeText", contentTitle: "contentTitle", contentText: "contentText", ariaLabelCloseIcon: "ariaLabelCloseIcon", showCloseIcon: "showCloseIcon", matActionInputs: "matActionInputs" }, outputs: { closeIconButtonClicked: "closeIconButtonClicked", actionPrimaryButtonClicked: "actionPrimaryButtonClicked", actionSecondaryButtonClicked: "actionSecondaryButtonClicked", actionTertiaryButtonClicked: "actionTertiaryButtonClicked", actionLinkClicked: "actionLinkClicked" }, host: { listeners: { "document:keydown.escape": "onEscape($event)" } }, viewQueries: [{ propertyName: "vpgFocusTrap", first: true, predicate: FocusTrapDirective, descendants: true }], usesOnChanges: true, ngImport: i0, template: "<div\n    vpgFocusTrap\n    class=\"vpg-welcome-mat-modal-main\"\n    role=\"dialog\"\n    tabindex=\"-1\"\n    [attr.aria-modal]=\"isOpen\"\n    [attr.aria-labelledby]=\"titleId\"\n    [attr.aria-describedby]=\"showCloseIcon ? null : modalContentId\"\n>\n    <div class=\"vpg-welcome-mat-container\">\n        <div class=\"vpg-welcome-mat-img\">\n            <vpg-button\n                *ngIf=\"showCloseIcon\"\n                [id]=\"closeId\"\n                class=\"vpg-welcome-mat-close-btn\"\n                [btnType]=\"buttonType.IconTertiary\"\n                [className]=\"closeIcon\"\n                [ariaLabel]=\"ariaLabelCloseIcon\"\n                (buttonClicked)=\"onCloseIconButtonClicked()\"\n            >\n            </vpg-button>\n            <img\n                *ngIf=\"showImage\"\n                [id]=\"imageId\"\n                [src]=\"imageSource\"\n                [alt]=\"alternativeText\"\n                (error)=\"onImageError()\"\n            >\n            <div\n                *ngIf=\"!showImage\"\n                class=\"vpg-welcome-mat-placeholder-img\"\n                [attr.aria-label]=\"alternativeText\"\n                role=\"img\"\n            ></div>\n        </div>\n        <div class=\"vpg-welcome-mat-content\">\n            <h2\n                class=\"vpg-welcome-mat-content-title\"\n                [id]=\"titleId\"\n                aria-hidden=\"true\"\n            >\n                {{ contentTitle }}\n            </h2>\n            <div [id]=\"modalContentId\">\n                <div\n                    [attr.tabIndex]=\"showCloseIcon ? null : '-1'\"\n                    [id]=\"contentId\"\n                    class=\"vpg-welcome-mat-content-text\"\n                    [innerHtml]=\"contentText\"\n                >\n                </div>\n            </div>\n        </div>\n    </div>\n    <div\n        class=\"vpg-welcome-mat-actions\"\n        [id]=\"actionsId\"\n    >\n        <vpg-modal-actions\n            [id]=\"modalActionsId\"\n            [modalActionType]=\"matActionInputs.modalActionType\"\n            [primaryButtonLabel]=\"matActionInputs.primaryButtonLabel\"\n            [secondaryButtonLabel]=\"matActionInputs.secondaryButtonLabel\"\n            [tertiaryButtonLabel]=\"matActionInputs.tertiaryButtonLabel\"\n            [linkLabel]=\"matActionInputs.linkLabel\"\n            [linkUrl]=\"matActionInputs.linkUrl\"\n            (primaryButtonClicked)=\"onPrimaryButtonClicked()\"\n            (secondaryButtonClicked)=\"onSecondaryButtonClicked()\"\n            (tertiaryButtonClicked)=\"onTertiaryButtonClicked()\"\n            (linkClicked)=\"onLinkClicked()\">\n        </vpg-modal-actions>\n    </div>\n</div>\n<div class=\"vpg-welcome-mat-background\" (click)=\"close()\"></div>\n", styles: [".vpg-welcome-mat-placeholder-img{height:320px;background:url(/assets/images/heroPlaceholderImage.png) no-repeat center;background-size:cover}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: ButtonComponent, selector: "vpg-button", inputs: ["id", "buttonSubmit", "label", "isDisabled", "className", "btnType", "ariaLabel", "isCompleted", "buttonSize", "iconOnTheRight", "fullWidth", "buttonAlignment"], outputs: ["buttonClicked"] }, { kind: "component", type: ModalActionsComponent, selector: "vpg-modal-actions", inputs: ["modalActionType", "primaryButtonLabel", "secondaryButtonLabel", "tertiaryButtonLabel", "linkLabel", "linkUrl", "id"], outputs: ["primaryButtonClicked", "secondaryButtonClicked", "tertiaryButtonClicked", "linkClicked"] }, { kind: "directive", type: FocusTrapDirective, selector: "[vpgFocusTrap]" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: WelcomeMatComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-welcome-mat', template: "<div\n    vpgFocusTrap\n    class=\"vpg-welcome-mat-modal-main\"\n    role=\"dialog\"\n    tabindex=\"-1\"\n    [attr.aria-modal]=\"isOpen\"\n    [attr.aria-labelledby]=\"titleId\"\n    [attr.aria-describedby]=\"showCloseIcon ? null : modalContentId\"\n>\n    <div class=\"vpg-welcome-mat-container\">\n        <div class=\"vpg-welcome-mat-img\">\n            <vpg-button\n                *ngIf=\"showCloseIcon\"\n                [id]=\"closeId\"\n                class=\"vpg-welcome-mat-close-btn\"\n                [btnType]=\"buttonType.IconTertiary\"\n                [className]=\"closeIcon\"\n                [ariaLabel]=\"ariaLabelCloseIcon\"\n                (buttonClicked)=\"onCloseIconButtonClicked()\"\n            >\n            </vpg-button>\n            <img\n                *ngIf=\"showImage\"\n                [id]=\"imageId\"\n                [src]=\"imageSource\"\n                [alt]=\"alternativeText\"\n                (error)=\"onImageError()\"\n            >\n            <div\n                *ngIf=\"!showImage\"\n                class=\"vpg-welcome-mat-placeholder-img\"\n                [attr.aria-label]=\"alternativeText\"\n                role=\"img\"\n            ></div>\n        </div>\n        <div class=\"vpg-welcome-mat-content\">\n            <h2\n                class=\"vpg-welcome-mat-content-title\"\n                [id]=\"titleId\"\n                aria-hidden=\"true\"\n            >\n                {{ contentTitle }}\n            </h2>\n            <div [id]=\"modalContentId\">\n                <div\n                    [attr.tabIndex]=\"showCloseIcon ? null : '-1'\"\n                    [id]=\"contentId\"\n                    class=\"vpg-welcome-mat-content-text\"\n                    [innerHtml]=\"contentText\"\n                >\n                </div>\n            </div>\n        </div>\n    </div>\n    <div\n        class=\"vpg-welcome-mat-actions\"\n        [id]=\"actionsId\"\n    >\n        <vpg-modal-actions\n            [id]=\"modalActionsId\"\n            [modalActionType]=\"matActionInputs.modalActionType\"\n            [primaryButtonLabel]=\"matActionInputs.primaryButtonLabel\"\n            [secondaryButtonLabel]=\"matActionInputs.secondaryButtonLabel\"\n            [tertiaryButtonLabel]=\"matActionInputs.tertiaryButtonLabel\"\n            [linkLabel]=\"matActionInputs.linkLabel\"\n            [linkUrl]=\"matActionInputs.linkUrl\"\n            (primaryButtonClicked)=\"onPrimaryButtonClicked()\"\n            (secondaryButtonClicked)=\"onSecondaryButtonClicked()\"\n            (tertiaryButtonClicked)=\"onTertiaryButtonClicked()\"\n            (linkClicked)=\"onLinkClicked()\">\n        </vpg-modal-actions>\n    </div>\n</div>\n<div class=\"vpg-welcome-mat-background\" (click)=\"close()\"></div>\n", styles: [".vpg-welcome-mat-placeholder-img{height:320px;background:url(/assets/images/heroPlaceholderImage.png) no-repeat center;background-size:cover}\n"] }]
        }], ctorParameters: () => [{ type: VpgModalService }, { type: i0.ElementRef }], propDecorators: { id: [{
                type: Input
            }], imageSource: [{
                type: Input
            }], alternativeText: [{
                type: Input
            }], contentTitle: [{
                type: Input
            }], contentText: [{
                type: Input
            }], ariaLabelCloseIcon: [{
                type: Input
            }], showCloseIcon: [{
                type: Input
            }], matActionInputs: [{
                type: Input
            }], closeIconButtonClicked: [{
                type: Output
            }], actionPrimaryButtonClicked: [{
                type: Output
            }], actionSecondaryButtonClicked: [{
                type: Output
            }], actionTertiaryButtonClicked: [{
                type: Output
            }], actionLinkClicked: [{
                type: Output
            }], vpgFocusTrap: [{
                type: ViewChild,
                args: [FocusTrapDirective, { static: false }]
            }], onEscape: [{
                type: HostListener,
                args: ['document:keydown.escape', ['$event']]
            }] } });

function valueAccessor(type) {
    return {
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => type),
        multi: true
    };
}
class AbstractFormComponent {
    constructor() {
        this.#changeFn = Function.prototype;
        this.#touchedFn = Function.prototype;
        this.#value = null;
        this.disabled = false;
        this.id = '';
        this.name = '';
        this.valueChange = new EventEmitter();
    }
    #changeFn;
    #touchedFn;
    #value;
    set value(value) {
        if (this.#value !== value) {
            this.#value = value;
            this.notifyValueChange(this.#value);
        }
    }
    get value() {
        return this.#value;
    }
    writeValue(obj) {
        this.value = obj;
    }
    notifyValueChange(value) {
        this.valueChange.emit(this.#value);
        this.#changeFn(value);
    }
    notifyTouched() {
        this.#touchedFn();
    }
    registerOnChange(fn) {
        this.#changeFn = fn;
    }
    registerOnTouched(fn) {
        this.#touchedFn = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    setName(control) {
        if (control && typeof control.name == 'string' && !this.name) {
            this.name = control.name;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: AbstractFormComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: AbstractFormComponent, selector: "ng-component", inputs: { id: "id", name: "name", value: "value" }, outputs: { valueChange: "valueChange" }, ngImport: i0, template: '', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: AbstractFormComponent, decorators: [{
            type: Component,
            args: [{ template: '' }]
        }], propDecorators: { id: [{
                type: Input
            }], name: [{
                type: Input
            }], valueChange: [{
                type: Output
            }], value: [{
                type: Input
            }] } });

class RadioButtonComponent extends AbstractFormComponent {
    constructor(injector) {
        super();
        this.injector = injector;
        this.label = '';
        this.align = 'left';
        this.optionValue = '';
        this.size = 'large';
        this.required = false;
    }
    ngOnInit() {
        this.setName(this.injector.get(NgControl));
    }
    onChange(event) {
        this.value = null;
        this.value = event.target.value;
    }
    get classNames() {
        return {
            ['v-radio-button']: true,
            [`is-${this.size}`]: true
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: RadioButtonComponent, deps: [{ token: i0.Injector }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: RadioButtonComponent, selector: "vpg-radio-button", inputs: { label: "label", align: "align", optionValue: "optionValue", size: "size", required: "required" }, host: { properties: { "id": "id", "class.v-radio-button": "true", "class.is-disabled": "disabled", "class": "this.classNames" } }, providers: [valueAccessor(RadioButtonComponent)], usesInheritance: true, ngImport: i0, template: "<input\n\ttype=\"radio\"\n    id=\"{{id}}-input\"\n    class=\"v-radio-button-input\"\n    [disabled]=\"disabled\"\n    [name]=\"name\"\n    [value]=\"optionValue\"\n    [checked]=\"value === optionValue\"\n    (change)=\"onChange($event)\"\n    [attr.aria-required]=\"required\"\n/>\n<label\n    *ngIf=\"label; else content\"\n    for=\"{{id}}-input\"\n    id=\"{{id}}-label\"\n    class=\"v-radio-button-label\"\n>\n    {{ label }}\n</label>\n<ng-template #content>\n    <ng-content></ng-content>\n</ng-template>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: RadioButtonComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-radio-button', providers: [valueAccessor(RadioButtonComponent)], host: {
                        '[id]': 'id',
                        '[class.v-radio-button]': 'true',
                        '[class.is-disabled]': 'disabled'
                    }, template: "<input\n\ttype=\"radio\"\n    id=\"{{id}}-input\"\n    class=\"v-radio-button-input\"\n    [disabled]=\"disabled\"\n    [name]=\"name\"\n    [value]=\"optionValue\"\n    [checked]=\"value === optionValue\"\n    (change)=\"onChange($event)\"\n    [attr.aria-required]=\"required\"\n/>\n<label\n    *ngIf=\"label; else content\"\n    for=\"{{id}}-input\"\n    id=\"{{id}}-label\"\n    class=\"v-radio-button-label\"\n>\n    {{ label }}\n</label>\n<ng-template #content>\n    <ng-content></ng-content>\n</ng-template>\n" }]
        }], ctorParameters: () => [{ type: i0.Injector }], propDecorators: { label: [{
                type: Input
            }], align: [{
                type: Input
            }], optionValue: [{
                type: Input
            }], size: [{
                type: Input
            }], required: [{
                type: Input
            }], classNames: [{
                type: HostBinding,
                args: ['class']
            }] } });

class RadioButtonGroupComponent {
    constructor() {
        this.id = '';
        this.legend = '';
        this.align = 'left';
        this.required = false;
    }
    get classNames() {
        return {
            ['v-radio-button-group']: true,
            [`is-${this.align}`]: true
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: RadioButtonGroupComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: RadioButtonGroupComponent, selector: "vpg-radio-button-group", inputs: { id: "id", legend: "legend", align: "align", required: "required" }, host: { properties: { "id": "id", "class": "this.classNames" } }, ngImport: i0, template: "<fieldset class=\"v-radio-button-group-fieldset\">\n    <legend class=\"v-radio-button-group-legend\">\n        {{ legend }}\n        <span\n            *ngIf=\"required\"\n            class=\"v-control-required\"\n            aria-hidden=\"true\"\n        >\n            *\n        </span>\n    </legend>\n    <ng-content></ng-content>\n</fieldset>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: RadioButtonGroupComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-radio-button-group', host: {
                        '[id]': 'id'
                    }, template: "<fieldset class=\"v-radio-button-group-fieldset\">\n    <legend class=\"v-radio-button-group-legend\">\n        {{ legend }}\n        <span\n            *ngIf=\"required\"\n            class=\"v-control-required\"\n            aria-hidden=\"true\"\n        >\n            *\n        </span>\n    </legend>\n    <ng-content></ng-content>\n</fieldset>\n" }]
        }], propDecorators: { id: [{
                type: Input
            }], legend: [{
                type: Input
            }], align: [{
                type: Input
            }], required: [{
                type: Input
            }], classNames: [{
                type: HostBinding,
                args: ['class']
            }] } });

class InteractiveElementDirective {
    constructor() {
        this.action = new EventEmitter();
        this.tabindex = 0;
        this.role = 'button';
        this.screenReaderText = '';
    }
    emitAction() {
        this.action.emit();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: InteractiveElementDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.9", type: InteractiveElementDirective, isStandalone: true, selector: "[vpg-interactive-element]", inputs: { tabindex: "tabindex", role: "role", screenReaderText: "screenReaderText" }, outputs: { action: "vpg-interactive-element" }, host: { listeners: { "keydown.space": "emitAction($event)", "keydown.enter": "emitAction($event)", "click": "emitAction($event)" }, properties: { "tabindex": "this.tabindex", "role": "this.role", "attr.aria-label": "this.screenReaderText" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: InteractiveElementDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[vpg-interactive-element]',
                    standalone: true
                }]
        }], propDecorators: { action: [{
                type: Output,
                args: ['vpg-interactive-element']
            }], tabindex: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['tabindex']
            }], role: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['role']
            }], screenReaderText: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['attr.aria-label']
            }], emitAction: [{
                type: HostListener,
                args: ['keydown.space', ['$event']]
            }, {
                type: HostListener,
                args: ['keydown.enter', ['$event']]
            }, {
                type: HostListener,
                args: ['click', ['$event']]
            }] } });

var Keys;
(function (Keys) {
    Keys["Enter"] = "Enter";
    Keys["Space"] = "Space";
    Keys["Escape"] = "Escape";
    Keys["Tab"] = "Tab";
    Keys["ArrowLeft"] = "ArrowLeft";
    Keys["ArrowRight"] = "ArrowRight";
    Keys["ArrowDown"] = "ArrowDown";
    Keys["ArrowUp"] = "ArrowUp";
    Keys["Home"] = "Home";
    Keys["End"] = "End";
    Keys["PageUp"] = "PageUp";
    Keys["PageDown"] = "PageDown";
})(Keys || (Keys = {}));

class KeyboardNavigateDirective {
    constructor(element) {
        this.element = element;
        this.controlOptionClassName = 'v-control-option';
        this.optionFocused = new EventEmitter();
        this.destroy$ = new Subject();
        this.index = -1;
    }
    ngOnInit() {
        fromEvent(document, 'keydown')
            .pipe(takeUntil(this.destroy$))
            .subscribe((e) => {
            if (e.key === Keys.Enter) {
                this.selectActiveOptionOnEnter(e);
            }
            this.navigate(e.key);
        });
    }
    ngOnDestroy() {
        this.destroy$.next();
    }
    selectActiveOptionOnEnter(e) {
        e.preventDefault();
        const activeOption = this.getActiveOption();
        if (activeOption) {
            activeOption.click();
        }
    }
    getActiveOption() {
        return this.element.nativeElement.querySelector('.is-active');
    }
    getOptions() {
        return this.element.nativeElement.querySelectorAll('.' + this.controlOptionClassName);
    }
    selectOption(option) {
        option.setAttribute('aria-selected', 'true');
        option.classList.add('is-active');
        this.optionFocused.emit(option);
        this.scrollTo(option);
    }
    unselectOption(option) {
        option.setAttribute('aria-selected', 'false');
        option.classList.remove('is-active');
    }
    scrollTo(option) {
        option.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest'
        });
    }
    navigate(key) {
        const options = this.getOptions();
        const length = options.length - 1;
        const first = options[0];
        const last = options[length];
        if (key === Keys.ArrowDown) {
            this.index = this.index + 1;
            const next = options[this.index];
            const prev = options[this.index - 1];
            if (next && this.index <= length) {
                this.selectOption(next);
                if (prev !== undefined) {
                    this.unselectOption(prev);
                }
            }
            else {
                this.selectOption(first);
                this.unselectOption(last);
                this.index = 0;
            }
        }
        if (key === Keys.ArrowUp) {
            this.index = this.index - 1;
            const next = options[this.index];
            const prev = options[this.index + 1];
            if (next && this.index >= 0) {
                this.selectOption(next);
                if (prev !== undefined) {
                    this.unselectOption(prev);
                }
            }
            else {
                this.selectOption(last);
                this.unselectOption(first);
                this.index = length;
            }
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: KeyboardNavigateDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.9", type: KeyboardNavigateDirective, isStandalone: true, selector: "[vpg-keyboard-navigate]", inputs: { controlOptionClassName: "controlOptionClassName" }, outputs: { optionFocused: "optionFocused" }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: KeyboardNavigateDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[vpg-keyboard-navigate]',
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { controlOptionClassName: [{
                type: Input
            }], optionFocused: [{
                type: Output
            }] } });

class InputSelectionRangeDirective {
    constructor(element) {
        this.element = element;
    }
    setSelectionRange() {
        const input = this.element.nativeElement;
        const length = input.value.length;
        input.setSelectionRange(length, length);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: InputSelectionRangeDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.9", type: InputSelectionRangeDirective, isStandalone: true, selector: "[vpgInputSelectionRange]", host: { listeners: { "click": "setSelectionRange()" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: InputSelectionRangeDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[vpgInputSelectionRange]',
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { setSelectionRange: [{
                type: HostListener,
                args: ['click']
            }] } });

class FallbackImageDirective {
    constructor(element, renderer) {
        this.element = element;
        this.renderer = renderer;
        this.fallbackImageUrl = '/assets/images/heroPlaceholderImage.png';
        this.fallbackImageClass = '';
    }
    get nativeElement() {
        return this.element.nativeElement;
    }
    setFallbackImage() {
        this.renderer.setAttribute(this.nativeElement, 'src', this.fallbackImageUrl);
        if (this.fallbackImageClass) {
            this.renderer.addClass(this.nativeElement, this.fallbackImageClass);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: FallbackImageDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.9", type: FallbackImageDirective, selector: "img[vpgFallbackImage]", inputs: { fallbackImageUrl: "fallbackImageUrl", fallbackImageClass: "fallbackImageClass" }, host: { listeners: { "error": "setFallbackImage()" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: FallbackImageDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'img[vpgFallbackImage]'
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.Renderer2 }], propDecorators: { fallbackImageUrl: [{
                type: Input
            }], fallbackImageClass: [{
                type: Input
            }], setFallbackImage: [{
                type: HostListener,
                args: ['error']
            }] } });

class RepeatDirective {
    constructor(template, container) {
        this.template = template;
        this.container = container;
        this.times = 0;
    }
    ngOnInit() {
        for (let i = 0; i < this.times; i++) {
            const embeddedView = this.container.createEmbeddedView(this.template);
            this.container.insert(embeddedView);
        }
    }
    ngOnDestroy() {
        this.container.clear();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: RepeatDirective, deps: [{ token: i0.TemplateRef }, { token: i0.ViewContainerRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.9", type: RepeatDirective, isStandalone: true, selector: "[repeat]", inputs: { times: ["repeat", "times"] }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: RepeatDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[repeat]',
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: i0.TemplateRef }, { type: i0.ViewContainerRef }], propDecorators: { times: [{
                type: Input,
                args: ['repeat']
            }] } });

class TruncatePipe {
    transform(value, args) {
        const limit = args.length > 0 ? parseInt(args[0], 10) : 20;
        const trail = args.length > 1 ? args[1] : '...';
        return value.length > limit ? value.substring(0, limit) + trail : value;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: TruncatePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.3.9", ngImport: i0, type: TruncatePipe, name: "truncate" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: TruncatePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'truncate'
                }]
        }] });

class HighlightPipe {
    transform(value, args) {
        if (typeof (args[0]) === 'boolean' && !args[0]) {
            return value;
        }
        const searchTextLength = args[1].length;
        const substringIndices = this.getAllIndicesForSearchValue(value, args[1]?.toString());
        if (!searchTextLength || !substringIndices.length) {
            return value;
        }
        substringIndices.forEach((substringIndex, i) => {
            const indexValue = this.modifyIndexValue(substringIndex, i);
            value = this.highlightSubstring(value, indexValue, searchTextLength);
        });
        return value.replace(new RegExp(/([*])(?:(?=(\\?))\2.)*?\1/g, 'gi'), (match) => `<strong>${match.replace(/\*/g, '')}</strong>`);
    }
    modifyIndexValue(substringIndex, index) {
        return substringIndex + (index * 2);
    }
    highlightSubstring(value, startIndex, substringLength) {
        const stringStart = value.substring(0, startIndex);
        const highlightedString = `*${value.substring(startIndex, (startIndex + substringLength))}*`;
        const stringEnd = value.substring(startIndex + substringLength);
        return `${stringStart}${highlightedString}${stringEnd}`;
    }
    getAllIndicesForSearchValue(inputValue, searchValue) {
        const searchStrLen = searchValue.length;
        const indices = [];
        const value = inputValue.toLowerCase();
        const substringValue = searchValue.toLowerCase();
        let startIndex = 0;
        let index = 0;
        if (searchStrLen === 0) {
            return [];
        }
        while ((index = value.indexOf(substringValue, startIndex)) > -1) {
            indices.push(index);
            startIndex = index + searchStrLen;
        }
        return indices;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: HighlightPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.3.9", ngImport: i0, type: HighlightPipe, isStandalone: true, name: "highlight" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: HighlightPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'highlight',
                    standalone: true
                }]
        }] });

var VPGrooveDefaultColorHexValues;
(function (VPGrooveDefaultColorHexValues) {
    VPGrooveDefaultColorHexValues["None"] = "";
    VPGrooveDefaultColorHexValues["PrimaryBase"] = "#003C44";
    VPGrooveDefaultColorHexValues["PrimaryDark"] = "#002C33";
    VPGrooveDefaultColorHexValues["PrimaryLight"] = "#809DA1";
    VPGrooveDefaultColorHexValues["SecondaryOrangeBase"] = "#FF8B63";
    VPGrooveDefaultColorHexValues["SecondaryOrangeDark"] = "#ED5500";
    VPGrooveDefaultColorHexValues["SecondaryOrangeLight"] = "#FFD4BC";
    VPGrooveDefaultColorHexValues["SecondaryBlueBase"] = "#809DA1";
    VPGrooveDefaultColorHexValues["SecondaryBlueDark"] = "#003C44";
    VPGrooveDefaultColorHexValues["SecondaryBlueLight"] = "#CCD8DA";
    VPGrooveDefaultColorHexValues["SecondaryTealBase"] = "#7FD8B8";
    VPGrooveDefaultColorHexValues["SecondaryTealDark"] = "#228477";
    VPGrooveDefaultColorHexValues["SecondaryTealLight"] = "#D9EFE7";
    VPGrooveDefaultColorHexValues["NeutralGray8"] = "#202020";
    VPGrooveDefaultColorHexValues["NeutralGray7"] = "#333333";
    VPGrooveDefaultColorHexValues["NeutralGray6"] = "#525252";
    VPGrooveDefaultColorHexValues["NeutralGray5"] = "#767676";
    VPGrooveDefaultColorHexValues["NeutralGray4"] = "#989898";
    VPGrooveDefaultColorHexValues["NeutralGray3"] = "#D2D2D2";
    VPGrooveDefaultColorHexValues["NeutralGray2"] = "#ECECEC";
    VPGrooveDefaultColorHexValues["NeutralGray1"] = "#F4F4F4";
    VPGrooveDefaultColorHexValues["NeutralGrayWhite"] = "#FFFFFF";
    VPGrooveDefaultColorHexValues["StatusGreenBase"] = "#3FA256";
    VPGrooveDefaultColorHexValues["StatusGreenLight"] = "#C8FFC7";
    VPGrooveDefaultColorHexValues["StatusGreenDark"] = "#347B48";
    VPGrooveDefaultColorHexValues["StatusGoldBase"] = "#FFF600";
    VPGrooveDefaultColorHexValues["StatusGoldLight"] = "#FFFCB2";
    VPGrooveDefaultColorHexValues["StatusGoldDark"] = "#767676";
    VPGrooveDefaultColorHexValues["StatusRedBase"] = "#AB2D26";
    VPGrooveDefaultColorHexValues["StatusRedLight"] = "#FFE5E0";
    VPGrooveDefaultColorHexValues["StatusRedDark"] = "#882620";
    VPGrooveDefaultColorHexValues["Urgent"] = "#AB2D26";
})(VPGrooveDefaultColorHexValues || (VPGrooveDefaultColorHexValues = {}));

const BLACK = '#000000';
const YELLOW = '#FFCB12';
const NEUTRAL_GRAY_WHITE_1 = '#F3F0E7';
const DEFAULT_THEME = {
    name: 'DefaultVPGrooveTheme',
    themeProperties: {
        '--VPGrooveColor-ButtonBackgroundColor': VPGrooveDefaultColorHexValues.PrimaryBase,
        '--VPGrooveColor-ButtonTextColor': VPGrooveDefaultColorHexValues.NeutralGrayWhite,
        '--VPGrooveColor-LinkColor': VPGrooveDefaultColorHexValues.SecondaryTealDark,
        '--VPGrooveColor-ButtonBackgroundColorDark': VPGrooveDefaultColorHexValues.PrimaryDark,
        '--VPGrooveColor-ButtonBackgroundColorLight': VPGrooveDefaultColorHexValues.PrimaryLight,
        '--VPGrooveColor-BackgroundPrimary': VPGrooveDefaultColorHexValues.PrimaryBase,
        '--VPGrooveColor-BackgroundPrimaryTextColor': VPGrooveDefaultColorHexValues.NeutralGrayWhite,
        '--VPGrooveColor-BackgroundInfo': YELLOW,
        '--VPGrooveColor-BackgroundInfoTextColor': BLACK,
        '--VPGrooveColor-ButtonInfo': VPGrooveDefaultColorHexValues.PrimaryBase,
        '--VPGrooveColor-ButtonInfoTextColor': VPGrooveDefaultColorHexValues.NeutralGrayWhite,
        '--VPGrooveColor-HighlightInfo': YELLOW,
        '--VPGrooveColor-HighlightInfoTextColor': BLACK,
        '--VPGrooveColor-ProgressBarInfo': VPGrooveDefaultColorHexValues.SecondaryTealDark,
        '--VPGrooveColor-ProgressBarInfoTextColor': BLACK,
        '--VPGrooveColor-ProgressBarRewards': VPGrooveDefaultColorHexValues.SecondaryTealDark,
        '--VPGrooveColor-ProgressBarRewardsTextColor': BLACK,
        '--VPGrooveColor-ProgressRingInfo': VPGrooveDefaultColorHexValues.SecondaryTealDark,
        '--VPGrooveColor-ProgressRingPrimary': VPGrooveDefaultColorHexValues.SecondaryTealDark,
        '--VPGrooveColor-Ribbon': YELLOW,
        '--VPGrooveColor-RibbonTextColor': BLACK,
        '--VPGrooveColor-TrimInfo': VPGrooveDefaultColorHexValues.PrimaryBase,
        '--VPGrooveColor-BodyBackground': NEUTRAL_GRAY_WHITE_1,
        '--VPGrooveColor-RewardsBarSecondary': YELLOW
    }
};

class ThemeManagerService {
    getActiveTheme() {
        const name = this.getThemeName();
        const themeProperties = {
            '--VPGrooveColor-ButtonBackgroundColor': this.getThemePropertyValue('--VPGrooveColor-ButtonBackgroundColor'),
            '--VPGrooveColor-ButtonTextColor': this.getThemePropertyValue('--VPGrooveColor-ButtonTextColor'),
            '--VPGrooveColor-LinkColor': this.getThemePropertyValue('--VPGrooveColor-LinkColor'),
            '--VPGrooveColor-ButtonBackgroundColorDark': this.getThemePropertyValue('--VPGrooveColor-ButtonBackgroundColorDark'),
            '--VPGrooveColor-ButtonBackgroundColorLight': this.getThemePropertyValue('--VPGrooveColor-ButtonBackgroundColorLight'),
            '--VPGrooveColor-BackgroundPrimary': this.getThemePropertyValue('--VPGrooveColor-BackgroundPrimary'),
            '--VPGrooveColor-BackgroundPrimaryTextColor': this.getThemePropertyValue('--VPGrooveColor-BackgroundPrimaryTextColor'),
            '--VPGrooveColor-BackgroundInfo': this.getThemePropertyValue('--VPGrooveColor-BackgroundInfo'),
            '--VPGrooveColor-BackgroundInfoTextColor': this.getThemePropertyValue('--VPGrooveColor-BackgroundInfoTextColor'),
            '--VPGrooveColor-ButtonInfo': this.getThemePropertyValue('--VPGrooveColor-ButtonInfo'),
            '--VPGrooveColor-ButtonInfoTextColor': this.getThemePropertyValue('--VPGrooveColor-ButtonInfoTextColor'),
            '--VPGrooveColor-HighlightInfo': this.getThemePropertyValue('--VPGrooveColor-HighlightInfo'),
            '--VPGrooveColor-HighlightInfoTextColor': this.getThemePropertyValue('--VPGrooveColor-HighlightInfoTextColor'),
            '--VPGrooveColor-ProgressBarInfo': this.getThemePropertyValue('--VPGrooveColor-ProgressBarInfo'),
            '--VPGrooveColor-ProgressBarInfoTextColor': this.getThemePropertyValue('--VPGrooveColor-BackgroundPrimary'),
            '--VPGrooveColor-ProgressBarRewards': this.getThemePropertyValue('--VPGrooveColor-ProgressBarRewards'),
            '--VPGrooveColor-ProgressBarRewardsTextColor': this.getThemePropertyValue('--VPGrooveColor-ProgressBarRewardsTextColor'),
            '--VPGrooveColor-ProgressRingInfo': this.getThemePropertyValue('--VPGrooveColor-ProgressRingInfo'),
            '--VPGrooveColor-ProgressRingInfoTextColor': this.getThemePropertyValue('--VPGrooveColor-ProgressRingInfoTextColor'),
            '--VPGrooveColor-ProgressRingPrimary': this.getThemePropertyValue('--VPGrooveColor-ProgressRingPrimary'),
            '--VPGrooveColor-ProgressRingPrimaryTextColor': this.getThemePropertyValue('--VPGrooveColor-ProgressRingPrimaryTextColor'),
            '--VPGrooveColor-Ribbon': this.getThemePropertyValue('--VPGrooveColor-Ribbon'),
            '--VPGrooveColor-RibbonTextColor': this.getThemePropertyValue('--VPGrooveColor-RibbonTextColor'),
            '--VPGrooveColor-TextLink': this.getThemePropertyValue('--VPGrooveColor-TextLink'),
            '--VPGrooveColor-TrimInfo': this.getThemePropertyValue('--VPGrooveColor-TrimInfo'),
            '--VPGrooveColor-BodyBackground': this.getThemePropertyValue('--VPGrooveColor-BodyBackground'),
            '--VPGrooveColor-RewardsBarSecondary': this.getThemePropertyValue('--VPGrooveColor-RewardsBarSecondary')
        };
        return {
            name,
            themeProperties
        };
    }
    updateActiveTheme(theme) {
        document.documentElement.style.setProperty('--VPGrooveTheme-name', theme.name);
        if (theme.name !== 'Default') {
            this.updateColorTransparency(theme);
        }
        Object.keys(theme.themeProperties).forEach((property) => {
            const propertyValue = theme.themeProperties[property];
            document.documentElement.style.setProperty(property, propertyValue ? propertyValue : '');
        });
    }
    getDefaultTheme() {
        return DEFAULT_THEME;
    }
    registerTheme(theme) {
        if (!this.isThemeDefined()) {
            this.updateActiveTheme(theme || DEFAULT_THEME);
        }
    }
    isThemeDefined() {
        const themeName = document.documentElement.style.getPropertyValue('--VPGrooveTheme-name');
        return Boolean(themeName);
    }
    isDefaultThemeActive() {
        return this.getThemeName() === 'DefaultVPGrooveTheme';
    }
    getThemeName() {
        return this.getThemePropertyValue('--VPGrooveTheme-name');
    }
    getThemePropertyValue(property) {
        return document.documentElement.style.getPropertyValue(property);
    }
    updateColorTransparency(theme) {
        const buttonBackgroundColor = theme.themeProperties['--VPGrooveColor-ButtonBackgroundColor'];
        theme.themeProperties['--VPGrooveColor-ButtonBackgroundColorLight'] = this.changeColor(buttonBackgroundColor, 0.15);
        theme.themeProperties['--VPGrooveColor-ButtonBackgroundColorDark'] = this.changeColor(buttonBackgroundColor, -0.15);
    }
    changeColor(colorCode, luminosity) {
        colorCode = String(colorCode).replace(/[^0-9a-f]/gi, '');
        if (colorCode.length < 6) {
            colorCode = colorCode[0] + colorCode[0] + colorCode[1] + colorCode[1] + colorCode[2] + colorCode[2];
        }
        let rgbColor = '#', c, i;
        for (i = 0; i < 3; i++) {
            c = parseInt(colorCode.substring(i * 2, i * 2 + 2), 16);
            c = Math.round(Math.min(Math.max(0, c + c * luminosity), 255)).toString(16);
            rgbColor += ('00' + c).substring(c.length);
        }
        return rgbColor;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ThemeManagerService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ThemeManagerService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ThemeManagerService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

class TranslationService {
    constructor() {
        this.translations = new Map([
            ['NoResultsHeader', 'No results found'],
            ['NoResultsText', 'Try changing the search term']
        ]);
    }
    setTranslation(key, value) {
        this.translations.set(key, value);
    }
    getTranslation(key) {
        return this.translations.get(key) || '';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: TranslationService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: TranslationService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: TranslationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

var CheckmarkType;
(function (CheckmarkType) {
    CheckmarkType["Gated"] = "gated";
    CheckmarkType["Earned"] = "earned";
})(CheckmarkType || (CheckmarkType = {}));

var IconName;
(function (IconName) {
    IconName["NoIcon"] = "";
    IconName["Edit"] = "edit";
    IconName["InfoCircle"] = "info-circle";
    IconName["EllipsisVertical"] = "ellipsis-v (vertical)";
    IconName["EllipsisHorizontal"] = "ellipsis-h (horizontal)";
    IconName["Search"] = "search";
    IconName["Trophy"] = "trophy";
    IconName["ShoppingCart"] = "shopping-cart";
    IconName["Building"] = "building";
    IconName["CalendarAlt"] = "calendar-alt";
    IconName["Calendar"] = "calendar";
    IconName["MapMarkerAlt"] = "map-marker-alt";
    IconName["Heart"] = "heart";
    IconName["Stethoscope"] = "stethoscope";
    IconName["CalendarCheck"] = "calendar-check";
    IconName["LockAlt"] = "lock-alt";
    IconName["HandPaper"] = "hand-paper";
    IconName["Times"] = "times";
    IconName["Star"] = "star";
    IconName["Eye"] = "eye";
    IconName["EyeSlash"] = "eye-slash";
    IconName["AngleDown"] = "angle-down";
    IconName["AngleUp"] = "angle-up";
})(IconName || (IconName = {}));

var KeyCode;
(function (KeyCode) {
    KeyCode[KeyCode["backspace"] = 8] = "backspace";
    KeyCode[KeyCode["tab"] = 9] = "tab";
    KeyCode[KeyCode["enter"] = 13] = "enter";
    KeyCode[KeyCode["shift"] = 16] = "shift";
    KeyCode[KeyCode["ctrl"] = 17] = "ctrl";
    KeyCode[KeyCode["alt"] = 18] = "alt";
    KeyCode[KeyCode["pausebreak"] = 19] = "pausebreak";
    KeyCode[KeyCode["capslock"] = 20] = "capslock";
    KeyCode[KeyCode["escape"] = 27] = "escape";
    KeyCode[KeyCode["spacebar"] = 32] = "spacebar";
    KeyCode[KeyCode["pageup"] = 33] = "pageup";
    KeyCode[KeyCode["pagedown"] = 34] = "pagedown";
    KeyCode[KeyCode["end"] = 35] = "end";
    KeyCode[KeyCode["home"] = 36] = "home";
    KeyCode[KeyCode["leftarrow"] = 37] = "leftarrow";
    KeyCode[KeyCode["uparrow"] = 38] = "uparrow";
    KeyCode[KeyCode["rightarrow"] = 39] = "rightarrow";
    KeyCode[KeyCode["downarrow"] = 40] = "downarrow";
    KeyCode[KeyCode["insert"] = 45] = "insert";
    KeyCode[KeyCode["delete"] = 46] = "delete";
    KeyCode[KeyCode["n0"] = 48] = "n0";
    KeyCode[KeyCode["n1"] = 49] = "n1";
    KeyCode[KeyCode["n2"] = 50] = "n2";
    KeyCode[KeyCode["n3"] = 51] = "n3";
    KeyCode[KeyCode["n4"] = 52] = "n4";
    KeyCode[KeyCode["n5"] = 53] = "n5";
    KeyCode[KeyCode["n6"] = 54] = "n6";
    KeyCode[KeyCode["n7"] = 55] = "n7";
    KeyCode[KeyCode["n8"] = 56] = "n8";
    KeyCode[KeyCode["n9"] = 57] = "n9";
    KeyCode[KeyCode["a"] = 65] = "a";
    KeyCode[KeyCode["b"] = 66] = "b";
    KeyCode[KeyCode["c"] = 67] = "c";
    KeyCode[KeyCode["d"] = 68] = "d";
    KeyCode[KeyCode["e"] = 69] = "e";
    KeyCode[KeyCode["f"] = 70] = "f";
    KeyCode[KeyCode["g"] = 71] = "g";
    KeyCode[KeyCode["h"] = 72] = "h";
    KeyCode[KeyCode["i"] = 73] = "i";
    KeyCode[KeyCode["j"] = 74] = "j";
    KeyCode[KeyCode["k"] = 75] = "k";
    KeyCode[KeyCode["l"] = 76] = "l";
    KeyCode[KeyCode["m"] = 77] = "m";
    KeyCode[KeyCode["n"] = 78] = "n";
    KeyCode[KeyCode["o"] = 79] = "o";
    KeyCode[KeyCode["p"] = 80] = "p";
    KeyCode[KeyCode["q"] = 81] = "q";
    KeyCode[KeyCode["r"] = 82] = "r";
    KeyCode[KeyCode["s"] = 83] = "s";
    KeyCode[KeyCode["t"] = 84] = "t";
    KeyCode[KeyCode["u"] = 85] = "u";
    KeyCode[KeyCode["v"] = 86] = "v";
    KeyCode[KeyCode["w"] = 87] = "w";
    KeyCode[KeyCode["x"] = 88] = "x";
    KeyCode[KeyCode["y"] = 89] = "y";
    KeyCode[KeyCode["z"] = 90] = "z";
    KeyCode[KeyCode["leftwindowkey"] = 91] = "leftwindowkey";
    KeyCode[KeyCode["rightwindowkey"] = 92] = "rightwindowkey";
    KeyCode[KeyCode["selectkey"] = 93] = "selectkey";
    KeyCode[KeyCode["numpad0"] = 96] = "numpad0";
    KeyCode[KeyCode["numpad1"] = 97] = "numpad1";
    KeyCode[KeyCode["numpad2"] = 98] = "numpad2";
    KeyCode[KeyCode["numpad3"] = 99] = "numpad3";
    KeyCode[KeyCode["numpad4"] = 100] = "numpad4";
    KeyCode[KeyCode["numpad5"] = 101] = "numpad5";
    KeyCode[KeyCode["numpad6"] = 102] = "numpad6";
    KeyCode[KeyCode["numpad7"] = 103] = "numpad7";
    KeyCode[KeyCode["numpad8"] = 104] = "numpad8";
    KeyCode[KeyCode["numpad9"] = 105] = "numpad9";
    KeyCode[KeyCode["multiply"] = 106] = "multiply";
    KeyCode[KeyCode["add"] = 107] = "add";
    KeyCode[KeyCode["subtract"] = 109] = "subtract";
    KeyCode[KeyCode["decimalpoint"] = 110] = "decimalpoint";
    KeyCode[KeyCode["divide"] = 111] = "divide";
    KeyCode[KeyCode["f1"] = 112] = "f1";
    KeyCode[KeyCode["f2"] = 113] = "f2";
    KeyCode[KeyCode["f3"] = 114] = "f3";
    KeyCode[KeyCode["f4"] = 115] = "f4";
    KeyCode[KeyCode["f5"] = 116] = "f5";
    KeyCode[KeyCode["f6"] = 117] = "f6";
    KeyCode[KeyCode["f7"] = 118] = "f7";
    KeyCode[KeyCode["f8"] = 119] = "f8";
    KeyCode[KeyCode["f9"] = 120] = "f9";
    KeyCode[KeyCode["f10"] = 121] = "f10";
    KeyCode[KeyCode["f11"] = 122] = "f11";
    KeyCode[KeyCode["f12"] = 123] = "f12";
    KeyCode[KeyCode["numlock"] = 144] = "numlock";
    KeyCode[KeyCode["scrolllock"] = 145] = "scrolllock";
    KeyCode[KeyCode["semicolon"] = 186] = "semicolon";
    KeyCode[KeyCode["equalsign"] = 187] = "equalsign";
    KeyCode[KeyCode["comma"] = 188] = "comma";
    KeyCode[KeyCode["dash"] = 189] = "dash";
    KeyCode[KeyCode["period"] = 190] = "period";
    KeyCode[KeyCode["forwardslash"] = 191] = "forwardslash";
    KeyCode[KeyCode["graveaccent"] = 192] = "graveaccent";
    KeyCode[KeyCode["openbracket"] = 219] = "openbracket";
    KeyCode[KeyCode["backslash"] = 220] = "backslash";
    KeyCode[KeyCode["closebraket"] = 221] = "closebraket";
    KeyCode[KeyCode["singlequote"] = 222] = "singlequote";
})(KeyCode || (KeyCode = {}));

var ProgressRingType;
(function (ProgressRingType) {
    ProgressRingType["Small"] = "small";
    ProgressRingType["Large"] = "large";
})(ProgressRingType || (ProgressRingType = {}));

var QuestionnaireType;
(function (QuestionnaireType) {
    QuestionnaireType[QuestionnaireType["QuestionnaireTypeThree"] = 3] = "QuestionnaireTypeThree";
    QuestionnaireType[QuestionnaireType["QuestionnaireTypeFive"] = 5] = "QuestionnaireTypeFive";
    QuestionnaireType[QuestionnaireType["QuestionnaireTypeSeven"] = 7] = "QuestionnaireTypeSeven";
    QuestionnaireType[QuestionnaireType["QuestionnaireTypeNine"] = 9] = "QuestionnaireTypeNine";
})(QuestionnaireType || (QuestionnaireType = {}));

var SimpleProgressBarType;
(function (SimpleProgressBarType) {
    SimpleProgressBarType["Bar"] = "bar";
    SimpleProgressBarType["Ring"] = "ring";
})(SimpleProgressBarType || (SimpleProgressBarType = {}));

var SmallProgressRingType;
(function (SmallProgressRingType) {
    SmallProgressRingType["Gated"] = "gated";
    SmallProgressRingType["NotGated"] = "not-gated";
})(SmallProgressRingType || (SmallProgressRingType = {}));

var VPGrooveSpacingShirtSize;
(function (VPGrooveSpacingShirtSize) {
    VPGrooveSpacingShirtSize["xs"] = "xs (extra small)";
    VPGrooveSpacingShirtSize["s"] = "s (small)";
    VPGrooveSpacingShirtSize["m"] = "m (medium)";
    VPGrooveSpacingShirtSize["l"] = "l (large)";
    VPGrooveSpacingShirtSize["xl"] = "xl (extra large)";
})(VPGrooveSpacingShirtSize || (VPGrooveSpacingShirtSize = {}));

var VPGrooveSpacingVariable;
(function (VPGrooveSpacingVariable) {
    VPGrooveSpacingVariable["xs"] = "ref-xs";
    VPGrooveSpacingVariable["s"] = "ref-s";
    VPGrooveSpacingVariable["m"] = "ref-m";
    VPGrooveSpacingVariable["l"] = "ref-l";
    VPGrooveSpacingVariable["xl"] = "ref-xl";
})(VPGrooveSpacingVariable || (VPGrooveSpacingVariable = {}));

var VPGrooveTextStyleNames;
(function (VPGrooveTextStyleNames) {
    VPGrooveTextStyleNames["header1"] = "VPGrooveFont-header1";
    VPGrooveTextStyleNames["header2"] = "VPGrooveFont-header2";
    VPGrooveTextStyleNames["header3"] = "VPGrooveFont-header3";
    VPGrooveTextStyleNames["header4"] = "VPGrooveFont-header4";
    VPGrooveTextStyleNames["header5"] = "VPGrooveFont-header5";
    VPGrooveTextStyleNames["bodyLarge"] = "VPGrooveFont-bodyLarge";
    VPGrooveTextStyleNames["bodyRegular"] = "VPGrooveFont-bodyRegular";
    VPGrooveTextStyleNames["bodySmall"] = "VPGrooveFont-bodySmall";
    VPGrooveTextStyleNames["linkHeader"] = "VPGrooveFont-linkHeader";
    VPGrooveTextStyleNames["linkBody"] = "VPGrooveFont-linkBody";
    VPGrooveTextStyleNames["linkSmall"] = "VPGrooveFont-linkSmall";
    VPGrooveTextStyleNames["linkMedium"] = "VPGrooveFont-linkMedium";
    VPGrooveTextStyleNames["linkLarge"] = "VPGrooveFont-linkLarge";
    VPGrooveTextStyleNames["iconDefault"] = "VPGrooveFont-icon";
})(VPGrooveTextStyleNames || (VPGrooveTextStyleNames = {}));

class AccordionComponent {
    constructor() {
        this.id = '';
        this.title = '';
        this.disabled = false;
        this.transparent = false;
        this.label = '';
        this.headerTextBold = false;
        this.chevron = false;
        this.expandedLabel = '';
        this.collapsedLabel = '';
        this.icon = '';
        this.icons = {
            iconAngleDown: 'angle-down',
            iconAngleUp: 'angle-up'
        };
        this.disabledBtnScreenReaderText = '';
        this.buttonId = '';
        this.panelId = '';
        this.expanded = false;
    }
    get classNames() {
        return {
            ['is-transparent']: this.transparent,
            ['is-disabled']: this.disabled,
            ['has-shadow']: this.expanded
        };
    }
    ngOnInit() {
        this.setScreenReaderText();
        this.setIdReferences();
    }
    setExpanded() {
        this.expanded = !this.expanded;
    }
    setScreenReaderText() {
        if (!this.disabled) {
            this.disabledBtnScreenReaderText = '';
            return;
        }
        this.disabledBtnScreenReaderText = this.title.concat(this.label, ', ');
    }
    setIdReferences() {
        this.buttonId = `v-accordion-header-${this.id}`;
        this.panelId = `v-accordion-content-${this.id}`;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: AccordionComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: AccordionComponent, selector: "vpg-accordion", inputs: { id: "id", title: "title", disabled: "disabled", transparent: "transparent", label: "label", headerTextBold: "headerTextBold", chevron: "chevron", expandedLabel: "expandedLabel", collapsedLabel: "collapsedLabel", icon: "icon" }, host: { properties: { "class": "this.classNames" }, classAttribute: "v-accordion" }, ngImport: i0, template: "\n<button\n    class=\"v-accordion-header\"\n    [attr.aria-controls]=\"panelId\"\n    [attr.aria-expanded]=\"expanded\"\n    [attr.aria-label]=\"disabledBtnScreenReaderText\"\n    [disabled]=\"disabled\"\n    [id]=\"buttonId\"\n    (click)=\"setExpanded()\"\n>\n    <vpg-icon-next\n        *ngIf=\"icon\"\n        [icon]=\"icon\"\n        [id]=\"id\"\n    ></vpg-icon-next>\n    <div\n        [id]=\"'accordion-title-' + id\"\n        class=\"v-accordion-header-text\"\n        [ngClass]=\"{ 'is-bold' : headerTextBold }\"\n        [attr.aria-hidden]=\"disabled\"\n    >{{ title }}</div>\n    <div\n        *ngIf=\"label\"\n        [id]=\"'v-accordion-label-' + id\"\n        class=\"v-accordion-label\"\n        [attr.aria-hidden]=\"disabled\"\n    >\n        {{ label }}\n    </div>\n    <vpg-icon-next\n        *ngIf=\"chevron\"\n        [icon]=\"expanded ? icons.iconAngleUp : icons.iconAngleDown\"\n        size=\"2x\"\n        [id]=\"'chevron-icon-' + id\"\n    ></vpg-icon-next>\n    <div\n        *ngIf=\"!chevron\"\n        class=\"v-accordion-state-label\"\n        [id]=\"'v-accordion-state-label-' + id\"\n    >{{ expanded ? expandedLabel : collapsedLabel }}</div>\n</button>\n<section\n    *ngIf=\"expanded\"\n    class=\"v-accordion-content-wrapper\"\n    [attr.aria-labelledby]=\"buttonId\"\n    [id]=\"panelId\"\n>\n    <ng-content></ng-content>\n</section>\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: IconNextComponent, selector: "vpg-icon-next", inputs: ["icon", "size", "prefix", "screenReaderText"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: AccordionComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-accordion', host: {
                        'class': 'v-accordion'
                    }, template: "\n<button\n    class=\"v-accordion-header\"\n    [attr.aria-controls]=\"panelId\"\n    [attr.aria-expanded]=\"expanded\"\n    [attr.aria-label]=\"disabledBtnScreenReaderText\"\n    [disabled]=\"disabled\"\n    [id]=\"buttonId\"\n    (click)=\"setExpanded()\"\n>\n    <vpg-icon-next\n        *ngIf=\"icon\"\n        [icon]=\"icon\"\n        [id]=\"id\"\n    ></vpg-icon-next>\n    <div\n        [id]=\"'accordion-title-' + id\"\n        class=\"v-accordion-header-text\"\n        [ngClass]=\"{ 'is-bold' : headerTextBold }\"\n        [attr.aria-hidden]=\"disabled\"\n    >{{ title }}</div>\n    <div\n        *ngIf=\"label\"\n        [id]=\"'v-accordion-label-' + id\"\n        class=\"v-accordion-label\"\n        [attr.aria-hidden]=\"disabled\"\n    >\n        {{ label }}\n    </div>\n    <vpg-icon-next\n        *ngIf=\"chevron\"\n        [icon]=\"expanded ? icons.iconAngleUp : icons.iconAngleDown\"\n        size=\"2x\"\n        [id]=\"'chevron-icon-' + id\"\n    ></vpg-icon-next>\n    <div\n        *ngIf=\"!chevron\"\n        class=\"v-accordion-state-label\"\n        [id]=\"'v-accordion-state-label-' + id\"\n    >{{ expanded ? expandedLabel : collapsedLabel }}</div>\n</button>\n<section\n    *ngIf=\"expanded\"\n    class=\"v-accordion-content-wrapper\"\n    [attr.aria-labelledby]=\"buttonId\"\n    [id]=\"panelId\"\n>\n    <ng-content></ng-content>\n</section>\n" }]
        }], propDecorators: { id: [{
                type: Input
            }], title: [{
                type: Input
            }], disabled: [{
                type: Input
            }], transparent: [{
                type: Input
            }], label: [{
                type: Input
            }], headerTextBold: [{
                type: Input
            }], chevron: [{
                type: Input
            }], expandedLabel: [{
                type: Input
            }], collapsedLabel: [{
                type: Input
            }], icon: [{
                type: Input
            }], classNames: [{
                type: HostBinding,
                args: ['class']
            }] } });

class AccordionItemContentComponent {
    constructor() {
        this.id = '';
        this.content = '';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: AccordionItemContentComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: AccordionItemContentComponent, selector: "vpg-accordion-item-content", inputs: { id: "id", content: "content" }, host: { classAttribute: "v-accordion-content" }, ngImport: i0, template: "<p\n    class=\"v-accordion-item-content-text mb-md mt-none\"\n    [innerHTML]=\"content\"\n>\n</p>\n<ng-content></ng-content>\n" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: AccordionItemContentComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-accordion-item-content', host: {
                        'class': 'v-accordion-content'
                    }, template: "<p\n    class=\"v-accordion-item-content-text mb-md mt-none\"\n    [innerHTML]=\"content\"\n>\n</p>\n<ng-content></ng-content>\n" }]
        }], propDecorators: { id: [{
                type: Input
            }], content: [{
                type: Input
            }] } });

class IconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: IconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.9", ngImport: i0, type: IconModule, declarations: [IconNextComponent], imports: [CommonModule], exports: [IconNextComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: IconModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: IconModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule
                    ],
                    declarations: [
                        IconNextComponent
                    ],
                    exports: [
                        IconNextComponent
                    ]
                }]
        }] });

class LinkModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: LinkModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.9", ngImport: i0, type: LinkModule, declarations: [LinkDirective], imports: [CommonModule], exports: [LinkDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: LinkModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: LinkModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule
                    ],
                    declarations: [
                        LinkDirective
                    ],
                    exports: [
                        LinkDirective
                    ]
                }]
        }] });

class AccordionModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: AccordionModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.9", ngImport: i0, type: AccordionModule, declarations: [AccordionComponent,
            AccordionItemContentComponent], imports: [CommonModule,
            IconModule,
            LinkModule], exports: [AccordionComponent,
            AccordionItemContentComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: AccordionModule, imports: [CommonModule,
            IconModule,
            LinkModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: AccordionModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        IconModule,
                        LinkModule
                    ],
                    declarations: [
                        AccordionComponent,
                        AccordionItemContentComponent
                    ],
                    exports: [
                        AccordionComponent,
                        AccordionItemContentComponent
                    ]
                }]
        }] });

class BackgroundCircleIconComponent {
    constructor() {
        this.iconSrc = '';
        this.icon = '';
        this.backgroundColor = '';
        this.color = '';
        this.role = 'img';
    }
    get classNames() {
        return {
            ['v-background-circle-icon']: true
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: BackgroundCircleIconComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: BackgroundCircleIconComponent, selector: "vpg-background-circle-icon", inputs: { iconSrc: "iconSrc", icon: "icon", backgroundColor: "backgroundColor", color: "color" }, host: { properties: { "style.background": "this.backgroundColor", "attr.role": "this.role", "class": "this.classNames" } }, ngImport: i0, template: " <vpg-icon-next\n        *ngIf=\"icon\"\n        [style.color]=\"color\"\n        [icon]=\"icon\"\n />\n <img\n        alt=\"\"\n        *ngIf=\"iconSrc\"\n        [src]=\"iconSrc\"\n >\n <ng-content/>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: IconNextComponent, selector: "vpg-icon-next", inputs: ["icon", "size", "prefix", "screenReaderText"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: BackgroundCircleIconComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-background-circle-icon', template: " <vpg-icon-next\n        *ngIf=\"icon\"\n        [style.color]=\"color\"\n        [icon]=\"icon\"\n />\n <img\n        alt=\"\"\n        *ngIf=\"iconSrc\"\n        [src]=\"iconSrc\"\n >\n <ng-content/>\n" }]
        }], propDecorators: { iconSrc: [{
                type: Input
            }], icon: [{
                type: Input
            }], backgroundColor: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['style.background']
            }], color: [{
                type: Input
            }], role: [{
                type: HostBinding,
                args: ['attr.role']
            }], classNames: [{
                type: HostBinding,
                args: ['class']
            }] } });

class BackgroundCircleIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: BackgroundCircleIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.9", ngImport: i0, type: BackgroundCircleIconModule, declarations: [BackgroundCircleIconComponent], imports: [CommonModule,
            IconModule], exports: [BackgroundCircleIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: BackgroundCircleIconModule, imports: [CommonModule,
            IconModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: BackgroundCircleIconModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        IconModule
                    ],
                    declarations: [
                        BackgroundCircleIconComponent
                    ],
                    exports: [
                        BackgroundCircleIconComponent
                    ]
                }]
        }] });

class FormatValuePipe {
    transform(value) {
        if (value < 1 || !value) {
            return '';
        }
        return value >= 100 ? '99+' : value.toString();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: FormatValuePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.3.9", ngImport: i0, type: FormatValuePipe, name: "formatValue" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: FormatValuePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'formatValue'
                }]
        }] });

class BadgeComponent {
    constructor() {
        this.content = 0;
        this.stroke = false;
    }
    get classNames() {
        return {
            ['v-badge']: true,
            ['has-content']: this.content > 0,
            ['has-stroke']: this.stroke
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: BadgeComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: BadgeComponent, selector: "vpg-badge", inputs: { content: "content", stroke: "stroke" }, host: { properties: { "attr.aria-hidden": "true", "class": "this.classNames" } }, ngImport: i0, template: '{{ content | formatValue }}', isInline: true, dependencies: [{ kind: "pipe", type: FormatValuePipe, name: "formatValue" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: BadgeComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vpg-badge',
                    template: '{{ content | formatValue }}',
                    host: {
                        '[attr.aria-hidden]': 'true'
                    }
                }]
        }], propDecorators: { content: [{
                type: Input
            }], stroke: [{
                type: Input
            }], classNames: [{
                type: HostBinding,
                args: ['class']
            }] } });

class BadgeModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: BadgeModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.9", ngImport: i0, type: BadgeModule, declarations: [BadgeComponent, FormatValuePipe], imports: [CommonModule], exports: [BadgeComponent, FormatValuePipe] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: BadgeModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: BadgeModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [BadgeComponent, FormatValuePipe],
                    imports: [CommonModule],
                    exports: [BadgeComponent, FormatValuePipe]
                }]
        }] });

class BreadcrumbsComponent {
    constructor() {
        this.id = '';
        this.screenReaderText = '';
        this.items = [];
        this.itemClick = new EventEmitter();
    }
    onClick(item) {
        this.itemClick.emit(item);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: BreadcrumbsComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: BreadcrumbsComponent, selector: "vpg-breadcrumbs", inputs: { id: "id", screenReaderText: "screenReaderText", items: "items" }, outputs: { itemClick: "itemClick" }, host: { properties: { "id": "id" }, classAttribute: "v-breadcrumbs" }, ngImport: i0, template: "<nav [attr.aria-label]=\"screenReaderText\">\n    <ol class=\"v-breadcrumbs-list\">\n        <li class=\"v-breadcrumbs-list-item\" *ngFor=\"let item of items; let isFirst = first; let isLast = last; let i = index;\">\n            <vpg-icon-next *ngIf=\"!isFirst\" icon=\"angle-right\" size=\"lg\" />\n            <a\n                *ngIf=\"!isLast\"\n                vpgLink\n                id=\"{{id}}-link-{{i}}\"\n                href=\"{{ item.url }}\"\n                (click)=\"onClick(item)\"\n            >\n                {{ item.label }}\n            </a>\n            <span\n                *ngIf=\"isLast\"\n                id=\"{{id}}-text\"\n                class=\"v-breadcrumbs-text body-regular inline-flex\"\n                aria-current=\"page\"\n            >\n                {{ item.label }}\n            </span>\n        </li>\n    </ol>\n</nav>", dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: LinkDirective, selector: "a[vpg-link], a[vpgLink], button[vpg-link], button[vpgLink]", inputs: ["size", "align", "bold"] }, { kind: "component", type: IconNextComponent, selector: "vpg-icon-next", inputs: ["icon", "size", "prefix", "screenReaderText"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: BreadcrumbsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-breadcrumbs', host: {
                        '[id]': 'id',
                        'class': 'v-breadcrumbs'
                    }, template: "<nav [attr.aria-label]=\"screenReaderText\">\n    <ol class=\"v-breadcrumbs-list\">\n        <li class=\"v-breadcrumbs-list-item\" *ngFor=\"let item of items; let isFirst = first; let isLast = last; let i = index;\">\n            <vpg-icon-next *ngIf=\"!isFirst\" icon=\"angle-right\" size=\"lg\" />\n            <a\n                *ngIf=\"!isLast\"\n                vpgLink\n                id=\"{{id}}-link-{{i}}\"\n                href=\"{{ item.url }}\"\n                (click)=\"onClick(item)\"\n            >\n                {{ item.label }}\n            </a>\n            <span\n                *ngIf=\"isLast\"\n                id=\"{{id}}-text\"\n                class=\"v-breadcrumbs-text body-regular inline-flex\"\n                aria-current=\"page\"\n            >\n                {{ item.label }}\n            </span>\n        </li>\n    </ol>\n</nav>" }]
        }], propDecorators: { id: [{
                type: Input
            }], screenReaderText: [{
                type: Input
            }], items: [{
                type: Input
            }], itemClick: [{
                type: Output
            }] } });

class BreadcrumbsModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: BreadcrumbsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.9", ngImport: i0, type: BreadcrumbsModule, declarations: [BreadcrumbsComponent], imports: [CommonModule, LinkModule, IconModule], exports: [BreadcrumbsComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: BreadcrumbsModule, imports: [CommonModule, LinkModule, IconModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: BreadcrumbsModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, LinkModule, IconModule],
                    declarations: [BreadcrumbsComponent],
                    exports: [BreadcrumbsComponent]
                }]
        }] });

class ButtonBaseDirective {
    constructor() {
        this.variant = 'primary';
        this.size = 'large';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ButtonBaseDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.9", type: ButtonBaseDirective, selector: "[vpg-button-base]", inputs: { variant: "variant", size: "size" }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ButtonBaseDirective, decorators: [{
            type: Directive,
            args: [{ selector: '[vpg-button-base]' }]
        }], propDecorators: { variant: [{
                type: Input
            }], size: [{
                type: Input
            }] } });

class ButtonIconDirective extends ButtonBaseDirective {
    constructor() {
        super();
        this.completed = false;
    }
    get classNames() {
        return {
            ['v-icon-button']: true,
            ['is-completed']: this.completed,
            [`is-${this.variant}`]: true,
            [`is-${this.size}`]: true
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ButtonIconDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.9", type: ButtonIconDirective, selector: "[vpg-button-icon], [vpgButtonIcon]", inputs: { completed: "completed" }, host: { properties: { "class": "this.classNames" } }, usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ButtonIconDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[vpg-button-icon], [vpgButtonIcon]'
                }]
        }], ctorParameters: () => [], propDecorators: { completed: [{
                type: Input
            }], classNames: [{
                type: HostBinding,
                args: ['class']
            }] } });

class ButtonDirective extends ButtonBaseDirective {
    constructor() {
        super();
        this.align = 'center';
        this.block = false;
    }
    get classNames() {
        return {
            ['v-button']: true,
            ['is-block']: this.block,
            [`is-${this.variant}`]: true,
            [`is-${this.size}`]: true,
            [`is-${this.align}`]: true
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ButtonDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.9", type: ButtonDirective, selector: "[vpg-button], [vpgButton]", inputs: { align: "align", block: "block" }, host: { properties: { "class": "this.classNames" } }, usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ButtonDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[vpg-button], [vpgButton]'
                }]
        }], ctorParameters: () => [], propDecorators: { align: [{
                type: Input
            }], block: [{
                type: Input
            }], classNames: [{
                type: HostBinding,
                args: ['class']
            }] } });

class ButtonModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ButtonModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.9", ngImport: i0, type: ButtonModule, declarations: [ButtonBaseDirective,
            ButtonDirective,
            ButtonIconDirective], imports: [CommonModule], exports: [ButtonDirective,
            ButtonIconDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ButtonModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ButtonModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule
                    ],
                    declarations: [
                        ButtonBaseDirective,
                        ButtonDirective,
                        ButtonIconDirective
                    ],
                    exports: [
                        ButtonDirective,
                        ButtonIconDirective
                    ]
                }]
        }] });

class CalendarService {
    constructor() {
        this.DAYS_IN_WEEK = 7;
        this.FIRST_DAY_IN_MONTH = 1;
        this.JANUARY = 0;
        this.DECEMBER = 11;
        this.today = new Date();
    }
    getMonthData(date) {
        const year = date.getFullYear();
        const month = date.getMonth();
        const days = [];
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        for (let i = 0; i < firstDay.getDay(); i++) {
            days.push(null);
        }
        for (let day = 1; day <= lastDay.getDate(); day++) {
            days.push(new Date(year, month, day));
        }
        return this.getWeeks(days);
    }
    getWeekDays() {
        return [
            { text: 'Sun', ariaLabel: 'Sunday' },
            { text: 'Mon', ariaLabel: 'Monday' },
            { text: 'Tue', ariaLabel: 'Tuesday' },
            { text: 'Wed', ariaLabel: 'Wednesday' },
            { text: 'Thu', ariaLabel: 'Thursday' },
            { text: 'Fri', ariaLabel: 'Friday' },
            { text: 'Sat', ariaLabel: 'Saturday' }
        ];
    }
    getNextMonth(date) {
        let year = date.getFullYear();
        let month = date.getMonth() + 1;
        if (month > this.DECEMBER) {
            month = this.JANUARY;
            year += 1;
        }
        return new Date(year, month, this.FIRST_DAY_IN_MONTH);
    }
    getPrevMonth(date) {
        let year = date.getFullYear();
        let month = date.getMonth() - 1;
        if (month < this.JANUARY) {
            month = this.DECEMBER;
            year -= 1;
        }
        return new Date(year, month, this.FIRST_DAY_IN_MONTH);
    }
    isSelected(day, selectedDate) {
        return this.isSame(day, selectedDate);
    }
    isToday(day) {
        const today = new Date();
        return this.isSame(day, today);
    }
    isFirstInMonth(currentDate, day) {
        return new Date(currentDate.getFullYear(), currentDate.getMonth(), day.getDate()).getDate() === 1;
    }
    isLastInMonth(currentDate, day) {
        return new Date(currentDate.getFullYear(), currentDate.getMonth(), day.getDate()).getDate() === new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
    }
    isPast(date) {
        const today = new Date();
        const normalizedDate = this.normalizeDate(date);
        const normalizedToday = this.normalizeDate(today);
        return normalizedDate < normalizedToday;
    }
    isBefore(dateA, dateB) {
        return dateA < dateB;
    }
    isAfter(dateA, dateB) {
        return dateA > dateB;
    }
    isInDateRange(date, startDate, endDate) {
        if (!startDate || !endDate) {
            return false;
        }
        const normalizedDate = this.normalizeDate(date);
        const normalizedStart = this.normalizeDate(startDate);
        const normalizedEnd = this.normalizeDate(endDate);
        return normalizedDate > normalizedStart && normalizedDate < normalizedEnd;
    }
    isSame(dateA, dateB) {
        return (dateA.getDate() === dateB.getDate() &&
            dateA.getMonth() === dateB.getMonth() &&
            dateA.getFullYear() === dateB.getFullYear());
    }
    normalizeDate(date) {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    }
    getWeeks(days) {
        const weeks = [];
        let week = [];
        for (let day = 0; day < days.length; day++) {
            week.push(days[day]);
            if (week.length === this.DAYS_IN_WEEK || day === days.length - 1) {
                while (week.length < this.DAYS_IN_WEEK) {
                    week.push(null);
                }
                weeks.push(week);
                week = [];
            }
        }
        return weeks;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CalendarService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CalendarService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CalendarService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

const CALENDAR_COMPONENT = new InjectionToken('CalendarComponent');

var SelectionState;
(function (SelectionState) {
    SelectionState["START"] = "start";
    SelectionState["END"] = "end";
})(SelectionState || (SelectionState = {}));

class CalendarNavigationDirective {
    constructor(el, ngZone, calendar) {
        this.el = el;
        this.ngZone = ngZone;
        this.calendar = calendar;
        this.weeks = [];
        this.focusedDay = null;
    }
    ngAfterViewInit() {
        this.registerKeydownListener();
        this.setInitialFocus();
        this.calendar.currentDateChange.subscribe(() => {
            setTimeout(() => {
                this.setInitialFocus();
            });
        });
    }
    onKeyDown(event) {
        if (![
            Keys.ArrowLeft,
            Keys.ArrowRight,
            Keys.ArrowUp,
            Keys.ArrowDown,
            Keys.Home,
            Keys.End,
            Keys.PageUp,
            Keys.PageDown
        ].includes(event.key)) {
            return;
        }
        event.preventDefault();
        if (!this.focusedDay) {
            this.ngZone.run(() => {
                this.focusedDay = this.findFirstFocusableDay();
            });
            if (this.focusedDay) {
                this.setFocus(this.focusedDay);
            }
            return;
        }
        const weekIndex = parseInt(this.focusedDay?.getAttribute('data-week-index') ?? '0', 10);
        const dayIndex = parseInt(this.focusedDay?.getAttribute('data-day-index') ?? '0', 10);
        const dayNumber = parseInt(this.focusedDay?.getAttribute('data-day-number') ?? '0', 10);
        const isFirstDayInMonth = this.focusedDay?.getAttribute('data-first-in-month') === 'true';
        const isLastDayInMonth = this.focusedDay?.getAttribute('data-last-in-month') === 'true';
        switch (event.key) {
            case Keys.ArrowLeft:
                if (this.isRtl()) {
                    this.navigateRight(weekIndex, dayIndex, isLastDayInMonth);
                }
                else {
                    this.navigateLeft(weekIndex, dayIndex, isFirstDayInMonth);
                }
                break;
            case Keys.ArrowRight:
                if (this.isRtl()) {
                    this.navigateLeft(weekIndex, dayIndex, isFirstDayInMonth);
                }
                else {
                    this.navigateRight(weekIndex, dayIndex, isLastDayInMonth);
                }
                break;
            case Keys.ArrowUp:
                this.navigateUp(weekIndex, dayIndex);
                break;
            case Keys.ArrowDown:
                this.navigateDown(weekIndex, dayIndex);
                break;
            case Keys.Home:
                this.navigateToFirstDayOfMonth();
                break;
            case Keys.End:
                this.navigateToLastDayOfMonth();
                break;
            case Keys.PageUp:
                this.goToPreviousMonth(dayNumber);
                break;
            case Keys.PageDown:
                this.goToNextMonth(dayNumber);
                break;
        }
    }
    isRtl() {
        const direction = getComputedStyle(this.el.nativeElement).direction ||
            getComputedStyle(document.documentElement).direction;
        return direction === 'rtl';
    }
    registerKeydownListener() {
        this.ngZone.runOutsideAngular(() => {
            this.el.nativeElement.addEventListener('keydown', this.onKeyDown.bind(this));
        });
    }
    setInitialFocus() {
        this.focusedDay = this.findFirstFocusableDay();
    }
    navigateLeft(weekIndex, dayIndex, isFirstDayInMonth) {
        if (isFirstDayInMonth) {
            this.goToPreviousMonth();
            return;
        }
        if (dayIndex > 0) {
            this.focusDay(weekIndex, dayIndex - 1);
        }
        else if (weekIndex > 0) {
            this.focusDay(weekIndex - 1, 6);
        }
    }
    navigateRight(weekIndex, dayIndex, isLastDayInMonth) {
        if (isLastDayInMonth) {
            this.goToNextMonth();
            return;
        }
        if (dayIndex < 6) {
            this.focusDay(weekIndex, dayIndex + 1);
        }
        else if (weekIndex < this.weeks.length - 1) {
            this.focusDay(weekIndex + 1, 0);
        }
    }
    navigateUp(weekIndex, dayIndex) {
        if (weekIndex > 0) {
            this.focusDay(weekIndex - 1, dayIndex);
        }
        else {
            this.goToPreviousMonth();
        }
    }
    navigateDown(weekIndex, dayIndex) {
        if (weekIndex < this.weeks.length - 1) {
            this.focusDay(weekIndex + 1, dayIndex);
        }
        else {
            this.goToNextMonth();
        }
    }
    navigateToFirstDayOfMonth() {
        for (let i = 0; i < this.weeks[0].length; i++) {
            if (this.weeks[0][i]) {
                this.focusDay(0, i);
                return;
            }
        }
    }
    navigateToLastDayOfMonth() {
        const lastWeekIndex = this.weeks.length - 1;
        for (let i = this.weeks[lastWeekIndex].length - 1; i >= 0; i--) {
            if (this.weeks[lastWeekIndex][i]) {
                this.focusDay(lastWeekIndex, i);
                return;
            }
        }
    }
    focusDay(weekIndex, dayIndex) {
        const dayButton = this.findDayButton(weekIndex, dayIndex);
        if (dayButton) {
            this.setFocus(dayButton);
        }
    }
    findDayButton(weekIndex, dayIndex) {
        return this.el.nativeElement.querySelector(`.v-calendar-day[data-week-index="${weekIndex}"][data-day-index="${dayIndex}"]:not([disabled])`);
    }
    findDayByNumber(dayNumber) {
        return this.el.nativeElement.querySelector(`.v-calendar-day[data-day-number="${dayNumber}"]`);
    }
    findFirstFocusableDay() {
        const today = this.el.nativeElement
            .querySelector('.v-calendar-day.is-today:not([disabled])');
        if (today) {
            return today;
        }
        const firstDay = this.el.nativeElement
            .querySelector('.v-calendar-day[tabindex="0"]:not([disabled])');
        if (firstDay) {
            return firstDay;
        }
        return this.el.nativeElement.querySelector('.v-calendar-day:not([disabled])');
    }
    setFocus(element) {
        const days = this.el.nativeElement.querySelectorAll('.v-calendar-day:not([disabled])');
        days.forEach((day) => {
            day.classList.remove('is-highlighted');
            day.setAttribute('tabindex', '-1');
            day.setAttribute('aria-selected', 'false');
        });
        element.classList.add('is-highlighted');
        element.setAttribute('tabindex', '0');
        element.setAttribute('aria-selected', 'true');
        element.focus();
        this.ngZone.run(() => {
            this.focusedDay = element;
        });
    }
    goToPreviousMonth(dayNumber) {
        this.ngZone.run(() => {
            this.calendar.prevMonth();
            this.calendar.refresh();
        });
        const day = dayNumber ? this.findDayByNumber(dayNumber) : null;
        if (day) {
            this.setFocus(day);
        }
        else {
            this.navigateToLastDayOfMonth();
        }
    }
    goToNextMonth(dayNumber) {
        this.ngZone.run(() => {
            this.calendar.nextMonth();
            this.calendar.refresh();
        });
        const day = dayNumber ? this.findDayByNumber(dayNumber) : null;
        if (day) {
            this.setFocus(day);
        }
        else {
            this.navigateToFirstDayOfMonth();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CalendarNavigationDirective, deps: [{ token: i0.ElementRef }, { token: i0.NgZone }, { token: CALENDAR_COMPONENT }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.9", type: CalendarNavigationDirective, selector: "[vpgCalendarNavigation]", inputs: { weeks: "weeks" }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CalendarNavigationDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[vpgCalendarNavigation]'
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.NgZone }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [CALENDAR_COMPONENT]
                }] }], propDecorators: { weeks: [{
                type: Input
            }] } });

const VPG_TRANSLATE_CONFIG = new InjectionToken('VPG_TRANSLATE_CONFIG');
class VpgTranslateModule {
    static forRoot(config) {
        return {
            ngModule: VpgTranslateModule,
            providers: [
                {
                    provide: VPG_TRANSLATE_CONFIG,
                    useValue: config || {}
                },
                VpgTranslateService
            ]
        };
    }
    static forChild() {
        return {
            ngModule: VpgTranslateModule,
            providers: []
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: VpgTranslateModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.9", ngImport: i0, type: VpgTranslateModule, imports: [CommonModule,
            TranslateModule], exports: [TranslateModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: VpgTranslateModule, imports: [CommonModule,
            TranslateModule, TranslateModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: VpgTranslateModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        TranslateModule
                    ],
                    exports: [
                        TranslateModule
                    ]
                }]
        }] });

class VpgTranslateService {
    constructor(translateService, config) {
        this.translateService = translateService;
        this.config = config;
        this.prefix = '';
        this.prefix = this.config?.prefix ? `${this.config.prefix}.` : '';
    }
    get currentLang() {
        if (!this.translateService) {
            return null;
        }
        return this.translateService.currentLang || this.translateService.defaultLang;
    }
    translate(key, params) {
        if (!this.translateService) {
            return key;
        }
        const fullKey = this.getFullKey(key);
        return this.translateService.instant(fullKey, params);
    }
    get(key, params) {
        if (!this.translateService) {
            return null;
        }
        const fullKey = this.getFullKey(key);
        return this.translateService.get(fullKey, params);
    }
    getFullKey(key) {
        return `${this.prefix}${key}`;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: VpgTranslateService, deps: [{ token: i1$2.TranslateService, optional: true }, { token: VPG_TRANSLATE_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: VpgTranslateService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: VpgTranslateService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: i1$2.TranslateService, decorators: [{
                    type: Optional
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [VPG_TRANSLATE_CONFIG]
                }] }] });

class CalendarRangeAriaLabelPipe {
    constructor(calendar, translateService) {
        this.calendar = calendar;
        this.translateService = translateService;
        this.DATE_FORMAT = 'MMMM dd, yyyy';
        this.TRANSLATIONS = {
            TODAY: 'Today',
            SELECTED: 'Selected',
            SELECTED_RANGE: 'SelectedRange'
        };
        this.DAYS = {
            0: 'Sunday',
            1: 'Monday',
            2: 'Tuesday',
            3: 'Wednesday',
            4: 'Thursday',
            5: 'Friday',
            6: 'Saturday'
        };
        this.datePipe = new DatePipe(this.translateService.currentLang ?? 'en');
    }
    transform(date, dayIndex) {
        const day = this.calendar.weekDays[dayIndex].ariaLabel;
        const strings = [];
        if (this.calendar.isRangeSelected() && (this.calendar.isRangeStart(date) || this.calendar.isRangeEnd(date))) {
            strings.push(this.translate(this.TRANSLATIONS.SELECTED_RANGE, {
                startDay: this.translate(this.DAYS[this.calendar.startDate?.getDay() ?? 0]),
                startDate: this.transformDate(this.calendar.startDate),
                endDay: this.translate(this.DAYS[this.calendar.endDate?.getDay() ?? 0]),
                endDate: this.transformDate(this.calendar.endDate)
            }));
        }
        if (this.calendar.isToday(date)) {
            strings.push(this.translate(this.TRANSLATIONS.TODAY));
        }
        strings.push(`${day}, ${this.transformDate(date)}`);
        if (this.calendar.isSelected(date)) {
            strings.push(this.TRANSLATIONS.SELECTED);
        }
        return strings.join(', ');
    }
    transformDate(date) {
        return this.datePipe.transform(date, this.DATE_FORMAT);
    }
    translate(key, params) {
        return this.translateService.translate(key, params);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CalendarRangeAriaLabelPipe, deps: [{ token: CALENDAR_COMPONENT }, { token: VpgTranslateService }], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.3.9", ngImport: i0, type: CalendarRangeAriaLabelPipe, name: "calendarRangeAriaLabel", pure: false }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CalendarRangeAriaLabelPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'calendarRangeAriaLabel',
                    pure: false
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [CALENDAR_COMPONENT]
                }] }, { type: VpgTranslateService }] });

function transformDate(date) {
    if (date === null) {
        return date;
    }
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0);
}
class CalendarComponent {
    constructor(calendarService, detector) {
        this.calendarService = calendarService;
        this.detector = detector;
        this.selectedDate = null;
        this.selectedDateChange = new EventEmitter();
        this.currentDate = new Date();
        this.currentDateChange = new EventEmitter();
        this.clearButton = false;
        this.rangeMode = false;
        this.startDate = null;
        this.startDateChange = new EventEmitter();
        this.endDate = null;
        this.endDateChange = new EventEmitter();
        this.minDate = null;
        this.maxDate = null;
        this.dateFormat = 'mm-dd-yyyy';
        this.ariaLabel = '';
        this.id = '';
        this.className = 'v-calendar';
        this.selectionState = SelectionState.START;
        this.weekDays = [];
        this.weeks = [];
        this.hoveredDate = null;
        this.TRANSLATIONS = {
            'PREVIOUS_MONTH': 'Previous month',
            'NEXT_MONTH': 'Next month'
        };
    }
    ngOnInit() {
        this.currentDate = this.selectedDate ?? this.currentDate ?? this.startDate;
        this.weekDays = this.calendarService.getWeekDays();
        this.generateCalendar(this.currentDate);
    }
    days() {
        return this.dayButtons
            .toArray()
            .map((button) => button.nativeElement);
    }
    refresh() {
        this.detector.detectChanges();
    }
    generateCalendar(date) {
        this.weeks = this.calendarService.getMonthData(date);
        this.updateCurrentDate(date);
    }
    updateCurrentDate(date) {
        this.currentDate = date;
        this.currentDateChange.emit(date);
    }
    prevMonth() {
        const date = this.calendarService.getPrevMonth(this.currentDate);
        this.generateCalendar(date);
    }
    nextMonth() {
        const date = this.calendarService.getNextMonth(this.currentDate);
        this.generateCalendar(date);
    }
    selectDate(day) {
        if (this.rangeMode) {
            this.handleDateRangeSelection(day);
        }
        else {
            this.handleSingleDaySelection(day);
        }
    }
    isDisabled(day) {
        if (this.minDate && this.calendarService.isBefore(day, this.minDate)) {
            return true;
        }
        if (this.maxDate && this.calendarService.isAfter(day, this.maxDate)) {
            return true;
        }
        return false;
    }
    isInRange(day) {
        return (this.startDate !== null && this.endDate !== null) &&
            day >= this.startDate && day <= this.endDate;
    }
    isRangeStart(day) {
        return this.startDate !== null &&
            this.calendarService.isSelected(day, this.startDate);
    }
    isRangeEnd(day) {
        return this.endDate !== null &&
            this.calendarService.isSelected(day, this.endDate);
    }
    isRangeSelected() {
        return this.startDate !== null && this.endDate !== null;
    }
    isSelected(day) {
        if (this.rangeMode) {
            return this.isRangeStart(day) || this.isRangeEnd(day);
        }
        return this.selectedDate !== null &&
            this.calendarService.isSelected(day, this.selectedDate);
    }
    isToday(day) {
        return this.calendarService.isToday(day);
    }
    isPast(day) {
        return this.calendarService.isPast(day);
    }
    isFirstInMonth(day) {
        return this.calendarService.isFirstInMonth(this.currentDate, day);
    }
    isLastInMonth(day) {
        return this.calendarService.isLastInMonth(this.currentDate, day);
    }
    clearDate() {
        if (this.rangeMode) {
            this.startDate = null;
            this.startDateChange.emit(null);
            this.endDate = null;
            this.endDateChange.emit(null);
            this.selectionState = SelectionState.START;
        }
        else {
            this.selectedDate = null;
            this.selectedDateChange.emit(null);
        }
    }
    isInHoverRange(day) {
        if (!this.rangeMode || !this.startDate || !this.hoveredDate) {
            return false;
        }
        if (this.hoveredDate > this.startDate) {
            return day > this.startDate && day <= this.hoveredDate;
        }
        return day < this.startDate && day >= this.hoveredDate;
    }
    isPotentialEndDate(day) {
        if (!this.rangeMode || !this.startDate || this.endDate || !this.hoveredDate) {
            return false;
        }
        if (this.calendarService.isSelected(day, this.startDate)) {
            return false;
        }
        return this.calendarService.isSelected(day, this.hoveredDate);
    }
    handleSingleDaySelection(day) {
        this.selectedDate = day;
        this.selectedDateChange.emit(this.selectedDate);
    }
    handleDateRangeSelection(day) {
        if (this.selectionState === SelectionState.START || (this.startDate && this.endDate)) {
            this.startDate = day;
            this.endDate = null;
            this.startDateChange.emit(this.startDate);
            this.selectionState = SelectionState.END;
        }
        else {
            if (this.startDate && this.calendarService.isSelected(day, this.startDate)) {
                return;
            }
            if (this.startDate && day < this.startDate) {
                this.endDate = this.startDate;
                this.startDate = day;
            }
            else {
                this.endDate = day;
            }
            this.startDateChange.emit(this.startDate);
            this.endDateChange.emit(this.endDate);
            this.selectionState = SelectionState.START;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CalendarComponent, deps: [{ token: CalendarService }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "17.3.9", type: CalendarComponent, selector: "vpg-calendar", inputs: { selectedDate: "selectedDate", currentDate: "currentDate", clearButton: "clearButton", rangeMode: "rangeMode", startDate: "startDate", endDate: "endDate", minDate: ["minDate", "minDate", transformDate], maxDate: ["maxDate", "maxDate", transformDate], dateFormat: "dateFormat", ariaLabel: "ariaLabel", id: "id" }, outputs: { selectedDateChange: "selectedDateChange", currentDateChange: "currentDateChange", startDateChange: "startDateChange", endDateChange: "endDateChange" }, host: { properties: { "id": "this.id", "class": "this.className" } }, providers: [
            {
                provide: CALENDAR_COMPONENT,
                useExisting: CalendarComponent
            }
        ], viewQueries: [{ propertyName: "dayButtons", predicate: ["dayButton"], descendants: true }], ngImport: i0, template: "<header class=\"v-calendar-header\">\n    <button\n        class=\"v-calendar-nav-button\"\n        [attr.aria-label]=\"TRANSLATIONS.PREVIOUS_MONTH | translate\"\n        id=\"{{id}}-prev-month\"\n        (click)=\"prevMonth()\"\n    >\n        <vpg-icon-next icon=\"chevron-left\" />\n    </button>\n    <h2\n        class=\"v-calendar-title m-none\"\n        id=\"{{id}}-current-date\"\n        aria-live=\"polite\"\n    >\n        {{ currentDate | date: 'MMMM yyyy' }}\n    </h2>\n    <button\n        class=\"v-calendar-nav-button\"\n        [attr.aria-label]=\"TRANSLATIONS.NEXT_MONTH\"\n        id=\"{{id}}-next-month\"\n        (click)=\"nextMonth()\"\n    >\n        <vpg-icon-next icon=\"chevron-right\" />\n    </button>\n</header>\n<table\n    role=\"grid\"\n    vpgCalendarNavigation\n    [weeks]=\"weeks\"\n    attr.aria-labelledby=\"{{id}}-current-date\"\n    [attr.multiselectable]=\"rangeMode\"\n    class=\"v-calendar-table\"\n    id=\"{{id}}-table\"\n>\n    <thead aria-hidden=\"true\">\n        <tr class=\"v-calendar-week\">\n            @for(day of weekDays; track day) {\n                <th\n                    class=\"v-calendar-week-day\"\n                    id=\"{{id}}-column-{{day.text | lowercase}}\"\n                >\n                    {{ day.text }}\n                </th>\n            }\n        </tr>\n    </thead>\n    <tbody>\n        @for(week of weeks; track week; let weekIndex = $index) {\n            <tr role=\"row\" class=\"v-calendar-week\">\n                @for(day of week; track day; let dayIndex = $index) {\n                    <td role=\"gridcell\" class=\"v-calendar-cell\">\n                        @if(day) {\n                            <button\n                                #dayButton\n                                class=\"v-calendar-day body-small\"\n                                id=\"{{id}}-day-{{day | date: 'yyyy-MM-dd'}}\"\n                                [disabled]=\"isDisabled(day)\"\n                                [class.is-disabled]=\"isDisabled(day)\"\n                                [class.is-today]=\"isToday(day)\"\n                                [class.is-selected]=\"isSelected(day)\"\n                                [class.is-range-start]=\"rangeMode && isRangeStart(day)\"\n                                [class.is-range-end]=\"rangeMode && isRangeEnd(day)\"\n                                [class.is-in-range]=\"rangeMode && isInRange(day)\"\n                                [attr.aria-label]=\"day | calendarRangeAriaLabel : dayIndex\"\n                                [attr.aria-selected]=\"isSelected(day) || isRangeStart(day) || isRangeEnd(day) || isInRange(day)\"\n                                [attr.data-week-index]=\"weekIndex\"\n                                [attr.data-day-index]=\"dayIndex\"\n                                [attr.data-day-number]=\"day.getDate()\"\n                                [attr.data-first-in-month]=\"isFirstInMonth(day)\"\n                                [attr.data-last-in-month]=\"isLastInMonth(day)\"\n                                [tabIndex]=\"isSelected(day) || isToday(day) || isFirstInMonth(day) ? 0 : -1\"\n                                (click)=\"selectDate(day)\"\n                            >\n                                <span class=\"v-calendar-day-text\">\n                                    {{ day | date: 'd' }}\n                                </span>\n                            </button>\n                        }\n                    </td>\n                }\n            </tr>\n        }\n    </tbody>\n</table>\n<footer>\n    @if(clearButton) {\n        <button\n            vpg-link\n            size=\"small\"\n            class=\"v-calendar-clear-button\"\n            id=\"{{id}}-clear-date\"\n            (click)=\"clearDate()\"\n        >\n            Clear\n        </button>\n    }\n</footer>\n", dependencies: [{ kind: "component", type: IconNextComponent, selector: "vpg-icon-next", inputs: ["icon", "size", "prefix", "screenReaderText"] }, { kind: "directive", type: LinkDirective, selector: "a[vpg-link], a[vpgLink], button[vpg-link], button[vpgLink]", inputs: ["size", "align", "bold"] }, { kind: "directive", type: CalendarNavigationDirective, selector: "[vpgCalendarNavigation]", inputs: ["weeks"] }, { kind: "pipe", type: i1.LowerCasePipe, name: "lowercase" }, { kind: "pipe", type: i1.DatePipe, name: "date" }, { kind: "pipe", type: i1$2.TranslatePipe, name: "translate" }, { kind: "pipe", type: CalendarRangeAriaLabelPipe, name: "calendarRangeAriaLabel" }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CalendarComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-calendar', changeDetection: ChangeDetectionStrategy.OnPush, providers: [
                        {
                            provide: CALENDAR_COMPONENT,
                            useExisting: CalendarComponent
                        }
                    ], template: "<header class=\"v-calendar-header\">\n    <button\n        class=\"v-calendar-nav-button\"\n        [attr.aria-label]=\"TRANSLATIONS.PREVIOUS_MONTH | translate\"\n        id=\"{{id}}-prev-month\"\n        (click)=\"prevMonth()\"\n    >\n        <vpg-icon-next icon=\"chevron-left\" />\n    </button>\n    <h2\n        class=\"v-calendar-title m-none\"\n        id=\"{{id}}-current-date\"\n        aria-live=\"polite\"\n    >\n        {{ currentDate | date: 'MMMM yyyy' }}\n    </h2>\n    <button\n        class=\"v-calendar-nav-button\"\n        [attr.aria-label]=\"TRANSLATIONS.NEXT_MONTH\"\n        id=\"{{id}}-next-month\"\n        (click)=\"nextMonth()\"\n    >\n        <vpg-icon-next icon=\"chevron-right\" />\n    </button>\n</header>\n<table\n    role=\"grid\"\n    vpgCalendarNavigation\n    [weeks]=\"weeks\"\n    attr.aria-labelledby=\"{{id}}-current-date\"\n    [attr.multiselectable]=\"rangeMode\"\n    class=\"v-calendar-table\"\n    id=\"{{id}}-table\"\n>\n    <thead aria-hidden=\"true\">\n        <tr class=\"v-calendar-week\">\n            @for(day of weekDays; track day) {\n                <th\n                    class=\"v-calendar-week-day\"\n                    id=\"{{id}}-column-{{day.text | lowercase}}\"\n                >\n                    {{ day.text }}\n                </th>\n            }\n        </tr>\n    </thead>\n    <tbody>\n        @for(week of weeks; track week; let weekIndex = $index) {\n            <tr role=\"row\" class=\"v-calendar-week\">\n                @for(day of week; track day; let dayIndex = $index) {\n                    <td role=\"gridcell\" class=\"v-calendar-cell\">\n                        @if(day) {\n                            <button\n                                #dayButton\n                                class=\"v-calendar-day body-small\"\n                                id=\"{{id}}-day-{{day | date: 'yyyy-MM-dd'}}\"\n                                [disabled]=\"isDisabled(day)\"\n                                [class.is-disabled]=\"isDisabled(day)\"\n                                [class.is-today]=\"isToday(day)\"\n                                [class.is-selected]=\"isSelected(day)\"\n                                [class.is-range-start]=\"rangeMode && isRangeStart(day)\"\n                                [class.is-range-end]=\"rangeMode && isRangeEnd(day)\"\n                                [class.is-in-range]=\"rangeMode && isInRange(day)\"\n                                [attr.aria-label]=\"day | calendarRangeAriaLabel : dayIndex\"\n                                [attr.aria-selected]=\"isSelected(day) || isRangeStart(day) || isRangeEnd(day) || isInRange(day)\"\n                                [attr.data-week-index]=\"weekIndex\"\n                                [attr.data-day-index]=\"dayIndex\"\n                                [attr.data-day-number]=\"day.getDate()\"\n                                [attr.data-first-in-month]=\"isFirstInMonth(day)\"\n                                [attr.data-last-in-month]=\"isLastInMonth(day)\"\n                                [tabIndex]=\"isSelected(day) || isToday(day) || isFirstInMonth(day) ? 0 : -1\"\n                                (click)=\"selectDate(day)\"\n                            >\n                                <span class=\"v-calendar-day-text\">\n                                    {{ day | date: 'd' }}\n                                </span>\n                            </button>\n                        }\n                    </td>\n                }\n            </tr>\n        }\n    </tbody>\n</table>\n<footer>\n    @if(clearButton) {\n        <button\n            vpg-link\n            size=\"small\"\n            class=\"v-calendar-clear-button\"\n            id=\"{{id}}-clear-date\"\n            (click)=\"clearDate()\"\n        >\n            Clear\n        </button>\n    }\n</footer>\n" }]
        }], ctorParameters: () => [{ type: CalendarService }, { type: i0.ChangeDetectorRef }], propDecorators: { selectedDate: [{
                type: Input
            }], selectedDateChange: [{
                type: Output
            }], currentDate: [{
                type: Input
            }], currentDateChange: [{
                type: Output
            }], clearButton: [{
                type: Input
            }], rangeMode: [{
                type: Input
            }], startDate: [{
                type: Input
            }], startDateChange: [{
                type: Output
            }], endDate: [{
                type: Input
            }], endDateChange: [{
                type: Output
            }], minDate: [{
                type: Input,
                args: [{ transform: transformDate }]
            }], maxDate: [{
                type: Input,
                args: [{ transform: transformDate }]
            }], dateFormat: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], id: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['id']
            }], className: [{
                type: HostBinding,
                args: ['class']
            }], dayButtons: [{
                type: ViewChildren,
                args: ['dayButton']
            }] } });

class CalendarModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CalendarModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.9", ngImport: i0, type: CalendarModule, declarations: [CalendarComponent,
            CalendarNavigationDirective,
            CalendarRangeAriaLabelPipe], imports: [CommonModule,
            IconModule,
            LinkModule, VpgTranslateModule], exports: [CalendarComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CalendarModule, imports: [CommonModule,
            IconModule,
            LinkModule,
            VpgTranslateModule.forRoot()] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CalendarModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        IconModule,
                        LinkModule,
                        VpgTranslateModule.forRoot()
                    ],
                    declarations: [
                        CalendarComponent,
                        CalendarNavigationDirective,
                        CalendarRangeAriaLabelPipe
                    ],
                    exports: [
                        CalendarComponent
                    ]
                }]
        }] });

class CardNextComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CardNextComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: CardNextComponent, selector: "vpg-card-next", host: { classAttribute: "v-card v-container" }, ngImport: i0, template: "<ng-content select=\"vpg-card-next-header\"></ng-content>\n<ng-content select=\"vpg-card-next-link-header\"></ng-content>\n<ng-content select=\"vpg-card-next-image\"></ng-content>\n<ng-content select=\"vpg-card-next-content\"></ng-content>\n<ng-content select=\"vpg-card-next-text\"></ng-content>\n<ng-content select=\"vpg-card-next-actions\"></ng-content>" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CardNextComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-card-next', host: {
                        'class': 'v-card v-container'
                    }, template: "<ng-content select=\"vpg-card-next-header\"></ng-content>\n<ng-content select=\"vpg-card-next-link-header\"></ng-content>\n<ng-content select=\"vpg-card-next-image\"></ng-content>\n<ng-content select=\"vpg-card-next-content\"></ng-content>\n<ng-content select=\"vpg-card-next-text\"></ng-content>\n<ng-content select=\"vpg-card-next-actions\"></ng-content>" }]
        }] });

class CardNextActionsComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CardNextActionsComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: CardNextActionsComponent, selector: "vpg-card-next-actions", host: { classAttribute: "v-card-actions" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CardNextActionsComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vpg-card-next-actions',
                    template: '<ng-content></ng-content>',
                    host: {
                        'class': 'v-card-actions'
                    }
                }]
        }] });

class CardNextContentComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CardNextContentComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: CardNextContentComponent, selector: "vpg-card-next-content", host: { classAttribute: "v-card-content" }, ngImport: i0, template: `
        <ng-content></ng-content>
    `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CardNextContentComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vpg-card-next-content',
                    template: `
        <ng-content></ng-content>
    `,
                    host: {
                        'class': 'v-card-content'
                    }
                }]
        }] });

class CardNextHeaderComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CardNextHeaderComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: CardNextHeaderComponent, selector: "vpg-card-next-header", host: { classAttribute: "v-card-header" }, ngImport: i0, template: `
        <div class="v-card-titles">
            <ng-content select="vpg-card-next-title"></ng-content>
            <ng-content select="vpg-card-next-subtitle"></ng-content>
        </div>
        <ng-content select="[vpg-button-icon]"></ng-content>
    `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CardNextHeaderComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vpg-card-next-header',
                    template: `
        <div class="v-card-titles">
            <ng-content select="vpg-card-next-title"></ng-content>
            <ng-content select="vpg-card-next-subtitle"></ng-content>
        </div>
        <ng-content select="[vpg-button-icon]"></ng-content>
    `,
                    host: {
                        'class': 'v-card-header'
                    }
                }]
        }] });

const placeholders = {
    hero: '/assets/images/heroPlaceholderImage.png',
    avatar: '/assets/images/avatarPlaceholderImage.png',
    squareThumbnail: '/assets/images/squareThumbnailPlaceholder.png'
};
const fallbackImages = new Map([
    ['hero', placeholders.hero],
    ['thumbnail', placeholders.hero],
    ['portrait', placeholders.hero],
    ['square-thumbnail', placeholders.squareThumbnail],
    ['avatar', placeholders.avatar]
]);
const getFallbackImage = (type) => {
    return fallbackImages.get(type) || placeholders.hero;
};

class ImageDirective {
    get nativeElement() {
        return this.element.nativeElement;
    }
    get classNames() {
        return {
            'v-image': true,
            'is-scaled-down': this.backgroundColor !== '',
            [`is-${this.type}`]: true,
            [`is-${this.size}`]: true
        };
    }
    get ariaHidden() {
        return this.alt === '';
    }
    setFallbackSrc() {
        this.renderer.setAttribute(this.nativeElement, 'src', this.fallbackSrc || getFallbackImage(this.type));
    }
    constructor(element, renderer) {
        this.element = element;
        this.renderer = renderer;
        this.fallbackSrc = '';
        this.type = 'hero';
        this.size = 'original';
        this.alt = '';
        this.backgroundColor = '';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ImageDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.9", type: ImageDirective, selector: "img[vpg-image], img[vpgImage]", inputs: { fallbackSrc: "fallbackSrc", type: "type", size: "size", alt: "alt", backgroundColor: "backgroundColor" }, host: { listeners: { "error": "setFallbackSrc()" }, properties: { "alt": "this.alt", "style.background": "this.backgroundColor", "class": "this.classNames", "attr.aria-hidden": "this.ariaHidden" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ImageDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'img[vpg-image], img[vpgImage]'
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.Renderer2 }], propDecorators: { fallbackSrc: [{
                type: Input
            }], type: [{
                type: Input
            }], size: [{
                type: Input
            }], alt: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['alt']
            }], backgroundColor: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['style.background']
            }], classNames: [{
                type: HostBinding,
                args: ['class']
            }], ariaHidden: [{
                type: HostBinding,
                args: ['attr.aria-hidden']
            }], setFallbackSrc: [{
                type: HostListener,
                args: ['error']
            }] } });

class CardNextImageComponent {
    constructor() {
        this.id = '';
        this.src = '';
        this.alt = '';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CardNextImageComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: CardNextImageComponent, selector: "vpg-card-next-image", inputs: { id: "id", src: "src", alt: "alt" }, host: { classAttribute: "v-card-image" }, ngImport: i0, template: `
        <img
            vpgImage
            [src]="src"
            [alt]="alt"
            [id]="id"
        />
    `, isInline: true, dependencies: [{ kind: "directive", type: ImageDirective, selector: "img[vpg-image], img[vpgImage]", inputs: ["fallbackSrc", "type", "size", "alt", "backgroundColor"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CardNextImageComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vpg-card-next-image',
                    template: `
        <img
            vpgImage
            [src]="src"
            [alt]="alt"
            [id]="id"
        />
    `,
                    host: {
                        'class': 'v-card-image'
                    }
                }]
        }], propDecorators: { id: [{
                type: Input
            }], src: [{
                type: Input
            }], alt: [{
                type: Input
            }] } });

class CardNextLinkHeaderComponent {
    constructor() {
        this.href = '';
        this.target = '_self';
        this.features = undefined;
    }
    handleClick() {
        if (this.href) {
            window.open(this.href, this.target, this.features);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CardNextLinkHeaderComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: CardNextLinkHeaderComponent, selector: "vpg-card-next-link-header", inputs: { href: "href", target: "target", features: "features" }, host: { attributes: { "role": "link", "tabindex": "0" }, listeners: { "click": "handleClick()", "keydown.enter": "handleClick()" }, classAttribute: "v-card-link-header" }, ngImport: i0, template: `
        <ng-content select="vpg-card-next-title" />
        <vpg-icon-next
            icon="chevron-right"
            size="2x"
        />
    `, isInline: true, dependencies: [{ kind: "component", type: IconNextComponent, selector: "vpg-icon-next", inputs: ["icon", "size", "prefix", "screenReaderText"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CardNextLinkHeaderComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vpg-card-next-link-header',
                    template: `
        <ng-content select="vpg-card-next-title" />
        <vpg-icon-next
            icon="chevron-right"
            size="2x"
        />
    `,
                    host: {
                        'class': 'v-card-link-header',
                        'role': 'link',
                        'tabindex': '0'
                    }
                }]
        }], propDecorators: { href: [{
                type: Input
            }], target: [{
                type: Input
            }], features: [{
                type: Input
            }], handleClick: [{
                type: HostListener,
                args: ['click']
            }, {
                type: HostListener,
                args: ['keydown.enter']
            }] } });

class CardNextSubtitleComponent {
    constructor() {
        this.level = '3';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CardNextSubtitleComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: CardNextSubtitleComponent, selector: "vpg-card-next-subtitle", inputs: { level: "level" }, host: { attributes: { "role": "heading" }, properties: { "attr.aria-level": "this.level" }, classAttribute: "v-card-subtitle" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CardNextSubtitleComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vpg-card-next-subtitle',
                    template: '<ng-content></ng-content>',
                    host: {
                        'class': 'v-card-subtitle',
                        'role': 'heading'
                    }
                }]
        }], propDecorators: { level: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['attr.aria-level']
            }] } });

class CardNextTextComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CardNextTextComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: CardNextTextComponent, selector: "vpg-card-next-text", host: { classAttribute: "v-card-text" }, ngImport: i0, template: `
        <ng-content></ng-content>
    `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CardNextTextComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vpg-card-next-text',
                    template: `
        <ng-content></ng-content>
    `,
                    host: {
                        'class': 'v-card-text'
                    }
                }]
        }] });

class CardNextTitleComponent {
    constructor() {
        this.level = '2';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CardNextTitleComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: CardNextTitleComponent, selector: "vpg-card-next-title", inputs: { level: "level" }, host: { attributes: { "role": "heading" }, properties: { "attr.aria-level": "this.level" }, classAttribute: "v-card-title h3" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CardNextTitleComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vpg-card-next-title',
                    template: '<ng-content></ng-content>',
                    host: {
                        'class': 'v-card-title h3',
                        'role': 'heading'
                    }
                }]
        }], propDecorators: { level: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['attr.aria-level']
            }] } });

class ImageModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ImageModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.9", ngImport: i0, type: ImageModule, declarations: [ImageDirective], imports: [CommonModule], exports: [ImageDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ImageModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ImageModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [ImageDirective],
                    exports: [ImageDirective]
                }]
        }] });

class CardModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CardModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.9", ngImport: i0, type: CardModule, declarations: [CardNextComponent,
            CardNextActionsComponent,
            CardNextHeaderComponent,
            CardNextTitleComponent,
            CardNextSubtitleComponent,
            CardNextImageComponent,
            CardNextContentComponent,
            CardNextTextComponent,
            CardNextLinkHeaderComponent], imports: [CommonModule,
            ImageModule,
            IconModule], exports: [CardNextComponent,
            CardNextActionsComponent,
            CardNextHeaderComponent,
            CardNextTitleComponent,
            CardNextSubtitleComponent,
            CardNextImageComponent,
            CardNextContentComponent,
            CardNextTextComponent,
            CardNextLinkHeaderComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CardModule, imports: [CommonModule,
            ImageModule,
            IconModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CardModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        ImageModule,
                        IconModule
                    ],
                    declarations: [
                        CardNextComponent,
                        CardNextActionsComponent,
                        CardNextHeaderComponent,
                        CardNextTitleComponent,
                        CardNextSubtitleComponent,
                        CardNextImageComponent,
                        CardNextContentComponent,
                        CardNextTextComponent,
                        CardNextLinkHeaderComponent
                    ],
                    exports: [
                        CardNextComponent,
                        CardNextActionsComponent,
                        CardNextHeaderComponent,
                        CardNextTitleComponent,
                        CardNextSubtitleComponent,
                        CardNextImageComponent,
                        CardNextContentComponent,
                        CardNextTextComponent,
                        CardNextLinkHeaderComponent
                    ]
                }]
        }] });

class CheckboxComponent extends AbstractFormComponent {
    constructor(injector) {
        super();
        this.injector = injector;
        this.label = '';
        this.align = 'left';
        this.optionValue = '';
        this.ariaLabel = '';
        this.size = 'large';
    }
    ngOnInit() {
        this.setName(this.injector.get(NgControl));
    }
    onChange() {
        this.writeValue(!this.value);
    }
    get classNames() {
        return {
            ['v-checkbox']: true,
            ['is-indeterminate']: this.value === '',
            ['is-checked']: this.value === true,
            ['is-disabled']: this.disabled,
            [`is-${this.align}`]: true,
            [`is-${this.size}`]: true
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CheckboxComponent, deps: [{ token: i0.Injector }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: CheckboxComponent, selector: "vpg-checkbox", inputs: { label: "label", align: "align", optionValue: "optionValue", ariaLabel: "ariaLabel", size: "size" }, host: { properties: { "id": "id", "class": "this.classNames" } }, providers: [valueAccessor(CheckboxComponent)], usesInheritance: true, ngImport: i0, template: "<label\n    class=\"v-checkbox-label\"\n    for=\"{{name}}-input\"\n    id=\"{{name}}-label\"\n>\n    <input\n        class=\"v-checkbox-input\"\n        type=\"checkbox\"\n        [checked]=\"value\"\n        [name]=\"name\"\n        [value]=\"optionValue\"\n        [disabled]=\"disabled\"\n        [attr.aria-label]=\"ariaLabel\"\n        id=\"{{name}}-input\"\n        (change)=\"onChange()\"\n    >\n    <span class=\"v-checkbox-checkmark\"></span>\n    <ng-container *ngIf=\"label else content\">\n        {{ label }}\n    </ng-container>\n</label>\n\n<ng-template #content>\n    <div>\n        <ng-content></ng-content>\n    </div>\n</ng-template>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CheckboxComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-checkbox', providers: [valueAccessor(CheckboxComponent)], host: {
                        '[id]': 'id'
                    }, template: "<label\n    class=\"v-checkbox-label\"\n    for=\"{{name}}-input\"\n    id=\"{{name}}-label\"\n>\n    <input\n        class=\"v-checkbox-input\"\n        type=\"checkbox\"\n        [checked]=\"value\"\n        [name]=\"name\"\n        [value]=\"optionValue\"\n        [disabled]=\"disabled\"\n        [attr.aria-label]=\"ariaLabel\"\n        id=\"{{name}}-input\"\n        (change)=\"onChange()\"\n    >\n    <span class=\"v-checkbox-checkmark\"></span>\n    <ng-container *ngIf=\"label else content\">\n        {{ label }}\n    </ng-container>\n</label>\n\n<ng-template #content>\n    <div>\n        <ng-content></ng-content>\n    </div>\n</ng-template>\n" }]
        }], ctorParameters: () => [{ type: i0.Injector }], propDecorators: { label: [{
                type: Input
            }], align: [{
                type: Input
            }], optionValue: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], size: [{
                type: Input
            }], classNames: [{
                type: HostBinding,
                args: ['class']
            }] } });

class CheckboxGroupComponent {
    constructor() {
        this.id = '';
        this.legend = '';
        this.align = 'left';
        this.required = false;
    }
    get classNames() {
        return {
            ['v-checkbox-group']: true,
            [`is-${this.align}`]: true
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CheckboxGroupComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: CheckboxGroupComponent, selector: "vpg-checkbox-group", inputs: { id: "id", legend: "legend", align: "align", required: "required" }, host: { properties: { "id": "id", "class": "this.classNames" } }, ngImport: i0, template: "<fieldset class=\"v-checkbox-group-fieldset\">\n    <legend class=\"v-checkbox-group-legend\">\n        {{ legend }}\n        <span\n            *ngIf=\"required\"\n            class=\"v-control-required\"\n            aria-hidden=\"true\"\n        >\n            *\n        </span>\n    </legend>\n    <ng-content></ng-content>\n</fieldset>\n\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CheckboxGroupComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-checkbox-group', host: {
                        '[id]': 'id'
                    }, template: "<fieldset class=\"v-checkbox-group-fieldset\">\n    <legend class=\"v-checkbox-group-legend\">\n        {{ legend }}\n        <span\n            *ngIf=\"required\"\n            class=\"v-control-required\"\n            aria-hidden=\"true\"\n        >\n            *\n        </span>\n    </legend>\n    <ng-content></ng-content>\n</fieldset>\n\n" }]
        }], propDecorators: { id: [{
                type: Input
            }], legend: [{
                type: Input
            }], align: [{
                type: Input
            }], required: [{
                type: Input
            }], classNames: [{
                type: HostBinding,
                args: ['class']
            }] } });

class ControlEmptyListComponent {
    constructor(translations) {
        this.translations = translations;
        this.heading = '';
        this.text = '';
        this.heading = this.translations.getTranslation('NoResultsHeader');
        this.text = this.translations.getTranslation('NoResultsText');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ControlEmptyListComponent, deps: [{ token: TranslationService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: ControlEmptyListComponent, selector: "vpg-control-empty-list", host: { attributes: { "role": "alert" }, classAttribute: "v-control-empty-list" }, ngImport: i0, template: `
        <div
            *ngIf="heading"
            class="v-control-empty-list-heading h4"
        >
            {{ heading }}
        </div>
        <div
            *ngIf="text"
            class="v-control-empty-list-text body-regular"
        >
            {{ text }}
        </div>
    `, isInline: true, dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ControlEmptyListComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vpg-control-empty-list',
                    template: `
        <div
            *ngIf="heading"
            class="v-control-empty-list-heading h4"
        >
            {{ heading }}
        </div>
        <div
            *ngIf="text"
            class="v-control-empty-list-text body-regular"
        >
            {{ text }}
        </div>
    `,
                    host: {
                        class: 'v-control-empty-list',
                        role: 'alert'
                    }
                }]
        }], ctorParameters: () => [{ type: TranslationService }] });

class ControlErrorComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ControlErrorComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: ControlErrorComponent, selector: "vpg-control-error", host: { classAttribute: "v-control-error body-regular text-red-50 block" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ControlErrorComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vpg-control-error',
                    template: '<ng-content></ng-content>',
                    host: {
                        class: 'v-control-error body-regular text-red-50 block'
                    }
                }]
        }] });

class ControlErrorsComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ControlErrorsComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: ControlErrorsComponent, selector: "vpg-control-errors", host: { attributes: { "role": "alert", "attr.aria-hidden": "true" }, classAttribute: "v-control-errors block" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ControlErrorsComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vpg-control-errors',
                    template: '<ng-content></ng-content>',
                    host: {
                        class: 'v-control-errors block',
                        role: 'alert',
                        'attr.aria-hidden': 'true'
                    }
                }]
        }] });

class ControlHintComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ControlHintComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: ControlHintComponent, selector: "vpg-control-hint", host: { attributes: { "attr.aria-hidden": "true" }, classAttribute: "v-control-hint body-regular block" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ControlHintComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vpg-control-hint',
                    template: '<ng-content></ng-content>',
                    host: {
                        class: 'v-control-hint body-regular block',
                        'attr.aria-hidden': 'true'
                    }
                }]
        }] });

class ControlFooterComponent {
    constructor() {
        this.name = '';
        this.hint = '';
        this.customHint = undefined;
        this.maxLength = null;
        this.currentLength = null;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ControlFooterComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: ControlFooterComponent, selector: "vpg-control-footer", inputs: { name: "name", hint: "hint", customHint: "customHint", maxLength: "maxLength", currentLength: "currentLength" }, ngImport: i0, template: `
        <div class="flex gap-20 justify-content-between">
            <vpg-control-hint *ngIf="hint" id="v-control-{{name}}-hint">
                {{ hint }}
            </vpg-control-hint>
            <ng-container
                *ngIf="customHint"
                [ngTemplateOutlet]="customHint"
            ></ng-container>
            <span></span>
            <span
                *ngIf="maxLength && currentLength !== null"
                class="body-regular"
            >
                {{ currentLength + '/' + maxLength }}
            </span>
        </div>
    `, isInline: true, dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "component", type: ControlHintComponent, selector: "vpg-control-hint" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ControlFooterComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vpg-control-footer',
                    template: `
        <div class="flex gap-20 justify-content-between">
            <vpg-control-hint *ngIf="hint" id="v-control-{{name}}-hint">
                {{ hint }}
            </vpg-control-hint>
            <ng-container
                *ngIf="customHint"
                [ngTemplateOutlet]="customHint"
            ></ng-container>
            <span></span>
            <span
                *ngIf="maxLength && currentLength !== null"
                class="body-regular"
            >
                {{ currentLength + '/' + maxLength }}
            </span>
        </div>
    `
                }]
        }], propDecorators: { name: [{
                type: Input
            }], hint: [{
                type: Input
            }], customHint: [{
                type: Input
            }], maxLength: [{
                type: Input
            }], currentLength: [{
                type: Input
            }] } });

class ControlListComponent {
    constructor() {
        this.options = [];
        this.optionLabel = 'name';
        this.value = '';
        this.name = '';
        this.truncateText = false;
        this.highlightText = '';
        this.highlightSearch = false;
        this.selected = new EventEmitter();
        this.focused = new EventEmitter();
    }
    preventMousedown(e) {
        e.preventDefault();
    }
    emitSelected(option) {
        this.selected.emit(option[this.optionLabel]);
    }
    emitFocused(option) {
        this.focused.emit(option);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ControlListComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: ControlListComponent, selector: "vpg-control-list", inputs: { options: "options", optionLabel: "optionLabel", value: "value", name: "name", truncateText: "truncateText", highlightText: "highlightText", highlightSearch: "highlightSearch" }, outputs: { selected: "selected", focused: "focused" }, ngImport: i0, template: `
        <ul
            vpg-keyboard-navigate
            (optionFocused)="emitFocused($event)"
            role="listbox"
            id="v-control-{{name}}-list"
            attr.aria-labelledby="{{name}}-label"
            class="v-control-list"
        >
            <li
                *ngFor="let option of options; let index = index"
                role="option"
                tabindex="-1"
                id="v-control-{{name}}-option-{{index}}"
                class="v-control-option"
                [class.is-truncated]=[]
                [class.is-selected]="value === option[optionLabel]"
                [attr.aria-selected]="value === option[optionLabel]"
                (click)="emitSelected(option)"
                (mousedown)="preventMousedown($event)"
            >
                <img
                    vpg-image
                    *ngIf="option.image"
                    [type]="option.image?.type"
                    [src]="option.image?.source"
                    [size]="option.image?.size"
                    alt=""
                />
                <vpg-icon-next
                    *ngIf="option.icon"
                    [icon]="option.icon"
                ></vpg-icon-next>
                <div
                    class="v-control-option-text"
                    [innerHTML]="option[optionLabel] | highlight: [highlightSearch, highlightText]"
                >
                </div>
            </li>
        </ul>
    `, isInline: true, dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: IconNextComponent, selector: "vpg-icon-next", inputs: ["icon", "size", "prefix", "screenReaderText"] }, { kind: "directive", type: ImageDirective, selector: "img[vpg-image], img[vpgImage]", inputs: ["fallbackSrc", "type", "size", "alt", "backgroundColor"] }, { kind: "directive", type: KeyboardNavigateDirective, selector: "[vpg-keyboard-navigate]", inputs: ["controlOptionClassName"], outputs: ["optionFocused"] }, { kind: "pipe", type: HighlightPipe, name: "highlight" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ControlListComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vpg-control-list',
                    template: `
        <ul
            vpg-keyboard-navigate
            (optionFocused)="emitFocused($event)"
            role="listbox"
            id="v-control-{{name}}-list"
            attr.aria-labelledby="{{name}}-label"
            class="v-control-list"
        >
            <li
                *ngFor="let option of options; let index = index"
                role="option"
                tabindex="-1"
                id="v-control-{{name}}-option-{{index}}"
                class="v-control-option"
                [class.is-truncated]=[]
                [class.is-selected]="value === option[optionLabel]"
                [attr.aria-selected]="value === option[optionLabel]"
                (click)="emitSelected(option)"
                (mousedown)="preventMousedown($event)"
            >
                <img
                    vpg-image
                    *ngIf="option.image"
                    [type]="option.image?.type"
                    [src]="option.image?.source"
                    [size]="option.image?.size"
                    alt=""
                />
                <vpg-icon-next
                    *ngIf="option.icon"
                    [icon]="option.icon"
                ></vpg-icon-next>
                <div
                    class="v-control-option-text"
                    [innerHTML]="option[optionLabel] | highlight: [highlightSearch, highlightText]"
                >
                </div>
            </li>
        </ul>
    `
                }]
        }], propDecorators: { options: [{
                type: Input
            }], optionLabel: [{
                type: Input
            }], value: [{
                type: Input
            }], name: [{
                type: Input
            }], truncateText: [{
                type: Input
            }], highlightText: [{
                type: Input
            }], highlightSearch: [{
                type: Input
            }], selected: [{
                type: Output
            }], focused: [{
                type: Output
            }] } });

class ControlPrefixComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ControlPrefixComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: ControlPrefixComponent, selector: "vpg-control-prefix", host: { classAttribute: "v-control-prefix" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ControlPrefixComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vpg-control-prefix',
                    template: '<ng-content></ng-content>',
                    host: {
                        class: 'v-control-prefix'
                    }
                }]
        }] });

class ControlSuffixComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ControlSuffixComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: ControlSuffixComponent, selector: "vpg-control-suffix", host: { classAttribute: "v-control-suffix" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ControlSuffixComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vpg-control-suffix',
                    template: '<ng-content></ng-content>',
                    host: {
                        class: 'v-control-suffix'
                    }
                }]
        }] });

class ControlModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ControlModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.9", ngImport: i0, type: ControlModule, declarations: [ControlHintComponent,
            ControlErrorComponent,
            ControlErrorsComponent,
            ControlPrefixComponent,
            ControlSuffixComponent,
            ControlEmptyListComponent,
            ControlFooterComponent,
            ControlListComponent], imports: [CommonModule,
            FormsModule,
            ReactiveFormsModule,
            IconModule,
            ImageModule,
            HighlightPipe,
            KeyboardNavigateDirective], exports: [ControlHintComponent,
            ControlErrorComponent,
            ControlErrorsComponent,
            ControlPrefixComponent,
            ControlSuffixComponent,
            ControlEmptyListComponent,
            ControlFooterComponent,
            ControlListComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ControlModule, imports: [CommonModule,
            FormsModule,
            ReactiveFormsModule,
            IconModule,
            ImageModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ControlModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule,
                        ReactiveFormsModule,
                        IconModule,
                        ImageModule,
                        HighlightPipe,
                        KeyboardNavigateDirective
                    ],
                    declarations: [
                        ControlHintComponent,
                        ControlErrorComponent,
                        ControlErrorsComponent,
                        ControlPrefixComponent,
                        ControlSuffixComponent,
                        ControlEmptyListComponent,
                        ControlFooterComponent,
                        ControlListComponent
                    ],
                    exports: [
                        ControlHintComponent,
                        ControlErrorComponent,
                        ControlErrorsComponent,
                        ControlPrefixComponent,
                        ControlSuffixComponent,
                        ControlEmptyListComponent,
                        ControlFooterComponent,
                        ControlListComponent
                    ]
                }]
        }] });

class CheckboxModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CheckboxModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.9", ngImport: i0, type: CheckboxModule, declarations: [CheckboxComponent,
            CheckboxGroupComponent], imports: [CommonModule,
            FormsModule,
            ReactiveFormsModule,
            ControlModule], exports: [CheckboxComponent,
            CheckboxGroupComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CheckboxModule, imports: [CommonModule,
            FormsModule,
            ReactiveFormsModule,
            ControlModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CheckboxModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule,
                        ReactiveFormsModule,
                        ControlModule
                    ],
                    declarations: [
                        CheckboxComponent,
                        CheckboxGroupComponent
                    ],
                    exports: [
                        CheckboxComponent,
                        CheckboxGroupComponent
                    ]
                }]
        }] });

class CheckmarkModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CheckmarkModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.9", ngImport: i0, type: CheckmarkModule, declarations: [CheckmarkComponent], imports: [CommonModule], exports: [CheckmarkComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CheckmarkModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: CheckmarkModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule
                    ],
                    declarations: [
                        CheckmarkComponent
                    ],
                    exports: [
                        CheckmarkComponent
                    ]
                }]
        }] });

class ContainerDirective {
    constructor() {
        this.transparent = false;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ContainerDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.9", type: ContainerDirective, selector: "[vpgContainer]", inputs: { transparent: "transparent" }, host: { properties: { "class.is-transparent": "this.transparent" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ContainerDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[vpgContainer]'
                }]
        }], propDecorators: { transparent: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.is-transparent']
            }] } });

class ContainerModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ContainerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.9", ngImport: i0, type: ContainerModule, declarations: [ContainerDirective,
            ContainerComponent], imports: [CommonModule], exports: [ContainerDirective,
            ContainerComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ContainerModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ContainerModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule
                    ],
                    declarations: [
                        ContainerDirective,
                        ContainerComponent
                    ],
                    exports: [
                        ContainerDirective,
                        ContainerComponent
                    ]
                }]
        }] });

class DateIconComponent {
    constructor() {
        this.id = '';
        this.screenReaderText = '';
        this.month = '';
        this.day = '';
        this.missed = false;
        this.missedIconSource = 'assets/images/date-icon-missed.svg';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: DateIconComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: DateIconComponent, selector: "vpg-date-icon", inputs: { id: "id", screenReaderText: "screenReaderText", month: "month", day: "day", missed: "missed" }, host: { attributes: { "attr.aria-label": "screenReaderText" }, properties: { "id": "id" }, classAttribute: "v-date-icon" }, ngImport: i0, template: "<div\n    *ngIf=\"!missed\"\n    class=\"v-date-icon-month\"\n    id=\"{{id}}-month\"\n    aria-hidden=\"true\"\n>{{ month }}\n</div>\n<div\n    *ngIf=\"!missed\"\n    class=\"v-date-icon-day\"\n    id=\"{{id}}-day\"\n    aria-hidden=\"true\"\n>{{ day }}\n</div>\n<img\n    *ngIf=\"missed\"\n    [src]=\"missedIconSource\"\n    aria-hidden=\"true\"\n    alt=\"\"\n    id=\"{{id}}-missed\"\n/>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: DateIconComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-date-icon', host: {
                        class: 'v-date-icon',
                        '[id]': 'id',
                        'attr.aria-label': 'screenReaderText'
                    }, template: "<div\n    *ngIf=\"!missed\"\n    class=\"v-date-icon-month\"\n    id=\"{{id}}-month\"\n    aria-hidden=\"true\"\n>{{ month }}\n</div>\n<div\n    *ngIf=\"!missed\"\n    class=\"v-date-icon-day\"\n    id=\"{{id}}-day\"\n    aria-hidden=\"true\"\n>{{ day }}\n</div>\n<img\n    *ngIf=\"missed\"\n    [src]=\"missedIconSource\"\n    aria-hidden=\"true\"\n    alt=\"\"\n    id=\"{{id}}-missed\"\n/>\n" }]
        }], propDecorators: { id: [{
                type: Input
            }], screenReaderText: [{
                type: Input
            }], month: [{
                type: Input
            }], day: [{
                type: Input
            }], missed: [{
                type: Input
            }] } });

class DateIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: DateIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.9", ngImport: i0, type: DateIconModule, declarations: [DateIconComponent], imports: [CommonModule], exports: [DateIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: DateIconModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: DateIconModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule
                    ],
                    declarations: [
                        DateIconComponent
                    ],
                    exports: [
                        DateIconComponent
                    ]
                }]
        }] });

class Stack {
    constructor() {
        this.stack = [];
    }
    push(value) {
        this.stack.push(value);
        return value;
    }
    remove(value) {
        this.stack = this.stack.filter((e) => e !== value);
    }
    removeAll() {
        this.stack = [];
    }
    get items() {
        return [...this.stack];
    }
    get top() {
        if (this.isEmpty) {
            return null;
        }
        return this.stack[this.stack.length - 1];
    }
    get isEmpty() {
        return this.stack.length === 0;
    }
    get hasAny() {
        return this.stack.length > 0;
    }
}

const VPG_DIALOG_OVERLAY = new InjectionToken('VpgDialogOverlay');
const VPG_DIALOG_OPTIONS = new InjectionToken('VpgDialogOptions');
class DialogRef {
    constructor() {
        this.subject = new Subject();
    }
    attach(stack) {
        this.stack = stack;
        stack.push(this);
        return this;
    }
    pop() {
        this.stack?.top?.close();
    }
    getStack() {
        return this.stack;
    }
    setOverlayRef(value) {
        this.overlayRef = value;
    }
    close(value = false) {
        clearTimeout(this.autoCloseTimer);
        const overlayRef = this.overlayRef;
        if (overlayRef) {
            const instance = overlayRef.instance;
            if (instance.animateRemoving) {
                instance.animateRemoving().then(() => {
                    overlayRef.destroy();
                });
            }
            else {
                overlayRef.destroy();
            }
        }
        this.stack?.remove(this);
        this.subject.next(value);
        this.subject.unsubscribe();
        this.subject = new Subject();
    }
    autoCloseAfter(durationInMiliseconds, result = false) {
        clearTimeout(this.autoCloseTimer);
        if (durationInMiliseconds > 0) {
            this.autoCloseTimer = setTimeout(() => {
                this.close(result);
            }, durationInMiliseconds);
        }
        return this;
    }
    subscribe(handler) {
        this.subject.subscribe({
            next: (value) => handler(value)
        });
    }
    value() {
        return new Promise((resolve) => {
            this.subscribe(resolve);
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: DialogRef, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: DialogRef }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: DialogRef, decorators: [{
            type: Injectable
        }] });
class DialogStack extends Stack {
    constructor() {
        super();
    }
    closeAll() {
        const dialogRefs = this.stack.reverse();
        this.removeAll();
        for (const ref of dialogRefs) {
            ref.close();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: DialogStack, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: DialogStack, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: DialogStack, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [] });
class DialogService {
    constructor(dialogOverlayType, dialogStack, injector, applicationRef, factoryResolver) {
        this.dialogOverlayType = dialogOverlayType;
        this.dialogStack = dialogStack;
        this.injector = injector;
        this.applicationRef = applicationRef;
        this.factoryResolver = factoryResolver;
    }
    open(component, options) {
        return this.openInternal(component, options, this.dialogOverlayType, this.dialogStack);
    }
    openInternal(component, options, overlayType, stack) {
        const componentFactory = this.factoryResolver.resolveComponentFactory(component);
        const dialogRef = new DialogRef();
        const injector = this.createInjector(dialogRef, options);
        const rootViewRef = this.getRootViewContainerRef();
        const overlayRef = this.createOverlay(overlayType, injector);
        const componentRef = componentFactory.create(injector);
        const triggerElement = document.activeElement;
        dialogRef.setOverlayRef(overlayRef);
        this.setComponentProps(componentRef.instance, options);
        const closeDialog = (value) => {
            dialogRef.close(value);
            triggerElement.focus();
        };
        componentRef.instance.closeDialog?.subscribe(closeDialog);
        overlayRef.instance.attach(componentRef);
        if (componentRef.instance.className) {
            overlayRef.setInput('className', componentRef.instance.className);
        }
        overlayRef.setInput('backdropClick', options?.backdropClick);
        rootViewRef.insert(overlayRef.hostView);
        return dialogRef.attach(stack);
    }
    confirm() {
        return new DialogRef();
    }
    openDialog(dialog) {
        return this.openDialogInternal(dialog, this.dialogOverlayType);
    }
    openDialogInternal(dialog, componentType) {
        const dialogRef = dialog.dialogRef;
        const injector = this.createInjector(dialogRef);
        const rootViewRef = this.getRootViewContainerRef();
        const overlayRef = this.createOverlay(componentType, injector);
        dialogRef.setOverlayRef(overlayRef);
        overlayRef.instance.attach(dialog.content);
        let className = 'v-dialog-host';
        if (dialog.className) {
            className += ` ${dialog.className}`;
        }
        overlayRef.setInput('className', className);
        overlayRef.setInput('backdropClick', dialog.backdropClick);
        setTimeout(() => {
            rootViewRef.insert(overlayRef.hostView);
        }, 0);
        return dialogRef.attach(this.dialogStack);
    }
    createInjector(dialogRef, options) {
        return Injector.create({
            providers: [
                { provide: VPG_DIALOG_OPTIONS, useValue: options },
                { provide: DialogRef, useValue: dialogRef }
            ],
            parent: this.injector
        });
    }
    createOverlay(componentType, injector) {
        const overlayFactory = this.factoryResolver.resolveComponentFactory(componentType);
        const overlayRef = overlayFactory.create(injector);
        return overlayRef;
    }
    setComponentProps(instance, options) {
        if (options && options.props) {
            const props = options.props;
            for (const key of Object.keys(props)) {
                instance[key] = props[key];
            }
        }
    }
    getRootViewContainerRef() {
        const rootComponent = this.applicationRef.components[0];
        return rootComponent.injector.get(ViewContainerRef);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: DialogService, deps: [{ token: VPG_DIALOG_OVERLAY }, { token: DialogStack }, { token: i0.Injector }, { token: i0.ApplicationRef }, { token: i0.ComponentFactoryResolver }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: DialogService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: DialogService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: i0.Type, decorators: [{
                    type: Inject,
                    args: [VPG_DIALOG_OVERLAY]
                }] }, { type: DialogStack }, { type: i0.Injector }, { type: i0.ApplicationRef }, { type: i0.ComponentFactoryResolver }] });

class DialogComponent {
    get open() {
        return this._open;
    }
    set open(value) {
        if (value === this._open) {
            return;
        }
        this._open = value;
        if (value) {
            this.dialogService.openDialog(this).subscribe(() => {
                this.open = false;
            });
        }
        else {
            this.dialogRef?.close();
        }
        this.openChange.emit(this._open);
    }
    constructor(dialogService, dialogRef) {
        this.dialogService = dialogService;
        this.dialogRef = dialogRef;
        this._open = false;
        this.openChange = new EventEmitter();
        this.className = '';
        this.backdropClick = true;
    }
    show() {
        this.open = true;
    }
    close() {
        this.open = false;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: DialogComponent, deps: [{ token: DialogService }, { token: DialogRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: DialogComponent, selector: "vpg-dialog", inputs: { className: ["class", "className"], backdropClick: "backdropClick", open: "open" }, outputs: { openChange: "openChange" }, providers: [
            { provide: DialogRef, useClass: DialogRef }
        ], viewQueries: [{ propertyName: "content", first: true, predicate: ["content"], descendants: true }], ngImport: i0, template: `
        <ng-template #content>
            <ng-content></ng-content>
        </ng-template>
    `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: DialogComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vpg-dialog',
                    template: `
        <ng-template #content>
            <ng-content></ng-content>
        </ng-template>
    `,
                    providers: [
                        { provide: DialogRef, useClass: DialogRef }
                    ]
                }]
        }], ctorParameters: () => [{ type: DialogService }, { type: DialogRef }], propDecorators: { openChange: [{
                type: Output
            }], content: [{
                type: ViewChild,
                args: ['content']
            }], className: [{
                type: Input,
                args: ['class']
            }], backdropClick: [{
                type: Input,
                args: ['backdropClick']
            }], open: [{
                type: Input
            }] } });

class DialogActionsComponent {
    constructor() {
        this.stacked = true;
        this.align = 'end';
    }
    get classNames() {
        return {
            ['v-dialog-actions']: true,
            ['is-stacked']: this.stacked,
            ['flex']: true,
            [`justify-${this.align}`]: true
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: DialogActionsComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: DialogActionsComponent, selector: "vpg-dialog-actions", inputs: { stacked: "stacked", align: "align" }, host: { properties: { "class": "this.classNames" } }, ngImport: i0, template: `
        <ng-content></ng-content>
    `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: DialogActionsComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vpg-dialog-actions',
                    template: `
        <ng-content></ng-content>
    `
                }]
        }], propDecorators: { stacked: [{
                type: Input
            }], align: [{
                type: Input
            }], classNames: [{
                type: HostBinding,
                args: ['class']
            }] } });

class DialogBodyComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: DialogBodyComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: DialogBodyComponent, selector: "vpg-dialog-body", host: { classAttribute: "v-dialog-body" }, ngImport: i0, template: `
        <ng-content></ng-content>
    `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: DialogBodyComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vpg-dialog-body',
                    template: `
        <ng-content></ng-content>
    `,
                    host: {
                        class: 'v-dialog-body'
                    }
                }]
        }] });

class DialogFocusTrapDirective {
    constructor(element) {
        this.element = element;
        this.TAB = 'Tab';
    }
    ngAfterViewInit() {
        this.trapFocus();
    }
    trapFocus() {
        this.focusableElements = 'button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';
        this.focusableContent = this.element.nativeElement.querySelectorAll(this.focusableElements);
        if (!this.focusableContent.length) {
            return;
        }
        this.firstFocusableElement = this.focusableContent[0];
        this.lastFocusableElement = this.focusableContent[this.focusableContent.length - 1];
        this.element.nativeElement.addEventListener('keydown', (e) => {
            const isTabPressed = e.key === this.TAB || e.shiftKey;
            if (!isTabPressed) {
                return;
            }
            if (e.key === this.TAB && e.shiftKey && document.activeElement === this.firstFocusableElement) {
                this.lastFocusableElement.focus();
                e.preventDefault();
                return;
            }
            if (e.key === this.TAB && !e.shiftKey && document.activeElement === this.lastFocusableElement) {
                this.firstFocusableElement.focus();
                e.preventDefault();
            }
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: DialogFocusTrapDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.9", type: DialogFocusTrapDirective, selector: "[vpg-dialog-focus-trap]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: DialogFocusTrapDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[vpg-dialog-focus-trap]'
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }] });

class DialogHeaderComponent {
    constructor(dialogRef) {
        this.dialogRef = dialogRef;
        this.closeButtonAriaLabel = '';
        this.backgroundImage = '';
        this.backgroundImageAlt = '';
        this.closeIcon = true;
    }
    ngAfterViewInit() {
        this.setTriggerElement();
    }
    get classNames() {
        return {
            'v-dialog-header': true,
            'flex': !this.backgroundImage,
            'align-items-start': true,
            'has-background': this.backgroundImage
        };
    }
    close() {
        this.dialogRef.close();
        this.triggerElement.focus();
    }
    setTriggerElement() {
        this.triggerElement = document.activeElement;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: DialogHeaderComponent, deps: [{ token: DialogRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: DialogHeaderComponent, selector: "vpg-dialog-header", inputs: { closeButtonAriaLabel: "closeButtonAriaLabel", backgroundImage: "backgroundImage", backgroundImageAlt: "backgroundImageAlt", closeIcon: "closeIcon" }, host: { properties: { "class": "this.classNames" } }, ngImport: i0, template: `
        <img
            *ngIf="backgroundImage"
            class="v-dialog-header-img"
            [src]="backgroundImage"
            [attr.aria-hidden]="backgroundImageAlt ? false : true"
            [alt]="backgroundImageAlt"
        />
        <div class="v-dialog-title flex-1"
            id="vpg-dialog-title"
            role="heading"
            [attr.aria-level]="backgroundImage ? 1 : 3"
            [ngClass]="{
                'v-dialog-title-bg-img': backgroundImage,
                'h1': backgroundImage,
                'h3': !backgroundImage
            }">
            <ng-content></ng-content>
        </div>
        <button
            *ngIf="closeIcon"
            class="v-dialog-close-button"
            [class.has-background]="backgroundImage"
            id="vpg-dialog-close-button"
            [attr.aria-label]="closeButtonAriaLabel"
            (click)="close()"
        >
            <vpg-icon-next icon="times"></vpg-icon-next>
        </button>
    `, isInline: true, dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: IconNextComponent, selector: "vpg-icon-next", inputs: ["icon", "size", "prefix", "screenReaderText"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: DialogHeaderComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vpg-dialog-header',
                    template: `
        <img
            *ngIf="backgroundImage"
            class="v-dialog-header-img"
            [src]="backgroundImage"
            [attr.aria-hidden]="backgroundImageAlt ? false : true"
            [alt]="backgroundImageAlt"
        />
        <div class="v-dialog-title flex-1"
            id="vpg-dialog-title"
            role="heading"
            [attr.aria-level]="backgroundImage ? 1 : 3"
            [ngClass]="{
                'v-dialog-title-bg-img': backgroundImage,
                'h1': backgroundImage,
                'h3': !backgroundImage
            }">
            <ng-content></ng-content>
        </div>
        <button
            *ngIf="closeIcon"
            class="v-dialog-close-button"
            [class.has-background]="backgroundImage"
            id="vpg-dialog-close-button"
            [attr.aria-label]="closeButtonAriaLabel"
            (click)="close()"
        >
            <vpg-icon-next icon="times"></vpg-icon-next>
        </button>
    `
                }]
        }], ctorParameters: () => [{ type: DialogRef }], propDecorators: { closeButtonAriaLabel: [{
                type: Input
            }], backgroundImage: [{
                type: Input
            }], backgroundImageAlt: [{
                type: Input
            }], closeIcon: [{
                type: Input
            }], classNames: [{
                type: HostBinding,
                args: ['class']
            }] } });

function animationCompleted(el, maxDurationInMs = 3000) {
    const animations = el.getAnimations({ subtree: true }).map((a) => a.finished);
    return Promise.race([
        Promise.all(animations),
        timeOut(maxDurationInMs)
    ]);
}
function timeOut(maxDurationInMs) {
    return new Promise((resolve) => {
        setTimeout(resolve, maxDurationInMs);
    });
}

class DialogScrollShadowDirective {
    constructor(element, ngZone, cd) {
        this.element = element;
        this.ngZone = ngZone;
        this.cd = cd;
        this.dialogBody = null;
        this.dialogActions = null;
    }
    onWindowResize() {
        this.ngZone.runOutsideAngular(() => {
            this.setBoxShadowIfDialogScrollable();
        });
    }
    ngAfterContentInit() {
        this.setDialogElements();
        this.ngZone.runOutsideAngular(() => {
            this.setBoxShadowIfDialogScrollable();
        });
    }
    setBoxShadowIfDialogScrollable() {
        if (this.dialogBody && this.dialogBody.scrollHeight > this.dialogBody.clientHeight) {
            this.dialogActions?.classList.add('has-shadow');
            this.cd.detectChanges();
        }
        else {
            this.dialogActions?.classList.remove('has-shadow');
            this.cd.detectChanges();
        }
    }
    setDialogElements() {
        this.dialogBody = this.element.nativeElement.querySelector('.v-dialog-body');
        this.dialogActions = this.element.nativeElement.querySelector('.v-dialog-actions');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: DialogScrollShadowDirective, deps: [{ token: i0.ElementRef }, { token: i0.NgZone }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.9", type: DialogScrollShadowDirective, selector: "[vpg-dialog-scroll-shadow]", host: { listeners: { "window:resize": "onWindowResize($event)" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: DialogScrollShadowDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[vpg-dialog-scroll-shadow]'
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.NgZone }, { type: i0.ChangeDetectorRef }], propDecorators: { onWindowResize: [{
                type: HostListener,
                args: ['window:resize', ['$event']]
            }] } });

class DialogOverlayComponent {
    constructor(elementRef, dialogRef, document) {
        this.elementRef = elementRef;
        this.dialogRef = dialogRef;
        this.document = document;
        this.templateRef = null;
        this.className = '';
        this.backdropClick = true;
    }
    ngAfterViewInit() {
        this.setTriggerElement();
        this.focusCloseButton();
        this.disableBodyScroll();
    }
    ngOnDestroy() {
        this.enableBodyScroll();
    }
    animateRemoving() {
        const el = this.elementRef.nativeElement;
        el.classList.add('removing');
        return animationCompleted(el);
    }
    attach(component) {
        if (component instanceof ComponentRef) {
            this.slot.insert(component.hostView);
            setTimeout(() => {
                this.attachDialogHostClass();
            });
        }
        else {
            this.templateRef = component;
        }
    }
    onEscape() {
        this.dialogRef.pop();
        this.triggerElement.focus();
    }
    onClick(event) {
        if (event.target === this.elementRef.nativeElement && this.backdropClick) {
            this.dialogRef.close(false);
        }
    }
    get ariaLabelledBy() {
        return 'vpg-dialog-title';
    }
    get role() {
        return 'dialog';
    }
    get ariaModal() {
        return true;
    }
    get classNames() {
        return {
            'v-dialog-overlay': true,
            'has-backdrop': true
        };
    }
    focusCloseButton() {
        const closeButton = this.elementRef.nativeElement.querySelector('#vpg-dialog-close-button');
        if (closeButton) {
            closeButton.focus();
            return;
        }
        const actionButton = this.elementRef.nativeElement.querySelector('.v-button');
        actionButton.focus();
    }
    setTriggerElement() {
        this.triggerElement = document.activeElement;
    }
    attachDialogHostClass() {
        const firstChild = document.querySelector('.v-dialog:first-child');
        const hostElement = firstChild.childNodes[0];
        hostElement.classList.add('v-dialog-host');
    }
    disableBodyScroll() {
        this.document.body.classList.add('overflow-hidden');
    }
    enableBodyScroll() {
        this.document.body.classList.remove('overflow-hidden');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: DialogOverlayComponent, deps: [{ token: i0.ElementRef }, { token: DialogRef }, { token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: DialogOverlayComponent, selector: "vpg-dialog-overlay", inputs: { className: "className", backdropClick: "backdropClick" }, host: { listeners: { "keydown.escape": "onEscape($event)", "click": "onClick($event)" }, properties: { "[attr.aria-labelledby]": "this.ariaLabelledBy", "attr.role": "this.role", "attr.aria-modal": "this.ariaModal", "class": "this.classNames" } }, viewQueries: [{ propertyName: "slot", first: true, predicate: ["slot"], descendants: true, read: ViewContainerRef, static: true }], ngImport: i0, template: `
        <div
            class="v-dialog"
            [class]="className"
            vpg-dialog-focus-trap
            vpg-dialog-scroll-shadow
        >
            <ng-container #slot [ngTemplateOutlet]="templateRef"></ng-container>
        </div>
    `, isInline: true, dependencies: [{ kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: DialogFocusTrapDirective, selector: "[vpg-dialog-focus-trap]" }, { kind: "directive", type: DialogScrollShadowDirective, selector: "[vpg-dialog-scroll-shadow]" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: DialogOverlayComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vpg-dialog-overlay',
                    template: `
        <div
            class="v-dialog"
            [class]="className"
            vpg-dialog-focus-trap
            vpg-dialog-scroll-shadow
        >
            <ng-container #slot [ngTemplateOutlet]="templateRef"></ng-container>
        </div>
    `
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: DialogRef }, { type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }], propDecorators: { slot: [{
                type: ViewChild,
                args: ['slot', { static: true, read: ViewContainerRef }]
            }], className: [{
                type: Input
            }], backdropClick: [{
                type: Input
            }], onEscape: [{
                type: HostListener,
                args: ['keydown.escape', ['$event']]
            }], onClick: [{
                type: HostListener,
                args: ['click', ['$event']]
            }], ariaLabelledBy: [{
                type: HostBinding,
                args: ['[attr.aria-labelledby]']
            }], role: [{
                type: HostBinding,
                args: ['attr.role']
            }], ariaModal: [{
                type: HostBinding,
                args: ['attr.aria-modal']
            }], classNames: [{
                type: HostBinding,
                args: ['class']
            }] } });

class DialogModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: DialogModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.9", ngImport: i0, type: DialogModule, declarations: [DialogComponent,
            DialogOverlayComponent,
            DialogHeaderComponent,
            DialogBodyComponent,
            DialogActionsComponent,
            DialogFocusTrapDirective,
            DialogScrollShadowDirective], imports: [CommonModule,
            IconModule,
            ButtonModule], exports: [DialogComponent,
            DialogHeaderComponent,
            DialogBodyComponent,
            DialogActionsComponent,
            DialogFocusTrapDirective,
            DialogScrollShadowDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: DialogModule, providers: [
            DialogStack,
            DialogService,
            DialogRef,
            { provide: VPG_DIALOG_OVERLAY, useValue: DialogOverlayComponent }
        ], imports: [CommonModule,
            IconModule,
            ButtonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: DialogModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        IconModule,
                        ButtonModule
                    ],
                    declarations: [
                        DialogComponent,
                        DialogOverlayComponent,
                        DialogHeaderComponent,
                        DialogBodyComponent,
                        DialogActionsComponent,
                        DialogFocusTrapDirective,
                        DialogScrollShadowDirective
                    ],
                    exports: [
                        DialogComponent,
                        DialogHeaderComponent,
                        DialogBodyComponent,
                        DialogActionsComponent,
                        DialogFocusTrapDirective,
                        DialogScrollShadowDirective
                    ],
                    providers: [
                        DialogStack,
                        DialogService,
                        DialogRef,
                        { provide: VPG_DIALOG_OVERLAY, useValue: DialogOverlayComponent }
                    ]
                }]
        }] });

class DippingDotsLoader {
    constructor() {
        this.success = false;
        this.checkmarkIcon = 'assets/images/checkmark-icon.svg';
        this.role = 'status';
    }
    get classNames() {
        return {
            ['v-dipping-dots-loader']: true,
            ['is-success']: this.success
        };
    }
    ngOnChanges(changes) {
        if (changes.success) {
            this.role = this.success ? 'image' : 'status';
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: DippingDotsLoader, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: DippingDotsLoader, selector: "vpg-dipping-dots-loader", inputs: { success: "success" }, host: { properties: { "role": "this.role", "class": "this.classNames" } }, usesOnChanges: true, ngImport: i0, template: "<ng-container *ngIf=\"!success\">\n    <div class=\"v-dot center\"></div>\n    <div class=\"v-dot v-upper-left-dot\"></div>\n    <div class=\"v-dot v-upper-right-dot\"></div>\n    <div class=\"v-dot v-lower-left-dot\"></div>\n    <div class=\"v-dot v-lower-right-dot\"></div>\n</ng-container>\n<div class=\"v-dot center\" *ngIf=\"success\">\n    <img\n        [src]=\"checkmarkIcon\"\n        alt=\"\"\n    />\n</div>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: DippingDotsLoader, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-dipping-dots-loader', template: "<ng-container *ngIf=\"!success\">\n    <div class=\"v-dot center\"></div>\n    <div class=\"v-dot v-upper-left-dot\"></div>\n    <div class=\"v-dot v-upper-right-dot\"></div>\n    <div class=\"v-dot v-lower-left-dot\"></div>\n    <div class=\"v-dot v-lower-right-dot\"></div>\n</ng-container>\n<div class=\"v-dot center\" *ngIf=\"success\">\n    <img\n        [src]=\"checkmarkIcon\"\n        alt=\"\"\n    />\n</div>\n" }]
        }], propDecorators: { success: [{
                type: Input
            }], role: [{
                type: HostBinding,
                args: ['role']
            }], classNames: [{
                type: HostBinding,
                args: ['class']
            }] } });

class DippingDotsLoaderModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: DippingDotsLoaderModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.9", ngImport: i0, type: DippingDotsLoaderModule, declarations: [DippingDotsLoader], imports: [CommonModule], exports: [DippingDotsLoader] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: DippingDotsLoaderModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: DippingDotsLoaderModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule
                    ],
                    declarations: [
                        DippingDotsLoader
                    ],
                    exports: [
                        DippingDotsLoader
                    ]
                }]
        }] });

const DROPDOWN_TOKEN = new InjectionToken('Dropdown');

class DropdownListPopoutDirective {
    constructor(dropdown, element) {
        this.dropdown = dropdown;
        this.element = element;
        this.scrollableContainerElement = null;
    }
    ngAfterContentInit() {
        if (!this.isFixedPosition()) {
            return;
        }
        this.setListTopPosition();
        this.setScrollableContainerElement();
        this.scrollableContainerElement?.addEventListener('scroll', this.handleListPosition.bind(this));
    }
    ngOnDestroy() {
        if (!this.isFixedPosition()) {
            return;
        }
        this.scrollableContainerElement?.removeEventListener('scroll', this.handleListPosition.bind(this));
    }
    setScrollableContainerElement() {
        this.scrollableContainerElement = this.dropdown.scrollableContainer?.nativeElement;
    }
    setListTopPosition() {
        this.element.nativeElement.style.top = this.getInputBottomPosition() + 'px';
    }
    handleListPosition() {
        const inputBottomPosition = this.getInputBottomPosition();
        const scrollableContainerBottomPosition = this.scrollableContainerElement?.getBoundingClientRect().bottom ?? 0;
        const isInputBottomVisible = scrollableContainerBottomPosition >= inputBottomPosition;
        if (this.dropdown.listOpen && !isInputBottomVisible) {
            this.dropdown.collapseList();
            return;
        }
        this.setListTopPosition();
    }
    getInputBottomPosition() {
        return this.dropdown.inputElement.nativeElement.getBoundingClientRect().bottom;
    }
    isFixedPosition() {
        return this.dropdown.listPosition === 'fixed';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: DropdownListPopoutDirective, deps: [{ token: DROPDOWN_TOKEN }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.9", type: DropdownListPopoutDirective, selector: "[vpg-dropdown-list-popout]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: DropdownListPopoutDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[vpg-dropdown-list-popout]'
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [DROPDOWN_TOKEN]
                }] }, { type: i0.ElementRef }] });

class DropdownListOptionDimensionsDirective {
    constructor(elementRef) {
        this.elementRef = elementRef;
        this.options = [];
    }
    ngOnChanges() {
        setTimeout(() => {
            this.setImageContainerWidth();
        });
    }
    ngAfterViewInit() {
        this.setListOptionsHeight();
        this.setImageContainerWidth();
    }
    setListOptionsHeight() {
        const options = this.elementRef.nativeElement.querySelectorAll('.v-dropdown-list-option');
        let maxHeight = 0;
        options.forEach((element) => {
            const elementHeight = element.offsetHeight;
            if (elementHeight > maxHeight) {
                maxHeight = elementHeight;
            }
        });
        options.forEach((element) => {
            element.style.setProperty('min-height', `${maxHeight}px`);
        });
    }
    setImageContainerWidth() {
        const options = this.elementRef.nativeElement.querySelectorAll('.v-dropdown-option-image');
        let maxWidth = 0;
        options.forEach((element) => {
            const elementHeight = element.offsetWidth;
            if (elementHeight > maxWidth) {
                maxWidth = elementHeight;
            }
        });
        if (!maxWidth) {
            return;
        }
        options.forEach((element) => {
            element.style.setProperty('margin-inline-end', '10px');
            element.style.setProperty('width', `${maxWidth}px`);
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: DropdownListOptionDimensionsDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.9", type: DropdownListOptionDimensionsDirective, selector: "[vpg-dropdown-list-option-dimensions]", inputs: { options: "options" }, usesOnChanges: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: DropdownListOptionDimensionsDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[vpg-dropdown-list-option-dimensions]'
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { options: [{
                type: Input
            }] } });

class DropdownNextComponent extends AbstractFormComponent {
    get canShowErrors() {
        return Boolean(this.control?.touched && this.control?.invalid && this.control?.errors);
    }
    get listWidth() {
        return this.inputElement.nativeElement.offsetWidth;
    }
    get listMaxHeightCalculated() {
        const inputBottomPosition = this.inputElement.nativeElement.getBoundingClientRect().bottom;
        const marginBottom = 10;
        if (this.listPosition === 'fixed' && inputBottomPosition + this.listMaxHeight + marginBottom > window.innerHeight) {
            return window.innerHeight - inputBottomPosition - marginBottom;
        }
        return this.listMaxHeight;
    }
    constructor(injector, ngZone, elementRef) {
        super();
        this.injector = injector;
        this.ngZone = ngZone;
        this.elementRef = elementRef;
        this.name = '';
        this.label = '';
        this.placeholder = '';
        this.ariaLabel = '';
        this.selectedOption = '';
        this.required = false;
        this.options = [];
        this.optionLabel = 'name';
        this.optionDescription = 'description';
        this.hint = '';
        this.maxLength = null;
        this.listMaxHeight = 245;
        this.listPosition = 'absolute';
        this.optionsShown = new EventEmitter();
        this.customHint = undefined;
        this.customAddons = undefined;
        this.customErrors = undefined;
        this.customOption = undefined;
        this.control = null;
        this.listOpen = false;
        this.filteredOptions = [];
        this.term$ = new BehaviorSubject('');
        this.destroy$ = new Subject();
    }
    ngOnChanges(changes) {
        if (changes.options) {
            this.filteredOptions = changes.options.currentValue;
        }
    }
    ngOnInit() {
        this.filteredOptions = this.options;
        this.term$.pipe(takeUntil(this.destroy$), debounceTime(300), distinctUntilChanged()).subscribe((term) => {
            this.filteredOptions = this.searchOptions(term, this.options);
            this.optionsShown.emit();
        });
    }
    ngAfterViewInit() {
        const control = this.getNgControl();
        this.setName(control);
        this.setControl(control);
        this.setInputValue(this.value || this.placeholder);
    }
    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }
    onKeyUp(e) {
        this.term$.next(e.target.value);
    }
    onKeypress(e) {
        if (this.maxLength && this.value.length === this.maxLength) {
            e.preventDefault();
        }
    }
    onArrowDown() {
        this.expandList();
    }
    onChange(e) {
        const newValue = e.target.value;
        this.notifyValueChange(newValue);
        this.writeValue(newValue);
    }
    onBlur() {
        this.notifyTouched();
        this.collapseList();
        this.setInputValue(this.value || this.placeholder);
        this.term$.next('');
    }
    onClick() {
        this.setInputValue('');
        this.expandList();
    }
    onFocus() {
        this.setInputValue('');
    }
    expandList() {
        setTimeout(() => {
            this.listOpen = true;
            this.optionsShown.emit();
        });
    }
    collapseList() {
        setTimeout(() => {
            this.listOpen = false;
            this.optionsShown.emit();
        });
    }
    toggleListOpen() {
        if (this.disabled) {
            return;
        }
        if (this.listOpen) {
            this.collapseList();
        }
        else {
            this.expandList();
        }
    }
    preventMousedown(e) {
        e.preventDefault();
    }
    selectOption(option) {
        const newValue = option[this.optionLabel];
        this.writeValue(newValue);
        this.setInputValue(newValue);
        this.focusInput();
        this.collapseList();
    }
    setInputAriaActiveDescendant(option) {
        this.inputElement.nativeElement.setAttribute('aria-activedescendant', option.id);
    }
    onIconOutsideClick(target) {
        this.ngZone.runOutsideAngular(() => {
            if (!this.elementRef.nativeElement.contains(target) || (!this.iconElement.nativeElement.contains(target) && this.listOpen)) {
                this.ngZone.run(() => {
                    this.collapseList();
                });
            }
        });
    }
    setInputValue(value) {
        this.inputElement.nativeElement.value = value;
    }
    searchOptions(term, options) {
        const searchSubstring = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        return options.filter((option) => {
            return (new RegExp(searchSubstring, 'gi').test(option[this.optionLabel]) ||
                new RegExp(searchSubstring, 'gi').test(option[this.optionDescription]));
        });
    }
    getNgControl() {
        return this.injector.get(NgControl);
    }
    setControl(ngControl) {
        this.control = ngControl.control;
    }
    focusInput() {
        this.inputElement.nativeElement.focus();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: DropdownNextComponent, deps: [{ token: i0.Injector }, { token: i0.NgZone }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: DropdownNextComponent, selector: "vpg-dropdown-next", inputs: { name: "name", label: "label", placeholder: "placeholder", ariaLabel: "ariaLabel", selectedOption: "selectedOption", required: "required", options: "options", optionLabel: "optionLabel", optionDescription: "optionDescription", hint: "hint", maxLength: "maxLength", listMaxHeight: "listMaxHeight", listPosition: "listPosition", scrollableContainer: "scrollableContainer" }, outputs: { optionsShown: "optionsShown" }, host: { listeners: { "document:click": "onIconOutsideClick($event.target)" }, classAttribute: "v-input-field v-dropdown" }, providers: [valueAccessor(DropdownNextComponent), { provide: DROPDOWN_TOKEN, useExisting: DropdownNextComponent }], queries: [{ propertyName: "customHint", first: true, predicate: ["hint"], descendants: true, read: TemplateRef }, { propertyName: "customAddons", first: true, predicate: ["addons"], descendants: true, read: TemplateRef }, { propertyName: "customErrors", first: true, predicate: ["errors"], descendants: true, read: TemplateRef }, { propertyName: "customOption", first: true, predicate: ["option"], descendants: true, read: TemplateRef }], viewQueries: [{ propertyName: "inputElement", first: true, predicate: ["input"], descendants: true, read: ElementRef }, { propertyName: "iconElement", first: true, predicate: ["icon"], descendants: true, read: ElementRef }], usesInheritance: true, usesOnChanges: true, ngImport: i0, template: "<label\n    class=\"v-control-label h4\"\n    id=\"v-control-{{name}}-label\"\n    [for]=\"name\">\n    {{ label }}\n    <span\n        *ngIf=\"required\"\n        class=\"v-control-required h4\"\n        aria-hidden=\"true\">\n        *\n    </span>\n    <ng-container\n        *ngIf=\"customAddons\"\n        [ngTemplateOutlet]=\"customAddons\"></ng-container>\n</label>\n\n<div\n    class=\"v-control-container\"\n    [class.has-popup]=\"listOpen\">\n    <input\n        #input\n        type=\"text\"\n        class=\"v-control-input has-suffix\"\n        [class.is-dirty]=\"control?.touched && control?.invalid\"\n        autocomplete=\"off\"\n        [disabled]=\"disabled\"\n        id=\"v-control-{{name}}-input\"\n        [name]=\"name\"\n        [value]=\"value\"\n        [placeholder]=\"value || placeholder\"\n        role=\"combobox\"\n        aria-haspopup=\"listbox\"\n        (click)=\"onClick()\"\n        (focus)=\"onFocus()\"\n        (blur)=\"onBlur()\"\n        (keyup.arrowdown)=\"onArrowDown()\"\n        (keyup)=\"onKeyUp($event)\"\n        (keyup.esc)=\"collapseList()\"\n        (keydown)=\"expandList()\"\n        [attr.aria-invalid]=\"control?.touched && control?.invalid\"\n        [attr.aria-required]=\"required\"\n        [attr.aria-expanded]=\"listOpen\"\n        attr.aria-describedby=\"v-control-{{name}}-empty-list v-control-{{name}}-errors v-control-{{name}}-hint\"\n        attr.aria-controls=\"v-control-{{name}}-list\"\n        [attr.aria-label]=\"ariaLabel\"\n        [attr.aria-activedescendant]=\"\" />\n    <vpg-control-suffix>\n        <vpg-icon-next\n            #icon\n            class=\"v-dropdown-handle\"\n            [class.is-disabled]=\"disabled\"\n            [icon]=\"listOpen ? 'angle-up' : 'angle-down'\"\n            [attr.aria-disabled]=\"disabled\"\n            [attr.aria-expanded]=\"listOpen\"\n            (click)=\"toggleListOpen()\"\n            (mousedown)=\"preventMousedown($event)\"></vpg-icon-next>\n    </vpg-control-suffix>\n\n    <ng-container *ngIf=\"listOpen\">\n        <ul\n            *ngIf=\"filteredOptions.length > 0 else emptyList\"\n            [style.width.px]=\"listWidth\"\n            [style.max-height.px]=\"listMaxHeightCalculated\"\n            class=\"v-dropdown-list has-position-{{listPosition}}\"\n            role=\"listbox\"\n            id=\"v-control-{{name}}-list\"\n            attr.aria-labelledby=\"{{name}}-label\"\n            vpg-dropdown-list-popout\n            vpg-dropdown-list-option-dimensions\n            [options]=\"filteredOptions\"\n            vpg-keyboard-navigate\n            controlOptionClassName=\"v-dropdown-list-option\"\n            (optionFocused)=\"setInputAriaActiveDescendant($event)\">\n            <li\n                *ngFor=\"let option of filteredOptions; let index = index\"\n                role=\"option\"\n                tabindex=\"-1\"\n                id=\"v-control-{{name}}-option-{{index}}\"\n                class=\"v-dropdown-list-option\"\n                [class.is-selected]=\"value === option[optionLabel]\"\n                [attr.aria-selected]=\"value === option[optionLabel]\"\n                (click)=\"selectOption(option)\"\n                (mousedown)=\"preventMousedown($event)\">\n                <div\n                    class=\"v-dropdown-option-image\"\n                    [attr.aria-hidden]=\"true\">\n                    <vpg-icon-next\n                        *ngIf=\"option.icon\"\n                        size=\"lg\"\n                        [icon]=\"option.icon\"\n                        id=\"v-control-{{name}}-option-{{index}}-icon\"></vpg-icon-next>\n                    <img\n                        *ngIf=\"option.img\"\n                        [src]=\"option.img\"\n                        class=\"v-dropdown-list-img-{{option.imgSize}}\"\n                        alt=\"\"\n                        id=\"v-control-{{name}}-option-{{index}}-img\" />\n                </div>\n                <div class=\"v-dropdown-text-container\">\n                    <p\n                        id=\"v-control-{{name}}-option-{{index}}-value\"\n                        class=\"v-dropdown-list-label\"\n                        [attr.aria-label]=\"option.textAriaLabel\">\n                        {{ option[optionLabel] }}\n                    </p>\n                    <p\n                        *ngIf=\"option[optionDescription]\"\n                        class=\"v-dropdown-list-description\"\n                        id=\"v-control-{{name}}-option-{{index}}-value\"\n                        [attr.aria-label]=\"option.descriptionAriaLabel\">\n                        {{ option[optionDescription] }}\n                    </p>\n                </div>\n                <vpg-icon-next\n                    class=\"v-dropdown-list-indicator\"\n                    [class.is-selected]=\"value === option[optionLabel]\"\n                    [icon]=\"value === option[optionLabel] ? 'fas fa-check-circle' : 'far fa-circle'\"></vpg-icon-next>\n            </li>\n        </ul>\n    </ng-container>\n</div>\n\n<ng-container\n    [ngTemplateOutlet]=\"canShowErrors\n    ? errorsTemplate\n    : hintTemplate\"></ng-container>\n\n<ng-template #errorsTemplate>\n    <vpg-control-errors id=\"v-control-{{name}}-errors\">\n        <ng-container\n            *ngIf=\"customErrors\"\n            [ngTemplateOutlet]=\"customErrors\"></ng-container>\n    </vpg-control-errors>\n</ng-template>\n\n<ng-template #hintTemplate>\n    <vpg-control-footer\n        [name]=\"name\"\n        [hint]=\"hint\"\n        [customHint]=\"customHint\"\n        [maxLength]=\"maxLength\"\n        [currentLength]=\"value?.length\"></vpg-control-footer>\n</ng-template>\n\n<ng-template #emptyList>\n    <vpg-control-empty-list id=\"v-control-{{name}}-empty-list\"></vpg-control-empty-list>\n</ng-template>\n", dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "component", type: IconNextComponent, selector: "vpg-icon-next", inputs: ["icon", "size", "prefix", "screenReaderText"] }, { kind: "component", type: ControlErrorsComponent, selector: "vpg-control-errors" }, { kind: "component", type: ControlSuffixComponent, selector: "vpg-control-suffix" }, { kind: "component", type: ControlEmptyListComponent, selector: "vpg-control-empty-list" }, { kind: "component", type: ControlFooterComponent, selector: "vpg-control-footer", inputs: ["name", "hint", "customHint", "maxLength", "currentLength"] }, { kind: "directive", type: KeyboardNavigateDirective, selector: "[vpg-keyboard-navigate]", inputs: ["controlOptionClassName"], outputs: ["optionFocused"] }, { kind: "directive", type: DropdownListPopoutDirective, selector: "[vpg-dropdown-list-popout]" }, { kind: "directive", type: DropdownListOptionDimensionsDirective, selector: "[vpg-dropdown-list-option-dimensions]", inputs: ["options"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: DropdownNextComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-dropdown-next', providers: [valueAccessor(DropdownNextComponent), { provide: DROPDOWN_TOKEN, useExisting: DropdownNextComponent }], host: {
                        class: 'v-input-field v-dropdown'
                    }, template: "<label\n    class=\"v-control-label h4\"\n    id=\"v-control-{{name}}-label\"\n    [for]=\"name\">\n    {{ label }}\n    <span\n        *ngIf=\"required\"\n        class=\"v-control-required h4\"\n        aria-hidden=\"true\">\n        *\n    </span>\n    <ng-container\n        *ngIf=\"customAddons\"\n        [ngTemplateOutlet]=\"customAddons\"></ng-container>\n</label>\n\n<div\n    class=\"v-control-container\"\n    [class.has-popup]=\"listOpen\">\n    <input\n        #input\n        type=\"text\"\n        class=\"v-control-input has-suffix\"\n        [class.is-dirty]=\"control?.touched && control?.invalid\"\n        autocomplete=\"off\"\n        [disabled]=\"disabled\"\n        id=\"v-control-{{name}}-input\"\n        [name]=\"name\"\n        [value]=\"value\"\n        [placeholder]=\"value || placeholder\"\n        role=\"combobox\"\n        aria-haspopup=\"listbox\"\n        (click)=\"onClick()\"\n        (focus)=\"onFocus()\"\n        (blur)=\"onBlur()\"\n        (keyup.arrowdown)=\"onArrowDown()\"\n        (keyup)=\"onKeyUp($event)\"\n        (keyup.esc)=\"collapseList()\"\n        (keydown)=\"expandList()\"\n        [attr.aria-invalid]=\"control?.touched && control?.invalid\"\n        [attr.aria-required]=\"required\"\n        [attr.aria-expanded]=\"listOpen\"\n        attr.aria-describedby=\"v-control-{{name}}-empty-list v-control-{{name}}-errors v-control-{{name}}-hint\"\n        attr.aria-controls=\"v-control-{{name}}-list\"\n        [attr.aria-label]=\"ariaLabel\"\n        [attr.aria-activedescendant]=\"\" />\n    <vpg-control-suffix>\n        <vpg-icon-next\n            #icon\n            class=\"v-dropdown-handle\"\n            [class.is-disabled]=\"disabled\"\n            [icon]=\"listOpen ? 'angle-up' : 'angle-down'\"\n            [attr.aria-disabled]=\"disabled\"\n            [attr.aria-expanded]=\"listOpen\"\n            (click)=\"toggleListOpen()\"\n            (mousedown)=\"preventMousedown($event)\"></vpg-icon-next>\n    </vpg-control-suffix>\n\n    <ng-container *ngIf=\"listOpen\">\n        <ul\n            *ngIf=\"filteredOptions.length > 0 else emptyList\"\n            [style.width.px]=\"listWidth\"\n            [style.max-height.px]=\"listMaxHeightCalculated\"\n            class=\"v-dropdown-list has-position-{{listPosition}}\"\n            role=\"listbox\"\n            id=\"v-control-{{name}}-list\"\n            attr.aria-labelledby=\"{{name}}-label\"\n            vpg-dropdown-list-popout\n            vpg-dropdown-list-option-dimensions\n            [options]=\"filteredOptions\"\n            vpg-keyboard-navigate\n            controlOptionClassName=\"v-dropdown-list-option\"\n            (optionFocused)=\"setInputAriaActiveDescendant($event)\">\n            <li\n                *ngFor=\"let option of filteredOptions; let index = index\"\n                role=\"option\"\n                tabindex=\"-1\"\n                id=\"v-control-{{name}}-option-{{index}}\"\n                class=\"v-dropdown-list-option\"\n                [class.is-selected]=\"value === option[optionLabel]\"\n                [attr.aria-selected]=\"value === option[optionLabel]\"\n                (click)=\"selectOption(option)\"\n                (mousedown)=\"preventMousedown($event)\">\n                <div\n                    class=\"v-dropdown-option-image\"\n                    [attr.aria-hidden]=\"true\">\n                    <vpg-icon-next\n                        *ngIf=\"option.icon\"\n                        size=\"lg\"\n                        [icon]=\"option.icon\"\n                        id=\"v-control-{{name}}-option-{{index}}-icon\"></vpg-icon-next>\n                    <img\n                        *ngIf=\"option.img\"\n                        [src]=\"option.img\"\n                        class=\"v-dropdown-list-img-{{option.imgSize}}\"\n                        alt=\"\"\n                        id=\"v-control-{{name}}-option-{{index}}-img\" />\n                </div>\n                <div class=\"v-dropdown-text-container\">\n                    <p\n                        id=\"v-control-{{name}}-option-{{index}}-value\"\n                        class=\"v-dropdown-list-label\"\n                        [attr.aria-label]=\"option.textAriaLabel\">\n                        {{ option[optionLabel] }}\n                    </p>\n                    <p\n                        *ngIf=\"option[optionDescription]\"\n                        class=\"v-dropdown-list-description\"\n                        id=\"v-control-{{name}}-option-{{index}}-value\"\n                        [attr.aria-label]=\"option.descriptionAriaLabel\">\n                        {{ option[optionDescription] }}\n                    </p>\n                </div>\n                <vpg-icon-next\n                    class=\"v-dropdown-list-indicator\"\n                    [class.is-selected]=\"value === option[optionLabel]\"\n                    [icon]=\"value === option[optionLabel] ? 'fas fa-check-circle' : 'far fa-circle'\"></vpg-icon-next>\n            </li>\n        </ul>\n    </ng-container>\n</div>\n\n<ng-container\n    [ngTemplateOutlet]=\"canShowErrors\n    ? errorsTemplate\n    : hintTemplate\"></ng-container>\n\n<ng-template #errorsTemplate>\n    <vpg-control-errors id=\"v-control-{{name}}-errors\">\n        <ng-container\n            *ngIf=\"customErrors\"\n            [ngTemplateOutlet]=\"customErrors\"></ng-container>\n    </vpg-control-errors>\n</ng-template>\n\n<ng-template #hintTemplate>\n    <vpg-control-footer\n        [name]=\"name\"\n        [hint]=\"hint\"\n        [customHint]=\"customHint\"\n        [maxLength]=\"maxLength\"\n        [currentLength]=\"value?.length\"></vpg-control-footer>\n</ng-template>\n\n<ng-template #emptyList>\n    <vpg-control-empty-list id=\"v-control-{{name}}-empty-list\"></vpg-control-empty-list>\n</ng-template>\n" }]
        }], ctorParameters: () => [{ type: i0.Injector }, { type: i0.NgZone }, { type: i0.ElementRef }], propDecorators: { name: [{
                type: Input
            }], label: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], selectedOption: [{
                type: Input
            }], required: [{
                type: Input
            }], options: [{
                type: Input
            }], optionLabel: [{
                type: Input
            }], optionDescription: [{
                type: Input
            }], hint: [{
                type: Input
            }], maxLength: [{
                type: Input
            }], listMaxHeight: [{
                type: Input
            }], listPosition: [{
                type: Input
            }], scrollableContainer: [{
                type: Input
            }], optionsShown: [{
                type: Output
            }], inputElement: [{
                type: ViewChild,
                args: ['input', { read: ElementRef }]
            }], iconElement: [{
                type: ViewChild,
                args: ['icon', { read: ElementRef }]
            }], customHint: [{
                type: ContentChild,
                args: ['hint', { read: TemplateRef }]
            }], customAddons: [{
                type: ContentChild,
                args: ['addons', { read: TemplateRef }]
            }], customErrors: [{
                type: ContentChild,
                args: ['errors', { read: TemplateRef }]
            }], customOption: [{
                type: ContentChild,
                args: ['option', { read: TemplateRef }]
            }], onIconOutsideClick: [{
                type: HostListener,
                args: ['document:click', ['$event.target']]
            }] } });

class DropdownModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: DropdownModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.9", ngImport: i0, type: DropdownModule, declarations: [DropdownNextComponent,
            DropdownListPopoutDirective,
            DropdownListOptionDimensionsDirective], imports: [CommonModule,
            FormsModule,
            ReactiveFormsModule,
            IconModule,
            ControlModule,
            KeyboardNavigateDirective], exports: [DropdownNextComponent,
            DropdownListPopoutDirective,
            DropdownListOptionDimensionsDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: DropdownModule, imports: [CommonModule,
            FormsModule,
            ReactiveFormsModule,
            IconModule,
            ControlModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: DropdownModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule,
                        ReactiveFormsModule,
                        IconModule,
                        ControlModule,
                        KeyboardNavigateDirective
                    ],
                    declarations: [
                        DropdownNextComponent,
                        DropdownListPopoutDirective,
                        DropdownListOptionDimensionsDirective
                    ],
                    exports: [
                        DropdownNextComponent,
                        DropdownListPopoutDirective,
                        DropdownListOptionDimensionsDirective
                    ]
                }]
        }] });

class FilterChipComponent {
    constructor() {
        this.id = '';
        this.label = '';
        this.value = '';
        this.active = false;
        this.disabled = false;
        this.activate = new EventEmitter();
    }
    toggleActivate() {
        this.activate.emit(!this.active);
    }
    get classNames() {
        return {
            ['is-active']: this.active,
            ['is-disabled']: this.disabled
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: FilterChipComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: FilterChipComponent, selector: "vpg-filter-chip", inputs: { id: "id", label: "label", value: "value", active: "active", disabled: "disabled" }, outputs: { activate: "activate" }, host: { properties: { "id": "id", "class": "this.classNames" }, classAttribute: "v-filter-chip" }, ngImport: i0, template: "<button\n    id=\"{{id}}-button\"\n    class=\"v-filter-chip-button\"\n    type=\"button\"\n    role=\"option\"\n    [attr.aria-selected]=\"active\"\n    [disabled]=\"disabled\"\n    (click)=\"toggleActivate()\"\n>\n    {{ label }}\n</button>\n" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: FilterChipComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-filter-chip', host: {
                        'class': 'v-filter-chip',
                        '[id]': 'id'
                    }, template: "<button\n    id=\"{{id}}-button\"\n    class=\"v-filter-chip-button\"\n    type=\"button\"\n    role=\"option\"\n    [attr.aria-selected]=\"active\"\n    [disabled]=\"disabled\"\n    (click)=\"toggleActivate()\"\n>\n    {{ label }}\n</button>\n" }]
        }], propDecorators: { id: [{
                type: Input
            }], label: [{
                type: Input
            }], value: [{
                type: Input
            }], active: [{
                type: Input
            }], disabled: [{
                type: Input
            }], activate: [{
                type: Output
            }], classNames: [{
                type: HostBinding,
                args: ['class']
            }] } });

class FilterChipsGroupComponent {
    constructor() {
        this.selectionRequired = false;
        this.multiselect = false;
        this.selected = new EventEmitter();
        this.chipsList = new QueryList();
        this.contentChanges = new Subscription();
        this.onChange = Function.prototype;
        this.onTouch = Function.prototype;
    }
    ngAfterContentInit() {
        this.handleChips();
        this.contentChanges = this.chipsList.changes.subscribe((changes) => {
            this.chipsList = changes;
            this.handleChips();
        });
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouch = fn;
    }
    writeValue(value) {
        if (!value) {
            this.deactivateUnselectedChips();
            this.emitSelected();
            return;
        }
        if (this.multiselect) {
            if (!Array.isArray(value) || this.selectionRequired && !this.chips.some((chip) => value.includes(chip.value))) {
                return;
            }
            setTimeout(() => {
                this.chips.forEach((chip) => {
                    chip.active = value.includes(chip.value);
                });
                this.emitSelected();
            });
        }
        else {
            if (Array.isArray(value)) {
                return;
            }
            setTimeout(() => {
                const chip = this.findChipBy(value);
                if (chip) {
                    this.activateChip(chip);
                    this.deactivateUnselectedChips(chip);
                    this.emitSelected();
                }
            });
        }
    }
    ngOnDestroy() {
        this.unsubscribeFromChipsActivate();
        this.unsubscribeFromContentChanges();
    }
    handleChips() {
        this.chips.forEach((chip) => {
            chip.activate.subscribe((value) => {
                if (!value && !this.canUnselect()) {
                    return;
                }
                chip.active = value;
                if (this.multiselect) {
                    const selectedValues = this.getSelectedValues();
                    this.onChange(selectedValues);
                }
                else {
                    this.deactivateUnselectedChips(chip);
                    this.onChange(value ? chip.value : undefined);
                }
                this.emitSelected();
            });
        });
    }
    get chips() {
        return this.chipsList.toArray();
    }
    canUnselect() {
        return !this.selectionRequired ||
            (this.multiselect && this.getSelectedValues().length > 1);
    }
    findChipBy(value) {
        return this.chips.find((chip) => {
            return chip.value === value;
        });
    }
    activateChip(chip) {
        chip.active = true;
    }
    deactivateUnselectedChips(selectedChip) {
        this.chips.map((chip) => {
            if (chip !== selectedChip) {
                chip.active = false;
            }
        });
    }
    emitSelected() {
        if (this.multiselect) {
            this.selected.emit(this.getSelectedValues());
        }
        else {
            const selectedChip = this.chips.find((chip) => chip.active);
            this.selected.emit(selectedChip ? selectedChip.value : undefined);
        }
    }
    getSelectedValues() {
        return this.chips.filter((chip) => chip.active).map((chip) => chip.value);
    }
    unsubscribeFromChipsActivate() {
        this.chips.forEach((chip) => {
            chip.activate.unsubscribe();
        });
    }
    unsubscribeFromContentChanges() {
        this.contentChanges.unsubscribe();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: FilterChipsGroupComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: FilterChipsGroupComponent, selector: "vpg-filter-chips-group", inputs: { selectionRequired: "selectionRequired", multiselect: "multiselect" }, outputs: { selected: "selected" }, host: { attributes: { "role": "listbox" }, properties: { "attr.aria-multiselectable": "multiselect" }, classAttribute: "v-filter-chips-group" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => FilterChipsGroupComponent),
                multi: true
            }
        ], queries: [{ propertyName: "chipsList", predicate: FilterChipComponent }], ngImport: i0, template: '<ng-content />', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: FilterChipsGroupComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vpg-filter-chips-group',
                    template: '<ng-content />',
                    host: {
                        'class': 'v-filter-chips-group',
                        '[attr.aria-multiselectable]': 'multiselect',
                        'role': 'listbox'
                    },
                    providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => FilterChipsGroupComponent),
                            multi: true
                        }
                    ]
                }]
        }], propDecorators: { selectionRequired: [{
                type: Input
            }], multiselect: [{
                type: Input
            }], selected: [{
                type: Output
            }], chipsList: [{
                type: ContentChildren,
                args: [FilterChipComponent]
            }] } });

class FilterChipsGroupModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: FilterChipsGroupModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.9", ngImport: i0, type: FilterChipsGroupModule, declarations: [FilterChipComponent,
            FilterChipsGroupComponent], imports: [CommonModule], exports: [FilterChipComponent,
            FilterChipsGroupComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: FilterChipsGroupModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: FilterChipsGroupModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule
                    ],
                    declarations: [
                        FilterChipComponent,
                        FilterChipsGroupComponent
                    ],
                    exports: [
                        FilterChipComponent,
                        FilterChipsGroupComponent
                    ]
                }]
        }] });

class HeaderComponent {
    constructor() {
        this.id = '';
        this.title = '';
        this.description = '';
        this.imageUrl = '';
        this.alternativeText = '';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: HeaderComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: HeaderComponent, selector: "vpg-header", inputs: { id: "id", title: "title", description: "description", imageUrl: "imageUrl", alternativeText: "alternativeText" }, ngImport: i0, template: '', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: HeaderComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vpg-header',
                    template: ''
                }]
        }], propDecorators: { id: [{
                type: Input
            }], title: [{
                type: Input
            }], description: [{
                type: Input
            }], imageUrl: [{
                type: Input
            }], alternativeText: [{
                type: Input
            }] } });

class HeaderActionsComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: HeaderActionsComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: HeaderActionsComponent, selector: "vpg-header-actions", host: { classAttribute: "v-header-actions flex gap-15 h-fit" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: HeaderActionsComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vpg-header-actions',
                    template: '<ng-content></ng-content>',
                    host: {
                        'class': 'v-header-actions flex gap-15 h-fit'
                    }
                }]
        }] });

class SimpleHeaderComponent extends HeaderComponent {
    constructor(themeManagerService) {
        super();
        this.themeManagerService = themeManagerService;
        this.defaultTheme = true;
    }
    ngOnInit() {
        this.defaultTheme = this.themeManagerService.isDefaultThemeActive();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: SimpleHeaderComponent, deps: [{ token: ThemeManagerService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: SimpleHeaderComponent, selector: "vpg-simple-header", host: { classAttribute: "v-simple-header v-container overflow-hidden" }, usesInheritance: true, ngImport: i0, template: "<div\n    class=\"v-simple-header-image-wrapper\"\n    *ngIf=\"imageUrl\"\n>\n    <img\n        vpg-image\n        [src]=\"imageUrl\"\n        class=\"v-simple-header-image w-full h-full\"\n        [alt]=\"alternativeText\"\n    />\n    <svg\n        id=\"{{id}}-simple-header-image-wrapper\"\n        class=\"v-simple-header-svg-overlay w-full h-full\"\n        width=\"100%\"\n        height=\"100%\"\n        viewBox=\"0 0 182 87\"\n        preserveAspectRatio=\"xMidYMid slice\"\n        fill=\"none\"\n    >\n        <path\n            opacity=\"0.7\"\n            d=\"M182 144.25H89.4284C99.7907 144.279 110.832 141.187 122.567 134.93C144.999 122.968 159.54 105.019 166.474 81.17C173.401 57.3384 171.866 28.5816 161.075 -6.97969C150.28 -42.5528 136.043 -65.3308 118.494 -75.7796C100.903 -86.2541 81.1611 -85.7549 59.1519 -74.0206C37.1428 -62.2863 22.6437 -44.5291 15.4217 -20.8266C8.22284 2.81624 10.0231 32.8872 20.6437 67.889C31.2637 102.882 45.357 125.606 62.802 136.443C71.1229 141.61 79.9944 144.224 89.4284 144.25H0V-83.25H182V144.25Z\"\n            [attr.fill]=\"defaultTheme ? 'var(--default-bg-color-with-opacity)' : 'var(--themable-bg-color-with-opacity)'\"\n        />\n    </svg>\n</div>\n<div\n    id=\"{{id}}-simple-header-content-wrapper\"\n    class=\"v-simple-header-content-wrapper gap-20 justify-between flex\"\n>\n    <div\n        id=\"{{id}}-simple-header-content\"\n        class=\"v-simple-header-content flex flex-column justify-center align-center\"\n    >\n        <h1\n            id=\"{{id}}-simple-header-title\"\n            class=\"h1 my-none\"\n        >{{ title }}\n        </h1>\n        <p\n            *ngIf=\"description\"\n            id=\"{{id}}-simple-header-description\"\n            class=\"body-large mb-none mt-sm\"\n        >{{ description }}\n        </p>\n    </div>\n    <ng-content select=\"vpg-header-actions\"></ng-content>\n</div>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: ImageDirective, selector: "img[vpg-image], img[vpgImage]", inputs: ["fallbackSrc", "type", "size", "alt", "backgroundColor"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: SimpleHeaderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-simple-header', host: {
                        'class': 'v-simple-header v-container overflow-hidden',
                    }, template: "<div\n    class=\"v-simple-header-image-wrapper\"\n    *ngIf=\"imageUrl\"\n>\n    <img\n        vpg-image\n        [src]=\"imageUrl\"\n        class=\"v-simple-header-image w-full h-full\"\n        [alt]=\"alternativeText\"\n    />\n    <svg\n        id=\"{{id}}-simple-header-image-wrapper\"\n        class=\"v-simple-header-svg-overlay w-full h-full\"\n        width=\"100%\"\n        height=\"100%\"\n        viewBox=\"0 0 182 87\"\n        preserveAspectRatio=\"xMidYMid slice\"\n        fill=\"none\"\n    >\n        <path\n            opacity=\"0.7\"\n            d=\"M182 144.25H89.4284C99.7907 144.279 110.832 141.187 122.567 134.93C144.999 122.968 159.54 105.019 166.474 81.17C173.401 57.3384 171.866 28.5816 161.075 -6.97969C150.28 -42.5528 136.043 -65.3308 118.494 -75.7796C100.903 -86.2541 81.1611 -85.7549 59.1519 -74.0206C37.1428 -62.2863 22.6437 -44.5291 15.4217 -20.8266C8.22284 2.81624 10.0231 32.8872 20.6437 67.889C31.2637 102.882 45.357 125.606 62.802 136.443C71.1229 141.61 79.9944 144.224 89.4284 144.25H0V-83.25H182V144.25Z\"\n            [attr.fill]=\"defaultTheme ? 'var(--default-bg-color-with-opacity)' : 'var(--themable-bg-color-with-opacity)'\"\n        />\n    </svg>\n</div>\n<div\n    id=\"{{id}}-simple-header-content-wrapper\"\n    class=\"v-simple-header-content-wrapper gap-20 justify-between flex\"\n>\n    <div\n        id=\"{{id}}-simple-header-content\"\n        class=\"v-simple-header-content flex flex-column justify-center align-center\"\n    >\n        <h1\n            id=\"{{id}}-simple-header-title\"\n            class=\"h1 my-none\"\n        >{{ title }}\n        </h1>\n        <p\n            *ngIf=\"description\"\n            id=\"{{id}}-simple-header-description\"\n            class=\"body-large mb-none mt-sm\"\n        >{{ description }}\n        </p>\n    </div>\n    <ng-content select=\"vpg-header-actions\"></ng-content>\n</div>\n" }]
        }], ctorParameters: () => [{ type: ThemeManagerService }] });

class StyledHeaderComponent extends HeaderComponent {
    constructor(themeManagerService) {
        super();
        this.themeManagerService = themeManagerService;
        this.defaultTheme = true;
    }
    ngOnInit() {
        this.defaultTheme = this.themeManagerService.isDefaultThemeActive();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: StyledHeaderComponent, deps: [{ token: ThemeManagerService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: StyledHeaderComponent, selector: "vpg-styled-header", host: { properties: { "class.is-default-theme-background": "defaultTheme" }, classAttribute: "v-styled-header v-container gap-20 overflow-hidden" }, usesInheritance: true, ngImport: i0, template: "<div\n    id=\"{{id}}-header-content\"\n    class=\"v-styled-header-content flex flex-column justify-center gap-10\"\n>\n    <h1\n        id=\"{{id}}-styled-header-title\"\n        class=\"h1 my-none\">{{ title }}</h1>\n    <p\n        *ngIf=\"description\"\n        id=\"{{id}}-styled-header-description\"\n        class=\"body-large my-none\"\n    >{{ description }}</p>\n    <ng-content select=\"vpg-header-actions\"></ng-content>\n</div>\n<div\n    id=\"{{id}}-styled-header-image-wrapper\"\n    class=\"v-styled-header-image-overlay\"\n>\n    <svg\n        id=\"{{id}}-styled-header-default-image\"\n        class=\"v-styled-header-default-image\"\n        [ngClass]=\"imageUrl ? 'is-rotated' : 'is-visible'\"\n        alt=\"\"\n        width=\"100%\"\n        height=\"100%\"\n        viewBox=\"0 0 413 562\"\n        preserveAspectRatio=\"xMidYMid slice\"\n        fill=\"none\"\n    >\n        <path\n            fill-rule=\"evenodd\"\n            clip-rule=\"evenodd\" d=\"M413.008 0.00250244C342.5 0.349481 307.735 5.50895 254.868 23.1999C200.263 41.4699 161.693 65.0038 119.695 105.675C56.275 167.091 16.0055 257.584 2.95741 368.017C-2.51706 414.342 -0.0111186 509.166 7.34924 560.983C7.34913 560.989 413.008 561.039 413.008 561.039L413.008 0.00250244Z\"\n            [attr.fill]=\"defaultTheme ? 'var(--v-color-earth-40)' : 'var(--v-themable-progress-ring-primary)'\"\n        />\n        <path\n            fill-rule=\"evenodd\"\n            clip-rule=\"evenodd\"\n            d=\"M413 119.22C361.652 119.495 336.216 123.3 297.597 136.251C257.587 149.666 229.326 166.946 198.553 196.809C152.084 241.905 122.578 308.35 113.018 389.438C108.539 427.415 111.35 509.783 118.262 543.031C119.529 549.13 120.905 555.114 122.389 560.982C122.385 560.979 413 560.988 412.994 560.992L413 119.22Z\"\n            [attr.fill]=\"defaultTheme ? 'var(--v-color-earth-60)' : 'var(--v-themable-button-background-color)'\"\n        />\n    </svg>\n    <svg\n        *ngIf=\"imageUrl\"\n        id=\"{{id}}-styled-header-custom-image-frame\"\n        class=\"v-styled-header-custom-image-frame\"\n        alt=\"\"\n        width=\"720px\"\n        height=\"645px\"\n        viewBox=\"0 0 720 645\"\n        fill=\"none\"\n    >\n        <defs>\n            <mask id=\"outer-shapes-mask\">\n                <rect width=\"100%\" height=\"100%\" fill=\"white\"/>\n                <path\n                    d=\"M563.205 287.171C547.654 191.722 476.419 140 360.51 140C262.871 140 196.543 176.594 168.789 245.773C159.36 269.272 156.165 288.526 156.244 321.335C156.323 353.716 158.609 369.23 166.818 393.052C175.14 417.209 185.861 434.271 204.388 452.851C232.365 480.906 273.588 498.721 323.895 504.493C347.456 507.197 398.557 505.5 419.184 501.327C498.671 485.25 546.53 440.361 561.653 367.702C565.318 350.093 566.18 305.423 563.205 287.171Z\"\n                    fill=\"black\"\n                />\n            </mask>\n            <mask id=\"inner-shape-mask\">\n                <rect width=\"100%\" height=\"100%\" fill=\"black\"/>\n                <path\n                    d=\"M563.205 287.171C547.654 191.722 476.419 140 360.51 140C262.871 140 196.543 176.594 168.789 245.773C159.36 269.272 156.165 288.526 156.244 321.335C156.323 353.716 158.609 369.23 166.818 393.052C175.14 417.209 185.861 434.271 204.388 452.851C232.365 480.906 273.588 498.721 323.895 504.493C347.456 507.197 398.557 505.5 419.184 501.327C498.671 485.25 546.53 440.361 561.653 367.702C565.318 350.093 566.18 305.423 563.205 287.171Z\"\n                    fill=\"white\"\n                />\n            </mask>\n        </defs>\n        <image\n            class=\"v-styled-header-custom-image\"\n            id=\"{{id}}-styled-header-custom-image\"\n            width=\"100%\"\n            height=\"100%\"\n            [attr.href]=\"imageUrl\"\n            [attr.alt]=\"alternativeText\"\n            preserveAspectRatio=\"xMidYMid slice\"\n            mask=\"url(#inner-shape-mask)\"\n        />\n        <path\n            fill-rule=\"evenodd\"\n            clip-rule=\"evenodd\"\n            d=\"M716.838 259.493C689.446 91.1975 563.969 -6.63086e-06 359.803 -1.53073e-05C187.819 -2.26162e-05 70.9879 64.5227 22.0996 186.5C5.49197 227.933 -0.136811 261.883 0.00252694 319.733C0.141865 376.826 4.16819 404.181 18.6277 446.186C33.2875 488.778 52.1711 518.864 84.8058 551.623C134.086 601.091 206.697 632.502 295.309 642.68C336.809 647.447 426.82 644.455 463.153 637.097C603.165 608.75 687.466 529.601 714.103 401.488C720.559 370.438 722.077 291.676 716.838 259.493Z\"\n            [attr.fill]=\"defaultTheme ? 'var(--v-color-earth-40)' : 'var(--v-themable-progress-ring-primary)'\"\n            mask=\"url(#outer-shapes-mask)\"\n        />\n        <path\n            fill-rule=\"evenodd\"\n            clip-rule=\"evenodd\"\n            d=\"M638.53 273.115C617.136 141.383 519.134 70 359.673 70C225.346 70 134.097 120.504 95.9129 215.98C82.9417 248.411 78.5454 274.985 78.6542 320.266C78.7631 364.955 81.9078 386.367 93.2012 419.245C104.651 452.584 119.4 476.133 144.889 501.774C183.379 540.495 240.091 565.081 309.3 573.048C341.713 576.779 412.015 574.438 440.393 568.678C549.747 546.49 615.59 484.537 636.394 384.258C641.437 359.955 642.623 298.305 638.53 273.115Z\"\n            [attr.fill]=\"defaultTheme ? 'var(--v-color-earth-60)' : 'var(--v-themable-button-background-color)'\"\n            mask=\"url(#outer-shapes-mask)\"\n        />\n    </svg>\n</div>\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: StyledHeaderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-styled-header', host: {
                        'class': 'v-styled-header v-container gap-20 overflow-hidden',
                        '[class.is-default-theme-background]': 'defaultTheme'
                    }, template: "<div\n    id=\"{{id}}-header-content\"\n    class=\"v-styled-header-content flex flex-column justify-center gap-10\"\n>\n    <h1\n        id=\"{{id}}-styled-header-title\"\n        class=\"h1 my-none\">{{ title }}</h1>\n    <p\n        *ngIf=\"description\"\n        id=\"{{id}}-styled-header-description\"\n        class=\"body-large my-none\"\n    >{{ description }}</p>\n    <ng-content select=\"vpg-header-actions\"></ng-content>\n</div>\n<div\n    id=\"{{id}}-styled-header-image-wrapper\"\n    class=\"v-styled-header-image-overlay\"\n>\n    <svg\n        id=\"{{id}}-styled-header-default-image\"\n        class=\"v-styled-header-default-image\"\n        [ngClass]=\"imageUrl ? 'is-rotated' : 'is-visible'\"\n        alt=\"\"\n        width=\"100%\"\n        height=\"100%\"\n        viewBox=\"0 0 413 562\"\n        preserveAspectRatio=\"xMidYMid slice\"\n        fill=\"none\"\n    >\n        <path\n            fill-rule=\"evenodd\"\n            clip-rule=\"evenodd\" d=\"M413.008 0.00250244C342.5 0.349481 307.735 5.50895 254.868 23.1999C200.263 41.4699 161.693 65.0038 119.695 105.675C56.275 167.091 16.0055 257.584 2.95741 368.017C-2.51706 414.342 -0.0111186 509.166 7.34924 560.983C7.34913 560.989 413.008 561.039 413.008 561.039L413.008 0.00250244Z\"\n            [attr.fill]=\"defaultTheme ? 'var(--v-color-earth-40)' : 'var(--v-themable-progress-ring-primary)'\"\n        />\n        <path\n            fill-rule=\"evenodd\"\n            clip-rule=\"evenodd\"\n            d=\"M413 119.22C361.652 119.495 336.216 123.3 297.597 136.251C257.587 149.666 229.326 166.946 198.553 196.809C152.084 241.905 122.578 308.35 113.018 389.438C108.539 427.415 111.35 509.783 118.262 543.031C119.529 549.13 120.905 555.114 122.389 560.982C122.385 560.979 413 560.988 412.994 560.992L413 119.22Z\"\n            [attr.fill]=\"defaultTheme ? 'var(--v-color-earth-60)' : 'var(--v-themable-button-background-color)'\"\n        />\n    </svg>\n    <svg\n        *ngIf=\"imageUrl\"\n        id=\"{{id}}-styled-header-custom-image-frame\"\n        class=\"v-styled-header-custom-image-frame\"\n        alt=\"\"\n        width=\"720px\"\n        height=\"645px\"\n        viewBox=\"0 0 720 645\"\n        fill=\"none\"\n    >\n        <defs>\n            <mask id=\"outer-shapes-mask\">\n                <rect width=\"100%\" height=\"100%\" fill=\"white\"/>\n                <path\n                    d=\"M563.205 287.171C547.654 191.722 476.419 140 360.51 140C262.871 140 196.543 176.594 168.789 245.773C159.36 269.272 156.165 288.526 156.244 321.335C156.323 353.716 158.609 369.23 166.818 393.052C175.14 417.209 185.861 434.271 204.388 452.851C232.365 480.906 273.588 498.721 323.895 504.493C347.456 507.197 398.557 505.5 419.184 501.327C498.671 485.25 546.53 440.361 561.653 367.702C565.318 350.093 566.18 305.423 563.205 287.171Z\"\n                    fill=\"black\"\n                />\n            </mask>\n            <mask id=\"inner-shape-mask\">\n                <rect width=\"100%\" height=\"100%\" fill=\"black\"/>\n                <path\n                    d=\"M563.205 287.171C547.654 191.722 476.419 140 360.51 140C262.871 140 196.543 176.594 168.789 245.773C159.36 269.272 156.165 288.526 156.244 321.335C156.323 353.716 158.609 369.23 166.818 393.052C175.14 417.209 185.861 434.271 204.388 452.851C232.365 480.906 273.588 498.721 323.895 504.493C347.456 507.197 398.557 505.5 419.184 501.327C498.671 485.25 546.53 440.361 561.653 367.702C565.318 350.093 566.18 305.423 563.205 287.171Z\"\n                    fill=\"white\"\n                />\n            </mask>\n        </defs>\n        <image\n            class=\"v-styled-header-custom-image\"\n            id=\"{{id}}-styled-header-custom-image\"\n            width=\"100%\"\n            height=\"100%\"\n            [attr.href]=\"imageUrl\"\n            [attr.alt]=\"alternativeText\"\n            preserveAspectRatio=\"xMidYMid slice\"\n            mask=\"url(#inner-shape-mask)\"\n        />\n        <path\n            fill-rule=\"evenodd\"\n            clip-rule=\"evenodd\"\n            d=\"M716.838 259.493C689.446 91.1975 563.969 -6.63086e-06 359.803 -1.53073e-05C187.819 -2.26162e-05 70.9879 64.5227 22.0996 186.5C5.49197 227.933 -0.136811 261.883 0.00252694 319.733C0.141865 376.826 4.16819 404.181 18.6277 446.186C33.2875 488.778 52.1711 518.864 84.8058 551.623C134.086 601.091 206.697 632.502 295.309 642.68C336.809 647.447 426.82 644.455 463.153 637.097C603.165 608.75 687.466 529.601 714.103 401.488C720.559 370.438 722.077 291.676 716.838 259.493Z\"\n            [attr.fill]=\"defaultTheme ? 'var(--v-color-earth-40)' : 'var(--v-themable-progress-ring-primary)'\"\n            mask=\"url(#outer-shapes-mask)\"\n        />\n        <path\n            fill-rule=\"evenodd\"\n            clip-rule=\"evenodd\"\n            d=\"M638.53 273.115C617.136 141.383 519.134 70 359.673 70C225.346 70 134.097 120.504 95.9129 215.98C82.9417 248.411 78.5454 274.985 78.6542 320.266C78.7631 364.955 81.9078 386.367 93.2012 419.245C104.651 452.584 119.4 476.133 144.889 501.774C183.379 540.495 240.091 565.081 309.3 573.048C341.713 576.779 412.015 574.438 440.393 568.678C549.747 546.49 615.59 484.537 636.394 384.258C641.437 359.955 642.623 298.305 638.53 273.115Z\"\n            [attr.fill]=\"defaultTheme ? 'var(--v-color-earth-60)' : 'var(--v-themable-button-background-color)'\"\n            mask=\"url(#outer-shapes-mask)\"\n        />\n    </svg>\n</div>\n" }]
        }], ctorParameters: () => [{ type: ThemeManagerService }] });

class HeaderModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: HeaderModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.9", ngImport: i0, type: HeaderModule, declarations: [StyledHeaderComponent,
            SimpleHeaderComponent,
            HeaderActionsComponent,
            HeaderComponent], imports: [CommonModule,
            ImageModule], exports: [StyledHeaderComponent,
            SimpleHeaderComponent,
            HeaderActionsComponent,
            HeaderComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: HeaderModule, imports: [CommonModule,
            ImageModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: HeaderModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        ImageModule
                    ],
                    declarations: [
                        StyledHeaderComponent,
                        SimpleHeaderComponent,
                        HeaderActionsComponent,
                        HeaderComponent
                    ],
                    exports: [
                        StyledHeaderComponent,
                        SimpleHeaderComponent,
                        HeaderActionsComponent,
                        HeaderComponent
                    ]
                }]
        }] });

class InlineLabelModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: InlineLabelModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.9", ngImport: i0, type: InlineLabelModule, declarations: [InlineLabelComponent], imports: [CommonModule,
            IconModule], exports: [InlineLabelComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: InlineLabelModule, imports: [CommonModule,
            IconModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: InlineLabelModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        IconModule
                    ],
                    declarations: [
                        InlineLabelComponent
                    ],
                    exports: [
                        InlineLabelComponent
                    ]
                }]
        }] });

class InputFieldComponent extends AbstractFormComponent {
    constructor(injector) {
        super();
        this.injector = injector;
        this.type = 'text';
        this.name = '';
        this.placeholder = '';
        this.autocomplete = 'off';
        this.label = '';
        this.hint = '';
        this.required = false;
        this.disabled = false;
        this.readOnly = false;
        this.ariaLabel = '';
        this.ariaLabelledBy = '';
        this.ariaDescribedBy = '';
        this.maxLength = null;
        this.maxLengthReachedText = '';
        this.control = null;
    }
    ngAfterViewInit() {
        const control = this.getNgControl();
        this.setName(control);
        this.setControl(control);
    }
    onChange(e) {
        const newValue = e.target.value;
        this.notifyValueChange(newValue);
        this.writeValue(newValue);
    }
    onBlur() {
        this.notifyTouched();
    }
    get isMaxLengthReached() {
        return this.maxLength ? this.value.length >= this.maxLength : false;
    }
    get canShowErrors() {
        return Boolean(this.control?.touched &&
            this.control?.invalid &&
            this.control?.errors);
    }
    get classNames() {
        return 'v-input-field';
    }
    getNgControl() {
        return this.injector.get(NgControl);
    }
    setControl(ngControl) {
        this.control = ngControl.control;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: InputFieldComponent, deps: [{ token: i0.Injector }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: InputFieldComponent, selector: "vpg-input-field", inputs: { type: "type", name: "name", placeholder: "placeholder", autocomplete: "autocomplete", label: "label", hint: "hint", required: "required", disabled: "disabled", readOnly: "readOnly", ariaLabel: "ariaLabel", ariaLabelledBy: "ariaLabelledBy", ariaDescribedBy: "ariaDescribedBy", maxLength: "maxLength", maxLengthReachedText: "maxLengthReachedText", min: "min", max: "max" }, host: { properties: { "class": "this.classNames" } }, providers: [valueAccessor(InputFieldComponent)], queries: [{ propertyName: "customHint", first: true, predicate: ["hint"], descendants: true }, { propertyName: "customErrors", first: true, predicate: ["errors"], descendants: true }, { propertyName: "prefix", first: true, predicate: ["prefix"], descendants: true }, { propertyName: "suffix", first: true, predicate: ["suffix"], descendants: true }], usesInheritance: true, ngImport: i0, template: "<label\n    *ngIf=\"label\"\n    class=\"v-control-label\"\n    id=\"{{name}}-label\"\n    [for]=\"name\"\n>\n    {{ label }}\n    <span\n        *ngIf=\"required\"\n        class=\"v-control-required\"\n        aria-hidden=\"true\"\n    >\n        *\n    </span>\n</label>\n<div class=\"v-control-container\">\n    <vpg-control-prefix *ngIf=\"prefix\">\n        <ng-container [ngTemplateOutlet]=\"prefix\"></ng-container>\n    </vpg-control-prefix>\n    <input\n        class=\"v-control-input\"\n        [class.has-prefix]=\"prefix\"\n        [class.has-suffix]=\"suffix\"\n        [id]=\"name\"\n        [type]=\"type\"\n        [name]=\"name\"\n        [value]=\"value\"\n        [disabled]=\"disabled\"\n        [readOnly]=\"readOnly\"\n        [placeholder]=\"placeholder\"\n        [autocomplete]=\"autocomplete\"\n        [min]=\"min\"\n        [max]=\"max\"\n        (input)=\"onChange($event)\"\n        (change)=\"onChange($event)\"\n        (blur)=\"onBlur()\"\n        [class.is-dirty]=\"control?.touched && control?.invalid\"\n        [attr.aria-invalid]=\"control?.touched && control?.invalid\"\n        [attr.aria-required]=\"required\"\n        [attr.aria-label]=\"ariaLabel\"\n        [attr.aria-labelledby]=\"ariaLabelledBy\"\n        attr.maxlength=\"{{maxLength}}\"\n        attr.aria-describedby=\"v-input-field-{{name}}-errors v-input-field-{{name}}-hint v-input-field-{{name}}-maxlength-reached\"\n    />\n    <vpg-control-suffix *ngIf=\"suffix\">\n        <ng-container [ngTemplateOutlet]=\"suffix\"></ng-container>\n    </vpg-control-suffix>\n</div>\n\n<ng-container [ngTemplateOutlet]=\"canShowErrors\n    ? errorsTemplate\n    : hintTemplate\"\n></ng-container>\n\n<span\n    *ngIf=\"isMaxLengthReached\"\n    id=\"v-input-field-{{name}}-maxlength-reached\"\n    class=\"sr-only\"\n    role=\"alert\"\n    aria-live=\"assertive\"\n>\n    {{ maxLengthReachedText }}\n</span>\n\n<ng-template #errorsTemplate>\n    <vpg-control-errors *ngIf=\"customErrors\" id=\"v-input-field-{{name}}-errors\">\n        <ng-container [ngTemplateOutlet]=\"customErrors\"></ng-container>\n    </vpg-control-errors>\n</ng-template>\n\n<ng-template #hintTemplate>\n    <div\n        *ngIf=\"hint || customHint || maxLength\"\n        class=\"flex gap-20 justify-content-between\" id=\"v-input-field-{{name}}-hint\"\n    >\n        <vpg-control-hint *ngIf=\"hint\">\n            {{ hint }}\n        </vpg-control-hint>\n        <ng-container\n            *ngIf=\"customHint\"\n            [ngTemplateOutlet]=\"customHint\"\n        ></ng-container>\n        <span></span>\n        <span\n            *ngIf=\"maxLength\"\n            class=\"body-regular\"\n        >\n            {{ value.length + '/' + maxLength }}\n        </span>\n    </div>\n</ng-template>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "component", type: ControlHintComponent, selector: "vpg-control-hint" }, { kind: "component", type: ControlErrorsComponent, selector: "vpg-control-errors" }, { kind: "component", type: ControlPrefixComponent, selector: "vpg-control-prefix" }, { kind: "component", type: ControlSuffixComponent, selector: "vpg-control-suffix" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: InputFieldComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-input-field', providers: [valueAccessor(InputFieldComponent)], template: "<label\n    *ngIf=\"label\"\n    class=\"v-control-label\"\n    id=\"{{name}}-label\"\n    [for]=\"name\"\n>\n    {{ label }}\n    <span\n        *ngIf=\"required\"\n        class=\"v-control-required\"\n        aria-hidden=\"true\"\n    >\n        *\n    </span>\n</label>\n<div class=\"v-control-container\">\n    <vpg-control-prefix *ngIf=\"prefix\">\n        <ng-container [ngTemplateOutlet]=\"prefix\"></ng-container>\n    </vpg-control-prefix>\n    <input\n        class=\"v-control-input\"\n        [class.has-prefix]=\"prefix\"\n        [class.has-suffix]=\"suffix\"\n        [id]=\"name\"\n        [type]=\"type\"\n        [name]=\"name\"\n        [value]=\"value\"\n        [disabled]=\"disabled\"\n        [readOnly]=\"readOnly\"\n        [placeholder]=\"placeholder\"\n        [autocomplete]=\"autocomplete\"\n        [min]=\"min\"\n        [max]=\"max\"\n        (input)=\"onChange($event)\"\n        (change)=\"onChange($event)\"\n        (blur)=\"onBlur()\"\n        [class.is-dirty]=\"control?.touched && control?.invalid\"\n        [attr.aria-invalid]=\"control?.touched && control?.invalid\"\n        [attr.aria-required]=\"required\"\n        [attr.aria-label]=\"ariaLabel\"\n        [attr.aria-labelledby]=\"ariaLabelledBy\"\n        attr.maxlength=\"{{maxLength}}\"\n        attr.aria-describedby=\"v-input-field-{{name}}-errors v-input-field-{{name}}-hint v-input-field-{{name}}-maxlength-reached\"\n    />\n    <vpg-control-suffix *ngIf=\"suffix\">\n        <ng-container [ngTemplateOutlet]=\"suffix\"></ng-container>\n    </vpg-control-suffix>\n</div>\n\n<ng-container [ngTemplateOutlet]=\"canShowErrors\n    ? errorsTemplate\n    : hintTemplate\"\n></ng-container>\n\n<span\n    *ngIf=\"isMaxLengthReached\"\n    id=\"v-input-field-{{name}}-maxlength-reached\"\n    class=\"sr-only\"\n    role=\"alert\"\n    aria-live=\"assertive\"\n>\n    {{ maxLengthReachedText }}\n</span>\n\n<ng-template #errorsTemplate>\n    <vpg-control-errors *ngIf=\"customErrors\" id=\"v-input-field-{{name}}-errors\">\n        <ng-container [ngTemplateOutlet]=\"customErrors\"></ng-container>\n    </vpg-control-errors>\n</ng-template>\n\n<ng-template #hintTemplate>\n    <div\n        *ngIf=\"hint || customHint || maxLength\"\n        class=\"flex gap-20 justify-content-between\" id=\"v-input-field-{{name}}-hint\"\n    >\n        <vpg-control-hint *ngIf=\"hint\">\n            {{ hint }}\n        </vpg-control-hint>\n        <ng-container\n            *ngIf=\"customHint\"\n            [ngTemplateOutlet]=\"customHint\"\n        ></ng-container>\n        <span></span>\n        <span\n            *ngIf=\"maxLength\"\n            class=\"body-regular\"\n        >\n            {{ value.length + '/' + maxLength }}\n        </span>\n    </div>\n</ng-template>\n" }]
        }], ctorParameters: () => [{ type: i0.Injector }], propDecorators: { type: [{
                type: Input
            }], name: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], autocomplete: [{
                type: Input
            }], label: [{
                type: Input
            }], hint: [{
                type: Input
            }], required: [{
                type: Input
            }], disabled: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], ariaLabelledBy: [{
                type: Input
            }], ariaDescribedBy: [{
                type: Input
            }], maxLength: [{
                type: Input
            }], maxLengthReachedText: [{
                type: Input
            }], min: [{
                type: Input
            }], max: [{
                type: Input
            }], customHint: [{
                type: ContentChild,
                args: ['hint']
            }], customErrors: [{
                type: ContentChild,
                args: ['errors']
            }], prefix: [{
                type: ContentChild,
                args: ['prefix']
            }], suffix: [{
                type: ContentChild,
                args: ['suffix']
            }], classNames: [{
                type: HostBinding,
                args: ['class']
            }] } });

class InputFieldModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: InputFieldModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.9", ngImport: i0, type: InputFieldModule, declarations: [InputFieldComponent], imports: [CommonModule,
            FormsModule,
            ReactiveFormsModule,
            IconModule,
            ControlModule], exports: [InputFieldComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: InputFieldModule, imports: [CommonModule,
            FormsModule,
            ReactiveFormsModule,
            IconModule,
            ControlModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: InputFieldModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule,
                        ReactiveFormsModule,
                        IconModule,
                        ControlModule
                    ],
                    declarations: [
                        InputFieldComponent
                    ],
                    exports: [
                        InputFieldComponent
                    ]
                }]
        }] });

class ListNextComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ListNextComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: ListNextComponent, selector: "vpg-list-next", host: { attributes: { "role": "list" }, classAttribute: "v-list" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ListNextComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vpg-list-next',
                    template: '<ng-content></ng-content>',
                    host: {
                        'class': 'v-list',
                        'role': 'list'
                    }
                }]
        }] });

class ListItemNextComponent {
    constructor() {
        this.backgroundColor = '';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ListItemNextComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: ListItemNextComponent, selector: "vpg-list-item-next", inputs: { backgroundColor: "backgroundColor" }, host: { attributes: { "role": "listitem" }, properties: { "style.background-color": "this.backgroundColor" }, classAttribute: "v-list-item" }, ngImport: i0, template: "<ng-content select=\"vpg-list-item-number\"></ng-content>\n<ng-content select=\"[vpgListItemNumber]\"></ng-content>\n<ng-content select=\"[vpgListItemImage]\"></ng-content>\n<div class=\"v-list-item-text\">\n    <ng-content select=\"vpg-list-item-heading\"></ng-content>\n    <ng-content select=\"[vpgListItemHeading]\"></ng-content>\n    <ng-content select=\"vpg-list-item-content\"></ng-content>\n    <ng-content select=\"[vpgListItemContent]\"></ng-content>\n</div>\n<ng-content select=\"vpg-list-item-action\"></ng-content>\n<ng-content select=\"[vpgListItemAction]\"></ng-content>" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ListItemNextComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-list-item-next', host: {
                        'class': 'v-list-item',
                        'role': 'listitem'
                    }, template: "<ng-content select=\"vpg-list-item-number\"></ng-content>\n<ng-content select=\"[vpgListItemNumber]\"></ng-content>\n<ng-content select=\"[vpgListItemImage]\"></ng-content>\n<div class=\"v-list-item-text\">\n    <ng-content select=\"vpg-list-item-heading\"></ng-content>\n    <ng-content select=\"[vpgListItemHeading]\"></ng-content>\n    <ng-content select=\"vpg-list-item-content\"></ng-content>\n    <ng-content select=\"[vpgListItemContent]\"></ng-content>\n</div>\n<ng-content select=\"vpg-list-item-action\"></ng-content>\n<ng-content select=\"[vpgListItemAction]\"></ng-content>" }]
        }], propDecorators: { backgroundColor: [{
                type: HostBinding,
                args: ['style.background-color']
            }, {
                type: Input
            }] } });

class ListItemActionComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ListItemActionComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: ListItemActionComponent, selector: "vpg-list-item-action", host: { classAttribute: "v-list-item-action" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ListItemActionComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vpg-list-item-action',
                    template: '<ng-content></ng-content>',
                    host: {
                        'class': 'v-list-item-action'
                    }
                }]
        }] });

class ListItemActionDirective {
    constructor(element) {
        this.element = element;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ListItemActionDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.9", type: ListItemActionDirective, selector: "[vpgListItemAction]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ListItemActionDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[vpgListItemAction]'
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }] });

class ListItemContentComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ListItemContentComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: ListItemContentComponent, selector: "vpg-list-item-content", host: { classAttribute: "v-list-item-content body-regular" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ListItemContentComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vpg-list-item-content',
                    template: '<ng-content></ng-content>',
                    host: {
                        'class': 'v-list-item-content body-regular'
                    }
                }]
        }] });

class ListItemContentDirective {
    constructor(element) {
        this.element = element;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ListItemContentDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.9", type: ListItemContentDirective, selector: "[vpgListItemContent]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ListItemContentDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[vpgListItemContent]'
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }] });

class ListItemHeadingComponent {
    constructor() {
        this.level = '2';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ListItemHeadingComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: ListItemHeadingComponent, selector: "vpg-list-item-heading", inputs: { level: "level" }, host: { attributes: { "role": "heading" }, properties: { "attr.aria-level": "this.level" }, classAttribute: "v-list-item-heading h4" }, ngImport: i0, template: `
        <ng-content></ng-content>
    `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ListItemHeadingComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vpg-list-item-heading',
                    template: `
        <ng-content></ng-content>
    `,
                    host: {
                        'class': 'v-list-item-heading h4',
                        'role': 'heading'
                    }
                }]
        }], propDecorators: { level: [{
                type: HostBinding,
                args: ['attr.aria-level']
            }, {
                type: Input
            }] } });

class ListItemHeadingDirective {
    constructor(element) {
        this.element = element;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ListItemHeadingDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.9", type: ListItemHeadingDirective, selector: "[vpgListItemHeading]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ListItemHeadingDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[vpgListItemHeading]'
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }] });

class ListItemImageDirective {
    constructor(element) {
        this.element = element;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ListItemImageDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.9", type: ListItemImageDirective, selector: "[vpgListItemImage]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ListItemImageDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[vpgListItemImage]'
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }] });

class ListItemNumberComponent {
    constructor() {
        this.screenReaderText = '';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ListItemNumberComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: ListItemNumberComponent, selector: "vpg-list-item-number", inputs: { screenReaderText: "screenReaderText" }, host: { classAttribute: "v-list-item-number body-regular" }, ngImport: i0, template: `
        <span aria-hidden="true">
            <ng-content></ng-content>
        </span>
        <span class="sr-only">{{ screenReaderText }}</span>
    `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ListItemNumberComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vpg-list-item-number',
                    template: `
        <span aria-hidden="true">
            <ng-content></ng-content>
        </span>
        <span class="sr-only">{{ screenReaderText }}</span>
    `,
                    host: {
                        'class': 'v-list-item-number body-regular'
                    }
                }]
        }], propDecorators: { screenReaderText: [{
                type: Input
            }] } });

class ListItemNumberDirective {
    constructor(element) {
        this.element = element;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ListItemNumberDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.9", type: ListItemNumberDirective, selector: "[vpgListItemNumber]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ListItemNumberDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[vpgListItemNumber]'
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }] });

class ListModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ListModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.9", ngImport: i0, type: ListModule, declarations: [ListNextComponent,
            ListItemNextComponent,
            ListItemNumberComponent,
            ListItemNumberDirective,
            ListItemImageDirective,
            ListItemHeadingComponent,
            ListItemHeadingDirective,
            ListItemContentComponent,
            ListItemContentDirective,
            ListItemActionComponent,
            ListItemActionDirective], imports: [CommonModule], exports: [ListNextComponent,
            ListItemNextComponent,
            ListItemNumberComponent,
            ListItemNumberDirective,
            ListItemImageDirective,
            ListItemHeadingComponent,
            ListItemHeadingDirective,
            ListItemContentComponent,
            ListItemContentDirective,
            ListItemActionComponent,
            ListItemActionDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ListModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ListModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule
                    ],
                    declarations: [
                        ListNextComponent,
                        ListItemNextComponent,
                        ListItemNumberComponent,
                        ListItemNumberDirective,
                        ListItemImageDirective,
                        ListItemHeadingComponent,
                        ListItemHeadingDirective,
                        ListItemContentComponent,
                        ListItemContentDirective,
                        ListItemActionComponent,
                        ListItemActionDirective
                    ],
                    exports: [
                        ListNextComponent,
                        ListItemNextComponent,
                        ListItemNumberComponent,
                        ListItemNumberDirective,
                        ListItemImageDirective,
                        ListItemHeadingComponent,
                        ListItemHeadingDirective,
                        ListItemContentComponent,
                        ListItemContentDirective,
                        ListItemActionComponent,
                        ListItemActionDirective
                    ]
                }]
        }] });

class MediaThumbnailComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: MediaThumbnailComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: MediaThumbnailComponent, selector: "vpg-media-thumbnail", host: { classAttribute: "v-media-thumbnail" }, ngImport: i0, template: "<ng-content select=\"vpg-media-thumbnail-image\"></ng-content>\n<ng-content select=\"vpg-media-thumbnail-details\"></ng-content>\n<ng-content select=\"vpg-media-thumbnail-categories\"></ng-content>\n" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: MediaThumbnailComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-media-thumbnail', host: {
                        'class': 'v-media-thumbnail'
                    }, template: "<ng-content select=\"vpg-media-thumbnail-image\"></ng-content>\n<ng-content select=\"vpg-media-thumbnail-details\"></ng-content>\n<ng-content select=\"vpg-media-thumbnail-categories\"></ng-content>\n" }]
        }] });

class MediaThumbnailCategoriesComponent {
    constructor() {
        this.screenReaderText = '';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: MediaThumbnailCategoriesComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: MediaThumbnailCategoriesComponent, selector: "vpg-media-thumbnail-categories", inputs: { screenReaderText: "screenReaderText" }, host: { properties: { "attr.aria-label": "this.screenReaderText" }, classAttribute: "v-media-thumbnail-categories" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: MediaThumbnailCategoriesComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vpg-media-thumbnail-categories',
                    template: '<ng-content></ng-content>',
                    host: {
                        'class': 'v-media-thumbnail-categories'
                    }
                }]
        }], propDecorators: { screenReaderText: [{
                type: HostBinding,
                args: ['attr.aria-label']
            }, {
                type: Input
            }] } });

class MediaThumbnailCategoryDirective {
    get nativeElement() {
        return this.element.nativeElement;
    }
    get classNames() {
        return {
            'v-media-thumbnail-category': true,
            'inline': true,
            'w-fit': true
        };
    }
    constructor(element) {
        this.element = element;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: MediaThumbnailCategoryDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.9", type: MediaThumbnailCategoryDirective, selector: "a[media-thumbnail-category], a[mediaThumbnailCategory], button[media-thumbnail-category], button[mediaThumbnailCategory]", host: { properties: { "class": "this.classNames" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: MediaThumbnailCategoryDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'a[media-thumbnail-category], a[mediaThumbnailCategory], button[media-thumbnail-category], button[mediaThumbnailCategory]'
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { classNames: [{
                type: HostBinding,
                args: ['class']
            }] } });

class MediaThumbnailDetailsComponent {
    constructor() {
        this.id = '';
        this.icon = '';
        this.mediaType = '';
        this.timestamp = '';
        this.title = '';
        this.mediaTypeClicked = new EventEmitter();
        this.titleClicked = new EventEmitter();
        this.mediaTypeTemplate = null;
        this.titleTemplate = null;
    }
    titleClick() {
        this.titleClicked.emit(this.title);
    }
    mediaTypeClick() {
        this.mediaTypeClicked.emit(this.mediaType);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: MediaThumbnailDetailsComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: MediaThumbnailDetailsComponent, selector: "vpg-media-thumbnail-details", inputs: { id: "id", icon: "icon", mediaType: "mediaType", timestamp: "timestamp", title: "title" }, outputs: { mediaTypeClicked: "mediaTypeClicked", titleClicked: "titleClicked" }, host: { properties: { "id": "this.id" }, classAttribute: "v-media-thumbnail-details" }, queries: [{ propertyName: "mediaTypeTemplate", first: true, predicate: ["mediaType"], descendants: true }, { propertyName: "titleTemplate", first: true, predicate: ["title"], descendants: true }], ngImport: i0, template: "<vpg-icon-next\n    *ngIf=\"icon\"\n    class=\"v-media-thumbnail-details-icon\"\n    [icon]=\"icon\"\n></vpg-icon-next>\n<div class=\"v-media-thumbnail-details-type\">\n    <a\n        *ngIf=\"mediaType\"\n        vpg-link\n        size=\"large\"\n        class=\"body-regular text-gray-90 v-media-thumbnail-details-link\"\n        (vpg-interactive-element)=\"mediaTypeClick()\"\n        role=\"link\"\n    >\n        {{ mediaType }}\n    </a>\n    <ng-container [ngTemplateOutlet]=\"mediaTypeTemplate\">\n    </ng-container>\n</div>\n<div class=\"v-media-thumbnail-details-title\">\n    <a\n        *ngIf=\"title\"\n        vpg-link\n        class=\"v-media-thumbnail-details-link\"\n        size=\"large\"\n        (vpg-interactive-element)=\"titleClick()\"\n        role=\"link\"\n    >\n        {{ title }}\n    </a>\n    <ng-container [ngTemplateOutlet]=\"titleTemplate\"></ng-container>\n</div>\n\n<div\n    class=\"body-regular text-gray-90 v-media-thumbnail-details-timestamp\"\n>{{ timestamp }}</div>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "component", type: IconNextComponent, selector: "vpg-icon-next", inputs: ["icon", "size", "prefix", "screenReaderText"] }, { kind: "directive", type: LinkDirective, selector: "a[vpg-link], a[vpgLink], button[vpg-link], button[vpgLink]", inputs: ["size", "align", "bold"] }, { kind: "directive", type: InteractiveElementDirective, selector: "[vpg-interactive-element]", inputs: ["tabindex", "role", "screenReaderText"], outputs: ["vpg-interactive-element"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: MediaThumbnailDetailsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-media-thumbnail-details', host: {
                        'class': 'v-media-thumbnail-details'
                    }, template: "<vpg-icon-next\n    *ngIf=\"icon\"\n    class=\"v-media-thumbnail-details-icon\"\n    [icon]=\"icon\"\n></vpg-icon-next>\n<div class=\"v-media-thumbnail-details-type\">\n    <a\n        *ngIf=\"mediaType\"\n        vpg-link\n        size=\"large\"\n        class=\"body-regular text-gray-90 v-media-thumbnail-details-link\"\n        (vpg-interactive-element)=\"mediaTypeClick()\"\n        role=\"link\"\n    >\n        {{ mediaType }}\n    </a>\n    <ng-container [ngTemplateOutlet]=\"mediaTypeTemplate\">\n    </ng-container>\n</div>\n<div class=\"v-media-thumbnail-details-title\">\n    <a\n        *ngIf=\"title\"\n        vpg-link\n        class=\"v-media-thumbnail-details-link\"\n        size=\"large\"\n        (vpg-interactive-element)=\"titleClick()\"\n        role=\"link\"\n    >\n        {{ title }}\n    </a>\n    <ng-container [ngTemplateOutlet]=\"titleTemplate\"></ng-container>\n</div>\n\n<div\n    class=\"body-regular text-gray-90 v-media-thumbnail-details-timestamp\"\n>{{ timestamp }}</div>\n" }]
        }], propDecorators: { id: [{
                type: Input
            }, {
                type: HostBinding
            }], icon: [{
                type: Input
            }], mediaType: [{
                type: Input
            }], timestamp: [{
                type: Input
            }], title: [{
                type: Input
            }], mediaTypeClicked: [{
                type: Output
            }], titleClicked: [{
                type: Output
            }], mediaTypeTemplate: [{
                type: ContentChild,
                args: ['mediaType']
            }], titleTemplate: [{
                type: ContentChild,
                args: ['title']
            }] } });

class MediaThumbnailImageComponent {
    constructor() {
        this.id = '';
        this.image = '';
        this.screenReaderText = '';
        this.playButton = false;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: MediaThumbnailImageComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: MediaThumbnailImageComponent, selector: "vpg-media-thumbnail-image", inputs: { id: "id", image: "image", screenReaderText: "screenReaderText", playButton: "playButton" }, host: { attributes: { "id": "id" }, properties: { "id": "this.id" }, classAttribute: "v-media-thumbnail-image" }, ngImport: i0, template: "<img\n    vpgImage\n    type=\"hero\"\n    [src]=\"image\"\n    [alt]=\"screenReaderText\"\n    [id]=\"id + '-img'\"\n/>\n<vpg-icon-next\n    class=\"v-media-thumbnail-image-play\"\n    *ngIf=\"playButton\"\n    icon=\"play\"\n>\n</vpg-icon-next>\n<ng-content></ng-content>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: IconNextComponent, selector: "vpg-icon-next", inputs: ["icon", "size", "prefix", "screenReaderText"] }, { kind: "directive", type: ImageDirective, selector: "img[vpg-image], img[vpgImage]", inputs: ["fallbackSrc", "type", "size", "alt", "backgroundColor"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: MediaThumbnailImageComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-media-thumbnail-image', host: {
                        'class': 'v-media-thumbnail-image',
                        'id': 'id'
                    }, template: "<img\n    vpgImage\n    type=\"hero\"\n    [src]=\"image\"\n    [alt]=\"screenReaderText\"\n    [id]=\"id + '-img'\"\n/>\n<vpg-icon-next\n    class=\"v-media-thumbnail-image-play\"\n    *ngIf=\"playButton\"\n    icon=\"play\"\n>\n</vpg-icon-next>\n<ng-content></ng-content>\n" }]
        }], propDecorators: { id: [{
                type: Input
            }, {
                type: HostBinding
            }], image: [{
                type: Input
            }], screenReaderText: [{
                type: Input
            }], playButton: [{
                type: Input
            }] } });

class MediaThumbnailProgressComponent {
    constructor() {
        this.id = '';
        this.progress = 0;
        this.screenReaderText = '';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: MediaThumbnailProgressComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: MediaThumbnailProgressComponent, selector: "vpg-media-thumbnail-progress", inputs: { id: "id", progress: "progress", screenReaderText: "screenReaderText" }, host: { properties: { "id": "this.id" }, classAttribute: "v-media-thumbnail-progress" }, ngImport: i0, template: "<progress\n    class=\"v-media-thumbnail-progress-bar\"\n    [attr.aria-label]=\"screenReaderText || progress\"\n    [attr.value]=\"progress\"\n    min=\"0\"\n    max=\"100\"\n></progress>\n" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: MediaThumbnailProgressComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-media-thumbnail-progress', host: {
                        'class': 'v-media-thumbnail-progress'
                    }, template: "<progress\n    class=\"v-media-thumbnail-progress-bar\"\n    [attr.aria-label]=\"screenReaderText || progress\"\n    [attr.value]=\"progress\"\n    min=\"0\"\n    max=\"100\"\n></progress>\n" }]
        }], propDecorators: { id: [{
                type: Input
            }, {
                type: HostBinding
            }], progress: [{
                type: Input
            }], screenReaderText: [{
                type: Input
            }] } });

class MediaThumbnailModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: MediaThumbnailModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.9", ngImport: i0, type: MediaThumbnailModule, declarations: [MediaThumbnailImageComponent,
            MediaThumbnailCategoryDirective,
            MediaThumbnailComponent,
            MediaThumbnailDetailsComponent,
            MediaThumbnailCategoriesComponent,
            MediaThumbnailProgressComponent], imports: [CommonModule,
            IconModule,
            LinkModule,
            ImageModule,
            InteractiveElementDirective], exports: [MediaThumbnailImageComponent,
            MediaThumbnailCategoryDirective,
            MediaThumbnailComponent,
            MediaThumbnailDetailsComponent,
            MediaThumbnailCategoriesComponent,
            MediaThumbnailProgressComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: MediaThumbnailModule, imports: [CommonModule,
            IconModule,
            LinkModule,
            ImageModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: MediaThumbnailModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        IconModule,
                        LinkModule,
                        ImageModule,
                        InteractiveElementDirective
                    ],
                    declarations: [
                        MediaThumbnailImageComponent,
                        MediaThumbnailCategoryDirective,
                        MediaThumbnailComponent,
                        MediaThumbnailDetailsComponent,
                        MediaThumbnailCategoriesComponent,
                        MediaThumbnailProgressComponent
                    ],
                    exports: [
                        MediaThumbnailImageComponent,
                        MediaThumbnailCategoryDirective,
                        MediaThumbnailComponent,
                        MediaThumbnailDetailsComponent,
                        MediaThumbnailCategoriesComponent,
                        MediaThumbnailProgressComponent
                    ]
                }]
        }] });

class NumberPaginationComponent {
    constructor() {
        this.id = '';
        this.totalPages = 0;
        this.currentPage = 0;
        this.backAriaLabel = '';
        this.forwardAriaLabel = '';
        this.textAriaLabel = '';
        this.pageChanged = new EventEmitter();
        this.backArrow = IconClassName.AngleLeft;
        this.forwardArrow = IconClassName.AngleRight;
    }
    get classNames() {
        return {
            ['v-container']: true,
            ['v-number-pagination']: true,
            ['inline-flex']: true,
            ['align-items-center']: true,
            ['gap-20']: true
        };
    }
    goForward() {
        this.currentPage = this.currentPage + 1;
        this.pageChanged.emit(this.currentPage);
    }
    goBack() {
        this.currentPage = this.currentPage - 1;
        this.pageChanged.emit(this.currentPage);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: NumberPaginationComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: NumberPaginationComponent, selector: "vpg-number-pagination", inputs: { id: "id", totalPages: "totalPages", currentPage: "currentPage", backAriaLabel: "backAriaLabel", forwardAriaLabel: "forwardAriaLabel", textAriaLabel: "textAriaLabel" }, outputs: { pageChanged: "pageChanged" }, host: { attributes: { "role": "navigation" }, properties: { "class": "this.classNames" } }, ngImport: i0, template: "<button\n    id=\"vpg-pagination-btn-back-{{id}}\"\n    class=\"v-number-pagination-btn\"\n    [disabled]=\"currentPage === 1\"\n    [attr.aria-disabled]=\"currentPage === 1\"\n    attr.aria-describedby=\"v-number-pagination-info-{{id}}\"\n    [attr.aria-label]=\"backAriaLabel\"\n    (click)=\"goBack()\"\n>\n    <vpg-icon-next icon=\"angle-left\"></vpg-icon-next>\n</button>\n<div\n    id=\"v-number-pagination-info-{{id}}\"\n    class=\"v-number-pagination-info text-bold\"\n    [attr.aria-label]=\"textAriaLabel\"\n>\n    <span aria-hidden=\"true\"> {{ currentPage }} / {{ totalPages }} </span>\n</div>\n<button\n    id=\"vpg-pagination-btn-forward-{{id}}\"\n    class=\"v-number-pagination-btn\"\n    [attr.aria-disabled]=\"currentPage === totalPages\"\n    attr.aria-describedby=\"v-number-pagination-info-{{id}}\"\n    [disabled]=\"currentPage === totalPages\"\n    [attr.aria-label]=\"forwardAriaLabel\"\n    (click)=\"goForward()\"\n >\n    <vpg-icon-next icon=\"angle-right\"></vpg-icon-next>\n</button>\n", dependencies: [{ kind: "component", type: IconNextComponent, selector: "vpg-icon-next", inputs: ["icon", "size", "prefix", "screenReaderText"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: NumberPaginationComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-number-pagination', host: {
                        role: 'navigation'
                    }, template: "<button\n    id=\"vpg-pagination-btn-back-{{id}}\"\n    class=\"v-number-pagination-btn\"\n    [disabled]=\"currentPage === 1\"\n    [attr.aria-disabled]=\"currentPage === 1\"\n    attr.aria-describedby=\"v-number-pagination-info-{{id}}\"\n    [attr.aria-label]=\"backAriaLabel\"\n    (click)=\"goBack()\"\n>\n    <vpg-icon-next icon=\"angle-left\"></vpg-icon-next>\n</button>\n<div\n    id=\"v-number-pagination-info-{{id}}\"\n    class=\"v-number-pagination-info text-bold\"\n    [attr.aria-label]=\"textAriaLabel\"\n>\n    <span aria-hidden=\"true\"> {{ currentPage }} / {{ totalPages }} </span>\n</div>\n<button\n    id=\"vpg-pagination-btn-forward-{{id}}\"\n    class=\"v-number-pagination-btn\"\n    [attr.aria-disabled]=\"currentPage === totalPages\"\n    attr.aria-describedby=\"v-number-pagination-info-{{id}}\"\n    [disabled]=\"currentPage === totalPages\"\n    [attr.aria-label]=\"forwardAriaLabel\"\n    (click)=\"goForward()\"\n >\n    <vpg-icon-next icon=\"angle-right\"></vpg-icon-next>\n</button>\n" }]
        }], propDecorators: { id: [{
                type: Input
            }], totalPages: [{
                type: Input
            }], currentPage: [{
                type: Input
            }], backAriaLabel: [{
                type: Input
            }], forwardAriaLabel: [{
                type: Input
            }], textAriaLabel: [{
                type: Input
            }], pageChanged: [{
                type: Output
            }], classNames: [{
                type: HostBinding,
                args: ['class']
            }] } });

class NumberPaginationModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: NumberPaginationModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.9", ngImport: i0, type: NumberPaginationModule, declarations: [NumberPaginationComponent], imports: [CommonModule,
            IconModule], exports: [NumberPaginationComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: NumberPaginationModule, imports: [CommonModule,
            IconModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: NumberPaginationModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        IconModule
                    ],
                    declarations: [
                        NumberPaginationComponent
                    ],
                    exports: [
                        NumberPaginationComponent
                    ]
                }]
        }] });

class PaginationComponent {
    constructor() {
        this.id = '';
        this.totalPages = 1;
        this.currentPage = 1;
        this.ariaLabels = {
            navigation: '',
            firstPageLink: '',
            prevPageLink: '',
            pageLink: '',
            nextPageLink: '',
            lastPageLink: ''
        };
        this.pageClicked = new EventEmitter();
        this.pages = [];
        this.MAX_NUMBER_OF_PAGES = 6;
        this.NUMBER_OF_PAGES_PER_CHUNK = 4;
        this.FIRST_PAGE = 1;
    }
    ngOnChanges(changes) {
        if (changes.currentPage || changes.totalPages) {
            this.pages = this.getPages();
        }
    }
    emitPageClicked(pageNumber) {
        this.pageClicked.emit(pageNumber);
    }
    goToFirstPage() {
        if (this.currentPage) {
            this.emitPageClicked(1);
        }
    }
    goToPreviousPage() {
        if (this.currentPage && this.currentPage > 1) {
            this.emitPageClicked(this.currentPage - 1);
        }
    }
    goToNextPage() {
        if (this.currentPage && this.currentPage < this.totalPages) {
            this.emitPageClicked(this.currentPage + 1);
        }
    }
    goToLastPage() {
        if (this.currentPage) {
            this.emitPageClicked(this.totalPages);
        }
    }
    range(start, end) {
        if (start === end) {
            return [start];
        }
        return [start, ...this.range(start + 1, end)];
    }
    getStartPage() {
        const chunk = Math.ceil(this.currentPage / this.NUMBER_OF_PAGES_PER_CHUNK);
        return (chunk * this.NUMBER_OF_PAGES_PER_CHUNK) - this.NUMBER_OF_PAGES_PER_CHUNK + 1;
    }
    getEndPage(startPage) {
        return startPage + this.NUMBER_OF_PAGES_PER_CHUNK - 1;
    }
    getPages() {
        if (!this.currentPage || this.currentPage < 0 || !this.totalPages || this.totalPages < 0) {
            return [];
        }
        if (this.totalPages <= this.MAX_NUMBER_OF_PAGES) {
            return this.range(1, this.totalPages);
        }
        if (this.currentPage >= (this.totalPages - (this.MAX_NUMBER_OF_PAGES - 1))) {
            return this.range(this.totalPages - (this.MAX_NUMBER_OF_PAGES - 1), this.totalPages);
        }
        const startPage = this.getStartPage();
        const endPage = this.getEndPage(startPage);
        return [...this.range(startPage, endPage), -1, this.totalPages];
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: PaginationComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: PaginationComponent, selector: "vpg-pagination", inputs: { id: "id", totalPages: "totalPages", currentPage: "currentPage", ariaLabels: "ariaLabels" }, outputs: { pageClicked: "pageClicked" }, host: { properties: { "id": "id" }, classAttribute: "v-pagination v-container" }, usesOnChanges: true, ngImport: i0, template: "<nav\n    role=\"navigation\"\n    [attr.aria-label]=\"ariaLabels.navigation\"\n    id=\"{{id}}-nav\"\n>\n    <ul class=\"v-pagination-list\" id=\"{{id}}-list\">\n        <li\n            *ngIf=\"totalPages > MAX_NUMBER_OF_PAGES && currentPage > FIRST_PAGE\"\n            class=\"v-pagination-list-item is-first\"\n        >\n            <button\n                (click)=\"goToFirstPage()\"\n                [attr.aria-label]=\"ariaLabels.firstPageLink\"\n                class=\"v-pagination-button\"\n                [id]=\"id + '-first'\"\n            >\n                <vpg-icon-next\n                    icon=\"angle-double-left\"\n                    size=\"lg\"\n                />\n            </button>\n        </li>\n        <li class=\"v-pagination-list-item\">\n            <button\n                (click)=\"goToPreviousPage()\"\n                [attr.aria-label]=\"ariaLabels.prevPageLink\"\n                class=\"v-pagination-button is-prev\"\n                [id]=\"id + '-prev'\"\n                [disabled]=\"currentPage === FIRST_PAGE\"\n            >\n                <vpg-icon-next\n                    icon=\"angle-left\"\n                    size=\"lg\"\n                />\n            </button>\n        </li>\n        <li\n            *ngFor=\"let page of pages\"\n            class=\"v-pagination-list-item\"\n        >\n            <button\n                *ngIf=\"page > 0\"\n                [attr.aria-label]=\"ariaLabels.pageLink + ' ' + page\"\n                [attr.aria-current]=\"page === currentPage ? 'page' : null\"\n                class=\"v-pagination-button is-number\"\n                [ngClass]=\"{\n                    'is-active': page === currentPage,\n                    'is-more': page === -1,\n                    'is-number': page !== -1\n                }\"\n                [id]=\"page === -1\n                    ? 'v-pagination-list-item-more-' +  id\n                    : 'v-pagination-list-item-' + page + '-' + id\"\n                (click)=\"emitPageClicked(page)\"\n            >\n                {{ page }}\n            </button>\n            <span\n                *ngIf=\"page === -1\"\n                aria-hidden=\"true\"\n                class=\"v-pagination-more\"\n            >\n                ...\n            </span>\n        </li>\n        <li class=\"v-pagination-list-item\">\n            <button\n                (click)=\"goToNextPage()\"\n                [attr.aria-label]=\"ariaLabels.nextPageLink\"\n                class=\"v-pagination-button is-next\"\n                [id]=\"id + '-next'\"\n                [disabled]=\"currentPage === totalPages\"\n            >\n                <vpg-icon-next\n                    icon=\"angle-right\"\n                    size=\"lg\"\n                />\n            </button>\n        </li>\n        <li\n            *ngIf=\"totalPages > MAX_NUMBER_OF_PAGES && currentPage < totalPages\"\n            class=\"v-pagination-list-item\"\n        >\n            <button\n                (click)=\"goToLastPage()\"\n                [attr.aria-label]=\"ariaLabels.lastPageLink\"\n                class=\"v-pagination-button is-last\"\n                [id]=\"id + '-last'\"\n            >\n                <vpg-icon-next\n                    icon=\"angle-double-right\"\n                    size=\"lg\"\n                />\n            </button>\n        </li>\n    </ul>\n</nav>\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: IconNextComponent, selector: "vpg-icon-next", inputs: ["icon", "size", "prefix", "screenReaderText"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: PaginationComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-pagination', changeDetection: ChangeDetectionStrategy.OnPush, host: {
                        '[id]': 'id',
                        class: 'v-pagination v-container'
                    }, template: "<nav\n    role=\"navigation\"\n    [attr.aria-label]=\"ariaLabels.navigation\"\n    id=\"{{id}}-nav\"\n>\n    <ul class=\"v-pagination-list\" id=\"{{id}}-list\">\n        <li\n            *ngIf=\"totalPages > MAX_NUMBER_OF_PAGES && currentPage > FIRST_PAGE\"\n            class=\"v-pagination-list-item is-first\"\n        >\n            <button\n                (click)=\"goToFirstPage()\"\n                [attr.aria-label]=\"ariaLabels.firstPageLink\"\n                class=\"v-pagination-button\"\n                [id]=\"id + '-first'\"\n            >\n                <vpg-icon-next\n                    icon=\"angle-double-left\"\n                    size=\"lg\"\n                />\n            </button>\n        </li>\n        <li class=\"v-pagination-list-item\">\n            <button\n                (click)=\"goToPreviousPage()\"\n                [attr.aria-label]=\"ariaLabels.prevPageLink\"\n                class=\"v-pagination-button is-prev\"\n                [id]=\"id + '-prev'\"\n                [disabled]=\"currentPage === FIRST_PAGE\"\n            >\n                <vpg-icon-next\n                    icon=\"angle-left\"\n                    size=\"lg\"\n                />\n            </button>\n        </li>\n        <li\n            *ngFor=\"let page of pages\"\n            class=\"v-pagination-list-item\"\n        >\n            <button\n                *ngIf=\"page > 0\"\n                [attr.aria-label]=\"ariaLabels.pageLink + ' ' + page\"\n                [attr.aria-current]=\"page === currentPage ? 'page' : null\"\n                class=\"v-pagination-button is-number\"\n                [ngClass]=\"{\n                    'is-active': page === currentPage,\n                    'is-more': page === -1,\n                    'is-number': page !== -1\n                }\"\n                [id]=\"page === -1\n                    ? 'v-pagination-list-item-more-' +  id\n                    : 'v-pagination-list-item-' + page + '-' + id\"\n                (click)=\"emitPageClicked(page)\"\n            >\n                {{ page }}\n            </button>\n            <span\n                *ngIf=\"page === -1\"\n                aria-hidden=\"true\"\n                class=\"v-pagination-more\"\n            >\n                ...\n            </span>\n        </li>\n        <li class=\"v-pagination-list-item\">\n            <button\n                (click)=\"goToNextPage()\"\n                [attr.aria-label]=\"ariaLabels.nextPageLink\"\n                class=\"v-pagination-button is-next\"\n                [id]=\"id + '-next'\"\n                [disabled]=\"currentPage === totalPages\"\n            >\n                <vpg-icon-next\n                    icon=\"angle-right\"\n                    size=\"lg\"\n                />\n            </button>\n        </li>\n        <li\n            *ngIf=\"totalPages > MAX_NUMBER_OF_PAGES && currentPage < totalPages\"\n            class=\"v-pagination-list-item\"\n        >\n            <button\n                (click)=\"goToLastPage()\"\n                [attr.aria-label]=\"ariaLabels.lastPageLink\"\n                class=\"v-pagination-button is-last\"\n                [id]=\"id + '-last'\"\n            >\n                <vpg-icon-next\n                    icon=\"angle-double-right\"\n                    size=\"lg\"\n                />\n            </button>\n        </li>\n    </ul>\n</nav>\n" }]
        }], propDecorators: { id: [{
                type: Input
            }], totalPages: [{
                type: Input
            }], currentPage: [{
                type: Input
            }], ariaLabels: [{
                type: Input
            }], pageClicked: [{
                type: Output
            }] } });

class PaginationModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: PaginationModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.9", ngImport: i0, type: PaginationModule, declarations: [PaginationComponent], imports: [CommonModule,
            IconModule], exports: [PaginationComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: PaginationModule, imports: [CommonModule,
            IconModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: PaginationModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        IconModule
                    ],
                    declarations: [
                        PaginationComponent
                    ],
                    exports: [
                        PaginationComponent
                    ]
                }]
        }] });

class PebbleIconComponent {
    constructor() {
        this.id = '';
        this.image = '';
        this.icon = '';
        this.backgroundColor = '';
        this.color = '';
        this.role = 'img';
    }
    get classNames() {
        return 'v-pebble-icon';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: PebbleIconComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: PebbleIconComponent, selector: "vpg-pebble-icon", inputs: { id: "id", image: "image", icon: "icon", backgroundColor: "backgroundColor", color: "color" }, host: { properties: { "attr.role": "this.role", "class": "this.classNames" } }, ngImport: i0, template: "<svg id=\"{{id}}-svg\" class=\"v-pebble-icon-background\" width=\"59\" height=\"64\" viewBox=\"0 0 59 64\" xmlns=\"http://www.w3.org/2000/svg\">\n    <path\n          d=\"M40.7592 60.5659C32.5984 63.8451 25.3603 63.9555 19.0049 60.981C12.6549 58.008 7.52505 51.7743 3.65936 42.1743C-0.206485 32.5722 -0.861818 24.3228 1.75855 17.8368C4.38733 11.3344 9.66495 6.46304 17.6762 3.24393C25.6875 0.0248103 32.8735 -0.112191 39.2766 2.76131C45.6644 5.62775 50.8465 11.8765 54.7761 21.6355C58.7037 31.3911 59.2626 39.2801 56.7413 45.8178C54.2173 52.3605 48.9242 57.2843 40.7592 60.5659Z\"\n          [style.fill]=\"backgroundColor\"\n    />\n</svg>\n<vpg-icon-next\n    id=\"{{id}}-icon\"\n    *ngIf=\"icon\"\n    [style.color]=\"color\"\n    [icon]=\"icon\"\n/>\n<img\n    class=\"v-pebble-icon-image\"\n    alt=\"\"\n    id=\"{{id}}-image\"\n    *ngIf=\"image\"\n    [src]=\"image\"\n>\n<ng-content/>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: IconNextComponent, selector: "vpg-icon-next", inputs: ["icon", "size", "prefix", "screenReaderText"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: PebbleIconComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-pebble-icon', template: "<svg id=\"{{id}}-svg\" class=\"v-pebble-icon-background\" width=\"59\" height=\"64\" viewBox=\"0 0 59 64\" xmlns=\"http://www.w3.org/2000/svg\">\n    <path\n          d=\"M40.7592 60.5659C32.5984 63.8451 25.3603 63.9555 19.0049 60.981C12.6549 58.008 7.52505 51.7743 3.65936 42.1743C-0.206485 32.5722 -0.861818 24.3228 1.75855 17.8368C4.38733 11.3344 9.66495 6.46304 17.6762 3.24393C25.6875 0.0248103 32.8735 -0.112191 39.2766 2.76131C45.6644 5.62775 50.8465 11.8765 54.7761 21.6355C58.7037 31.3911 59.2626 39.2801 56.7413 45.8178C54.2173 52.3605 48.9242 57.2843 40.7592 60.5659Z\"\n          [style.fill]=\"backgroundColor\"\n    />\n</svg>\n<vpg-icon-next\n    id=\"{{id}}-icon\"\n    *ngIf=\"icon\"\n    [style.color]=\"color\"\n    [icon]=\"icon\"\n/>\n<img\n    class=\"v-pebble-icon-image\"\n    alt=\"\"\n    id=\"{{id}}-image\"\n    *ngIf=\"image\"\n    [src]=\"image\"\n>\n<ng-content/>\n" }]
        }], propDecorators: { id: [{
                type: Input
            }], image: [{
                type: Input
            }], icon: [{
                type: Input
            }], backgroundColor: [{
                type: Input
            }], color: [{
                type: Input
            }], role: [{
                type: HostBinding,
                args: ['attr.role']
            }], classNames: [{
                type: HostBinding,
                args: ['class']
            }] } });

class PebbleIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: PebbleIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.9", ngImport: i0, type: PebbleIconModule, declarations: [PebbleIconComponent], imports: [CommonModule,
            IconModule], exports: [PebbleIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: PebbleIconModule, imports: [CommonModule,
            IconModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: PebbleIconModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        IconModule
                    ],
                    declarations: [
                        PebbleIconComponent
                    ],
                    exports: [
                        PebbleIconComponent
                    ]
                }]
        }] });

class ListboxSearchDirective {
    constructor(element) {
        this.element = element;
        this.searched = new EventEmitter();
        this.reset$ = new Subject();
        this.SEARCH_DEBOUNCE_TIME = 300;
        merge(this.reset$, fromEvent(this.element.nativeElement, 'keydown')
            .pipe(filter((e) => e.key.length === 1 && e.key.match(/[a-zA-Z]/)))).pipe(takeUntilDestroyed(), scan((state, event) => event ? state + event.key : '', ''), debounceTime(this.SEARCH_DEBOUNCE_TIME), distinctUntilChanged()).subscribe((value) => {
            this.reset$.next();
            this.searched.emit(value);
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ListboxSearchDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.9", type: ListboxSearchDirective, selector: "[vpgListboxSearch]", outputs: { searched: "vpgListboxSearch" }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ListboxSearchDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[vpgListboxSearch]'
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { searched: [{
                type: Output,
                args: ['vpgListboxSearch']
            }] } });

class PhoneUtilService {
    constructor() {
        this.phoneUtil = PhoneNumberUtil.getInstance();
    }
    getInstance() {
        return this.phoneUtil;
    }
    getNationalNumber(number, countryCode) {
        try {
            const parsedPhoneNumber = this.phoneUtil.parse(number, countryCode);
            return `${parsedPhoneNumber.getNationalNumber()}`;
        }
        catch (e) {
            return number;
        }
    }
    formatNumber(number, countryCode, format = PhoneNumberFormat.E164) {
        try {
            const parsed = this.phoneUtil.parse(number, countryCode);
            return this.phoneUtil.format(parsed, format);
        }
        catch (e) {
            return number;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: PhoneUtilService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: PhoneUtilService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: PhoneUtilService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [] });

class NationalNumberPipe {
    constructor(phoneUtil) {
        this.phoneUtil = phoneUtil;
    }
    transform(value, countryCode) {
        return this.phoneUtil.getNationalNumber(value, countryCode);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: NationalNumberPipe, deps: [{ token: PhoneUtilService }], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.3.9", ngImport: i0, type: NationalNumberPipe, name: "nationalNumber" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: NationalNumberPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'nationalNumber' }]
        }], ctorParameters: () => [{ type: PhoneUtilService }] });

class NumbersOnlyDirective {
    constructor() {
        this.navigationKeys = [
            'Backspace',
            'Delete',
            'Tab',
            'Escape',
            'Enter',
            'Home',
            'End',
            'ArrowLeft',
            'ArrowRight',
            'Clear',
            'Copy',
            'Paste'
        ];
    }
    onKeyDown(e) {
        if (this.navigationKeys.indexOf(e.key) > -1 ||
            (e.key === 'a' && e.ctrlKey === true) ||
            (e.key === 'c' && e.ctrlKey === true) ||
            (e.key === 'v' && e.ctrlKey === true) ||
            (e.key === 'x' && e.ctrlKey === true) ||
            (e.key === 'a' && e.metaKey === true) ||
            (e.key === 'c' && e.metaKey === true) ||
            (e.key === 'v' && e.metaKey === true) ||
            (e.key === 'x' && e.metaKey === true)) {
            return;
        }
        if (e.key === ' ' || isNaN(Number(e.key))) {
            e.preventDefault();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: NumbersOnlyDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.9", type: NumbersOnlyDirective, selector: "[vpgNumbersOnly]", host: { listeners: { "keydown": "onKeyDown($event)" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: NumbersOnlyDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[vpgNumbersOnly]'
                }]
        }], propDecorators: { onKeyDown: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }] } });

function phoneNumberFormatValidator(phoneUtil, countryCode) {
    return (control) => {
        const value = control.value;
        try {
            const parsedPhoneNumber = phoneUtil.parse(value, countryCode);
            if (!phoneUtil.isValidNumber(parsedPhoneNumber)) {
                return { phoneNumberFormatInvalid: true };
            }
            return null;
        }
        catch (e) {
            return null;
        }
    };
}

class PhoneNumberComponent extends AbstractFormComponent {
    constructor(injector, phoneUtil) {
        super();
        this.injector = injector;
        this.phoneUtil = phoneUtil;
        this.US = {
            countryCode: 'US',
            phoneCountryCode: '+1',
            name: 'United States of America'
        };
        this.id = '';
        this.name = '';
        this.label = '';
        this.options = [];
        this.placeholder = '';
        this.autocomplete = 'off';
        this.required = false;
        this.readOnly = false;
        this.selected = this.US;
        this.hint = '';
        this.ariaLabel = '';
        this.ariaLabelledBy = '';
        this.listboxAriaLabel = '';
        this.comboboxAriaLabel = '';
        this.maxLength = 20;
        this.maxLengthReachedText = '';
        this.listOpen = false;
        this.control = null;
        this.ariaActiveDescendant = '';
        this.maxInputLength = 20;
        this.isNumberValid = false;
    }
    ngAfterViewInit() {
        const control = this.getNgControl();
        this.setName(control);
        this.setControl(control);
        this.setValidators();
    }
    get classNames() {
        return 'v-input-field v-phone-number';
    }
    get isMaxLengthReached() {
        return this.maxLength
            ? this.value?.length === this.maxLength
            : false;
    }
    toggleList() {
        if (this.listOpen) {
            this.closeList();
        }
        else {
            this.openList();
        }
    }
    openList() {
        setTimeout(() => {
            this.listOpen = true;
        });
    }
    closeList() {
        setTimeout(() => {
            this.listOpen = false;
        });
    }
    onBlur() {
        this.closeList();
    }
    onInput(e) {
        const value = e.target.value;
        const newValue = this.phoneUtil.formatNumber(value, this.selected.countryCode);
        this.notifyValueChange(newValue);
        this.writeValue(newValue);
    }
    selectOption(option) {
        this.selected = option;
        const newValue = this.phoneUtil.formatNumber(this.phoneUtil.getNationalNumber(this.value, this.selected.countryCode), this.selected.countryCode);
        this.notifyValueChange(newValue);
        this.writeValue(newValue);
    }
    selectOptionAndHideList(option) {
        this.selectOption(option);
        this.closeList();
    }
    search(term) {
        if (term === '' || !this.listOpen) {
            return;
        }
        const result = this.options.find((o) => o.name
            .toLowerCase()
            .includes(term.toLowerCase()));
        if (!result) {
            return;
        }
        const option = this.getListboxOption(result.countryCode);
        const selected = this.getListboxSelectedOption();
        if (option) {
            this.focusOption(option);
            selected?.setAttribute('aria-selected', 'false');
            selected?.classList.remove('is-active');
            option.setAttribute('aria-selected', 'true');
            option.classList.add('is-active');
            option.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest'
            });
        }
    }
    focusOption(option) {
        this.combobox.nativeElement.setAttribute('aria-activedescendant', option.id);
    }
    preventMousedown(e) {
        e.preventDefault();
    }
    get canShowErrors() {
        return Boolean(this.control?.touched &&
            this.control?.invalid &&
            this.control?.errors);
    }
    getNgControl() {
        return this.injector.get(NgControl);
    }
    setControl(ngControl) {
        this.control = ngControl.control;
    }
    setValidators() {
        this.control?.addValidators([
            (control) => {
                return phoneNumberFormatValidator(this.phoneUtil.getInstance(), this.selected.countryCode)(control);
            }
        ]);
    }
    getListboxOption(countryCode) {
        return this.listbox.nativeElement.querySelector(`#${this.id}-option-${countryCode.toLowerCase()}`);
    }
    getListboxSelectedOption() {
        return this.listbox.nativeElement.querySelector('.v-control-option[aria-selected="true"]');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: PhoneNumberComponent, deps: [{ token: i0.Injector }, { token: PhoneUtilService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: PhoneNumberComponent, selector: "vpg-phone-number", inputs: { id: "id", name: "name", label: "label", options: "options", placeholder: "placeholder", autocomplete: "autocomplete", required: "required", readOnly: "readOnly", selected: "selected", hint: "hint", ariaLabel: "ariaLabel", ariaLabelledBy: "ariaLabelledBy", listboxAriaLabel: "listboxAriaLabel", comboboxAriaLabel: "comboboxAriaLabel", maxLength: "maxLength", maxLengthReachedText: "maxLengthReachedText" }, host: { properties: { "class": "this.classNames" } }, providers: [valueAccessor(PhoneNumberComponent)], queries: [{ propertyName: "customHint", first: true, predicate: ["hint"], descendants: true }, { propertyName: "customErrors", first: true, predicate: ["errors"], descendants: true }], viewQueries: [{ propertyName: "input", first: true, predicate: ["input"], descendants: true }, { propertyName: "listbox", first: true, predicate: ["listbox"], descendants: true }, { propertyName: "combobox", first: true, predicate: ["combobox"], descendants: true }], usesInheritance: true, ngImport: i0, template: "<label\n    *ngIf=\"label\"\n    class=\"v-control-label\"\n    id=\"{{id}}-label\"\n    for=\"{{id}}-input\"\n>\n    {{ label }}\n    <span\n        *ngIf=\"required\"\n        class=\"v-control-required\"\n        aria-hidden=\"true\"\n    >\n        *\n    </span>\n</label>\n\n<div class=\"v-control-container\">\n    <button\n        #combobox\n        type=\"button\"\n        role=\"combobox\"\n        id=\"{{id}}-list-trigger\"\n        class=\"v-control-button\"\n        aria-haspopup=\"listbox\"\n        [attr.aria-controls]=\"id + '-list'\"\n        [attr.aria-expanded]=\"listOpen\"\n        [attr.aria-activedescendant]=\"\"\n        [attr.aria-labelledby]=\"id + '-label'\"\n        [attr.aria-label]=\"comboboxAriaLabel\"\n        [disabled]=\"disabled\"\n        (click)=\"toggleList()\"\n        (keydown.escape)=\"closeList()\"\n        (keydown.arrowup)=\"openList()\"\n        (keydown.arrowdown)=\"openList()\"\n        (keydown.alt.arrowdown)=\"openList()\"\n        (keydown.enter)=\"toggleList()\"\n        (vpgListboxSearch)=\"search($event)\"\n        (blur)=\"closeList()\"\n    >\n        {{ selected.phoneCountryCode }}\n        <vpg-icon-next [icon]=\"listOpen ? 'chevron-up' : 'chevron-down'\" />\n    </button>\n\n    <input\n        #input\n        id=\"{{id}}-input\"\n        type=\"tel\"\n        class=\"v-control-input\"\n        [name]=\"name\"\n        vpgNumbersOnly\n        [value]=\"value | nationalNumber:selected.countryCode\"\n        [disabled]=\"disabled\"\n        [readOnly]=\"readOnly\"\n        [placeholder]=\"placeholder\"\n        [autocomplete]=\"autocomplete\"\n        (input)=\"onInput($event)\"\n        (blur)=\"notifyTouched()\"\n        [class.is-dirty]=\"control?.touched && control?.invalid\"\n        [attr.aria-invalid]=\"control?.touched && control?.invalid\"\n        [attr.aria-required]=\"required\"\n        [attr.aria-label]=\"ariaLabel\"\n        [attr.aria-labelledby]=\"ariaLabelledBy\"\n        attr.aria-describedby=\"{{id}}-hint {{id}}-errors {{id}}-maxlength-error\"\n        [attr.maxlength]=\"maxLength\"\n    />\n\n    <ul\n        #listbox\n        *ngIf=\"listOpen\"\n        role=\"listbox\"\n        id=\"{{id}}-list\"\n        [attr.aria-labelledby]=\"id + '-label'\"\n        [attr.aria-label]=\"listboxAriaLabel\"\n        class=\"v-control-list\"\n        vpg-keyboard-navigate\n        (optionFocused)=\"focusOption($event)\"\n    >\n        <li\n            *ngFor=\"let option of options\"\n            role=\"option\"\n            tabindex=\"-1\"\n            id=\"{{id}}-option-{{option.countryCode | lowercase}}\"\n            class=\"v-control-option\"\n            [class.is-selected]=\"selected.countryCode === option.countryCode\"\n            [attr.aria-selected]=\"selected.countryCode === option.countryCode\"\n            (click)=\"selectOptionAndHideList(option)\"\n            (mousedown)=\"preventMousedown($event)\"\n        >\n            {{ option.phoneCountryCode }} {{ option.name }} \n        </li>\n    </ul>\n</div>\n\n<ng-container [ngTemplateOutlet]=\"canShowErrors\n    ? errorsTemplate\n    : hintTemplate\"\n/>\n\n<ng-template #errorsTemplate>\n    <vpg-control-errors *ngIf=\"customErrors\" id=\"{{id}}-errors\">\n        <ng-container [ngTemplateOutlet]=\"customErrors\" />\n    </vpg-control-errors>\n</ng-template>\n\n<ng-template #hintTemplate>\n    <vpg-control-hint\n        *ngIf=\"hint\"\n        id=\"{{id}}-hint\"\n    >\n        {{ hint }}\n    </vpg-control-hint>\n</ng-template>\n\n<span\n    *ngIf=\"isMaxLengthReached\"\n    id=\"{{id}}-maxlength-error\"\n    class=\"sr-only\"\n    role=\"alert\"\n>\n    {{ maxLengthReachedText }}\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "component", type: IconNextComponent, selector: "vpg-icon-next", inputs: ["icon", "size", "prefix", "screenReaderText"] }, { kind: "component", type: ControlHintComponent, selector: "vpg-control-hint" }, { kind: "component", type: ControlErrorsComponent, selector: "vpg-control-errors" }, { kind: "directive", type: KeyboardNavigateDirective, selector: "[vpg-keyboard-navigate]", inputs: ["controlOptionClassName"], outputs: ["optionFocused"] }, { kind: "directive", type: ListboxSearchDirective, selector: "[vpgListboxSearch]", outputs: ["vpgListboxSearch"] }, { kind: "directive", type: NumbersOnlyDirective, selector: "[vpgNumbersOnly]" }, { kind: "pipe", type: i1.LowerCasePipe, name: "lowercase" }, { kind: "pipe", type: NationalNumberPipe, name: "nationalNumber" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: PhoneNumberComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-phone-number', providers: [valueAccessor(PhoneNumberComponent)], template: "<label\n    *ngIf=\"label\"\n    class=\"v-control-label\"\n    id=\"{{id}}-label\"\n    for=\"{{id}}-input\"\n>\n    {{ label }}\n    <span\n        *ngIf=\"required\"\n        class=\"v-control-required\"\n        aria-hidden=\"true\"\n    >\n        *\n    </span>\n</label>\n\n<div class=\"v-control-container\">\n    <button\n        #combobox\n        type=\"button\"\n        role=\"combobox\"\n        id=\"{{id}}-list-trigger\"\n        class=\"v-control-button\"\n        aria-haspopup=\"listbox\"\n        [attr.aria-controls]=\"id + '-list'\"\n        [attr.aria-expanded]=\"listOpen\"\n        [attr.aria-activedescendant]=\"\"\n        [attr.aria-labelledby]=\"id + '-label'\"\n        [attr.aria-label]=\"comboboxAriaLabel\"\n        [disabled]=\"disabled\"\n        (click)=\"toggleList()\"\n        (keydown.escape)=\"closeList()\"\n        (keydown.arrowup)=\"openList()\"\n        (keydown.arrowdown)=\"openList()\"\n        (keydown.alt.arrowdown)=\"openList()\"\n        (keydown.enter)=\"toggleList()\"\n        (vpgListboxSearch)=\"search($event)\"\n        (blur)=\"closeList()\"\n    >\n        {{ selected.phoneCountryCode }}\n        <vpg-icon-next [icon]=\"listOpen ? 'chevron-up' : 'chevron-down'\" />\n    </button>\n\n    <input\n        #input\n        id=\"{{id}}-input\"\n        type=\"tel\"\n        class=\"v-control-input\"\n        [name]=\"name\"\n        vpgNumbersOnly\n        [value]=\"value | nationalNumber:selected.countryCode\"\n        [disabled]=\"disabled\"\n        [readOnly]=\"readOnly\"\n        [placeholder]=\"placeholder\"\n        [autocomplete]=\"autocomplete\"\n        (input)=\"onInput($event)\"\n        (blur)=\"notifyTouched()\"\n        [class.is-dirty]=\"control?.touched && control?.invalid\"\n        [attr.aria-invalid]=\"control?.touched && control?.invalid\"\n        [attr.aria-required]=\"required\"\n        [attr.aria-label]=\"ariaLabel\"\n        [attr.aria-labelledby]=\"ariaLabelledBy\"\n        attr.aria-describedby=\"{{id}}-hint {{id}}-errors {{id}}-maxlength-error\"\n        [attr.maxlength]=\"maxLength\"\n    />\n\n    <ul\n        #listbox\n        *ngIf=\"listOpen\"\n        role=\"listbox\"\n        id=\"{{id}}-list\"\n        [attr.aria-labelledby]=\"id + '-label'\"\n        [attr.aria-label]=\"listboxAriaLabel\"\n        class=\"v-control-list\"\n        vpg-keyboard-navigate\n        (optionFocused)=\"focusOption($event)\"\n    >\n        <li\n            *ngFor=\"let option of options\"\n            role=\"option\"\n            tabindex=\"-1\"\n            id=\"{{id}}-option-{{option.countryCode | lowercase}}\"\n            class=\"v-control-option\"\n            [class.is-selected]=\"selected.countryCode === option.countryCode\"\n            [attr.aria-selected]=\"selected.countryCode === option.countryCode\"\n            (click)=\"selectOptionAndHideList(option)\"\n            (mousedown)=\"preventMousedown($event)\"\n        >\n            {{ option.phoneCountryCode }} {{ option.name }} \n        </li>\n    </ul>\n</div>\n\n<ng-container [ngTemplateOutlet]=\"canShowErrors\n    ? errorsTemplate\n    : hintTemplate\"\n/>\n\n<ng-template #errorsTemplate>\n    <vpg-control-errors *ngIf=\"customErrors\" id=\"{{id}}-errors\">\n        <ng-container [ngTemplateOutlet]=\"customErrors\" />\n    </vpg-control-errors>\n</ng-template>\n\n<ng-template #hintTemplate>\n    <vpg-control-hint\n        *ngIf=\"hint\"\n        id=\"{{id}}-hint\"\n    >\n        {{ hint }}\n    </vpg-control-hint>\n</ng-template>\n\n<span\n    *ngIf=\"isMaxLengthReached\"\n    id=\"{{id}}-maxlength-error\"\n    class=\"sr-only\"\n    role=\"alert\"\n>\n    {{ maxLengthReachedText }}\n</span>\n" }]
        }], ctorParameters: () => [{ type: i0.Injector }, { type: PhoneUtilService }], propDecorators: { id: [{
                type: Input
            }], name: [{
                type: Input
            }], label: [{
                type: Input
            }], options: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], autocomplete: [{
                type: Input
            }], required: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], selected: [{
                type: Input
            }], hint: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], ariaLabelledBy: [{
                type: Input
            }], listboxAriaLabel: [{
                type: Input
            }], comboboxAriaLabel: [{
                type: Input
            }], maxLength: [{
                type: Input
            }], maxLengthReachedText: [{
                type: Input
            }], input: [{
                type: ViewChild,
                args: ['input']
            }], listbox: [{
                type: ViewChild,
                args: ['listbox']
            }], combobox: [{
                type: ViewChild,
                args: ['combobox']
            }], customHint: [{
                type: ContentChild,
                args: ['hint']
            }], customErrors: [{
                type: ContentChild,
                args: ['errors']
            }], classNames: [{
                type: HostBinding,
                args: ['class']
            }] } });

class PhoneNumberModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: PhoneNumberModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.9", ngImport: i0, type: PhoneNumberModule, declarations: [PhoneNumberComponent,
            ListboxSearchDirective,
            NationalNumberPipe,
            NumbersOnlyDirective], imports: [CommonModule,
            IconModule,
            ControlModule,
            KeyboardNavigateDirective], exports: [PhoneNumberComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: PhoneNumberModule, providers: [PhoneUtilService], imports: [CommonModule,
            IconModule,
            ControlModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: PhoneNumberModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        IconModule,
                        ControlModule,
                        KeyboardNavigateDirective
                    ],
                    declarations: [
                        PhoneNumberComponent,
                        ListboxSearchDirective,
                        NationalNumberPipe,
                        NumbersOnlyDirective
                    ],
                    exports: [
                        PhoneNumberComponent
                    ],
                    providers: [PhoneUtilService]
                }]
        }] });

const OPEN_CLASS_NAME = 'show';
class PopoverMenuComponent {
    constructor(element) {
        this.element = element;
        this.position = 'center';
    }
    get nativeElement() {
        return this.element.nativeElement;
    }
    isOpen() {
        return this.nativeElement.classList.contains(OPEN_CLASS_NAME);
    }
    open(styles) {
        this.nativeElement.classList.add(OPEN_CLASS_NAME);
        Object.keys(styles).forEach((property) => {
            this.nativeElement.style.setProperty(property, styles[property]);
        });
    }
    close() {
        this.nativeElement.classList.remove(OPEN_CLASS_NAME);
    }
    getSize() {
        const { width, height } = this.nativeElement
            .getBoundingClientRect();
        return {
            width,
            height
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: PopoverMenuComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: PopoverMenuComponent, selector: "vpg-popover-menu", inputs: { position: "position" }, host: { attributes: { "role": "menu", "tabindex": "-1" }, classAttribute: "v-popover-menu" }, ngImport: i0, template: `
        <ng-content></ng-content>
    `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: PopoverMenuComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vpg-popover-menu',
                    template: `
        <ng-content></ng-content>
    `,
                    host: {
                        role: 'menu',
                        tabindex: '-1',
                        class: 'v-popover-menu'
                    }
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { position: [{
                type: Input
            }] } });

class PopoverMenuItemComponent {
    constructor(element) {
        this.element = element;
    }
    get nativeElement() {
        return this.element.nativeElement;
    }
    focus() {
        this.nativeElement.focus();
    }
    onClick(e) {
        e.preventDefault();
        this.nativeElement.click();
    }
    onArrowDown() {
        const next = this.nativeElement.nextElementSibling;
        if (next === null) {
            const parent = this.nativeElement.parentElement;
            const firstSibling = parent.firstElementChild;
            firstSibling.focus();
        }
        else {
            next.focus();
        }
    }
    onArrowUp() {
        const prev = this.nativeElement.previousElementSibling;
        if (prev === null) {
            const parent = this.nativeElement.parentElement;
            const lastSibling = parent.lastElementChild;
            lastSibling.focus();
        }
        else {
            prev.focus();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: PopoverMenuItemComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: PopoverMenuItemComponent, selector: "vpg-popover-menu-item", inputs: { text: "text", icon: "icon" }, host: { attributes: { "role": "menuitem", "tabindex": "-1" }, listeners: { "keydown.enter": "onClick($event)", "keydown.space": "onClick($event)", "keydown.arrowdown": "onArrowDown()", "keydown.arrowup": "onArrowUp()" }, classAttribute: "v-popover-menu-item body-regular" }, ngImport: i0, template: `
        <vpg-icon-next
            *ngIf="icon"
            [icon]="icon"
        ></vpg-icon-next>
        {{ text }}
        <ng-content></ng-content>
    `, isInline: true, dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: IconNextComponent, selector: "vpg-icon-next", inputs: ["icon", "size", "prefix", "screenReaderText"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: PopoverMenuItemComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vpg-popover-menu-item',
                    template: `
        <vpg-icon-next
            *ngIf="icon"
            [icon]="icon"
        ></vpg-icon-next>
        {{ text }}
        <ng-content></ng-content>
    `,
                    host: {
                        role: 'menuitem',
                        tabindex: '-1',
                        class: 'v-popover-menu-item body-regular'
                    }
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { text: [{
                type: Input
            }], icon: [{
                type: Input
            }], onClick: [{
                type: HostListener,
                args: ['keydown.enter', ['$event']]
            }, {
                type: HostListener,
                args: ['keydown.space', ['$event']]
            }], onArrowDown: [{
                type: HostListener,
                args: ['keydown.arrowdown']
            }], onArrowUp: [{
                type: HostListener,
                args: ['keydown.arrowup']
            }] } });

class PopoverMenuTriggerDirective {
    constructor(element) {
        this.element = element;
        this._ariaExpanded = 'false';
    }
    get ariaHasPopup() {
        return 'true';
    }
    get ariaExpanded() {
        return this._ariaExpanded;
    }
    setAriaExpanded(value) {
        this._ariaExpanded = value;
    }
    getNativeElement() {
        return this.element.nativeElement;
    }
    getSize() {
        const { width, height } = this
            .getNativeElement()
            .getBoundingClientRect();
        return {
            width,
            height
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: PopoverMenuTriggerDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.9", type: PopoverMenuTriggerDirective, selector: "[vpg-popover-menu-trigger]", host: { properties: { "attr.aria-haspopup": "this.ariaHasPopup", "attr.aria-expanded": "this.ariaExpanded" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: PopoverMenuTriggerDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[vpg-popover-menu-trigger]'
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { ariaHasPopup: [{
                type: HostBinding,
                args: ['attr.aria-haspopup']
            }], ariaExpanded: [{
                type: HostBinding,
                args: ['attr.aria-expanded']
            }] } });

class PopoverComponent {
    constructor(ngZone, element, document) {
        this.ngZone = ngZone;
        this.element = element;
        this.document = document;
        this.position = 'center';
        this.menuItems = new QueryList();
        this.destroy$ = new Subject();
    }
    getNativeElement() {
        return this.element.nativeElement;
    }
    ngAfterContentInit() {
        this.registerTriggerClickEvent();
        this.registerOutsideClickEvent();
        this.registerKeydownEvent(this.trigger.getNativeElement(), Keys.ArrowDown, this.openMenu);
        this.registerKeydownEvent(this.trigger.getNativeElement(), Keys.ArrowUp, this.closeMenu);
        this.registerKeydownEvent(this.getNativeElement(), Keys.Escape, this.closeMenu);
        this.registerKeydownEvent(this.getNativeElement(), Keys.Tab, this.closeMenu);
    }
    ngOnDestroy() {
        this.destroy$.complete();
    }
    isMenuOpen() {
        return this.menu.isOpen();
    }
    openMenu() {
        this.menu.open(this.calculateStyles());
        this.trigger.setAriaExpanded('true');
        this.menuItems.first.focus();
    }
    closeMenu() {
        this.menu.close();
        this.trigger.setAriaExpanded('false');
        this.trigger.getNativeElement().focus();
    }
    toggleMenu() {
        if (this.isMenuOpen()) {
            this.closeMenu();
        }
        else {
            this.openMenu();
        }
    }
    registerKeydownEvent(element, key, handler) {
        fromEvent(element, 'keydown')
            .pipe(takeUntil(this.destroy$), filter((e) => e.key === key))
            .subscribe(() => {
            handler.bind(this)();
        });
    }
    registerTriggerClickEvent() {
        fromEvent(this.trigger.getNativeElement(), 'click')
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
            this.toggleMenu();
        });
    }
    registerOutsideClickEvent() {
        this.ngZone.runOutsideAngular(() => {
            fromEvent(this.document, 'click')
                .pipe(takeUntil(this.destroy$))
                .subscribe((e) => {
                if (e.target !== this.trigger.getNativeElement() && this.isMenuOpen()) {
                    this.closeMenu();
                }
            });
        });
    }
    calculateStyles() {
        const menuSize = this.menu.getSize();
        const triggerSize = this.trigger.getSize();
        const styles = {
            'inset-block-start': `${triggerSize.height + 10}px`
        };
        if (this.position === 'center') {
            styles['inset-inline-start'] = `${(triggerSize.width - menuSize.width) / 2}px`;
        }
        if (this.position === 'left') {
            styles['inset-inline-start'] = '0';
        }
        if (this.position === 'right') {
            styles['inset-inline-end'] = '0';
        }
        return styles;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: PopoverComponent, deps: [{ token: i0.NgZone }, { token: i0.ElementRef }, { token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: PopoverComponent, selector: "vpg-popover", inputs: { position: "position" }, host: { classAttribute: "v-popover" }, queries: [{ propertyName: "trigger", first: true, predicate: PopoverMenuTriggerDirective, descendants: true }, { propertyName: "menu", first: true, predicate: PopoverMenuComponent, descendants: true }, { propertyName: "menuItems", predicate: PopoverMenuItemComponent, descendants: true }], ngImport: i0, template: `
        <ng-content></ng-content>
    `, isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: PopoverComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vpg-popover',
                    template: `
        <ng-content></ng-content>
    `,
                    host: {
                        class: 'v-popover'
                    },
                    changeDetection: ChangeDetectionStrategy.OnPush
                }]
        }], ctorParameters: () => [{ type: i0.NgZone }, { type: i0.ElementRef }, { type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }], propDecorators: { position: [{
                type: Input
            }], trigger: [{
                type: ContentChild,
                args: [PopoverMenuTriggerDirective]
            }], menu: [{
                type: ContentChild,
                args: [PopoverMenuComponent]
            }], menuItems: [{
                type: ContentChildren,
                args: [PopoverMenuItemComponent, { descendants: true }]
            }] } });

class PopoverMenuModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: PopoverMenuModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.9", ngImport: i0, type: PopoverMenuModule, declarations: [PopoverComponent,
            PopoverMenuComponent,
            PopoverMenuItemComponent,
            PopoverMenuTriggerDirective], imports: [CommonModule,
            IconModule], exports: [PopoverComponent,
            PopoverMenuComponent,
            PopoverMenuItemComponent,
            PopoverMenuTriggerDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: PopoverMenuModule, imports: [CommonModule,
            IconModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: PopoverMenuModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        IconModule
                    ],
                    declarations: [
                        PopoverComponent,
                        PopoverMenuComponent,
                        PopoverMenuItemComponent,
                        PopoverMenuTriggerDirective
                    ],
                    exports: [
                        PopoverComponent,
                        PopoverMenuComponent,
                        PopoverMenuItemComponent,
                        PopoverMenuTriggerDirective
                    ]
                }]
        }] });

var RADIUS;
(function (RADIUS) {
    RADIUS[RADIUS["SMALL"] = 50] = "SMALL";
    RADIUS[RADIUS["LARGE"] = 55] = "LARGE";
})(RADIUS || (RADIUS = {}));
class ToStrokeDashOffsetPipe {
    transform(percentage, size) {
        const radius = RADIUS[size.toUpperCase()];
        const circumference = Math.round(2 * Math.PI * radius);
        const offset = circumference - (percentage * (circumference / 100));
        const roundedOffset = Math.round(offset);
        return roundedOffset;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ToStrokeDashOffsetPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.3.9", ngImport: i0, type: ToStrokeDashOffsetPipe, name: "toStrokeDashOffset" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ToStrokeDashOffsetPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'toStrokeDashOffset'
                }]
        }] });

class ProgressRingNextComponent {
    constructor() {
        this.id = '';
        this.screenReaderText = '';
        this.percentage = 0;
        this.rail = true;
        this.thick = false;
        this.variant = 'themed';
        this.size = 'large';
        this.backgroundColor = '';
        this.backgroundImage = '';
        this.image = '';
        this.role = 'progressbar';
    }
    get classNames() {
        return {
            ['v-progress-ring']: true,
            [`is-${this.size}`]: true,
            [`is-${this.variant}`]: true,
            ['is-thick']: this.thick,
            ['is-completed']: this.percentage === 100,
            ['has-rail']: this.rail
        };
    }
    get completed() {
        return this.percentage === 100 &&
            this.size === 'small' &&
            (this.variant === 'gated' || this.variant === 'earned');
    }
    get checkmarkVariant() {
        return this.variant;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ProgressRingNextComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: ProgressRingNextComponent, selector: "vpg-progress-ring", inputs: { id: "id", screenReaderText: "screenReaderText", percentage: "percentage", rail: "rail", thick: "thick", variant: "variant", size: "size", backgroundColor: "backgroundColor", backgroundImage: "backgroundImage", image: "image" }, host: { properties: { "id": "this.id", "attr.aria-label": "this.screenReaderText", "attr.aria-valuenow": "this.percentage", "role": "this.role", "class": "this.classNames" } }, ngImport: i0, template: "<vpg-checkmark\n    *ngIf=\"completed\"\n    [variant]=\"checkmarkVariant\"\n/>\n\n<svg\n    *ngIf=\"!completed\"\n    viewBox=\"-5, -5, 120, 120\"\n    class=\"v-progress-ring-circles\"\n    aria-hidden=\"true\"\n>\n    <circle\n        class=\"v-progress-ring-circle\"\n        [style.fill]=\"backgroundColor\"\n        [style.r]=\"'var(--radius)'\"\n        cx=\"50\"\n        cy=\"50\"\n    />\n    <circle\n        class=\"v-progress-ring-circle\"\n        [class.is-progress]=\"percentage < 100\"\n        [class.is-completed]=\"percentage === 100\"\n        [style.stroke-dashoffset]=\"percentage | toStrokeDashOffset: size\"\n        [style.r]=\"'var(--radius)'\"\n        cx=\"50\"\n        cy=\"50\"\n    />\n</svg>\n\n<div\n    *ngIf=\"!completed\"\n    class=\"v-progress-bar-content\"\n    aria-hidden=\"true\"\n>\n    <img\n        vpgImage\n        *ngIf=\"image\"\n        [src]=\"image\"\n        alt=\"\"\n        class=\"v-progress-bar-image\"\n    />\n\n    <img\n        vpgImage\n        *ngIf=\"backgroundImage\"\n        [src]=\"backgroundImage\"\n        alt=\"\"\n        class=\"v-progress-bar-bg-image\"\n    />\n\n    <ng-content />\n</div>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: CheckmarkComponent, selector: "vpg-checkmark", inputs: ["variant"] }, { kind: "directive", type: ImageDirective, selector: "img[vpg-image], img[vpgImage]", inputs: ["fallbackSrc", "type", "size", "alt", "backgroundColor"] }, { kind: "pipe", type: ToStrokeDashOffsetPipe, name: "toStrokeDashOffset" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ProgressRingNextComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-progress-ring', template: "<vpg-checkmark\n    *ngIf=\"completed\"\n    [variant]=\"checkmarkVariant\"\n/>\n\n<svg\n    *ngIf=\"!completed\"\n    viewBox=\"-5, -5, 120, 120\"\n    class=\"v-progress-ring-circles\"\n    aria-hidden=\"true\"\n>\n    <circle\n        class=\"v-progress-ring-circle\"\n        [style.fill]=\"backgroundColor\"\n        [style.r]=\"'var(--radius)'\"\n        cx=\"50\"\n        cy=\"50\"\n    />\n    <circle\n        class=\"v-progress-ring-circle\"\n        [class.is-progress]=\"percentage < 100\"\n        [class.is-completed]=\"percentage === 100\"\n        [style.stroke-dashoffset]=\"percentage | toStrokeDashOffset: size\"\n        [style.r]=\"'var(--radius)'\"\n        cx=\"50\"\n        cy=\"50\"\n    />\n</svg>\n\n<div\n    *ngIf=\"!completed\"\n    class=\"v-progress-bar-content\"\n    aria-hidden=\"true\"\n>\n    <img\n        vpgImage\n        *ngIf=\"image\"\n        [src]=\"image\"\n        alt=\"\"\n        class=\"v-progress-bar-image\"\n    />\n\n    <img\n        vpgImage\n        *ngIf=\"backgroundImage\"\n        [src]=\"backgroundImage\"\n        alt=\"\"\n        class=\"v-progress-bar-bg-image\"\n    />\n\n    <ng-content />\n</div>\n" }]
        }], propDecorators: { id: [{
                type: Input
            }, {
                type: HostBinding
            }], screenReaderText: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['attr.aria-label']
            }], percentage: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['attr.aria-valuenow']
            }], rail: [{
                type: Input
            }], thick: [{
                type: Input
            }], variant: [{
                type: Input
            }], size: [{
                type: Input
            }], backgroundColor: [{
                type: Input
            }], backgroundImage: [{
                type: Input
            }], image: [{
                type: Input
            }], role: [{
                type: HostBinding,
                args: ['role']
            }], classNames: [{
                type: HostBinding,
                args: ['class']
            }] } });

class ProgressRingModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ProgressRingModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.9", ngImport: i0, type: ProgressRingModule, declarations: [ProgressRingNextComponent,
            ToStrokeDashOffsetPipe], imports: [CommonModule,
            CheckmarkModule,
            ImageModule], exports: [ProgressRingNextComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ProgressRingModule, imports: [CommonModule,
            CheckmarkModule,
            ImageModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ProgressRingModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        CheckmarkModule,
                        ImageModule
                    ],
                    declarations: [
                        ProgressRingNextComponent,
                        ToStrokeDashOffsetPipe
                    ],
                    exports: [
                        ProgressRingNextComponent
                    ]
                }]
        }] });

class PromotedContainerComponent {
    constructor() {
        this.id = '';
        this.variant = 'blue';
        this.triangleSize = 'medium';
        this.fullWidth = false;
        this.icon = '';
        this.image = '';
        this.actionTemplate = null;
    }
    get classNames() {
        return {
            [`is-${this.variant}`]: true,
            [`is-${this.triangleSize}`]: true,
            ['is-full-width']: this.fullWidth
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: PromotedContainerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: PromotedContainerComponent, selector: "vpg-promoted-container", inputs: { id: "id", variant: "variant", triangleSize: "triangleSize", fullWidth: "fullWidth", icon: "icon", image: "image" }, host: { properties: { "id": "this.id", "class": "this.classNames" }, classAttribute: "v-promoted-container" }, queries: [{ propertyName: "actionTemplate", first: true, predicate: ["action"], descendants: true }], ngImport: i0, template: "<ng-content></ng-content>\n<div class=\"v-promoted-container-triangle\">\n</div>\n<div class=\"v-promoted-container-triangle-content\">\n    <img\n        *ngIf=\"image\"\n        [id]=\"id + '-img'\"\n        class=\"v-promoted-container-icon\"\n        alt=\"\"\n        aria-hidden=\"true\"\n        [src]=\"image\"\n    />\n    <vpg-icon-next\n        *ngIf=\"icon\"\n        [id]=\"id + '-icon'\"\n        class=\"v-promoted-container-icon\"\n        [icon]=\"icon\"\n    >\n    </vpg-icon-next>\n    <ng-container [ngTemplateOutlet]=\"actionTemplate\">\n    </ng-container>\n</div>\n\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "component", type: IconNextComponent, selector: "vpg-icon-next", inputs: ["icon", "size", "prefix", "screenReaderText"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: PromotedContainerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-promoted-container', host: {
                        'class': 'v-promoted-container'
                    }, template: "<ng-content></ng-content>\n<div class=\"v-promoted-container-triangle\">\n</div>\n<div class=\"v-promoted-container-triangle-content\">\n    <img\n        *ngIf=\"image\"\n        [id]=\"id + '-img'\"\n        class=\"v-promoted-container-icon\"\n        alt=\"\"\n        aria-hidden=\"true\"\n        [src]=\"image\"\n    />\n    <vpg-icon-next\n        *ngIf=\"icon\"\n        [id]=\"id + '-icon'\"\n        class=\"v-promoted-container-icon\"\n        [icon]=\"icon\"\n    >\n    </vpg-icon-next>\n    <ng-container [ngTemplateOutlet]=\"actionTemplate\">\n    </ng-container>\n</div>\n\n" }]
        }], propDecorators: { id: [{
                type: Input
            }, {
                type: HostBinding
            }], variant: [{
                type: Input
            }], triangleSize: [{
                type: Input
            }], fullWidth: [{
                type: Input
            }], icon: [{
                type: Input
            }], image: [{
                type: Input
            }], actionTemplate: [{
                type: ContentChild,
                args: ['action']
            }], classNames: [{
                type: HostBinding,
                args: ['class']
            }] } });

class PromotedContainerModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: PromotedContainerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.9", ngImport: i0, type: PromotedContainerModule, declarations: [PromotedContainerComponent], imports: [CommonModule,
            IconModule,
            ImageModule], exports: [PromotedContainerComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: PromotedContainerModule, imports: [CommonModule,
            IconModule,
            ImageModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: PromotedContainerModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        IconModule,
                        ImageModule
                    ],
                    declarations: [
                        PromotedContainerComponent
                    ],
                    exports: [
                        PromotedContainerComponent
                    ]
                }]
        }] });

class QuestionnaireComponent {
    constructor() {
        this.id = '';
        this.leftLabel = '';
        this.rightLabel = '';
        this.questionnaireType = QuestionnaireType.QuestionnaireTypeSeven;
        this.valueSelected = new EventEmitter();
        this.data = [];
    }
    ngOnChanges(changes) {
        if (changes.questionnaireType) {
            this.setQuestionnaireArray();
        }
    }
    onValueChange() {
        this.valueSelected.emit(this.selectedIndex);
    }
    setQuestionnaireArray() {
        this.data = Array(this.questionnaireType);
        for (let i = 0; i < this.data.length; i++) {
            this.data[i] = i;
        }
        if (this.selectedIndex === undefined) {
            this.selectedIndex = Math.trunc(this.data.length / 2);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: QuestionnaireComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: QuestionnaireComponent, selector: "vpg-questionnaire", inputs: { id: "id", selectedIndex: "selectedIndex", leftLabel: "leftLabel", rightLabel: "rightLabel", questionnaireType: "questionnaireType" }, outputs: { valueSelected: "valueSelected" }, host: { properties: { "id": "this.id" }, classAttribute: "v-questionnaire" }, usesOnChanges: true, ngImport: i0, template: "<ng-content select=\"vpg-questionnaire-images\"></ng-content>\n<div class=\"v-questionnaire-inputs flex justify-content-between align-items-center\">\n    <input\n        class=\"v-questionnaire-input\"\n        *ngFor=\"let item of data; let i = index\"\n        role=\"radio\"\n        type=\"radio\"\n        [(ngModel)]=\"selectedIndex\"\n        (ngModelChange)=\"onValueChange()\"\n        [value]=\"item\"\n        name='questionnaireInput'\n        id=\"{{id}}-input-{{i}}\"\n        [attr.aria-describedby]=\"id + '-' + i\"\n    >\n</div>\n<div class=\"flex justify-content-between mt-md body-regular gap-10\">\n    <span\n        aria-hidden=\"true\"\n        class=\"v-questionnaire-left-label\"\n        id=\"{{id}}-0\"\n    >{{ leftLabel }}</span>\n    <span\n        aria-hidden=\"true\"\n        class=\"v-questionnaire-right-label\"\n        id=\"{{id}}-{{data.length - 1}}\"\n    >{{ rightLabel }}</span>\n</div>\n", dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2.RadioControlValueAccessor, selector: "input[type=radio][formControlName],input[type=radio][formControl],input[type=radio][ngModel]", inputs: ["name", "formControlName", "value"] }, { kind: "directive", type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: QuestionnaireComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-questionnaire', host: {
                        class: 'v-questionnaire'
                    }, template: "<ng-content select=\"vpg-questionnaire-images\"></ng-content>\n<div class=\"v-questionnaire-inputs flex justify-content-between align-items-center\">\n    <input\n        class=\"v-questionnaire-input\"\n        *ngFor=\"let item of data; let i = index\"\n        role=\"radio\"\n        type=\"radio\"\n        [(ngModel)]=\"selectedIndex\"\n        (ngModelChange)=\"onValueChange()\"\n        [value]=\"item\"\n        name='questionnaireInput'\n        id=\"{{id}}-input-{{i}}\"\n        [attr.aria-describedby]=\"id + '-' + i\"\n    >\n</div>\n<div class=\"flex justify-content-between mt-md body-regular gap-10\">\n    <span\n        aria-hidden=\"true\"\n        class=\"v-questionnaire-left-label\"\n        id=\"{{id}}-0\"\n    >{{ leftLabel }}</span>\n    <span\n        aria-hidden=\"true\"\n        class=\"v-questionnaire-right-label\"\n        id=\"{{id}}-{{data.length - 1}}\"\n    >{{ rightLabel }}</span>\n</div>\n" }]
        }], propDecorators: { id: [{
                type: Input
            }, {
                type: HostBinding
            }], selectedIndex: [{
                type: Input
            }], leftLabel: [{
                type: Input
            }], rightLabel: [{
                type: Input
            }], questionnaireType: [{
                type: Input
            }], valueSelected: [{
                type: Output
            }] } });

class QuestionnaireImagesComponent {
    constructor() {
        this.id = '';
        this.leftImageUrl = '';
        this.leftImageAlt = '';
        this.rightImageUrl = '';
        this.rightImageAlt = '';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: QuestionnaireImagesComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: QuestionnaireImagesComponent, selector: "vpg-questionnaire-images", inputs: { id: "id", leftImageUrl: "leftImageUrl", leftImageAlt: "leftImageAlt", rightImageUrl: "rightImageUrl", rightImageAlt: "rightImageAlt" }, host: { properties: { "id": "this.id" }, classAttribute: "v-questionnaire-images flex justify-content-between" }, ngImport: i0, template: "<img\n    vpg-image\n    class=\"h-96 w-96\"\n    id=\"{{id}}-left-image\"\n    [src]=\"leftImageUrl\"\n    [alt]=\"leftImageAlt\"\n/>\n<img\n    vpg-image\n    class=\"h-96 w-96\"\n    id=\"{{id}}-right-image\"\n    [src]=\"rightImageUrl\"\n    [alt]=\"rightImageAlt\"\n/>\n", dependencies: [{ kind: "directive", type: ImageDirective, selector: "img[vpg-image], img[vpgImage]", inputs: ["fallbackSrc", "type", "size", "alt", "backgroundColor"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: QuestionnaireImagesComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-questionnaire-images', host: {
                        'class': 'v-questionnaire-images flex justify-content-between'
                    }, template: "<img\n    vpg-image\n    class=\"h-96 w-96\"\n    id=\"{{id}}-left-image\"\n    [src]=\"leftImageUrl\"\n    [alt]=\"leftImageAlt\"\n/>\n<img\n    vpg-image\n    class=\"h-96 w-96\"\n    id=\"{{id}}-right-image\"\n    [src]=\"rightImageUrl\"\n    [alt]=\"rightImageAlt\"\n/>\n" }]
        }], propDecorators: { id: [{
                type: Input
            }, {
                type: HostBinding
            }], leftImageUrl: [{
                type: Input
            }], leftImageAlt: [{
                type: Input
            }], rightImageUrl: [{
                type: Input
            }], rightImageAlt: [{
                type: Input
            }] } });

class QuestionnaireModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: QuestionnaireModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.9", ngImport: i0, type: QuestionnaireModule, declarations: [QuestionnaireComponent,
            QuestionnaireImagesComponent], imports: [CommonModule,
            FormsModule,
            ImageModule], exports: [QuestionnaireComponent,
            QuestionnaireImagesComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: QuestionnaireModule, imports: [CommonModule,
            FormsModule,
            ImageModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: QuestionnaireModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule,
                        ImageModule
                    ],
                    declarations: [
                        QuestionnaireComponent,
                        QuestionnaireImagesComponent
                    ],
                    exports: [
                        QuestionnaireComponent,
                        QuestionnaireImagesComponent
                    ]
                }]
        }] });

class RadioButtonModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: RadioButtonModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.9", ngImport: i0, type: RadioButtonModule, declarations: [RadioButtonComponent,
            RadioButtonGroupComponent], imports: [CommonModule,
            FormsModule,
            ReactiveFormsModule], exports: [RadioButtonComponent,
            RadioButtonGroupComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: RadioButtonModule, imports: [CommonModule,
            FormsModule,
            ReactiveFormsModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: RadioButtonModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule,
                        ReactiveFormsModule
                    ],
                    declarations: [
                        RadioButtonComponent,
                        RadioButtonGroupComponent
                    ],
                    exports: [
                        RadioButtonComponent,
                        RadioButtonGroupComponent
                    ]
                }]
        }] });

const DEBOUNCE_TIME = 300;
class SearchComponent extends AbstractFormComponent {
    constructor() {
        super();
        this.id = '';
        this.placeholder = '';
        this.showResults = true;
        this.results = [];
        this.ariaLabel = '';
        this.clearIconLabel = '';
        this.highlightTextOnSearch = true;
        this.minCharacters = 3;
        this.searched = new EventEmitter();
        this.listOpen = false;
        this.term = new BehaviorSubject('');
        this.term$ = this.term.asObservable();
        this.search$ = new Observable();
        this.showClearIcon$ = new Observable();
        this.showEmptyList$ = new Observable();
    }
    ngAfterViewInit() {
        this.subscribeToTerm();
        this.initilizeShowClearIconStream();
        this.initilizeShowEmptyList();
        if (this.showResults) {
            this.setupCombobox();
        }
    }
    handleInput(e) {
        this.term.next(e.target.value);
    }
    selectOption(option) {
        this.writeValue(option);
        this.term.next(option);
    }
    setInputAriaActiveDescendant(option) {
        this.input.nativeElement.setAttribute('aria-activedescendant', option.id);
    }
    clearSearch() {
        this.input.nativeElement.focus();
        this.writeValue('');
        this.term.next('');
    }
    showListIfResultsExist() {
        if (this.results.length > 0) {
            this.openList();
        }
    }
    openList() {
        this.listOpen = true;
        if (this.showResults) {
            this.input.nativeElement.setAttribute('aria-expanded', 'true');
        }
    }
    hideList() {
        this.listOpen = false;
        if (this.showResults) {
            this.input.nativeElement.setAttribute('aria-expanded', 'false');
        }
    }
    subscribeToTerm() {
        this.term$.pipe(debounceTime(DEBOUNCE_TIME), distinctUntilChanged()).subscribe((term) => {
            if (term === this.value) {
                this.searched.emit(term);
                this.hideList();
                return;
            }
            if (term.length >= this.minCharacters) {
                this.searched.emit(term);
                this.openList();
            }
            else if (term.length < this.minCharacters) {
                this.searched.emit('');
                this.hideList();
            }
        });
    }
    initilizeShowClearIconStream() {
        this.showClearIcon$ = this.term$.pipe(map((term) => term.length > 0));
    }
    initilizeShowEmptyList() {
        this.showEmptyList$ = this.term$.pipe(map((term) => term.length > 0 && this.results.length > 0));
    }
    setupCombobox() {
        const input = this.input.nativeElement;
        input.setAttribute('role', 'combobox');
        input.setAttribute('aria-controls', `${this.id}-listbox`);
        input.setAttribute('aria-autocomplete', 'list');
        input.setAttribute('aria-activedescendant', '');
        input.setAttribute('aria-expanded', `${this.listOpen}`);
        input.setAttribute('aria-expanded', `${this.listOpen}`);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: SearchComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: SearchComponent, selector: "vpg-search", inputs: { id: "id", placeholder: "placeholder", showResults: "showResults", results: "results", ariaLabel: "ariaLabel", clearIconLabel: "clearIconLabel", highlightTextOnSearch: "highlightTextOnSearch", minCharacters: "minCharacters" }, outputs: { searched: "searched" }, host: { classAttribute: "v-input-field v-search" }, providers: [valueAccessor(SearchComponent)], viewQueries: [{ propertyName: "input", first: true, predicate: ["search"], descendants: true, read: ElementRef }], usesInheritance: true, ngImport: i0, template: "<div\n    class=\"v-control-container\"\n    [class.has-popup]=\"showResults && listOpen\"\n>\n    <vpg-control-prefix>\n        <vpg-icon-next icon=\"search\"></vpg-icon-next>\n    </vpg-control-prefix>\n    <input\n        #search\n        vpgInputSelectionRange\n        id=\"{{id}}-input\"\n        class=\"v-control-input has-prefix has-suffix\"\n        autocomplete=\"off\"\n        [name]=\"name\"\n        [value]=\"term$ | async\"\n        [placeholder]=\"placeholder\"\n        [disabled]=\"disabled\"\n        [attr.aria-label]=\"ariaLabel\"\n        [attr.aria-disabled]=\"disabled\"\n        (blur)=\"hideList()\"\n        (input)=\"handleInput($event)\"\n        (click)=\"showListIfResultsExist()\"\n        (keyup.arrowdown)=\"showListIfResultsExist()\"\n        (keyup.escape)=\"hideList()\"\n    />\n    <vpg-control-suffix *ngIf=\"showClearIcon$ | async\">\n        <vpg-icon-next\n            icon=\"times\"\n            id=\"{{id}}-times-icon\"\n            class=\"clear-button\"\n            [screenReaderText]=\"clearIconLabel\"\n            (vpg-interactive-element)=\"clearSearch()\"\n        ></vpg-icon-next>\n    </vpg-control-suffix>\n\n    <vpg-control-list\n        *ngIf=\"showResults && results.length > 0 && listOpen\"\n        [options]=\"results\"\n        [optionLabel]=\"'name'\"\n        [truncateText]=\"true\"\n        [highlightSearch]=\"highlightTextOnSearch\"\n        [highlightText]=\"(term$ | async) || ''\"\n        (selected)=\"selectOption($event)\"\n        (focused)=\"setInputAriaActiveDescendant($event)\"\n    ></vpg-control-list>\n\n    <vpg-control-empty-list\n        *ngIf=\"showResults && results.length === 0 && listOpen\"\n        id=\"{{id}}-empty-list\"\n    >\n    </vpg-control-empty-list>\n</div>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: IconNextComponent, selector: "vpg-icon-next", inputs: ["icon", "size", "prefix", "screenReaderText"] }, { kind: "component", type: ControlPrefixComponent, selector: "vpg-control-prefix" }, { kind: "component", type: ControlSuffixComponent, selector: "vpg-control-suffix" }, { kind: "component", type: ControlEmptyListComponent, selector: "vpg-control-empty-list" }, { kind: "component", type: ControlListComponent, selector: "vpg-control-list", inputs: ["options", "optionLabel", "value", "name", "truncateText", "highlightText", "highlightSearch"], outputs: ["selected", "focused"] }, { kind: "directive", type: InteractiveElementDirective, selector: "[vpg-interactive-element]", inputs: ["tabindex", "role", "screenReaderText"], outputs: ["vpg-interactive-element"] }, { kind: "directive", type: InputSelectionRangeDirective, selector: "[vpgInputSelectionRange]" }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: SearchComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-search', providers: [valueAccessor(SearchComponent)], host: {
                        class: 'v-input-field v-search'
                    }, template: "<div\n    class=\"v-control-container\"\n    [class.has-popup]=\"showResults && listOpen\"\n>\n    <vpg-control-prefix>\n        <vpg-icon-next icon=\"search\"></vpg-icon-next>\n    </vpg-control-prefix>\n    <input\n        #search\n        vpgInputSelectionRange\n        id=\"{{id}}-input\"\n        class=\"v-control-input has-prefix has-suffix\"\n        autocomplete=\"off\"\n        [name]=\"name\"\n        [value]=\"term$ | async\"\n        [placeholder]=\"placeholder\"\n        [disabled]=\"disabled\"\n        [attr.aria-label]=\"ariaLabel\"\n        [attr.aria-disabled]=\"disabled\"\n        (blur)=\"hideList()\"\n        (input)=\"handleInput($event)\"\n        (click)=\"showListIfResultsExist()\"\n        (keyup.arrowdown)=\"showListIfResultsExist()\"\n        (keyup.escape)=\"hideList()\"\n    />\n    <vpg-control-suffix *ngIf=\"showClearIcon$ | async\">\n        <vpg-icon-next\n            icon=\"times\"\n            id=\"{{id}}-times-icon\"\n            class=\"clear-button\"\n            [screenReaderText]=\"clearIconLabel\"\n            (vpg-interactive-element)=\"clearSearch()\"\n        ></vpg-icon-next>\n    </vpg-control-suffix>\n\n    <vpg-control-list\n        *ngIf=\"showResults && results.length > 0 && listOpen\"\n        [options]=\"results\"\n        [optionLabel]=\"'name'\"\n        [truncateText]=\"true\"\n        [highlightSearch]=\"highlightTextOnSearch\"\n        [highlightText]=\"(term$ | async) || ''\"\n        (selected)=\"selectOption($event)\"\n        (focused)=\"setInputAriaActiveDescendant($event)\"\n    ></vpg-control-list>\n\n    <vpg-control-empty-list\n        *ngIf=\"showResults && results.length === 0 && listOpen\"\n        id=\"{{id}}-empty-list\"\n    >\n    </vpg-control-empty-list>\n</div>\n" }]
        }], ctorParameters: () => [], propDecorators: { id: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], showResults: [{
                type: Input
            }], results: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], clearIconLabel: [{
                type: Input
            }], highlightTextOnSearch: [{
                type: Input
            }], minCharacters: [{
                type: Input
            }], searched: [{
                type: Output
            }], input: [{
                type: ViewChild,
                args: ['search', { read: ElementRef }]
            }] } });

class SearchModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: SearchModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.9", ngImport: i0, type: SearchModule, declarations: [SearchComponent], imports: [CommonModule,
            IconModule,
            ControlModule,
            InteractiveElementDirective,
            InputSelectionRangeDirective], exports: [SearchComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: SearchModule, imports: [CommonModule,
            IconModule,
            ControlModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: SearchModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        IconModule,
                        ControlModule,
                        InteractiveElementDirective,
                        InputSelectionRangeDirective
                    ],
                    declarations: [
                        SearchComponent
                    ],
                    exports: [
                        SearchComponent
                    ]
                }]
        }] });

class SegmentedProgressBarIndicatorComponent {
    constructor() {
        this.orientation = 'horizontal';
        this.min = 0;
        this.max = 0;
        this.progress = 0;
        this.ariaHidden = true;
    }
    get classNames() {
        return {
            ['v-segmented-progress-bar-indicator']: true,
            [`is-${this.orientation}`]: true,
            ['is-completed']: this.progress === 100
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: SegmentedProgressBarIndicatorComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: SegmentedProgressBarIndicatorComponent, selector: "vpg-segmented-progress-bar-indicator", inputs: { orientation: "orientation", min: "min", max: "max", progress: "progress" }, host: { properties: { "style.--progress.%": "this.progress", "attr.aria-hidden": "this.ariaHidden", "class": "this.classNames" } }, ngImport: i0, template: '', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: SegmentedProgressBarIndicatorComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vpg-segmented-progress-bar-indicator',
                    template: '',
                    changeDetection: ChangeDetectionStrategy.OnPush
                }]
        }], propDecorators: { orientation: [{
                type: Input
            }], min: [{
                type: Input
            }], max: [{
                type: Input
            }], progress: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['style.--progress.%']
            }], ariaHidden: [{
                type: HostBinding,
                args: ['attr.aria-hidden']
            }], classNames: [{
                type: HostBinding,
                args: ['class']
            }] } });

class SegmentedProgressBarSegmentComponent {
    constructor() {
        this.points = 0;
        this.orientation = 'horizontal';
        this.screenReaderText = '';
        this.id = '';
        this.role = 'listitem';
    }
    get classNames() {
        return {
            ['v-segmented-progress-bar-segment']: true,
            [`is-${this.orientation}`]: true
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: SegmentedProgressBarSegmentComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: SegmentedProgressBarSegmentComponent, selector: "vpg-segmented-progress-bar-segment", inputs: { points: "points", orientation: "orientation", screenReaderText: "screenReaderText", id: "id" }, host: { properties: { "id": "this.id", "role": "this.role", "class": "this.classNames" } }, ngImport: i0, template: `
        <span class="sr-only">
            {{ screenReaderText }}
        </span>
        <ng-content select="vpg-segmented-progress-bar-step" />
        <ng-content select="vpg-segmented-progress-bar-indicator" />
        <div class="v-segmented-progress-bar-text">
            <ng-content select="vpg-segmented-progress-bar-title" />
            <ng-content select="vpg-segmented-progress-bar-description" />
        </div>
    `, isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: SegmentedProgressBarSegmentComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vpg-segmented-progress-bar-segment',
                    template: `
        <span class="sr-only">
            {{ screenReaderText }}
        </span>
        <ng-content select="vpg-segmented-progress-bar-step" />
        <ng-content select="vpg-segmented-progress-bar-indicator" />
        <div class="v-segmented-progress-bar-text">
            <ng-content select="vpg-segmented-progress-bar-title" />
            <ng-content select="vpg-segmented-progress-bar-description" />
        </div>
    `,
                    changeDetection: ChangeDetectionStrategy.OnPush
                }]
        }], propDecorators: { points: [{
                type: Input
            }], orientation: [{
                type: Input
            }], screenReaderText: [{
                type: Input
            }], id: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['id']
            }], role: [{
                type: HostBinding,
                args: ['role']
            }], classNames: [{
                type: HostBinding,
                args: ['class']
            }] } });

class SegmentedProgressBarNumberComponent {
    get classNames() {
        return 'v-segmented-progress-bar-number';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: SegmentedProgressBarNumberComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: SegmentedProgressBarNumberComponent, selector: "vpg-segmented-progress-bar-number", host: { properties: { "class": "this.classNames" } }, ngImport: i0, template: '<ng-content />', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: SegmentedProgressBarNumberComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vpg-segmented-progress-bar-number',
                    template: '<ng-content />'
                }]
        }], propDecorators: { classNames: [{
                type: HostBinding,
                args: ['class']
            }] } });

class SegmentedProgressBarStepComponent {
    constructor() {
        this.number = '';
        this.icon = '';
        this.image = '';
        this.completed = false;
        this.checkmark = false;
        this.points = 0;
        this.ariaHidden = true;
    }
    get classNames() {
        return {
            ['v-segmented-progress-bar-step']: true,
            ['is-completed']: this.completed === true
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: SegmentedProgressBarStepComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: SegmentedProgressBarStepComponent, selector: "vpg-segmented-progress-bar-step", inputs: { number: "number", icon: "icon", image: "image", completed: "completed", checkmark: "checkmark", points: "points" }, host: { properties: { "attr.aria-hidden": "this.ariaHidden", "class": "this.classNames" } }, exportAs: ["step"], ngImport: i0, template: `
        <ng-content />
        <vpg-checkmark
            *ngIf="checkmark"
            class="v-segmented-progress-bar-circle-checkmark"
        />
        <vpg-icon-next
            *ngIf="icon"
            [icon]="icon"
            size="2x"
        />
        <img
            vpgImage
            type="avatar"
            size="small"
            *ngIf="image"
            [src]="image"
            [alt]="''"
            role="presentation"
            class="v-segmented-progress-bar-circle-image"
        />
        <vpg-segmented-progress-bar-number *ngIf="number">
            {{ number }}
        </vpg-segmented-progress-bar-number>

    `, isInline: true, dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: IconNextComponent, selector: "vpg-icon-next", inputs: ["icon", "size", "prefix", "screenReaderText"] }, { kind: "directive", type: ImageDirective, selector: "img[vpg-image], img[vpgImage]", inputs: ["fallbackSrc", "type", "size", "alt", "backgroundColor"] }, { kind: "component", type: CheckmarkComponent, selector: "vpg-checkmark", inputs: ["variant"] }, { kind: "component", type: SegmentedProgressBarNumberComponent, selector: "vpg-segmented-progress-bar-number" }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: SegmentedProgressBarStepComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vpg-segmented-progress-bar-step',
                    template: `
        <ng-content />
        <vpg-checkmark
            *ngIf="checkmark"
            class="v-segmented-progress-bar-circle-checkmark"
        />
        <vpg-icon-next
            *ngIf="icon"
            [icon]="icon"
            size="2x"
        />
        <img
            vpgImage
            type="avatar"
            size="small"
            *ngIf="image"
            [src]="image"
            [alt]="''"
            role="presentation"
            class="v-segmented-progress-bar-circle-image"
        />
        <vpg-segmented-progress-bar-number *ngIf="number">
            {{ number }}
        </vpg-segmented-progress-bar-number>

    `,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    exportAs: 'step'
                }]
        }], propDecorators: { number: [{
                type: Input
            }], icon: [{
                type: Input
            }], image: [{
                type: Input
            }], completed: [{
                type: Input
            }], checkmark: [{
                type: Input
            }], points: [{
                type: Input
            }], ariaHidden: [{
                type: HostBinding,
                args: ['attr.aria-hidden']
            }], classNames: [{
                type: HostBinding,
                args: ['class']
            }] } });

class SegmentedProgressBarComponent {
    constructor() {
        this.MAX_SEGMENTS = 5;
        this.MIN_SEGMENTS = 2;
        this.orientation = 'horizontal';
        this.progress = 0;
        this.total = 0;
        this.screenReaderText = '';
        this.role = 'list';
        this.segments = new QueryList();
        this.indicators = new QueryList();
        this.steps = new QueryList();
    }
    get classNames() {
        return {
            ['v-segmented-progress-bar']: true,
            [`is-${this.orientation}`]: true
        };
    }
    ngAfterContentInit() {
        this.updateChildren();
        this.validateNumberOfSegments();
    }
    ngOnChanges() {
        this.updateChildren();
    }
    validateNumberOfSegments() {
        if (this.segments.length < this.MIN_SEGMENTS) {
            throw new Error(`The minimum number of segments is ${this.MIN_SEGMENTS}.`);
        }
        else if (this.segments.length > this.MAX_SEGMENTS) {
            throw new Error(`The maximum number of segments is ${this.MAX_SEGMENTS}.`);
        }
    }
    updateChildren() {
        this.updateSegments();
        this.updateIndicators();
        this.updateSteps();
    }
    inRange(number, min, max) {
        return number >= min && number <= max;
    }
    overRange(progress, max) {
        return progress >= max;
    }
    calculatePercentage(segmentMax, segmentMin) {
        const max = segmentMax - segmentMin;
        const total = this.progress - segmentMin;
        return (100 * total) / max;
    }
    updateIndicators() {
        this.indicators.forEach((indicator) => {
            if (this.overRange(this.progress, indicator.max)) {
                indicator.progress = 100;
            }
            else if (this.inRange(this.progress, indicator.min, indicator.max)) {
                indicator.progress = this.calculatePercentage(indicator.max, indicator.min);
            }
            else {
                indicator.progress = 0;
            }
            indicator.orientation = this.orientation;
        });
    }
    updateSteps() {
        this.steps.forEach((step) => {
            step.completed = this.progress >= step.points;
        });
    }
    updateSegments() {
        this.segments.forEach((segment) => {
            segment.orientation = this.orientation;
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: SegmentedProgressBarComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: SegmentedProgressBarComponent, selector: "vpg-segmented-progress-bar", inputs: { orientation: "orientation", progress: "progress", total: "total", screenReaderText: "screenReaderText" }, host: { properties: { "attr.aria-label": "this.screenReaderText", "role": "this.role", "class": "this.classNames" } }, queries: [{ propertyName: "segments", predicate: SegmentedProgressBarSegmentComponent }, { propertyName: "indicators", predicate: SegmentedProgressBarIndicatorComponent, descendants: true }, { propertyName: "steps", predicate: SegmentedProgressBarStepComponent, descendants: true }], usesOnChanges: true, ngImport: i0, template: '<ng-content />', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: SegmentedProgressBarComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vpg-segmented-progress-bar',
                    template: '<ng-content />'
                }]
        }], propDecorators: { orientation: [{
                type: Input
            }], progress: [{
                type: Input
            }], total: [{
                type: Input
            }], screenReaderText: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['attr.aria-label']
            }], role: [{
                type: HostBinding,
                args: ['role']
            }], classNames: [{
                type: HostBinding,
                args: ['class']
            }], segments: [{
                type: ContentChildren,
                args: [SegmentedProgressBarSegmentComponent]
            }], indicators: [{
                type: ContentChildren,
                args: [SegmentedProgressBarIndicatorComponent, { descendants: true }]
            }], steps: [{
                type: ContentChildren,
                args: [SegmentedProgressBarStepComponent, { descendants: true }]
            }] } });

class SegmentedProgressBarDescription {
    get classNames() {
        return 'v-segmented-progress-bar-description';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: SegmentedProgressBarDescription, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: SegmentedProgressBarDescription, selector: "vpg-segmented-progress-bar-description", host: { properties: { "class": "this.classNames" } }, ngImport: i0, template: '<ng-content />', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: SegmentedProgressBarDescription, decorators: [{
            type: Component,
            args: [{
                    selector: 'vpg-segmented-progress-bar-description',
                    template: '<ng-content />'
                }]
        }], propDecorators: { classNames: [{
                type: HostBinding,
                args: ['class']
            }] } });

class SegmentedProgressBarTitle {
    constructor() {
        this.bold = false;
    }
    get classNames() {
        return {
            ['v-segmented-progress-bar-title']: true,
            ['is-bold']: this.bold
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: SegmentedProgressBarTitle, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: SegmentedProgressBarTitle, selector: "vpg-segmented-progress-bar-title", inputs: { bold: "bold" }, host: { properties: { "class": "this.classNames" } }, ngImport: i0, template: '<ng-content />', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: SegmentedProgressBarTitle, decorators: [{
            type: Component,
            args: [{
                    selector: 'vpg-segmented-progress-bar-title',
                    template: '<ng-content />'
                }]
        }], propDecorators: { bold: [{
                type: Input
            }], classNames: [{
                type: HostBinding,
                args: ['class']
            }] } });

class SegmentedProgressBarModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: SegmentedProgressBarModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.9", ngImport: i0, type: SegmentedProgressBarModule, declarations: [SegmentedProgressBarComponent,
            SegmentedProgressBarSegmentComponent,
            SegmentedProgressBarIndicatorComponent,
            SegmentedProgressBarStepComponent,
            SegmentedProgressBarNumberComponent,
            SegmentedProgressBarTitle,
            SegmentedProgressBarDescription], imports: [CommonModule,
            IconModule,
            ImageModule,
            CheckmarkModule], exports: [SegmentedProgressBarComponent,
            SegmentedProgressBarSegmentComponent,
            SegmentedProgressBarIndicatorComponent,
            SegmentedProgressBarStepComponent,
            SegmentedProgressBarNumberComponent,
            SegmentedProgressBarTitle,
            SegmentedProgressBarDescription] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: SegmentedProgressBarModule, imports: [CommonModule,
            IconModule,
            ImageModule,
            CheckmarkModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: SegmentedProgressBarModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        IconModule,
                        ImageModule,
                        CheckmarkModule
                    ],
                    declarations: [
                        SegmentedProgressBarComponent,
                        SegmentedProgressBarSegmentComponent,
                        SegmentedProgressBarIndicatorComponent,
                        SegmentedProgressBarStepComponent,
                        SegmentedProgressBarNumberComponent,
                        SegmentedProgressBarTitle,
                        SegmentedProgressBarDescription
                    ],
                    exports: [
                        SegmentedProgressBarComponent,
                        SegmentedProgressBarSegmentComponent,
                        SegmentedProgressBarIndicatorComponent,
                        SegmentedProgressBarStepComponent,
                        SegmentedProgressBarNumberComponent,
                        SegmentedProgressBarTitle,
                        SegmentedProgressBarDescription
                    ]
                }]
        }] });

class SimpleProgressBarComponent {
    constructor() {
        this.id = '';
        this.size = 'large';
        this.headerText = '';
        this.additionalInfo = '';
        this.additionalInfoRight = '';
        this.progressAriaLabel = '';
        this.indicator = true;
        this.progressVariant = 'themed';
        this.completionVariant = 'success';
        this.progress = 0;
    }
    get classNames() {
        return {
            ['v-simple-progress-bar']: true,
            [`is-${this.size}`]: true,
            [`is-in-progress-${this.progressVariant}`]: this.progress > 0 && this.progress < 100,
            [`is-completed-${this.completionVariant}`]: this.progress >= 100
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: SimpleProgressBarComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: SimpleProgressBarComponent, selector: "vpg-simple-progress-bar", inputs: { id: "id", size: "size", headerText: "headerText", additionalInfo: "additionalInfo", additionalInfoRight: "additionalInfoRight", progressAriaLabel: "progressAriaLabel", indicator: "indicator", progressVariant: "progressVariant", completionVariant: "completionVariant", progress: "progress" }, host: { properties: { "style.--v-simple-progress-bar-progress-value.%": "this.progress", "class": "this.classNames" } }, ngImport: i0, template: "<h4\n    class=\"v-simple-progress-bar-heading text-bold\"\n    id=\"{{id}}-heading\"\n>\n    {{ headerText }}\n</h4>\n<div\n    class=\"v-simple-progress-bar-progress\"\n    id=\"{{id}}-progress-bar\"\n    role=\"progressbar\"\n    [attr.aria-valuemin]=\"0\"\n    [attr.aria-valuemax]=\"100\"\n    [attr.aria-valuenow]=\"progress\"\n    [attr.aria-valuetext]=\"progressAriaLabel\">\n</div>\n<div\n    class=\"v-simple-progress-bar-info body-regular flex justify-between gap-5\"\n>\n    <span\n        *ngIf=\"indicator\"\n        class=\"v-simple-progress-bar-indicator\"\n        aria-hidden=\"true\">\n    </span>\n    <span\n        id=\"{{id}}-additional-info\"\n        class=\"v-simple-progress-bar-additional-info\"\n    >\n        {{ additionalInfo }}\n    </span>\n    <span\n        *ngIf=\"additionalInfoRight\"\n        class=\"v-simple-progress-bar-additional-info is-right\"\n        id=\"{{id}}-additional-info-right\"\n    >\n        {{ additionalInfoRight }}\n    </span>\n</div>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: SimpleProgressBarComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-simple-progress-bar', template: "<h4\n    class=\"v-simple-progress-bar-heading text-bold\"\n    id=\"{{id}}-heading\"\n>\n    {{ headerText }}\n</h4>\n<div\n    class=\"v-simple-progress-bar-progress\"\n    id=\"{{id}}-progress-bar\"\n    role=\"progressbar\"\n    [attr.aria-valuemin]=\"0\"\n    [attr.aria-valuemax]=\"100\"\n    [attr.aria-valuenow]=\"progress\"\n    [attr.aria-valuetext]=\"progressAriaLabel\">\n</div>\n<div\n    class=\"v-simple-progress-bar-info body-regular flex justify-between gap-5\"\n>\n    <span\n        *ngIf=\"indicator\"\n        class=\"v-simple-progress-bar-indicator\"\n        aria-hidden=\"true\">\n    </span>\n    <span\n        id=\"{{id}}-additional-info\"\n        class=\"v-simple-progress-bar-additional-info\"\n    >\n        {{ additionalInfo }}\n    </span>\n    <span\n        *ngIf=\"additionalInfoRight\"\n        class=\"v-simple-progress-bar-additional-info is-right\"\n        id=\"{{id}}-additional-info-right\"\n    >\n        {{ additionalInfoRight }}\n    </span>\n</div>\n" }]
        }], propDecorators: { id: [{
                type: Input
            }], size: [{
                type: Input
            }], headerText: [{
                type: Input
            }], additionalInfo: [{
                type: Input
            }], additionalInfoRight: [{
                type: Input
            }], progressAriaLabel: [{
                type: Input
            }], indicator: [{
                type: Input
            }], progressVariant: [{
                type: Input
            }], completionVariant: [{
                type: Input
            }], progress: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['style.--v-simple-progress-bar-progress-value.%']
            }], classNames: [{
                type: HostBinding,
                args: ['class']
            }] } });

class SimpleProgressBarModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: SimpleProgressBarModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.9", ngImport: i0, type: SimpleProgressBarModule, declarations: [SimpleProgressBarComponent], imports: [CommonModule], exports: [SimpleProgressBarComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: SimpleProgressBarModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: SimpleProgressBarModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule
                    ],
                    declarations: [
                        SimpleProgressBarComponent
                    ],
                    exports: [
                        SimpleProgressBarComponent
                    ]
                }]
        }] });

class StarRatingComponent {
    constructor() {
        this.id = '';
        this.ariaLabel = '';
        this.rating = 0;
        this.starIcon = 'assets/images/star.svg';
        this.halfStarIcon = 'assets/images/half-star.svg';
        this.emptyStarIcon = 'assets/images/empty-star.svg';
        this.stars = Array(5);
        this.iconSources = [];
    }
    ngOnChanges() {
        this.setIconSources();
    }
    setIconSources() {
        this.iconSources = Array(5).fill(this.emptyStarIcon);
        let value = 1;
        while (this.rating >= value) {
            this.iconSources[value - 1] = this.starIcon;
            value++;
        }
        if (this.rating >= value - 0.5) {
            this.iconSources[value - 1] = this.halfStarIcon;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: StarRatingComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: StarRatingComponent, selector: "vpg-star-rating", inputs: { id: "id", ariaLabel: "ariaLabel", rating: "rating" }, host: { attributes: { "role": "img" }, properties: { "id": "this.id", "attr.aria-label": "this.ariaLabel" }, classAttribute: "v-star-rating" }, usesOnChanges: true, ngImport: i0, template: "<img\n    *ngFor=\"let star of stars; let i = index\"\n    id=\"{{id}}-icon-{{i}}\"\n    [src]=\"iconSources[i]\"\n    alt=\"\"\n    class=\"v-star-rating-icon\"\n/>\n", dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: StarRatingComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-star-rating', host: {
                        'class': 'v-star-rating',
                        'role': 'img'
                    }, template: "<img\n    *ngFor=\"let star of stars; let i = index\"\n    id=\"{{id}}-icon-{{i}}\"\n    [src]=\"iconSources[i]\"\n    alt=\"\"\n    class=\"v-star-rating-icon\"\n/>\n" }]
        }], propDecorators: { id: [{
                type: Input
            }, {
                type: HostBinding
            }], ariaLabel: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['attr.aria-label']
            }], rating: [{
                type: Input
            }] } });

class StarRatingModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: StarRatingModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.9", ngImport: i0, type: StarRatingModule, declarations: [StarRatingComponent], imports: [CommonModule], exports: [StarRatingComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: StarRatingModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: StarRatingModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule
                    ],
                    declarations: [
                        StarRatingComponent
                    ],
                    exports: [
                        StarRatingComponent
                    ]
                }]
        }] });

class TableColumnComponent {
    constructor() {
        this.name = '';
        this.title = '';
        this.sortable = false;
        this.contentTemplate = null;
        this.headerTemplate = null;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: TableColumnComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: TableColumnComponent, selector: "vpg-table-column", inputs: { name: "name", title: "title", sortable: "sortable" }, queries: [{ propertyName: "contentTemplate", first: true, predicate: ["content"], descendants: true }, { propertyName: "headerTemplate", first: true, predicate: ["header"], descendants: true }], ngImport: i0, template: '', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: TableColumnComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vpg-table-column',
                    template: ''
                }]
        }], propDecorators: { name: [{
                type: Input
            }], title: [{
                type: Input
            }], sortable: [{
                type: Input
            }], contentTemplate: [{
                type: ContentChild,
                args: ['content']
            }], headerTemplate: [{
                type: ContentChild,
                args: ['header']
            }] } });

class TableComponent {
    constructor() {
        this.data = [];
        this.verticalAlign = 'middle';
        this.horizontalAlign = 'left';
        this.hoverable = false;
        this.draggable = false;
        this.ariaLabel = 'Table of data';
        this.localSort = false;
        this.sorted = new EventEmitter();
        this.columnItems = [];
        this.tableRows = new QueryList();
        this.rowItems = [];
        this.lifecycleSubject = new Subject();
        this.sortedColumn = '';
        this.sortDirection = 'asc';
        this.draggedIndex = null;
        this.dragOverIndex = null;
        this.dragOverPosition = null;
    }
    ngAfterContentInit() {
        this.columns.changes
            .pipe(takeUntil(this.lifecycleSubject))
            .subscribe(() => {
            this.columnItems = this.columns.toArray();
        });
        this.columnItems = this.columns.toArray();
    }
    ngAfterViewInit() {
        if (this.tableRows) {
            this.tableRows.changes.subscribe(() => {
                this.rowItems = this.tableRows.toArray();
            });
            this.rowItems = this.tableRows.toArray();
        }
    }
    ngOnDestroy() {
        this.lifecycleSubject.next();
        this.lifecycleSubject.complete();
    }
    get classNames() {
        return {
            ['v-table']: true,
            [`is-${this.verticalAlign}`]: true,
            [`is-${this.horizontalAlign}`]: true,
            ['is-hoverable']: this.hoverable
        };
    }
    onSort(column) {
        if (this.sortedColumn === column.name) {
            this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
        }
        else {
            this.sortDirection = 'asc';
        }
        this.sortedColumn = column.name;
        if (this.localSort) {
            this.sortData();
        }
        else {
            this.sorted.emit({
                column: column.name,
                direction: this.sortDirection
            });
        }
    }
    sortData() {
        if (this.sortedColumn) {
            this.data.sort((a, b) => {
                const aValue = a[this.sortedColumn];
                const bValue = b[this.sortedColumn];
                if (aValue < bValue) {
                    return this.sortDirection === 'asc' ? -1 : 1;
                }
                else if (aValue > bValue) {
                    return this.sortDirection === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }
    }
    getSortIcon(columnName) {
        if (this.sortedColumn !== columnName) {
            return 'fa-sort';
        }
        else if (this.sortDirection === 'asc') {
            return 'fa-arrow-up';
        }
        return 'fa-arrow-down';
    }
    onDragStart(event, index) {
        this.draggedIndex = index;
        event.dataTransfer.setData('text/plain', index.toString());
        event.dataTransfer.effectAllowed = 'move';
        this.rowItems[index].nativeElement.classList.add('v-table-row-dragging');
    }
    onDragOver(event, index, onSpacerRow = false) {
        event.preventDefault();
        if (this.draggedIndex === null || this.draggedIndex === index) {
            return;
        }
        if (onSpacerRow) {
            this.dragOverIndex = index;
            this.dragOverPosition = 'below';
            return;
        }
        const rect = event.currentTarget.getBoundingClientRect();
        const relativeY = event.clientY - rect.top;
        const threshold = rect.height / 2;
        this.dragOverIndex = index;
        this.dragOverPosition = relativeY < threshold ? 'above' : 'below';
    }
    onDragLeave() {
        this.dragOverIndex = null;
        this.dragOverPosition = null;
    }
    onDrop(event, targetIndex) {
        event.preventDefault();
        if (this.draggedIndex === null || this.draggedIndex === targetIndex) {
            return;
        }
        const finalIndex = this.dragOverPosition === 'below' ? targetIndex + 1 : targetIndex;
        const adjustedIndex = finalIndex > this.draggedIndex ? finalIndex - 1 : finalIndex;
        const movedItem = this.data[this.draggedIndex];
        this.data.splice(this.draggedIndex, 1);
        this.data.splice(adjustedIndex, 0, movedItem);
        this.resetDragState();
    }
    onDragEnd() {
        this.resetDragState();
    }
    resetDragState() {
        this.rowItems[this.draggedIndex ?? 0].nativeElement.classList.remove('v-table-row-dragging');
        this.draggedIndex = null;
        this.dragOverIndex = null;
        this.dragOverPosition = null;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: TableComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "17.3.9", type: TableComponent, selector: "vpg-table", inputs: { data: "data", verticalAlign: "verticalAlign", horizontalAlign: "horizontalAlign", hoverable: "hoverable", draggable: "draggable", ariaLabel: "ariaLabel", localSort: "localSort" }, outputs: { sorted: "sorted" }, host: { properties: { "class": "this.classNames" } }, queries: [{ propertyName: "columns", predicate: TableColumnComponent }], viewQueries: [{ propertyName: "tableRows", predicate: ["tableRow"], descendants: true }], ngImport: i0, template: "<table class=\"v-table-table\" [attr.aria-label]=\"ariaLabel\">\n    <thead>\n        <tr class=\"v-table-tr\">\n            @for(column of columnItems; track column.name; let firstCol = $first) {\n                <th\n                    id=\"column-header-{{ column.title || column.name }}\"\n                    class=\"v-table-th h5\"\n                    scope=\"col\"\n                    [attr.aria-label]=\"column.title || column.name\"\n                >\n                    @if (draggable && firstCol) {\n                        <div>\n                            <span class=\"sr-only\">Select and Drag</span>\n\n                            @if (column.headerTemplate) {\n                                <ng-container *ngTemplateOutlet=\"column.headerTemplate; context: {$implicit: column}\" />\n                            }\n                        </div>\n                    }\n                    @else if (column.sortable) {\n                        <button\n                            id=\"column-header-{{ column.title || column.name }}-sort-button\"\n                            type=\"button\"\n                            class=\"v-table-sort-button full-width\"\n                            (click)=\"onSort(column)\"\n                        >\n                            <ng-container *ngTemplateOutlet=\"headerContent; context: {$implicit: column}\" />\n                            <vpg-icon-next prefix=\"fas\" [icon]=\"getSortIcon(column.name)\" />\n                        </button>\n                    }\n                    @else  {\n                        <ng-container *ngTemplateOutlet=\"headerContent; context: {$implicit: column}\" />\n                    }\n\n                    <ng-template #headerContent let-column>\n                        @if (column.headerTemplate) {\n                            <ng-container *ngTemplateOutlet=\"column.headerTemplate; context: {$implicit: column}\" />\n                        } @else {\n                            {{ column.title || column.name }}\n                        }\n                    </ng-template>\n                </th>\n            }\n        </tr>\n    </thead>\n    <tbody>\n        @for(rowItem of data; track rowItem.id; let i = $index) {\n            @if (dragOverIndex === i && dragOverPosition === 'above' && i === 0) {\n                <tr class=\"v-table-spacer-row v-table-drop-arrow\"\n                    (dragover)=\"onDragOver($event, i, true)\"\n                    (drop)=\"onDrop($event, i)\"\n                >\n                    @for(column of columnItems; track column.name) {\n                        <td></td>\n                    }\n                </tr>\n            }\n\n            <tr class=\"v-table-tr\" #tableRow\n                [attr.aria-label]=\"'Row ' + (data.indexOf(rowItem) + 1) + ' of ' + data.length\"\n                [attr.aria-selected]=\"rowItem.selected\"\n                [attr.draggable]=\"draggable\"\n                [class.is-selected]=\"rowItem.selected\"\n                (drag)=\"onDragStart($event, i)\"\n                (dragover)=\"onDragOver($event, i)\"\n                (drop)=\"onDrop($event, i)\"\n                (dragend)=\"onDragEnd()\"\n            >\n                @for(column of columnItems; track column.name; let firstCol = $first) {\n                    <td class=\"v-table-td\">\n                        @if (draggable && firstCol) {\n                            <div class=\"v-table-drag-handle\">\n                                <i class=\"fas fa-grip-lines-vertical\"></i>\n\n                                @if (column.contentTemplate) {\n                                    <ng-container *ngTemplateOutlet=\"column.contentTemplate; context: {$implicit: rowItem}\" />\n                                } @else {\n                                    <p \n                                        id=\"{{ column.name }}-{{ i }}\"\n                                        class=\"body-regular m-none\"\n                                    >\n                                        {{ rowItem[column.name] }}\n                                    </p>\n                                }\n                            </div>\n                        }\n                        @else if (column.contentTemplate) {\n                            <ng-container *ngTemplateOutlet=\"column.contentTemplate; context: {$implicit: rowItem}\" />\n                        } @else {\n                            <p \n                                id=\"{{ column.name }}-{{ i }}\"\n                                class=\"body-regular m-none\"\n                            >\n                                {{ rowItem[column.name] }}\n                            </p>\n                        }\n                    </td>\n                }\n            </tr>\n\n            @if (((dragOverIndex === i && dragOverPosition === 'below') || (dragOverIndex === i + 1 && dragOverPosition === 'above'))\n            && ![i, i + 1].includes(draggedIndex!) ) {\n                <tr class=\"v-table-spacer-row v-table-drop-arrow\"\n                    (dragover)=\"onDragOver($event, i, true)\"\n                    (drop)=\"onDrop($event, i)\"\n                >\n                    @for(column of columnItems; track column.name) {\n                        <td></td>\n                    }\n                </tr>\n            }\n        }\n    </tbody>\n</table>\n", dependencies: [{ kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "component", type: IconNextComponent, selector: "vpg-icon-next", inputs: ["icon", "size", "prefix", "screenReaderText"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: TableComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-table', template: "<table class=\"v-table-table\" [attr.aria-label]=\"ariaLabel\">\n    <thead>\n        <tr class=\"v-table-tr\">\n            @for(column of columnItems; track column.name; let firstCol = $first) {\n                <th\n                    id=\"column-header-{{ column.title || column.name }}\"\n                    class=\"v-table-th h5\"\n                    scope=\"col\"\n                    [attr.aria-label]=\"column.title || column.name\"\n                >\n                    @if (draggable && firstCol) {\n                        <div>\n                            <span class=\"sr-only\">Select and Drag</span>\n\n                            @if (column.headerTemplate) {\n                                <ng-container *ngTemplateOutlet=\"column.headerTemplate; context: {$implicit: column}\" />\n                            }\n                        </div>\n                    }\n                    @else if (column.sortable) {\n                        <button\n                            id=\"column-header-{{ column.title || column.name }}-sort-button\"\n                            type=\"button\"\n                            class=\"v-table-sort-button full-width\"\n                            (click)=\"onSort(column)\"\n                        >\n                            <ng-container *ngTemplateOutlet=\"headerContent; context: {$implicit: column}\" />\n                            <vpg-icon-next prefix=\"fas\" [icon]=\"getSortIcon(column.name)\" />\n                        </button>\n                    }\n                    @else  {\n                        <ng-container *ngTemplateOutlet=\"headerContent; context: {$implicit: column}\" />\n                    }\n\n                    <ng-template #headerContent let-column>\n                        @if (column.headerTemplate) {\n                            <ng-container *ngTemplateOutlet=\"column.headerTemplate; context: {$implicit: column}\" />\n                        } @else {\n                            {{ column.title || column.name }}\n                        }\n                    </ng-template>\n                </th>\n            }\n        </tr>\n    </thead>\n    <tbody>\n        @for(rowItem of data; track rowItem.id; let i = $index) {\n            @if (dragOverIndex === i && dragOverPosition === 'above' && i === 0) {\n                <tr class=\"v-table-spacer-row v-table-drop-arrow\"\n                    (dragover)=\"onDragOver($event, i, true)\"\n                    (drop)=\"onDrop($event, i)\"\n                >\n                    @for(column of columnItems; track column.name) {\n                        <td></td>\n                    }\n                </tr>\n            }\n\n            <tr class=\"v-table-tr\" #tableRow\n                [attr.aria-label]=\"'Row ' + (data.indexOf(rowItem) + 1) + ' of ' + data.length\"\n                [attr.aria-selected]=\"rowItem.selected\"\n                [attr.draggable]=\"draggable\"\n                [class.is-selected]=\"rowItem.selected\"\n                (drag)=\"onDragStart($event, i)\"\n                (dragover)=\"onDragOver($event, i)\"\n                (drop)=\"onDrop($event, i)\"\n                (dragend)=\"onDragEnd()\"\n            >\n                @for(column of columnItems; track column.name; let firstCol = $first) {\n                    <td class=\"v-table-td\">\n                        @if (draggable && firstCol) {\n                            <div class=\"v-table-drag-handle\">\n                                <i class=\"fas fa-grip-lines-vertical\"></i>\n\n                                @if (column.contentTemplate) {\n                                    <ng-container *ngTemplateOutlet=\"column.contentTemplate; context: {$implicit: rowItem}\" />\n                                } @else {\n                                    <p \n                                        id=\"{{ column.name }}-{{ i }}\"\n                                        class=\"body-regular m-none\"\n                                    >\n                                        {{ rowItem[column.name] }}\n                                    </p>\n                                }\n                            </div>\n                        }\n                        @else if (column.contentTemplate) {\n                            <ng-container *ngTemplateOutlet=\"column.contentTemplate; context: {$implicit: rowItem}\" />\n                        } @else {\n                            <p \n                                id=\"{{ column.name }}-{{ i }}\"\n                                class=\"body-regular m-none\"\n                            >\n                                {{ rowItem[column.name] }}\n                            </p>\n                        }\n                    </td>\n                }\n            </tr>\n\n            @if (((dragOverIndex === i && dragOverPosition === 'below') || (dragOverIndex === i + 1 && dragOverPosition === 'above'))\n            && ![i, i + 1].includes(draggedIndex!) ) {\n                <tr class=\"v-table-spacer-row v-table-drop-arrow\"\n                    (dragover)=\"onDragOver($event, i, true)\"\n                    (drop)=\"onDrop($event, i)\"\n                >\n                    @for(column of columnItems; track column.name) {\n                        <td></td>\n                    }\n                </tr>\n            }\n        }\n    </tbody>\n</table>\n" }]
        }], propDecorators: { data: [{
                type: Input
            }], verticalAlign: [{
                type: Input
            }], horizontalAlign: [{
                type: Input
            }], hoverable: [{
                type: Input
            }], draggable: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], localSort: [{
                type: Input
            }], sorted: [{
                type: Output
            }], columns: [{
                type: ContentChildren,
                args: [TableColumnComponent]
            }], tableRows: [{
                type: ViewChildren,
                args: ['tableRow']
            }], classNames: [{
                type: HostBinding,
                args: ['class']
            }] } });

class TableModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: TableModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.9", ngImport: i0, type: TableModule, declarations: [TableComponent,
            TableColumnComponent], imports: [CommonModule,
            IconModule], exports: [TableComponent,
            TableColumnComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: TableModule, imports: [CommonModule,
            IconModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: TableModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        IconModule
                    ],
                    declarations: [
                        TableComponent,
                        TableColumnComponent
                    ],
                    exports: [
                        TableComponent,
                        TableColumnComponent
                    ]
                }]
        }] });

class Tabs {
    constructor() {
        this.actionsTemplate = null;
        this.selectedTabIndexChange = new EventEmitter();
        this.selectedTabKeyChange = new EventEmitter();
        this.selectedTabChange = new EventEmitter();
        this._selectedTabIndex = 0;
        this._selectedTabKey = 0;
        this.size = 'large';
        this.scale = 'large';
        this.role = 'tablist';
    }
    set selectedTabIndex(value) {
        const index = Number(value);
        if (index !== this._selectedTabIndex) {
            this._selectedTabIndex = index;
            const tabItem = this.tabItems?.get(index);
            if (tabItem) {
                this.selectTab(tabItem);
            }
        }
    }
    get selectedTabIndex() {
        return this._selectedTabIndex;
    }
    set selectedTabKey(value) {
        if (this._selectedTabKey !== value) {
            this._selectedTabKey = value;
            const tabByKey = this.tabItems?.find((e) => e.key === this.selectedTabKey);
            if (tabByKey) {
                this.selectTab(tabByKey);
            }
        }
    }
    get selectedTabKey() {
        return this._selectedTabKey;
    }
    get currentContent() {
        return this._selectedTab?.content ?? null;
    }
    get currentActionsTemplate() {
        const result = this._selectedTab?.actionsTemplate ?? this.actionsTemplate;
        return result;
    }
    ngAfterContentInit() {
        this.updateSelectedTab();
    }
    updateSelectedTab() {
        const tabByIndex = this.tabItems.get(this.selectedTabIndex);
        const tabByKey = this.tabItems.find((e) => e.key === this.selectedTabKey);
        const tab = tabByKey ?? tabByIndex ?? this.tabItems.first;
        this.selectTab(tab);
    }
    get classNames() {
        return {
            ['v-tabs']: true,
            [`is-${this.size}-size`]: true,
            [`is-${this.scale}-scale`]: true
        };
    }
    selectTab(tab) {
        const currentActiveTab = this.tabItems.find((item) => item.active);
        if (currentActiveTab) {
            currentActiveTab.active = false;
        }
        tab.active = true;
        this._selectedTab = tab;
        this._selectedTab.refresh();
        this.notifyTabChange();
    }
    notifyTabChange() {
        if (this._selectedTab) {
            const tab = this._selectedTab;
            setTimeout(() => {
                if (tab.key) {
                    this.selectedTabKeyChange.emit(tab?.key);
                }
                const index = this.tabItems.toArray().indexOf(tab);
                this.selectedTabIndexChange.emit(index);
                this.selectedTabChange.emit({ selectedTab: this._selectedTab });
            });
        }
    }
    onKeyDown(event) {
        const elements = this.tabItems.toArray();
        const focusedTabIndex = elements.findIndex((tab) => tab.hasFocus());
        if (focusedTabIndex >= 0) {
            if ([Keys.Space.toString(), Keys.Enter.toString()].includes(event.code)) {
                const selectTabItem = this.tabItems.get(focusedTabIndex);
                if (selectTabItem) {
                    this.selectTab(selectTabItem);
                }
            }
            if (event.code === Keys.ArrowRight) {
                const nextIndex = focusedTabIndex + 1;
                elements[nextIndex].focus();
                return;
            }
            if (event.code === Keys.ArrowLeft) {
                const nextIndex = focusedTabIndex - 1;
                elements[nextIndex].focus();
                return;
            }
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: Tabs, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: Tabs, selector: "vpg-tabs", inputs: { size: "size", scale: "scale", selectedTabIndex: "selectedTabIndex", selectedTabKey: "selectedTabKey" }, outputs: { selectedTabIndexChange: "selectedTabIndexChange", selectedTabKeyChange: "selectedTabKeyChange", selectedTabChange: "selectedTabChange" }, host: { listeners: { "keydown": "onKeyDown($event)" }, properties: { "attr.role": "this.role", "class": "this.classNames" } }, queries: [{ propertyName: "actionsTemplate", first: true, predicate: ["actions"], descendants: true, static: true }, { propertyName: "tabItems", predicate: i0.forwardRef(() => Tab) }], ngImport: i0, template: "<div class=\"v-tab-items-container\">\n    <div class=\"v-tab-items\">\n        <ng-content></ng-content>\n    </div>\n    <div class=\"v-tab-action\">\n        <ng-container [ngTemplateOutlet]=\"currentActionsTemplate\"></ng-container>\n    </div>\n</div>\n<div class=\"v-tab-content\">\n    <ng-container [ngTemplateOutlet]=\"currentContent\"></ng-container>\n</div>", dependencies: [{ kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: Tabs, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-tabs', template: "<div class=\"v-tab-items-container\">\n    <div class=\"v-tab-items\">\n        <ng-content></ng-content>\n    </div>\n    <div class=\"v-tab-action\">\n        <ng-container [ngTemplateOutlet]=\"currentActionsTemplate\"></ng-container>\n    </div>\n</div>\n<div class=\"v-tab-content\">\n    <ng-container [ngTemplateOutlet]=\"currentContent\"></ng-container>\n</div>" }]
        }], propDecorators: { tabItems: [{
                type: ContentChildren,
                args: [forwardRef(() => Tab)]
            }], actionsTemplate: [{
                type: ContentChild,
                args: ['actions', { static: true }]
            }], selectedTabIndexChange: [{
                type: Output
            }], selectedTabKeyChange: [{
                type: Output
            }], selectedTabChange: [{
                type: Output
            }], size: [{
                type: Input
            }], scale: [{
                type: Input
            }], selectedTabIndex: [{
                type: Input
            }], selectedTabKey: [{
                type: Input
            }], role: [{
                type: HostBinding,
                args: ['attr.role']
            }], classNames: [{
                type: HostBinding,
                args: ['class']
            }], onKeyDown: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }] } });

class Tab {
    constructor(parent, changeDetection, elementRef) {
        this.parent = parent;
        this.changeDetection = changeDetection;
        this.elementRef = elementRef;
        this.disabled = false;
        this.active = false;
        this.id = '';
        this.label = '';
        this.ariaLabel = '';
        this.content = null;
        this.actionsTemplate = null;
        this.labelTemplate = null;
        this.role = 'tab';
    }
    get tabIndex() {
        return this.disabled ? -1 : 0;
    }
    get classNames() {
        return {
            'v-tab': true,
            'is-disabled': this.disabled,
            'is-active': this.active
        };
    }
    onClick() {
        this.parent.selectTab(this);
    }
    refresh() {
        this.changeDetection.detectChanges();
    }
    hasFocus() {
        return this.elementRef.nativeElement === document.activeElement;
    }
    focus() {
        this.elementRef.nativeElement.focus();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: Tab, deps: [{ token: forwardRef(() => Tabs) }, { token: i0.ChangeDetectorRef }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: Tab, selector: "vpg-tab", inputs: { key: "key", disabled: "disabled", active: "active", id: "id", label: "label", ariaLabel: "ariaLabel" }, host: { listeners: { "click": "onClick()" }, properties: { "attr.aria-selected": "this.active", "attr.role": "this.role", "tabindex": "this.tabIndex", "class": "this.classNames" } }, queries: [{ propertyName: "actionsTemplate", first: true, predicate: ["tabActions"], descendants: true }, { propertyName: "labelTemplate", first: true, predicate: ["label"], descendants: true }], viewQueries: [{ propertyName: "content", first: true, predicate: ["content"], descendants: true, static: true }], ngImport: i0, template: "<div\n    class=\"v-tab-item\"\n    [attr.aria-label]=\"ariaLabel\"\n>\n    <span *ngIf=\"label\">{{label}}</span>\n    <ng-container [ngTemplateOutlet]=\"labelTemplate\">\n    </ng-container>\n</div>\n\n<ng-template #content>\n    <ng-content></ng-content>\n</ng-template>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: Tab, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-tab', template: "<div\n    class=\"v-tab-item\"\n    [attr.aria-label]=\"ariaLabel\"\n>\n    <span *ngIf=\"label\">{{label}}</span>\n    <ng-container [ngTemplateOutlet]=\"labelTemplate\">\n    </ng-container>\n</div>\n\n<ng-template #content>\n    <ng-content></ng-content>\n</ng-template>\n" }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [forwardRef(() => Tabs)]
                }] }, { type: i0.ChangeDetectorRef }, { type: i0.ElementRef }], propDecorators: { key: [{
                type: Input
            }], disabled: [{
                type: Input
            }], active: [{
                type: HostBinding,
                args: ['attr.aria-selected']
            }, {
                type: Input
            }], id: [{
                type: Input
            }], label: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], content: [{
                type: ViewChild,
                args: ['content', { static: true }]
            }], actionsTemplate: [{
                type: ContentChild,
                args: ['tabActions']
            }], labelTemplate: [{
                type: ContentChild,
                args: ['label']
            }], role: [{
                type: HostBinding,
                args: ['attr.role']
            }], tabIndex: [{
                type: HostBinding,
                args: ['tabindex']
            }], classNames: [{
                type: HostBinding,
                args: ['class']
            }], onClick: [{
                type: HostListener,
                args: ['click']
            }] } });

class TabsModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: TabsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.9", ngImport: i0, type: TabsModule, declarations: [Tabs,
            Tab], imports: [CommonModule,
            BadgeModule], exports: [Tabs,
            Tab] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: TabsModule, imports: [CommonModule,
            BadgeModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: TabsModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        BadgeModule
                    ],
                    declarations: [
                        Tabs,
                        Tab
                    ],
                    exports: [
                        Tabs,
                        Tab
                    ]
                }]
        }] });

class TextAreaComponent extends AbstractFormComponent {
    constructor(injector) {
        super();
        this.injector = injector;
        this.name = '';
        this.placeholder = '';
        this.autocomplete = 'off';
        this.label = '';
        this.hint = '';
        this.required = false;
        this.readOnly = false;
        this.ariaLabel = '';
        this.ariaLabelledBy = '';
        this.ariaDescribedBy = '';
        this.maxLength = null;
        this.maxLengthReachedText = '';
        this.control = null;
    }
    ngAfterViewInit() {
        const control = this.getNgControl();
        this.setName(control);
        this.setControl(control);
    }
    onChange(e) {
        const newValue = e.target.value;
        this.notifyValueChange(newValue);
        this.writeValue(newValue);
    }
    onBlur() {
        this.notifyTouched();
    }
    get isMaxLengthReached() {
        return this.maxLength ? this.value.length >= this.maxLength : false;
    }
    get canShowErrors() {
        return Boolean(this.control?.touched &&
            this.control?.invalid &&
            this.control?.errors);
    }
    get classNames() {
        return 'v-input-field';
    }
    getNgControl() {
        return this.injector.get(NgControl);
    }
    setControl(ngControl) {
        this.control = ngControl.control;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: TextAreaComponent, deps: [{ token: i0.Injector }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: TextAreaComponent, selector: "vpg-text-area", inputs: { name: "name", placeholder: "placeholder", autocomplete: "autocomplete", label: "label", hint: "hint", required: "required", readOnly: "readOnly", ariaLabel: "ariaLabel", ariaLabelledBy: "ariaLabelledBy", ariaDescribedBy: "ariaDescribedBy", maxLength: "maxLength", maxLengthReachedText: "maxLengthReachedText" }, host: { properties: { "class": "this.classNames" } }, providers: [valueAccessor(TextAreaComponent)], queries: [{ propertyName: "customHint", first: true, predicate: ["hint"], descendants: true }, { propertyName: "customErrors", first: true, predicate: ["errors"], descendants: true }], usesInheritance: true, ngImport: i0, template: "<label\n    *ngIf=\"label\"\n    class=\"v-control-label\"\n    id=\"{{name}}-label\"\n    [for]=\"name\"\n>\n    {{ label }}\n    <span\n        *ngIf=\"required\"\n        class=\"v-control-required\"\n        aria-hidden=\"true\"\n    >\n        *\n    </span>\n</label>\n\n<textarea\n    class=\"v-control-textarea\"\n    [id]=\"name\"\n    [value]=\"value\"\n    [placeholder]=\"placeholder\"\n    [disabled]=\"disabled\"\n    [readOnly]=\"readOnly\"\n    (input)=\"onChange($event)\"\n    (change)=\"onChange($event)\"\n    (blur)=\"onBlur()\"\n    [class.is-dirty]=\"control?.touched && control?.invalid\"\n    [attr.aria-invalid]=\"control?.touched && control?.invalid\"\n    [attr.aria-required]=\"required\"\n    [attr.aria-disabled]=\"disabled\"\n    [attr.autocomplete]=\"autocomplete\"\n    [attr.aria-label]=\"ariaLabel\"\n    [attr.aria-labelledby]=\"ariaLabelledBy\"\n\taria-multiline=\"true\"\n    attr.maxlength=\"{{maxLength}}\"\n    attr.aria-describedby=\"v-text-area-{{name}}-errors v-text-area-{{name}}-hint v-text-area-{{name}}-maxlength-reached\"\n>\n</textarea>\n\n<ng-container [ngTemplateOutlet]=\"canShowErrors\n    ? errorsTemplate\n    : hintTemplate\"\n></ng-container>\n\n<span\n    *ngIf=\"isMaxLengthReached\"\n    id=\"v-input-field-{{name}}-maxlength-reached\"\n    class=\"sr-only\"\n    role=\"alert\"\n    aria-live=\"assertive\"\n>\n    {{ maxLengthReachedText }}\n</span>\n\n<ng-template #errorsTemplate>\n    <vpg-control-errors *ngIf=\"customErrors\" id=\"v-text-area-{{name}}-errors\">\n        <ng-container [ngTemplateOutlet]=\"customErrors\"></ng-container>\n    </vpg-control-errors>\n</ng-template>\n\n<ng-template #hintTemplate>\n    <div\n        *ngIf=\"hint || customHint || maxLength\"\n        class=\"flex gap-20 justify-content-between\" id=\"v-text-area-{{name}}-hint\"\n    >\n        <vpg-control-hint\n            *ngIf=\"hint\"\n            id=\"v-text-area-{{name}}-control-hint\"\n        >\n            {{ hint }}\n        </vpg-control-hint>\n        <ng-container\n            *ngIf=\"customHint\"\n            [ngTemplateOutlet]=\"customHint\"\n        ></ng-container>\n        <span></span>\n        <span\n            *ngIf=\"maxLength\"\n            id=\"v-text-area-{{name}}-counter\"\n            class=\"body-regular\"\n        >\n            {{ value.length + '/' + maxLength }}\n        </span>\n    </div>\n</ng-template>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "component", type: ControlHintComponent, selector: "vpg-control-hint" }, { kind: "component", type: ControlErrorsComponent, selector: "vpg-control-errors" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: TextAreaComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-text-area', providers: [valueAccessor(TextAreaComponent)], template: "<label\n    *ngIf=\"label\"\n    class=\"v-control-label\"\n    id=\"{{name}}-label\"\n    [for]=\"name\"\n>\n    {{ label }}\n    <span\n        *ngIf=\"required\"\n        class=\"v-control-required\"\n        aria-hidden=\"true\"\n    >\n        *\n    </span>\n</label>\n\n<textarea\n    class=\"v-control-textarea\"\n    [id]=\"name\"\n    [value]=\"value\"\n    [placeholder]=\"placeholder\"\n    [disabled]=\"disabled\"\n    [readOnly]=\"readOnly\"\n    (input)=\"onChange($event)\"\n    (change)=\"onChange($event)\"\n    (blur)=\"onBlur()\"\n    [class.is-dirty]=\"control?.touched && control?.invalid\"\n    [attr.aria-invalid]=\"control?.touched && control?.invalid\"\n    [attr.aria-required]=\"required\"\n    [attr.aria-disabled]=\"disabled\"\n    [attr.autocomplete]=\"autocomplete\"\n    [attr.aria-label]=\"ariaLabel\"\n    [attr.aria-labelledby]=\"ariaLabelledBy\"\n\taria-multiline=\"true\"\n    attr.maxlength=\"{{maxLength}}\"\n    attr.aria-describedby=\"v-text-area-{{name}}-errors v-text-area-{{name}}-hint v-text-area-{{name}}-maxlength-reached\"\n>\n</textarea>\n\n<ng-container [ngTemplateOutlet]=\"canShowErrors\n    ? errorsTemplate\n    : hintTemplate\"\n></ng-container>\n\n<span\n    *ngIf=\"isMaxLengthReached\"\n    id=\"v-input-field-{{name}}-maxlength-reached\"\n    class=\"sr-only\"\n    role=\"alert\"\n    aria-live=\"assertive\"\n>\n    {{ maxLengthReachedText }}\n</span>\n\n<ng-template #errorsTemplate>\n    <vpg-control-errors *ngIf=\"customErrors\" id=\"v-text-area-{{name}}-errors\">\n        <ng-container [ngTemplateOutlet]=\"customErrors\"></ng-container>\n    </vpg-control-errors>\n</ng-template>\n\n<ng-template #hintTemplate>\n    <div\n        *ngIf=\"hint || customHint || maxLength\"\n        class=\"flex gap-20 justify-content-between\" id=\"v-text-area-{{name}}-hint\"\n    >\n        <vpg-control-hint\n            *ngIf=\"hint\"\n            id=\"v-text-area-{{name}}-control-hint\"\n        >\n            {{ hint }}\n        </vpg-control-hint>\n        <ng-container\n            *ngIf=\"customHint\"\n            [ngTemplateOutlet]=\"customHint\"\n        ></ng-container>\n        <span></span>\n        <span\n            *ngIf=\"maxLength\"\n            id=\"v-text-area-{{name}}-counter\"\n            class=\"body-regular\"\n        >\n            {{ value.length + '/' + maxLength }}\n        </span>\n    </div>\n</ng-template>\n" }]
        }], ctorParameters: () => [{ type: i0.Injector }], propDecorators: { name: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], autocomplete: [{
                type: Input
            }], label: [{
                type: Input
            }], hint: [{
                type: Input
            }], required: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], ariaLabelledBy: [{
                type: Input
            }], ariaDescribedBy: [{
                type: Input
            }], maxLength: [{
                type: Input
            }], maxLengthReachedText: [{
                type: Input
            }], customHint: [{
                type: ContentChild,
                args: ['hint']
            }], customErrors: [{
                type: ContentChild,
                args: ['errors']
            }], classNames: [{
                type: HostBinding,
                args: ['class']
            }] } });

class TextAreaModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: TextAreaModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.9", ngImport: i0, type: TextAreaModule, declarations: [TextAreaComponent], imports: [CommonModule,
            ReactiveFormsModule,
            ControlModule], exports: [TextAreaComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: TextAreaModule, imports: [CommonModule,
            ReactiveFormsModule,
            ControlModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: TextAreaModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        ReactiveFormsModule,
                        ControlModule
                    ],
                    declarations: [
                        TextAreaComponent
                    ],
                    exports: [
                        TextAreaComponent
                    ]
                }]
        }] });

class ThreeDotsLoaderComponent {
    constructor() {
        this.screenReaderText = '';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ThreeDotsLoaderComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: ThreeDotsLoaderComponent, selector: "vpg-three-dots-loader", inputs: { screenReaderText: "screenReaderText" }, host: { attributes: { "role": "status" }, properties: { "attr.aria-label": "this.screenReaderText" }, classAttribute: "v-three-dots-loader flex" }, ngImport: i0, template: "<div class=\"v-dot-container\">\n    <div class=\"v-dot v-dot-one\"></div>\n</div>\n<div class=\"v-dot-container\">\n    <div class=\"v-dot v-dot-two\"></div>\n</div>\n<div class=\"v-dot-container\">\n    <div class=\"v-dot v-dot-three\"></div>\n</div>\n" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ThreeDotsLoaderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-three-dots-loader', host: {
                        'role': 'status',
                        'class': 'v-three-dots-loader flex'
                    }, template: "<div class=\"v-dot-container\">\n    <div class=\"v-dot v-dot-one\"></div>\n</div>\n<div class=\"v-dot-container\">\n    <div class=\"v-dot v-dot-two\"></div>\n</div>\n<div class=\"v-dot-container\">\n    <div class=\"v-dot v-dot-three\"></div>\n</div>\n" }]
        }], propDecorators: { screenReaderText: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['attr.aria-label']
            }] } });

class ThreeDotsLoaderModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ThreeDotsLoaderModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.9", ngImport: i0, type: ThreeDotsLoaderModule, declarations: [ThreeDotsLoaderComponent], imports: [CommonModule], exports: [ThreeDotsLoaderComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ThreeDotsLoaderModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ThreeDotsLoaderModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule
                    ],
                    declarations: [
                        ThreeDotsLoaderComponent
                    ],
                    exports: [
                        ThreeDotsLoaderComponent
                    ]
                }]
        }] });

const slideInFadeOut = trigger('slideInFadeOut', [
    transition(':enter', [
        style({ transform: 'translateX({{rtl}})' }),
        animate('300ms ease-out', style({ transform: 'translateX(0)' }))
    ], { params: { rtl: '100%' } }),
    transition(':leave', [
        animate('200ms ease-out', style({ opacity: '0' }))
    ])
]);

class ToastComponent {
    constructor(document) {
        this.document = document;
        this.id = '';
        this.variant = 'neutral';
        this.text = '';
        this.icon = '';
        this.closeButton = false;
        this.autoDismiss = true;
        this.dismissTimeout = 5000;
        this.top = 176;
        this.side = 20;
        this.dismissed = new EventEmitter();
        this.content = null;
        this.rtl = '100%';
        this.rtl = (this.document.body.dir === 'rtl' || this.document.dir === 'rtl')
            ? '-100%'
            : '100%';
    }
    get classNames() {
        return {
            ['v-toast']: true,
            [`is-${this.variant}`]: true
        };
    }
    ngOnInit() {
        this.autoDismissIfEnabled();
    }
    hideToast() {
        this.dismissed.emit();
    }
    autoDismissIfEnabled() {
        if (this.autoDismiss) {
            setTimeout(() => {
                this.hideToast();
            }, this.dismissTimeout);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ToastComponent, deps: [{ token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: ToastComponent, selector: "vpg-toast", inputs: { id: "id", variant: "variant", text: "text", icon: "icon", closeButton: "closeButton", autoDismiss: "autoDismiss", dismissTimeout: "dismissTimeout", top: "top", side: "side" }, outputs: { dismissed: "dismissed" }, host: { attributes: { "role": "alert" }, properties: { "id": "id", "@slideInFadeOut": "{ value:slideInFadeOut, params: { rtl } }", "style.--v-toast-top-position.px": "this.top", "style.--v-toast-side-position.px": "this.side", "class": "this.classNames" } }, queries: [{ propertyName: "content", first: true, predicate: ["content"], descendants: true, read: (TemplateRef) }], ngImport: i0, template: "<vpg-icon-next\n    *ngIf=\"icon\"\n    [icon]=\"icon\"\n    size=\"2x\"\n/>\n<p\n    *ngIf=\"text\"\n    class=\"v-toast-text m-none\"\n>\n    {{ text }}\n</p>\n<ng-container [ngTemplateOutlet]=\"content\"></ng-container>\n<vpg-icon-next\n    *ngIf=\"closeButton\"\n    icon=\"times\"\n    size=\"2x\"\n    class=\"v-toast-close-button\"\n    (click)=\"hideToast()\"\n/>", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "component", type: IconNextComponent, selector: "vpg-icon-next", inputs: ["icon", "size", "prefix", "screenReaderText"] }], animations: [slideInFadeOut], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ToastComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-toast', host: {
                        'role': 'alert',
                        '[id]': 'id',
                        '[@slideInFadeOut]': '{ value:slideInFadeOut, params: { rtl } }'
                    }, animations: [slideInFadeOut], changeDetection: ChangeDetectionStrategy.OnPush, template: "<vpg-icon-next\n    *ngIf=\"icon\"\n    [icon]=\"icon\"\n    size=\"2x\"\n/>\n<p\n    *ngIf=\"text\"\n    class=\"v-toast-text m-none\"\n>\n    {{ text }}\n</p>\n<ng-container [ngTemplateOutlet]=\"content\"></ng-container>\n<vpg-icon-next\n    *ngIf=\"closeButton\"\n    icon=\"times\"\n    size=\"2x\"\n    class=\"v-toast-close-button\"\n    (click)=\"hideToast()\"\n/>" }]
        }], ctorParameters: () => [{ type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }], propDecorators: { id: [{
                type: Input
            }], variant: [{
                type: Input
            }], text: [{
                type: Input
            }], icon: [{
                type: Input
            }], closeButton: [{
                type: Input
            }], autoDismiss: [{
                type: Input
            }], dismissTimeout: [{
                type: Input
            }], top: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['style.--v-toast-top-position.px']
            }], side: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['style.--v-toast-side-position.px']
            }], dismissed: [{
                type: Output
            }], content: [{
                type: ContentChild,
                args: ['content', { read: (TemplateRef) }]
            }], classNames: [{
                type: HostBinding,
                args: ['class']
            }] } });

class ToastService {
    constructor() {
        this.toasts = new BehaviorSubject([]);
        this.toasts$ = this.toasts.asObservable();
    }
    show(config) {
        if (this.toasts.value.length) {
            this.toasts.next([]);
        }
        this.toasts.next([...this.toasts.value, config]);
    }
    neutral(config) {
        this.show({ ...config, variant: 'neutral' });
    }
    positive(config) {
        this.show({ ...config, variant: 'positive' });
    }
    negative(config) {
        this.show({ ...config, variant: 'negative' });
    }
    warning(config) {
        this.show({ ...config, variant: 'warning' });
    }
    dismiss(toastId) {
        this.toasts.next(this.toasts.value.filter((toast) => toast.id !== toastId));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ToastService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ToastService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ToastService, decorators: [{
            type: Injectable
        }] });

class ToasterComponent {
    constructor(toasts) {
        this.toasts = toasts;
        this.top = 176;
        this.side = 20;
        this.toasts$ = this.toasts.toasts$;
    }
    dismiss(toastId) {
        this.toasts.dismiss(toastId);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ToasterComponent, deps: [{ token: ToastService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: ToasterComponent, selector: "vpg-toaster", inputs: { top: "top", side: "side" }, ngImport: i0, template: "<ng-container *ngFor=\"\n    let toast of toasts$ | async;\n    let first = first\"\n>\n    <vpg-toast\n        *ngIf=\"first\"\n        [id]=\"toast.id\"\n        [variant]=\"toast.variant\"\n        [text]=\"toast.text\"\n        [icon]=\"toast.icon ?? ''\"\n        [closeButton]=\"toast.closeButton ?? false\"\n        [autoDismiss]=\"toast.autoDismiss ?? true\"\n        [dismissTimeout]=\"toast.dismissTimeout ?? 5000\"\n        [top]=\"toast.top || top || 176\"\n        [side]=\"toast.side || side || 20\"\n        (dismissed)=\"dismiss(toast.id)\"\n    />\n</ng-container>", dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: ToastComponent, selector: "vpg-toast", inputs: ["id", "variant", "text", "icon", "closeButton", "autoDismiss", "dismissTimeout", "top", "side"], outputs: ["dismissed"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ToasterComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-toaster', changeDetection: ChangeDetectionStrategy.OnPush, template: "<ng-container *ngFor=\"\n    let toast of toasts$ | async;\n    let first = first\"\n>\n    <vpg-toast\n        *ngIf=\"first\"\n        [id]=\"toast.id\"\n        [variant]=\"toast.variant\"\n        [text]=\"toast.text\"\n        [icon]=\"toast.icon ?? ''\"\n        [closeButton]=\"toast.closeButton ?? false\"\n        [autoDismiss]=\"toast.autoDismiss ?? true\"\n        [dismissTimeout]=\"toast.dismissTimeout ?? 5000\"\n        [top]=\"toast.top || top || 176\"\n        [side]=\"toast.side || side || 20\"\n        (dismissed)=\"dismiss(toast.id)\"\n    />\n</ng-container>" }]
        }], ctorParameters: () => [{ type: ToastService }], propDecorators: { top: [{
                type: Input
            }], side: [{
                type: Input
            }] } });

class ToastModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ToastModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.9", ngImport: i0, type: ToastModule, declarations: [ToastComponent,
            ToasterComponent], imports: [CommonModule,
            IconModule], exports: [ToastComponent,
            ToasterComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ToastModule, providers: [
            ToastService
        ], imports: [CommonModule,
            IconModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ToastModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        IconModule
                    ],
                    declarations: [
                        ToastComponent,
                        ToasterComponent
                    ],
                    exports: [
                        ToastComponent,
                        ToasterComponent
                    ],
                    providers: [
                        ToastService
                    ]
                }]
        }] });

class ToggleSwitchComponent extends AbstractFormComponent {
    constructor(injector) {
        super();
        this.injector = injector;
        this.name = '';
        this.label = '';
        this.size = 'large';
    }
    ngOnInit() {
        const control = this.injector.get(NgControl);
        if (control && typeof control.name == 'string' && !this.name) {
            this.name = control.name;
        }
    }
    /**
     * onChange is called when toggle switch is clicked.
     * @param event Click event
     */
    onChange(event) {
        this.value = event.target.checked;
    }
    onToggleClick() {
        this.inputControl.nativeElement?.click();
    }
    get classNames() {
        return {
            ['v-toggle-switch']: true,
            [`is-${this.size}`]: true
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ToggleSwitchComponent, deps: [{ token: i0.Injector }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: ToggleSwitchComponent, selector: "vpg-toggle-switch", inputs: { name: "name", label: "label", size: "size" }, host: { properties: { "class": "this.classNames" }, classAttribute: "v-toggle-switch" }, providers: [valueAccessor(ToggleSwitchComponent)], viewQueries: [{ propertyName: "inputControl", first: true, predicate: ["inputControl"], descendants: true }], usesInheritance: true, ngImport: i0, template: "<label \n    class=\"v-toggle-switch-label\"\n    for=\"{{ name }}Id\">{{ label }}</label>\n<div\n    id=\"{{ name }}Toggle\"\n    class=\"v-toggle-switch-input-wrapper\" \n    (click)=\"onToggleClick()\"\n>\n    <input\n        #inputControl\n        role=\"switch\"\n        type=\"checkbox\"\n        class=\"v-toggle-switch-input\"\n        id=\"{{ name }}Id\"\n        name=\"{{ name }}\"\n        [value]=\"value\"\n        [disabled]=\"disabled\"\n        [attr.aria-disabled]=\"disabled\"\n        (click)=\"onChange($event)\"\n        [checked]=\"value\"\n        [attr.aria-checked]=\"value\"\n    />\n    <span class=\"v-toggle-switch-slider\"></span>\n</div>\n" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ToggleSwitchComponent, decorators: [{
            type: Component,
            args: [{ selector: 'vpg-toggle-switch', host: {
                        class: 'v-toggle-switch'
                    }, providers: [valueAccessor(ToggleSwitchComponent)], template: "<label \n    class=\"v-toggle-switch-label\"\n    for=\"{{ name }}Id\">{{ label }}</label>\n<div\n    id=\"{{ name }}Toggle\"\n    class=\"v-toggle-switch-input-wrapper\" \n    (click)=\"onToggleClick()\"\n>\n    <input\n        #inputControl\n        role=\"switch\"\n        type=\"checkbox\"\n        class=\"v-toggle-switch-input\"\n        id=\"{{ name }}Id\"\n        name=\"{{ name }}\"\n        [value]=\"value\"\n        [disabled]=\"disabled\"\n        [attr.aria-disabled]=\"disabled\"\n        (click)=\"onChange($event)\"\n        [checked]=\"value\"\n        [attr.aria-checked]=\"value\"\n    />\n    <span class=\"v-toggle-switch-slider\"></span>\n</div>\n" }]
        }], ctorParameters: () => [{ type: i0.Injector }], propDecorators: { name: [{
                type: Input
            }], label: [{
                type: Input
            }], size: [{
                type: Input
            }], inputControl: [{
                type: ViewChild,
                args: ['inputControl']
            }], classNames: [{
                type: HostBinding,
                args: ['class']
            }] } });

class ToggleSwitchModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ToggleSwitchModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.9", ngImport: i0, type: ToggleSwitchModule, declarations: [ToggleSwitchComponent], imports: [CommonModule], exports: [ToggleSwitchComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ToggleSwitchModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ToggleSwitchModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule
                    ],
                    declarations: [
                        ToggleSwitchComponent
                    ],
                    exports: [
                        ToggleSwitchComponent
                    ]
                }]
        }] });

const fadeAnimation = trigger('fade', [
    transition('void => active', [
        style({ opacity: 0 }),
        animate(200, style({ opacity: 1 }))
    ]),
    transition('* => void', [
        animate(200, style({ opacity: 0 }))
    ])
]);
class TooltipComponent {
    constructor() {
        this.id = '';
        this.icon = '';
        this.theme = 'dark';
        this.position = '';
        this.text = '';
        this.template = null;
        this.top = 0;
        this.left = 0;
        this.role = 'tooltip';
    }
    set content(value) {
        if (typeof value === 'string') {
            this.text = value;
        }
        else if (value instanceof TemplateRef) {
            this.template = value;
        }
    }
    get classNames() {
        return {
            ['v-tooltip']: true,
            [`is-${this.position}`]: true
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: TooltipComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: TooltipComponent, selector: "vpg-tooltip", inputs: { id: "id", icon: "icon", theme: "theme", position: "position", content: "content", top: "top", left: "left" }, host: { properties: { "@fade": "true", "id": "this.id", "style.top.px": "this.top", "style.left.px": "this.left", "role": "this.role", "class": "this.classNames" } }, ngImport: i0, template: `
        <ng-container [ngTemplateOutlet]="template || default">
        </ng-container>

        <ng-template #default>
            <div class="v-tooltip-content is-{{theme}}">
                <vpg-icon-next *ngIf="icon" [icon]="icon">
                </vpg-icon-next>
                {{ text }}
            </div>
        </ng-template>
    `, isInline: true, dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "component", type: IconNextComponent, selector: "vpg-icon-next", inputs: ["icon", "size", "prefix", "screenReaderText"] }], animations: [fadeAnimation] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: TooltipComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vpg-tooltip',
                    template: `
        <ng-container [ngTemplateOutlet]="template || default">
        </ng-container>

        <ng-template #default>
            <div class="v-tooltip-content is-{{theme}}">
                <vpg-icon-next *ngIf="icon" [icon]="icon">
                </vpg-icon-next>
                {{ text }}
            </div>
        </ng-template>
    `,
                    animations: [fadeAnimation],
                    host: { '[@fade]': 'true' }
                }]
        }], propDecorators: { id: [{
                type: HostBinding,
                args: ['id']
            }, {
                type: Input
            }], icon: [{
                type: Input
            }], theme: [{
                type: Input
            }], position: [{
                type: Input
            }], content: [{
                type: Input
            }], top: [{
                type: HostBinding,
                args: ['style.top.px']
            }, {
                type: Input
            }], left: [{
                type: HostBinding,
                args: ['style.left.px']
            }, {
                type: Input
            }], role: [{
                type: HostBinding
            }], classNames: [{
                type: HostBinding,
                args: ['class']
            }] } });

class TooltipService {
    constructor(applicationRef, injector, document) {
        this.applicationRef = applicationRef;
        this.injector = injector;
        this.document = document;
    }
    createTooltip(component, properties) {
        const componentRef = createComponent(component, {
            environmentInjector: this.injector
        });
        Object.keys(properties).forEach((key) => {
            componentRef.instance[key] = properties[key];
        });
        this.applicationRef.attachView(componentRef.hostView);
        const domElem = componentRef.hostView
            .rootNodes[0];
        this.document.body.appendChild(domElem);
        return componentRef;
    }
    removeTooltip(componentRef) {
        this.applicationRef.detachView(componentRef.hostView);
        componentRef.destroy();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: TooltipService, deps: [{ token: i0.ApplicationRef }, { token: i0.EnvironmentInjector }, { token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: TooltipService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: TooltipService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i0.ApplicationRef }, { type: i0.EnvironmentInjector }, { type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }] });

class TooltipDirective {
    constructor(element, tooltipService, document) {
        this.element = element;
        this.tooltipService = tooltipService;
        this.document = document;
        this.tooltip = '';
        this.tooltipPosition = 'center';
        this.tooltipTheme = 'light';
        this.tooltipIcon = '';
        this.tooltipId = '';
        this.TOP = 20;
        this.tooltipRef = null;
    }
    showTooltip() {
        if (this.tooltipRef === null) {
            this.createTooltip();
        }
    }
    hideTooltip() {
        if (this.tooltipRef !== null) {
            this.tooltipService.removeTooltip(this.tooltipRef);
            this.tooltipRef = null;
        }
    }
    createTooltip() {
        const { right, bottom, left } = this.element.nativeElement.getBoundingClientRect();
        const isRTL = this.document.body.dir === 'rtl' || this.document.dir === 'rtl';
        const positions = {
            left: isRTL ? right : left,
            right: isRTL ? left : right,
            center: left + (right - left) / 2
        };
        this.tooltipRef = this.tooltipService.createTooltip(TooltipComponent, {
            id: this.tooltipId,
            icon: this.tooltipIcon,
            theme: this.tooltipTheme,
            position: this.tooltipPosition,
            top: bottom + this.TOP,
            left: positions[this.tooltipPosition],
            content: this.tooltip
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: TooltipDirective, deps: [{ token: i0.ElementRef }, { token: TooltipService }, { token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.9", type: TooltipDirective, selector: "[vpgTooltip]", inputs: { tooltip: ["vpgTooltip", "tooltip"], tooltipPosition: "tooltipPosition", tooltipTheme: "tooltipTheme", tooltipIcon: "tooltipIcon", tooltipId: "tooltipId" }, host: { listeners: { "mouseover": "showTooltip()", "focus": "showTooltip()", "mouseout": "hideTooltip()", "blur": "hideTooltip()" }, properties: { "attr.aria-describedby": "this.tooltipId" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: TooltipDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[vpgTooltip]'
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: TooltipService }, { type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }], propDecorators: { tooltip: [{
                type: Input,
                args: ['vpgTooltip']
            }], tooltipPosition: [{
                type: Input
            }], tooltipTheme: [{
                type: Input
            }], tooltipIcon: [{
                type: Input
            }], tooltipId: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['attr.aria-describedby']
            }], showTooltip: [{
                type: HostListener,
                args: ['mouseover']
            }, {
                type: HostListener,
                args: ['focus']
            }], hideTooltip: [{
                type: HostListener,
                args: ['mouseout']
            }, {
                type: HostListener,
                args: ['blur']
            }] } });

class TooltipModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: TooltipModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.9", ngImport: i0, type: TooltipModule, declarations: [TooltipComponent,
            TooltipDirective], imports: [CommonModule,
            IconModule], exports: [TooltipComponent,
            TooltipDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: TooltipModule, imports: [CommonModule,
            IconModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: TooltipModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        IconModule
                    ],
                    declarations: [
                        TooltipComponent,
                        TooltipDirective
                    ],
                    exports: [
                        TooltipComponent,
                        TooltipDirective
                    ]
                }]
        }] });

class VpgrooveModule {
    static forRoot(config) {
        return {
            ngModule: VpgrooveModule,
            providers: [
                ...(VpgTranslateModule.forRoot(config).providers || [])
            ]
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: VpgrooveModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.9", ngImport: i0, type: VpgrooveModule, declarations: [IconComponent,
            ButtonComponent,
            ImageComponent,
            DividerLineComponent,
            CardContentImgTxtComponent,
            CardHeaderComponent,
            CardActionComponent,
            CardComponent,
            CardContentPromotionComponent,
            CardContentComponent,
            ListComponent,
            ListItemComponent,
            OneColumnRowComponent,
            TwoColumnRowComponent,
            ThreeColumnRowComponent,
            FourColumnRowComponent,
            MainSideComponent,
            DropdownComponent,
            DropdownResultItemComponent,
            TruncatePipe,
            WelcomeMatComponent,
            ModalActionsComponent,
            ModalComponent,
            SafeUrlPipe,
            IdDirective,
            FallbackImageDirective,
            FocusTrapDirective], imports: [CommonModule,
            FormsModule,
            ReactiveFormsModule,
            DialogModule,
            ButtonModule,
            IconModule,
            ImageModule,
            ContainerModule,
            ControlModule,
            InputFieldModule,
            TextAreaModule,
            ContainerModule,
            DropdownModule,
            InlineLabelModule,
            CheckboxModule,
            RadioButtonModule,
            LinkModule,
            PopoverMenuModule,
            TabsModule,
            TooltipModule,
            BadgeModule,
            DateIconModule,
            HeaderModule,
            QuestionnaireModule,
            PaginationModule,
            SearchModule,
            HighlightPipe,
            InteractiveElementDirective,
            KeyboardNavigateDirective,
            InputSelectionRangeDirective,
            NumberPaginationModule,
            ListModule,
            ToastModule,
            ToggleSwitchModule,
            CardModule,
            SimpleProgressBarModule,
            CheckmarkModule,
            RepeatDirective,
            BackgroundCircleIconModule,
            ProgressRingModule,
            StarRatingModule,
            AccordionModule,
            DippingDotsLoaderModule,
            MediaThumbnailModule,
            ThreeDotsLoaderModule,
            SegmentedProgressBarModule,
            PebbleIconModule,
            FilterChipsGroupModule,
            BreadcrumbsModule,
            PhoneNumberModule,
            TableModule,
            CalendarModule, VpgTranslateModule], exports: [IconComponent,
            ButtonComponent,
            ImageComponent,
            DividerLineComponent,
            CardContentImgTxtComponent,
            CardHeaderComponent,
            CardComponent,
            CardContentPromotionComponent,
            ListComponent,
            ListItemComponent,
            OneColumnRowComponent,
            TwoColumnRowComponent,
            ThreeColumnRowComponent,
            FourColumnRowComponent,
            MainSideComponent,
            DropdownComponent,
            DropdownResultItemComponent,
            TruncatePipe,
            HighlightPipe,
            WelcomeMatComponent,
            ModalActionsComponent,
            ModalComponent,
            SafeUrlPipe,
            CardActionComponent,
            CardContentComponent,
            IdDirective,
            FocusTrapDirective,
            DialogModule,
            ButtonModule,
            IconModule,
            ImageModule,
            ContainerModule,
            ControlModule,
            InputFieldModule,
            TextAreaModule,
            InteractiveElementDirective,
            DropdownModule,
            InlineLabelModule,
            CheckboxModule,
            RadioButtonModule,
            LinkModule,
            PopoverMenuModule,
            TabsModule,
            TooltipModule,
            BadgeModule,
            SearchModule,
            HighlightPipe,
            InteractiveElementDirective,
            KeyboardNavigateDirective,
            InputSelectionRangeDirective,
            NumberPaginationModule,
            DateIconModule,
            HeaderModule,
            PaginationModule,
            ListModule,
            QuestionnaireModule,
            ToastModule,
            ToggleSwitchModule,
            CardModule,
            CheckmarkModule,
            FallbackImageDirective,
            SimpleProgressBarModule,
            RepeatDirective,
            BackgroundCircleIconModule,
            ProgressRingModule,
            PromotedContainerModule,
            StarRatingModule,
            AccordionModule,
            DippingDotsLoaderModule,
            MediaThumbnailModule,
            ThreeDotsLoaderModule,
            SegmentedProgressBarModule,
            PebbleIconModule,
            FilterChipsGroupModule,
            BreadcrumbsModule,
            PhoneNumberModule,
            TableModule,
            CalendarModule,
            VpgTranslateModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: VpgrooveModule, imports: [CommonModule,
            FormsModule,
            ReactiveFormsModule,
            DialogModule,
            ButtonModule,
            IconModule,
            ImageModule,
            ContainerModule,
            ControlModule,
            InputFieldModule,
            TextAreaModule,
            ContainerModule,
            DropdownModule,
            InlineLabelModule,
            CheckboxModule,
            RadioButtonModule,
            LinkModule,
            PopoverMenuModule,
            TabsModule,
            TooltipModule,
            BadgeModule,
            DateIconModule,
            HeaderModule,
            QuestionnaireModule,
            PaginationModule,
            SearchModule,
            NumberPaginationModule,
            ListModule,
            ToastModule,
            ToggleSwitchModule,
            CardModule,
            SimpleProgressBarModule,
            CheckmarkModule,
            BackgroundCircleIconModule,
            ProgressRingModule,
            StarRatingModule,
            AccordionModule,
            DippingDotsLoaderModule,
            MediaThumbnailModule,
            ThreeDotsLoaderModule,
            SegmentedProgressBarModule,
            PebbleIconModule,
            FilterChipsGroupModule,
            BreadcrumbsModule,
            PhoneNumberModule,
            TableModule,
            CalendarModule,
            VpgTranslateModule.forRoot(), DialogModule,
            ButtonModule,
            IconModule,
            ImageModule,
            ContainerModule,
            ControlModule,
            InputFieldModule,
            TextAreaModule,
            DropdownModule,
            InlineLabelModule,
            CheckboxModule,
            RadioButtonModule,
            LinkModule,
            PopoverMenuModule,
            TabsModule,
            TooltipModule,
            BadgeModule,
            SearchModule,
            NumberPaginationModule,
            DateIconModule,
            HeaderModule,
            PaginationModule,
            ListModule,
            QuestionnaireModule,
            ToastModule,
            ToggleSwitchModule,
            CardModule,
            CheckmarkModule,
            SimpleProgressBarModule,
            BackgroundCircleIconModule,
            ProgressRingModule,
            PromotedContainerModule,
            StarRatingModule,
            AccordionModule,
            DippingDotsLoaderModule,
            MediaThumbnailModule,
            ThreeDotsLoaderModule,
            SegmentedProgressBarModule,
            PebbleIconModule,
            FilterChipsGroupModule,
            BreadcrumbsModule,
            PhoneNumberModule,
            TableModule,
            CalendarModule,
            VpgTranslateModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: VpgrooveModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule,
                        ReactiveFormsModule,
                        DialogModule,
                        ButtonModule,
                        IconModule,
                        ImageModule,
                        ContainerModule,
                        ControlModule,
                        InputFieldModule,
                        TextAreaModule,
                        ContainerModule,
                        DropdownModule,
                        InlineLabelModule,
                        CheckboxModule,
                        RadioButtonModule,
                        LinkModule,
                        PopoverMenuModule,
                        TabsModule,
                        TooltipModule,
                        BadgeModule,
                        DateIconModule,
                        HeaderModule,
                        QuestionnaireModule,
                        PaginationModule,
                        SearchModule,
                        HighlightPipe,
                        InteractiveElementDirective,
                        KeyboardNavigateDirective,
                        InputSelectionRangeDirective,
                        NumberPaginationModule,
                        ListModule,
                        ToastModule,
                        ToggleSwitchModule,
                        CardModule,
                        SimpleProgressBarModule,
                        CheckmarkModule,
                        RepeatDirective,
                        BackgroundCircleIconModule,
                        ProgressRingModule,
                        StarRatingModule,
                        AccordionModule,
                        DippingDotsLoaderModule,
                        MediaThumbnailModule,
                        ThreeDotsLoaderModule,
                        SegmentedProgressBarModule,
                        PebbleIconModule,
                        FilterChipsGroupModule,
                        BreadcrumbsModule,
                        PhoneNumberModule,
                        TableModule,
                        CalendarModule,
                        VpgTranslateModule.forRoot()
                    ],
                    declarations: [
                        IconComponent,
                        ButtonComponent,
                        ImageComponent,
                        DividerLineComponent,
                        CardContentImgTxtComponent,
                        CardHeaderComponent,
                        CardActionComponent,
                        CardComponent,
                        CardContentPromotionComponent,
                        CardContentComponent,
                        ListComponent,
                        ListItemComponent,
                        OneColumnRowComponent,
                        TwoColumnRowComponent,
                        ThreeColumnRowComponent,
                        FourColumnRowComponent,
                        MainSideComponent,
                        DropdownComponent,
                        DropdownResultItemComponent,
                        TruncatePipe,
                        WelcomeMatComponent,
                        ModalActionsComponent,
                        ModalComponent,
                        SafeUrlPipe,
                        IdDirective,
                        FallbackImageDirective,
                        FocusTrapDirective
                    ],
                    exports: [
                        IconComponent,
                        ButtonComponent,
                        ImageComponent,
                        DividerLineComponent,
                        CardContentImgTxtComponent,
                        CardHeaderComponent,
                        CardComponent,
                        CardContentPromotionComponent,
                        ListComponent,
                        ListItemComponent,
                        OneColumnRowComponent,
                        TwoColumnRowComponent,
                        ThreeColumnRowComponent,
                        FourColumnRowComponent,
                        MainSideComponent,
                        DropdownComponent,
                        DropdownResultItemComponent,
                        TruncatePipe,
                        HighlightPipe,
                        WelcomeMatComponent,
                        ModalActionsComponent,
                        ModalComponent,
                        SafeUrlPipe,
                        CardActionComponent,
                        CardContentComponent,
                        IdDirective,
                        FocusTrapDirective,
                        DialogModule,
                        ButtonModule,
                        IconModule,
                        ImageModule,
                        ContainerModule,
                        ControlModule,
                        InputFieldModule,
                        TextAreaModule,
                        InteractiveElementDirective,
                        DropdownModule,
                        InlineLabelModule,
                        CheckboxModule,
                        RadioButtonModule,
                        LinkModule,
                        PopoverMenuModule,
                        TabsModule,
                        TooltipModule,
                        BadgeModule,
                        SearchModule,
                        HighlightPipe,
                        InteractiveElementDirective,
                        KeyboardNavigateDirective,
                        InputSelectionRangeDirective,
                        NumberPaginationModule,
                        DateIconModule,
                        HeaderModule,
                        PaginationModule,
                        ListModule,
                        QuestionnaireModule,
                        ToastModule,
                        ToggleSwitchModule,
                        CardModule,
                        CheckmarkModule,
                        FallbackImageDirective,
                        SimpleProgressBarModule,
                        RepeatDirective,
                        BackgroundCircleIconModule,
                        ProgressRingModule,
                        PromotedContainerModule,
                        StarRatingModule,
                        AccordionModule,
                        DippingDotsLoaderModule,
                        MediaThumbnailModule,
                        ThreeDotsLoaderModule,
                        SegmentedProgressBarModule,
                        PebbleIconModule,
                        FilterChipsGroupModule,
                        BreadcrumbsModule,
                        PhoneNumberModule,
                        TableModule,
                        CalendarModule,
                        VpgTranslateModule
                    ]
                }]
        }] });

class ListItemImageComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ListItemImageComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.9", type: ListItemImageComponent, selector: "vpg-list-item-image", host: { classAttribute: "v-list-item-image" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: ListItemImageComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'vpg-list-item-image',
                    template: '<ng-content></ng-content>',
                    host: {
                        'class': 'v-list-item-image'
                    }
                }]
        }] });

class VpgTranslatePipe {
    constructor(translateService) {
        this.translateService = translateService;
    }
    transform(value, args) {
        return this.translateService.translate(value, args);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: VpgTranslatePipe, deps: [{ token: VpgTranslateService }], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.3.9", ngImport: i0, type: VpgTranslatePipe, name: "vpgTranslate" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.9", ngImport: i0, type: VpgTranslatePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'vpgTranslate'
                }]
        }], ctorParameters: () => [{ type: VpgTranslateService }] });

/*
 * Public API Surface of ui
 */
// components

/**
 * Generated bundle index. Do not edit.
 */

export { AbstractFormComponent, AccordionComponent, AccordionItemContentComponent, AccordionModule, BackgroundCircleIconComponent, BackgroundCircleIconModule, BadgeComponent, BadgeModule, BreadcrumbsComponent, BreadcrumbsModule, BtnClassName, ButtonAlignment, ButtonBaseDirective, ButtonComponent, ButtonDirective, ButtonIconDirective, ButtonModule, ButtonSize, ButtonType, CALENDAR_COMPONENT, CalendarComponent, CalendarModule, CalendarNavigationDirective, CalendarRangeAriaLabelPipe, CalendarService, CardActionComponent, CardActionType, CardComponent, CardContentComponent, CardContentImgTxtComponent, CardContentPromotionComponent, CardContentType, CardHeaderComponent, CardModule, CardNextActionsComponent, CardNextComponent, CardNextContentComponent, CardNextHeaderComponent, CardNextImageComponent, CardNextLinkHeaderComponent, CardNextSubtitleComponent, CardNextTextComponent, CardNextTitleComponent, CheckboxComponent, CheckboxGroupComponent, CheckboxModule, CheckmarkComponent, CheckmarkModule, CheckmarkType, ContainerComponent, ContainerDirective, ContainerModule, ControlEmptyListComponent, ControlErrorComponent, ControlErrorsComponent, ControlFooterComponent, ControlHintComponent, ControlListComponent, ControlModule, ControlPrefixComponent, ControlSuffixComponent, DEFAULT_THEME, DROPDOWN_TOKEN, DateIconComponent, DateIconModule, DialogActionsComponent, DialogBodyComponent, DialogComponent, DialogFocusTrapDirective, DialogHeaderComponent, DialogModule, DialogOverlayComponent, DialogRef, DialogScrollShadowDirective, DialogService, DialogStack, DippingDotsLoader, DippingDotsLoaderModule, DividerLineComponent, DropdownComponent, DropdownListOptionDimensionsDirective, DropdownListPopoutDirective, DropdownModule, DropdownNextComponent, DropdownResultItemComponent, FallbackImageDirective, FilterChipComponent, FilterChipsGroupComponent, FilterChipsGroupModule, FocusTrapDirective, FormatValuePipe, FourColumnRowComponent, HeaderActionsComponent, HeaderComponent, HeaderModule, HighlightPipe, IconClassName, IconComponent, IconModule, IconName, IconNextComponent, IconService, IdDirective, ImageComponent, ImageDirective, ImageModule, ImageType, InlineLabelComponent, InlineLabelModule, InputFieldComponent, InputFieldModule, InputSelectionRangeDirective, InteractiveElementDirective, KeyCode, KeyboardNavigateDirective, LabelType, LinkDirective, LinkModule, ListComponent, ListItemActionComponent, ListItemActionDirective, ListItemComponent, ListItemContentComponent, ListItemContentDirective, ListItemHeadingComponent, ListItemHeadingDirective, ListItemImageComponent, ListItemImageDirective, ListItemNextComponent, ListItemNumberComponent, ListItemNumberDirective, ListModule, ListNextComponent, ListboxSearchDirective, MainSideComponent, MediaThumbnailCategoriesComponent, MediaThumbnailCategoryDirective, MediaThumbnailComponent, MediaThumbnailDetailsComponent, MediaThumbnailImageComponent, MediaThumbnailModule, MediaThumbnailProgressComponent, ModalActionType, ModalActionsComponent, ModalComponent, ModalType, NationalNumberPipe, NumberPaginationComponent, NumberPaginationModule, OneColumnRowComponent, PaginationComponent, PaginationModule, PebbleIconComponent, PebbleIconModule, PhoneNumberComponent, PhoneNumberModule, PhoneUtilService, PopoverComponent, PopoverMenuComponent, PopoverMenuItemComponent, PopoverMenuModule, PopoverMenuTriggerDirective, ProgressRingModule, ProgressRingNextComponent, ProgressRingType, PromotedContainerComponent, PromotedContainerModule, QuestionnaireComponent, QuestionnaireImagesComponent, QuestionnaireModule, QuestionnaireType, RadioButtonComponent, RadioButtonGroupComponent, RadioButtonModule, RepeatDirective, SafeUrlPipe, SearchComponent, SearchModule, SegmentedProgressBarComponent, SegmentedProgressBarDescription, SegmentedProgressBarIndicatorComponent, SegmentedProgressBarModule, SegmentedProgressBarNumberComponent, SegmentedProgressBarSegmentComponent, SegmentedProgressBarStepComponent, SegmentedProgressBarTitle, SelectionState, SimpleHeaderComponent, SimpleProgressBarComponent, SimpleProgressBarModule, SimpleProgressBarType, SmallProgressRingType, StarRatingComponent, StarRatingModule, StyledHeaderComponent, Tab, TableColumnComponent, TableComponent, TableModule, Tabs, TabsModule, TextAreaComponent, TextAreaModule, ThemeManagerService, ThreeColumnRowComponent, ThreeDotsLoaderComponent, ThreeDotsLoaderModule, ToastComponent, ToastModule, ToastService, ToasterComponent, ToggleSwitchComponent, ToggleSwitchModule, TooltipComponent, TooltipDirective, TooltipModule, TooltipService, TranslationService, TruncatePipe, TwoColumnRowComponent, VPG_DIALOG_OPTIONS, VPG_DIALOG_OVERLAY, VPG_TRANSLATE_CONFIG, VPGrooveDefaultColorHexValues, VPGrooveSpacingShirtSize, VPGrooveSpacingVariable, VPGrooveTextStyleNames, VpgModalService, VpgTranslateModule, VpgTranslatePipe, VpgTranslateService, VpgrooveModule, WelcomeMatComponent, phoneNumberFormatValidator, slideInFadeOut, valueAccessor };
